import { useState, useMemo, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import {
  useTable, useALCasas, useALReservas, useALLimpezas, useALManutencao, useALTarefas, useALDividas,
  useKairosNotificacoes,
  useVJEventos, useVJDespesas, useVJTarefas, useVJPerfil, useVJEquipa, useVJAtribuicoes,
  useHW3DClientes, useHW3DProjetos, useHW3DDespesas, useHW3DTarefas,
  useKaiClientes, useKaiProjetos, useKaiRetainers, useKaiDespesas, useKaiCalendario,
  usePBEventos,
  useHBTransacoes, useHBObjetivos, useHBWishlist, useHBPagamentos, useHBTarefas,
  useHBPatrimonios, useHBDividas,
  useHoldingsCalendario,
  addLogDB,
} from "./useSupabase";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine
} from "recharts";

/* ─── LOGO ─────────────────────────────────────────────────────────────────── */
const LOGO_SRC = "/logo-k.png";

/* ─── DESIGN SYSTEM 2026 ─────────────────────────────────────────────────── */
// Palette: Deep obsidian base · Electric accents · Warm neutrals
// Concept: "Command Interface" — precision tool aesthetic, not corporate dashboard
const T = {
  // Backgrounds — layered depth
  bg:       "#050810",
  surface:  "#0a0d18",
  panel:    "rgba(255,255,255,0.032)",
  panel2:   "rgba(255,255,255,0.055)",
  overlay:  "rgba(5,8,16,0.92)",
  // Borders — subtle glass effect
  border:   "rgba(255,255,255,0.075)",
  borderHi: "rgba(255,255,255,0.16)",
  borderFocus: "rgba(59,157,232,0.5)",
  // Text
  text:     "#e2e8f8",
  textSub:  "#94a3b8",
  muted:    "#4a5568",
  dim:      "#1a2236",
  // Brand accent spectrum
  blue:     "#1a6bbf",
  blueL:    "#3b9de8",
  blueDark: "#0d3565",
  cyan:     "#22d3ee",
  // Semantic colours
  green:    "#10b981",
  greenL:   "#34d399",
  amber:    "#f59e0b",
  amberL:   "#fbbf24",
  red:      "#ef4444",
  redL:     "#f87171",
  // Brand palette
  purple:   "#8b5cf6",
  purpleL:  "#a78bfa",
  teal:     "#14b8a6",
  tealL:    "#2dd4bf",
  pink:     "#ec4899",
  pinkL:    "#f472b6",
  gold:     "#d97706",
  goldL:    "#fbbf24",
};

const CO = {
  holdings:   T.blueL,
  hw3d:       T.teal,
  kai_ai:     T.blueL,
  quinta:     T.amber,
  photobooth: T.pink,
  vj:         T.purple,
  media:      T.red,
  influencer: T.pink,
  kids:       T.green,
  homebase:   "#6366f1",
};

/* ─── THEME SYSTEM ──────────────────────────────────────────────────────────── */
// Themes: dark (default) and light
const THEMES = {
  dark: {
    bg:"#050810", surface:"#0a0d18",
    panel:"rgba(255,255,255,0.032)", panel2:"rgba(255,255,255,0.055)",
    border:"rgba(255,255,255,0.075)", borderHi:"rgba(255,255,255,0.16)",
    borderFocus:"rgba(59,157,232,0.5)",
    text:"#e2e8f8", textSub:"#94a3b8", muted:"#4a5568", dim:"#1a2236",
    overlay:"rgba(5,8,16,0.92)",
    inputBg:"rgba(255,255,255,0.04)",
  },
  light: {
    bg:"#f1f5f9", surface:"#ffffff",
    panel:"rgba(0,0,0,0.03)", panel2:"rgba(0,0,0,0.06)",
    border:"rgba(0,0,0,0.1)", borderHi:"rgba(0,0,0,0.2)",
    borderFocus:"rgba(59,130,246,0.5)",
    text:"#0f172a", textSub:"#475569", muted:"#94a3b8", dim:"#e2e8f0",
    overlay:"rgba(241,245,249,0.92)",
    inputBg:"rgba(0,0,0,0.04)",
  },
};

// Current theme — read from localStorage, default dark
let _themeKey: "dark"|"light" = (localStorage.getItem("kairos_theme") as any) || "dark";
// T is now a mutable reference — components read from it at render time
// We use a React context approach via re-render trigger

/* ─── i18n ───────────────────────────────────────────────────────────────────── */
const TRANSLATIONS: Record<string, Record<string,string>> = {
  pt: {
    overview:"Overview", reservas:"Reservas", financeiro:"Financeiro",
    previsoes:"Previsões", limpezas:"Limpezas", tarefas:"Tarefas",
    despesas:"Despesas", historico:"Histórico", log:"Log",
    manutencao:"Manutenção", calendario:"Calendário",
    entrar:"Entrar", sair:"Terminar Sessão", conta:"A minha conta",
    configuracoes:"Configurações", ajuda:"Ajuda & Suporte",
    feedback:"Enviar Feedback", subscricao:"Subscrição",
    nova_reserva:"Nova Reserva", novo_evento:"Novo Evento",
    guardar:"Guardar", cancelar:"Cancelar", editar:"Editar",
    adicionar:"Adicionar", concluido:"Concluído", pendente:"Pendente",
    hoje:"Hoje", amanha:"Amanhã", urgente:"URGENTE", aviso:"AVISO",
    sem_alertas:"Tudo em dia — sem alertas",
    homebase:"Home Base", financas:"Finanças", objetivos:"Objectivos",
    wishlist:"Wishlist", pagamentos:"Pagamentos",
  },
  en: {
    overview:"Overview", reservas:"Bookings", financeiro:"Finance",
    previsoes:"Forecasts", limpezas:"Cleaning", tarefas:"Tasks",
    despesas:"Expenses", historico:"History", log:"Log",
    manutencao:"Maintenance", calendario:"Calendar",
    entrar:"Sign In", sair:"Sign Out", conta:"My Account",
    configuracoes:"Settings", ajuda:"Help & Support",
    feedback:"Send Feedback", subscricao:"Subscription",
    nova_reserva:"New Booking", novo_evento:"New Event",
    guardar:"Save", cancelar:"Cancel", editar:"Edit",
    adicionar:"Add", concluido:"Completed", pendente:"Pending",
    hoje:"Today", amanha:"Tomorrow", urgente:"URGENT", aviso:"WARNING",
    sem_alertas:"All clear — no alerts",
    homebase:"Home Base", financas:"Finances", objetivos:"Goals",
    wishlist:"Wishlist", pagamentos:"Payments",
  },
  es: {
    overview:"Resumen", reservas:"Reservas", financeiro:"Finanzas",
    previsoes:"Previsiones", limpezas:"Limpieza", tarefas:"Tareas",
    despesas:"Gastos", historico:"Historial", log:"Registro",
    manutencao:"Mantenimiento", calendario:"Calendario",
    entrar:"Entrar", sair:"Cerrar sesión", conta:"Mi cuenta",
    configuracoes:"Configuración", ajuda:"Ayuda y soporte",
    feedback:"Enviar comentarios", subscricao:"Suscripción",
    nova_reserva:"Nueva reserva", novo_evento:"Nuevo evento",
    guardar:"Guardar", cancelar:"Cancelar", editar:"Editar",
    adicionar:"Añadir", concluido:"Completado", pendente:"Pendiente",
    hoje:"Hoy", amanha:"Mañana", urgente:"URGENTE", aviso:"AVISO",
    sem_alertas:"Todo al día — sin alertas",
    homebase:"Base Personal", financas:"Finanzas", objetivos:"Objetivos",
    wishlist:"Lista de deseos", pagamentos:"Pagos",
  },
};

function useT(lang: string) {
  return (key: string) => TRANSLATIONS[lang]?.[key] || TRANSLATIONS.pt[key] || key;
}

/* ─── NAVIGATION STRUCTURE ──────────────────────────────────────────────────── */

/* ─── Tipos de negócio (onboarding) ─────────────────────────────────────── */
const BUSINESS_TYPES = [
  { id:"al",      label:"Alojamento Local",    icon:"🏠", desc:"Gestão de casas de férias" },
  { id:"eventos", label:"Eventos & DJ",         icon:"🎤", desc:"Eventos, casamentos, DJ" },
  { id:"studio3d",label:"Studio 3D / Design",  icon:"🧊", desc:"Renders e visualização 3D" },
  { id:"fotografia",label:"Fotografia",         icon:"📸", desc:"Fotografia e PhotoBooth" },
  { id:"ai",      label:"Consultoria / AI",     icon:"🤖", desc:"Automações e IA" },
  { id:"media",   label:"Media / Conteúdo",     icon:"📺", desc:"Canais e conteúdo digital" },
];

const BIZ_TO_COMPANY: Record<string,string[]> = {
  al:       ["quinta"],
  eventos:  ["vj"],
  studio3d: ["hw3d"],
  fotografia:["photobooth"],
  ai:       ["kai_ai"],
  media:    ["media","influencer","kids"],
};

const COMPANIES = [
  { id:"homebase",   label:"Home Base",             icon:"🏡", color:"#6366f1",
    menu:[
      { id:"overview",   label:"Dashboard Pessoal",  icon:"▦" },
      { id:"financas",   label:"Finanças",           icon:"💰" },
      { id:"poupancas",  label:"Poupanças & Dívidas", icon:"🏦" },
      { id:"objetivos",  label:"Objectivos",         icon:"🎯" },
      { id:"wishlist",   label:"Wishlist",           icon:"🛒" },
      { id:"pagamentos", label:"Pagamentos",         icon:"💳" },
      { id:"tarefas",    label:"Tarefas Pessoais",   icon:"✅" },
    ]
  },
  { id:"holdings",   label:"Kairos Holdings",      icon:"⚡", color:CO.holdings,
    menu:[
      { id:"overview",   label:"Overview",           icon:"▦" },
      { id:"calendario", label:"Calendário",         icon:"📅" },
      { id:"fin_global", label:"Financeiro Global",  icon:"◈" },
      { id:"tesouraria", label:"Tesouraria",         icon:"🏦" },
      { id:"logs",       label:"Log Global",         icon:"📋" },
    ]
  },
  { id:"hw3d",       label:"Homework 3D Studio",   icon:"🧊", color:CO.hw3d,
    menu:[
      { id:"overview",   label:"Overview",           icon:"▦" },
      { id:"clientes",   label:"Clientes (CRM)",     icon:"👥" },
      { id:"projetos",   label:"Projetos",           icon:"◉" },
      { id:"funil",      label:"Funil de Projetos",  icon:"🔄" },
      { id:"fin",        label:"Financeiro",         icon:"◈" },
      { id:"despesas",   label:"Despesas",           icon:"💸" },
      { id:"kpi",        label:"KPIs & Análise",     icon:"📊" },
      { id:"tarefas",    label:"Tarefas",            icon:"✅" },
      { id:"fiverr",     label:"Fiverr",             icon:"🛒" },
      { id:"assets",     label:"Assets 3D Passivos", icon:"📦" },
      { id:"logs",       label:"Log",                icon:"📋" },
    ]
  },
  { id:"quinta",     label:"Quinta da Regada",      icon:"🏠", color:CO.quinta,
    menu:[
      { id:"overview",   label:"Overview",           icon:"▦" },
      { id:"reservas",   label:"Reservas",           icon:"📅" },
      { id:"fin",        label:"Gestão Financeira",  icon:"◈" },
      { id:"previsoes",  label:"Previsões",          icon:"📈" },
      { id:"limpezas",   label:"Limpezas",           icon:"🧹" },
      { id:"manutencao", label:"Manutenção",         icon:"🔧" },
      { id:"casas",      label:"Alojamentos",        icon:"🏘️" },
      { id:"tarefas",    label:"Tarefas",            icon:"✅" },
      { id:"logs",       label:"Log",                icon:"📋" },
    ]
  },
  { id:"vj",         label:"VJ Casamentos",         icon:"🎤", color:CO.vj,
    menu:[
      { id:"overview",    label:"Overview",          icon:"▦" },
      { id:"calendario",  label:"Calendário",        icon:"📅" },
      { id:"equipa",      label:"Equipa",            icon:"👥" },
      { id:"historico",   label:"Histórico",         icon:"📚" },
      { id:"tarefas",     label:"Tarefas",           icon:"✅" },
      { id:"despesas",    label:"Despesas",          icon:"💸" },
      { id:"logs",        label:"Log",               icon:"📋" },
    ]
  },
  { id:"kai_ai",     label:"Kairos AI",             icon:"🤖", color:CO.kai_ai,
    menu:[
      { id:"overview",   label:"Overview",           icon:"▦" },
      { id:"clientes",   label:"Clientes (CRM)",     icon:"👥" },
      { id:"projetos",   label:"Projetos",           icon:"◉" },
      { id:"retainers",  label:"Retainers",          icon:"🔄" },
      { id:"funil",      label:"Funil de Projetos",  icon:"📊" },
      { id:"despesas",   label:"Despesas",           icon:"💸" },
      { id:"kpi",        label:"KPIs & Análise",     icon:"📊" },
      { id:"tarefas",    label:"Tarefas",            icon:"✅" },
      { id:"calendario", label:"Calendário",         icon:"📅" },
      { id:"logs",       label:"Log",                icon:"📋" },
    ]
  },
  { id:"photobooth", label:"321 PhotoBooth",        icon:"📸", color:CO.photobooth,
    menu:[
      { id:"overview",   label:"Overview",           icon:"▦" },
      { id:"tarefas",    label:"Tarefas",            icon:"✅" },
      { id:"despesas",   label:"Despesas",           icon:"💸" },
      { id:"logs",       label:"Log",                icon:"📋" },
    ]
  },
  { id:"media",      label:"Kairos Media",          icon:"📺", color:CO.media,
    menu:[
      { id:"overview",   label:"Overview",           icon:"▦" },
      { id:"series",     label:"↳ Canal Séries BR",  icon:"🎬", sub:true },
      { id:"humor",      label:"↳ Canal Humor BR",   icon:"😂", sub:true },
      { id:"roadmap",    label:"Roadmap",            icon:"⏱" },
    ]
  },
  { id:"influencer", label:"Kairos Influencer",     icon:"🎵", color:CO.influencer,
    menu:[
      { id:"overview",   label:"Overview",           icon:"▦" },
      { id:"artista1",   label:"↳ Artista Principal",icon:"🎤", sub:true },
      { id:"roadmap",    label:"Roadmap",            icon:"⏱" },
    ]
  },
  { id:"kids",       label:"Kairos Kids",           icon:"👶", color:CO.kids,
    menu:[
      { id:"overview",   label:"Overview",           icon:"▦" },
      { id:"etsy",       label:"↳ Loja Etsy",        icon:"🛒", sub:true },
      { id:"kdp",        label:"↳ Amazon KDP",       icon:"📚", sub:true },
      { id:"roadmap",    label:"Roadmap",            icon:"⏱" },
    ]
  },
];
/* ─── DATA ──────────────────────────────────────────────────────────────────── */

// HW3D — serviços disponíveis (base do CRM)
const HW3D_SERVICES = [
  "Virtual Staging","Material/Item Change","Planta 2D Mobilada",
  "Axonométrico 3D","Render Interior","Render Exterior",
  "Multi-ângulo","Animação/Walkthrough","Upscale","Modelo 3D",
];

// Projetos HW3D (legado - substituído por HW3D_PIPELINE_INIT)
// const HW3D_PROJECTS_INIT = [];

// Fiverr gigs com serviço e contagem
const FIVERR_GIGS_INIT = [
  { id:1, title:"Virtual Staging Interior",  servico:"Virtual Staging",     cat:"CAT1", preco:"€32–60",  status:"ativo",    orders:0, rating:null, delivery:"<24h" },
  { id:2, title:"Material/Item Change",      servico:"Material/Item Change", cat:"CAT1", preco:"€28–46",  status:"rascunho", orders:0, rating:null, delivery:"<24h" },
  { id:3, title:"Planta 2D Mobilada",        servico:"Planta 2D Mobilada",  cat:"CAT2", preco:"€32–60",  status:"rascunho", orders:0, rating:null, delivery:"<48h" },
  { id:4, title:"Render Interior",           servico:"Render Interior",      cat:"CAT3", preco:"€138–325",status:"rascunho", orders:0, rating:null, delivery:"<72h" },
  { id:5, title:"Render Exterior",           servico:"Render Exterior",      cat:"CAT3", preco:"€185–460",status:"rascunho", orders:0, rating:null, delivery:"<72h" },
];

// Kairos AI — pacotes disponíveis
const KAI_PACOTES = [
  { id:"starter",  label:"Starter Automation", setup:1500, mrr:300,  cor:T.teal   },
  { id:"growth",   label:"Growth Package",     setup:3500, mrr:700,  cor:T.blueL  },
  { id:"full",     label:"Full AI Agency",     setup:7500, mrr:1500, cor:T.purple },
  { id:"macmini",  label:"Mini-Mac Worker",    setup:2000, mrr:400,  cor:T.amber  },
  { id:"hora",     label:"Consultoria/hora",   setup:0,    mrr:80,   cor:T.muted  },
];

// Projetos KAI (setup fees)
const KAI_PROJ_INIT: any[] = [];

// Retainers KAI (MRR recorrente)
const KAI_RET_INIT = [];

// ── HW3D CLIENTES ──
const HW3D_CLIENTES_INIT: any[] = [];

// ── HW3D PIPELINE ──
const HW3D_PIPELINE_STAGES = ["Briefing","Orçamentação","Negociação","Adjudicado","Em Produção","Concluído"];
const HW3D_PIPELINE_INIT: any[] = [];

// ── HW3D TAREFAS ──
const HW3D_TAREFAS_INIT:any[] = [];

// ── HOLDINGS TESOURARIA ──
const TESOURARIA_INIT = {
  saldoCaixa: 0,
  empresas: {
    hw3d:    { nome:"Homework 3D Studio", saldo:0, receitas:[], despesas:[], deve:[] },
    kai_ai:  { nome:"Kairos AI",          saldo:0, receitas:[], despesas:[], deve:[] },
    vj:      { nome:"VJ Casamentos",      saldo:0, receitas:[], despesas:[], deve:[] },
    quinta:  { nome:"Quinta da Regada",   saldo:0, receitas:[], despesas:[], deve:[] },
    pb:      { nome:"321 PhotoBooth",     saldo:0, receitas:[], despesas:[], deve:[] },
  },
  ordenados: { bruto:0, ss:0, irc:0, iva:0, valorOrdenados:0, valorOrdenadosUsado:0 },
  impostos: [
    { tipo:"SS", valor:0, data:"", pago:false },
    { tipo:"IRC", valor:0, data:"", pago:false },
    { tipo:"IVA", valor:0, data:"", pago:false },
  ],
};

// VJ Events
const VJ_EVENTS_INIT: any[] = [];
const VJ_TAREFAS_INIT: any[] = [];
const VJ_DESPESAS_INIT: any[] = [];

// AL Reservas — CASAS configurável por utilizador
const CASAS_ICONS = ["🏠","🏡","🌻","🏘️","🛖","🏰","🌊","⛰️","🌲","🏔️"];

function getCasasDefault(): any[] {
  return [
    { id:"catequese", label:"Casa Catequese", icon:"🏡", maxHospedes:2, maxPessoas:2, precoPorNoite:100 },
    { id:"girassol",  label:"Casa Girassol",  icon:"🌻", maxHospedes:4, maxPessoas:4, precoPorNoite:140 },
    { id:"avos",      label:"Casa dos Avós",  icon:"🏠", maxHospedes:6, maxPessoas:6, precoPorNoite:240 },
  ];
}
function loadCasas(): any[] {
  try {
    const saved = localStorage.getItem("kairos_casas");
    return saved ? JSON.parse(saved) : getCasasDefault();
  } catch { return getCasasDefault(); }
}
const CASAS: any[] = loadCasas();
const COMISSAO_PLAT = { Airbnb:0.03, Booking:0.15, Direto:0 };
const IVA_TAXA = 0.06;

const AL_RES_INIT: any[] = [];
const AL_DESPESAS_INIT: any[] = [];
const AL_TAREFAS_INIT: any[] = [];

// Projeções consolidadas (do dossier)
/* ─── DEMO DATA ─────────────────────────────────────────────────────────────── */
// Dados fictícios para a conta demo — Atlantic Group
const APP_VERSION = "8.7";

// Nomes fictícios das empresas demo — identificam o tipo de negócio
const DEMO_COMPANY_NAMES: Record<string, string> = {
  holdings:    "Atlantic Group",
  homebase:    "Home Base",
  quinta:      "Casa Verde — Alojamento Local",
  vj:          "DJ EventSound — Casamentos & Eventos",
  hw3d:        "RenderStudio 3D — Visualização Arquitectural",
  kai_ai:      "FlowAI — Automação & Inteligência Artificial",
  photobooth:  "SnapBooth — Fotografia & PhotoBooth",
};

// Holding name da conta demo
const DEMO_HOLDING_NAME = "Atlantic Group";
const DEMO_EMAIL = "demo@kairosholding.com";

const DEMO_PB_EVENTS = [
  { id:1, data:"2026-05-03", local:"Centro Comercial NorteShopping", horario:"11:00", valor:350, status:"confirmado", notas:"Festa aniversário 5 anos" },
  { id:2, data:"2026-05-24", local:"Quinta do Lago — Casamento Ferreira", horario:"17:00", valor:450, status:"confirmado", notas:"Casamento — 4h" },
  { id:3, data:"2026-06-15", local:"Câmara Municipal de Matosinhos", horario:"09:00", valor:280, status:"pendente", notas:"Evento corporativo" },
];

// Helper — ler comissão VJ do localStorage (usado onde não há acesso ao state)
function getVJComissao(): number {
  try { return JSON.parse(localStorage.getItem("kairos_vj_config")||"{}").comissaoColega||25; }
  catch { return 25; }
}


const DEMO_AL_RES = [
  { id:1, guest:"Marie Dupont", ci:"2026-05-03", co:"2026-05-07", noites:4, plat:"Airbnb", status:"confirmada", valor:480, casa:"solar", hospedes:2, criancas:false, animais:false, ciHora:"15:00", coHora:"11:00", notas:"Lua de mel" },
  { id:2, guest:"🧹 Limpeza", ci:"2026-05-07", co:"2026-05-07", noites:0, plat:"auto", status:"limpeza", valor:0, casa:"solar", hospedes:0, criancas:false, animais:false, notas:"" },
  { id:3, guest:"Thomas Müller", ci:"2026-05-10", co:"2026-05-14", noites:4, plat:"Booking", status:"confirmada", valor:640, casa:"vale", hospedes:4, criancas:true, animais:false, ciHora:"16:00", coHora:"10:00", notas:"Família com criança pequena" },
  { id:4, guest:"Inês & Tiago Costa", ci:"2026-05-18", co:"2026-05-22", noites:4, plat:"Direto", status:"confirmada", valor:360, casa:"ribeira", hospedes:2, criancas:false, animais:false, ciHora:"14:00", coHora:"11:00", notas:"" },
  { id:5, guest:"🧹 Limpeza", ci:"2026-05-22", co:"2026-05-22", noites:0, plat:"auto", status:"limpeza", valor:0, casa:"ribeira", hospedes:0, criancas:false, animais:false, notas:"" },
  { id:6, guest:"Sarah O'Brien", ci:"2026-06-01", co:"2026-06-06", noites:5, plat:"Airbnb", status:"pendente", valor:600, casa:"solar", hospedes:2, criancas:false, animais:true, ciHora:"15:00", coHora:"11:00", notas:"Cão pequeno" },
];

const DEMO_VJ_EVENTS = [
  { id:1, data:"2026-05-23", local:"Quinta das Amendoeiras, Viseu", horario:"18:00", valor:1800, status:"confirmado", noiteFora:false, colega:true, checklist:{setlist:true,equip:false} },
  { id:2, data:"2026-06-14", local:"Herdade do Pinheiro, Setúbal", horario:"17:30", valor:2200, status:"confirmado", noiteFora:true, colega:true, checklist:{setlist:false,equip:false} },
  { id:3, data:"2026-07-18", local:"Palácio da Bacalhoa, Azeitão", horario:"19:00", valor:1600, status:"pendente", noiteFora:false, colega:false, checklist:{setlist:false,equip:false} },
  { id:4, data:"2026-09-05", local:"Casa de Sezim, Guimarães", horario:"18:30", valor:1900, status:"pendente", noiteFora:true, colega:true, checklist:{setlist:false,equip:false} },
];

const DEMO_HW3D_CLIENTES = [
  { id:1, nome:"Margarida Fonseca", empresa:"MF Arquitectura", email:"m.fonseca@mfarq.pt", nif:"267891234", tipo:"Arquitetura", origem:"LinkedIn", projetos:3, faturado:1250, contacto:"916789012", notas:"Cliente recorrente, exigente com qualidade" },
  { id:2, nome:"Construções Horizonte", empresa:"Horizonte, Lda", email:"geral@horizonte-construcoes.pt", nif:"509876543", tipo:"Construção", origem:"Referência", projetos:2, faturado:890, contacto:"21 456 7890", notas:"Promotor imobiliário em expansão" },
  { id:3, nome:"Studio Interiores DK", empresa:"Design Kollective", email:"hello@designkollective.pt", nif:"512345678", tipo:"Design Interiores", origem:"Instagram", projetos:1, faturado:420, contacto:"961234567", notas:"Primeiro projecto, correu bem" },
];

const DEMO_HW3D_PIPELINE = [
  { id:1, clienteId:1, titulo:"Render Exterior Moradia Cascais", etapa:"Em Produção", prob:85, valorEst:520, dataInicio:"2026-04-01", dataFim:"2026-04-25", hh:10, hhReais:7, custoRender:18, assets:0, revisoes:1, notas:"2ª revisão pedida pelo cliente", fatura:{emitida:false,pago:false,parcial:false,valorPago:0,numFatura:""} },
  { id:2, clienteId:2, titulo:"Virtual Staging Apartamentos T2 e T3", etapa:"Adjudicado", prob:95, valorEst:890, dataInicio:"2026-05-02", dataFim:"2026-05-28", hh:8, hhReais:0, custoRender:0, assets:0, revisoes:0, notas:"Pack de 4 divisões", fatura:{emitida:false,pago:false,parcial:false,valorPago:0,numFatura:""} },
  { id:3, clienteId:3, titulo:"Render 3D Sala de Estar — Projecto Lisboa", etapa:"Briefing", prob:45, valorEst:320, dataInicio:"", dataFim:"", hh:4, hhReais:0, custoRender:0, assets:0, revisoes:0, notas:"Aguardar plantas e referências", fatura:{emitida:false,pago:false,parcial:false,valorPago:0,numFatura:""} },
  { id:4, clienteId:1, titulo:"Animação 3D — Percurso Exterior", etapa:"Proposta", prob:60, valorEst:1200, dataInicio:"", dataFim:"2026-06-15", hh:20, hhReais:0, custoRender:45, assets:0, revisoes:0, notas:"Proposta enviada, aguardar resposta", fatura:{emitida:false,pago:false,parcial:false,valorPago:0,numFatura:""} },
];

const DEMO_MANUTENCAO = [
  { id:1, tipo:"avaria", titulo:"Torneira casa de banho Solar a pingar", prioridade:"alta", status:"pendente", data:"2026-05-02", valor:0, notas:"Verificar vedante" },
  { id:2, tipo:"manutencao", titulo:"Pintura exterior terraço Vale", prioridade:"media", status:"planeado", data:"2026-07-01", valor:650, notas:"Após época alta" },
  { id:3, tipo:"investimento", titulo:"Instalação de painéis solares", prioridade:"baixa", status:"planeado", data:"2026-11-01", valor:4200, notas:"Reduz custos de electricidade" },
  { id:4, tipo:"avaria", titulo:"Ar condicionado Ribeira sem frio", prioridade:"alta", status:"planeado", data:"2026-05-10", valor:180, notas:"Técnico agendado" },
];

const DEMO_LOGS = [
  { id:"d1", ts:new Date(Date.now()-3600000).toISOString(), empresa:"quinta", tipo:"reserva", descricao:"Nova reserva: Marie Dupont · Casa Solar · €480", icon:"🏠", valor:480 },
  { id:"d2", ts:new Date(Date.now()-86400000).toISOString(), empresa:"vj", tipo:"evento", descricao:"Nova data DJ: Quinta das Amendoeiras · 2026-05-23", icon:"🎤", valor:1800 },
  { id:"d3", ts:new Date(Date.now()-172800000).toISOString(), empresa:"hw3d", tipo:"projeto", descricao:"Projecto iniciado: Render Exterior Moradia Cascais", icon:"🧊", valor:520 },
  { id:"d4", ts:new Date(Date.now()-259200000).toISOString(), empresa:"quinta", tipo:"limpeza", descricao:"Limpeza concluída: Casa Solar · 2.5h · €20", icon:"🧹", valor:20 },
  { id:"d5", ts:new Date(Date.now()-345600000).toISOString(), empresa:"photobooth", tipo:"evento", descricao:"Evento confirmado: NorteShopping · €350", icon:"📸", valor:350 },
];

const DEMO_PRECOS_HIST = [
  { data:"2025-01-01", solar:110, vale:160, ribeira:85, nota:"Preço inicial" },
  { data:"2026-01-01", solar:120, vale:175, ribeira:95, nota:"Ajuste anual 2026" },
];

const DEMO_LIMPEZAS = [
  { id:1, casa:"solar", data:"2026-05-07", hora:"10:00", horas:2.5, feita:true, valorReceber:20, inicio:null, fim:null },
  { id:2, casa:"vale", data:"2026-05-14", hora:"09:00", horas:0, feita:false, valorReceber:0, inicio:null, fim:null },
  { id:3, casa:"ribeira", data:"2026-05-22", hora:"10:30", horas:0, feita:false, valorReceber:0, inicio:null, fim:null },
];

function isDemoAccount() {
  return localStorage.getItem("kairos_user_email") === DEMO_EMAIL;
}

// Normalizar reserva — compatível com Supabase (check_in/check_out) e local (ci/co)
function normRes(r: any): any {
  if (!r) return r;
  const valor = parseFloat(r.valor || r.valor_total || "0") || 0;
  const guest = (r.guest === "Reserved" || r.guest === "Hóspede (Airbnb)" || r.guest === "Hóspede (Booking)")
    ? "" : (r.guest || "");
  const incompleta = !guest || valor === 0;
  return {
    ...r,
    ci:    r.ci    || r.check_in  || "",
    co:    r.co    || r.check_out || "",
    valor,
    valor_total: valor,
    plat:  r.plat  || r.plataforma || "direto",
    ciHora: r.ciHora || r.hora_ci || "16:00",
    coHora: r.coHora || r.hora_co || "10:00",
    casa:   r.casa  || r.casa_nome || "",
    guest,
    incompleta,
  };
}

// Normalizar array de reservas
function normResArr(arr: any[]): any[] {
  return (arr || []).map(normRes);
}

// Ler configurações AL (horários predefinidos)
function getALConfig() {
  try {
    const defaults = { horaCI:"16:00", horaCO:"10:00", horaLimpeza:"10:00", limpezaAuto:true, notifLimpeza:true };
    return { ...defaults, ...JSON.parse(localStorage.getItem("kairos_al_config")||"{}") };
  } catch { return { horaCI:"16:00", horaCO:"10:00", horaLimpeza:"10:00", limpezaAuto:true, notifLimpeza:true }; }
}

// Retorna o label correcto da empresa — nome demo ou nome real do utilizador
function getCompanyLabel(co: any): string {
  if (isDemoAccount() && DEMO_COMPANY_NAMES[co.id]) {
    return DEMO_COMPANY_NAMES[co.id];
  }
  return co.label;
}

const PROJ_CHART = [
  { m:"M1",  hw3d:-20,  kai:-90,  pb:0,  vj:0,   med:0,   total:-240  },
  { m:"M2",  hw3d:420,  kai:1390, pb:0,  vj:0,   med:0,   total:1680  },
  { m:"M3",  hw3d:1960, kai:3370, pb:250,vj:0,   med:0,   total:5450  },
  { m:"M6",  hw3d:4760, kai:8940, pb:900,vj:1225,med:280, total:15945 },
  { m:"M9",  hw3d:7880, kai:15510,pb:1600,vj:175,med:1000,total:25995 },
  { m:"M12", hw3d:11250,kai:24780,pb:2500,vj:1225,med:3000,total:42575},
];

// AL_CHART data available for future analytics use
// const AL_CHART = [...];

// Roadmap 33 tasks
const ROADMAP_INIT = [
  { id:1,  fase:1, co:"holdings",   title:"Supabase schema completo",              horas:"4–8h",   semana:"S1",   tipo:"Fundação",  status:"em curso"  },
  { id:2,  fase:1, co:"vj",         title:"VJ Dashboard (datas, ganhos, alertas)", horas:"3–5h",   semana:"S1",   tipo:"Fundação",  status:"pendente"  },
  { id:3,  fase:1, co:"quinta",     title:"AL automação base (reservas, limpezas)",horas:"6–12h",  semana:"S1-2", tipo:"Fundação",  status:"pendente"  },
  { id:4,  fase:1, co:"holdings",   title:"Kairos OS v1 build",                    horas:"12–20h", semana:"S1-2", tipo:"Fundação",  status:"em curso"  },
  { id:5,  fase:1, co:"hw3d",       title:"5 gigs Fiverr + pipeline Narva",        horas:"8–12h",  semana:"S2-3", tipo:"Receita",   status:"pendente"  },
  { id:6,  fase:1, co:"hw3d",       title:"Pipeline orçamentação 3D automático",   horas:"5–8h",   semana:"S2-3", tipo:"Automação", status:"pendente"  },
  { id:7,  fase:1, co:"kai_ai",     title:"Branding Kairos AI (logo + identidade)",horas:"3–5h",   semana:"S2",   tipo:"Marketing", status:"pendente"  },
  { id:8,  fase:1, co:"kai_ai",     title:"LinkedIn Build in Public v1",           horas:"6–10h",  semana:"S2-3", tipo:"Marketing", status:"pendente"  },
  { id:9,  fase:1, co:"kai_ai",     title:"Landing Page Kairos AI",                horas:"4–8h",   semana:"S3",   tipo:"Vendas",    status:"pendente"  },
  { id:10, fase:1, co:"kai_ai",     title:"3 contactos qualificados Kairos AI",    horas:"Manual", semana:"S3-4", tipo:"Vendas",    status:"pendente"  },
  { id:11, fase:2, co:"hw3d",       title:"Portfolio Behance + File Watcher",      horas:"5–8h",   semana:"S5-6", tipo:"Marketing", status:"pendente"  },
  { id:12, fase:2, co:"hw3d",       title:"10 gigs Fiverr adicionais",             horas:"4–6h",   semana:"S7-9", tipo:"Receita",   status:"pendente"  },
  { id:13, fase:2, co:"hw3d",       title:"Pipeline assets 3D CGTrader",           horas:"6–10h",  semana:"S6-7", tipo:"Passivo",   status:"pendente"  },
  { id:14, fase:2, co:"kai_ai",     title:"Primeiros 2 clientes Kairos AI pagos",  horas:"Manual", semana:"S5-8", tipo:"Vendas",    status:"pendente"  },
  { id:15, fase:2, co:"photobooth", title:"Lead Gen PhotoBooth (outreach)",        horas:"4–6h",   semana:"S6-7", tipo:"Vendas",    status:"pendente"  },
  { id:16, fase:2, co:"holdings",   title:"Social repurposing multi-plataforma",   horas:"5–8h",   semana:"S8-10",tipo:"Marketing", status:"pendente"  },
  { id:17, fase:2, co:"holdings",   title:"Kairos OS v2 (CRM + Tarefas)",          horas:"10–16h", semana:"S9-12",tipo:"Fundação",  status:"pendente"  },
  { id:18, fase:3, co:"kids",       title:"Kids — Pipeline KDP + Etsy v1",         horas:"8–14h",  semana:"M3",   tipo:"Passivo",   status:"pendente"  },
  { id:19, fase:3, co:"media",      title:"Canal Humor BR — 20 vídeos setup",      horas:"10–16h", semana:"M3-4", tipo:"Passivo",   status:"pendente"  },
  { id:20, fase:3, co:"media",      title:"Canal Séries — 10 vídeos + SEO",        horas:"8–12h",  semana:"M4",   tipo:"Passivo",   status:"pendente"  },
  { id:21, fase:3, co:"influencer", title:"17 Videoclipes artista (pipeline auto)", horas:"10–16h", semana:"M3-4", tipo:"Passivo",   status:"pendente"  },
  { id:22, fase:3, co:"kai_ai",     title:"3 retainers Kairos AI ativos",           horas:"—",      semana:"M3-4", tipo:"MRR",       status:"pendente"  },
  { id:23, fase:3, co:"holdings",   title:"Agente Kai v1 — Telegram COO",          horas:"6–10h",  semana:"M3-4", tipo:"Automação", status:"pendente"  },
  { id:24, fase:3, co:"holdings",   title:"Kairos OS v3 (analytics)",               horas:"8–14h",  semana:"M4",   tipo:"Fundação",  status:"pendente"  },
  { id:25, fase:3, co:"quinta",     title:"Dynamic pricing AL + relatórios auto",   horas:"3–5h",   semana:"M3",   tipo:"Automação", status:"pendente"  },
  { id:26, fase:4, co:"kids",       title:"Kids — 100+ produtos/mês automático",    horas:"—",      semana:"M6+",  tipo:"Escala",    status:"pendente"  },
  { id:27, fase:4, co:"kai_ai",     title:"Kairos AI — 10 retainers + SaaS v1",    horas:"—",      semana:"M6+",  tipo:"Escala",    status:"pendente"  },
  { id:28, fase:4, co:"media",      title:"Canais BR — VPN + paid traffic",         horas:"—",      semana:"M6+",  tipo:"Escala",    status:"pendente"  },
  { id:29, fase:4, co:"kai_ai",     title:"Mini-Mac Worker (produto Kairos AI)",    horas:"—",      semana:"M6+",  tipo:"Produto",   status:"pendente"  },
  { id:30, fase:4, co:"holdings",   title:"Clawbot v1 em produção",                 horas:"—",      semana:"M6+",  tipo:"Produto",   status:"pendente"  },
  { id:31, fase:4, co:"holdings",   title:"Kairos Product — Dropshipping Amazon",   horas:"—",      semana:"M8+",  tipo:"Escala",    status:"pendente"  },
  { id:32, fase:4, co:"holdings",   title:"Contratar 1ª pessoa (AL ou 3D Studio)", horas:"—",      semana:"M6-8", tipo:"Equipa",    status:"pendente"  },
  { id:33, fase:4, co:"holdings",   title:"Kairos OS v4 — UX polish + mobile",     horas:"—",      semana:"M6",   tipo:"Fundação",  status:"pendente"  },
];

const REGRAS = [
  "Cashflow primeiro. €1.500 net/mês por empresa antes de escalar.",
  "Tudo o que se mede, cresce. KPIs tangíveis. Se não se mede, não existe.",
  "Automatizar antes de contratar. Um sistema bom bate qualquer humano.",
  "Create once, sell forever. Produtos digitais, não tempo vendido.",
  "Cada empresa sustenta a seguinte. Sem cross-subsidiar indefinidamente.",
  "Tudo construído internamente é produto vendável da Kairos AI.",
  "A IA nunca manda diretamente — propõe. O sistema valida. O executor faz.",
  "Funcional primeiro. Polido depois. Milestones pequenos e concretos.",
  "Fake it until you make it. Branding de nível superior desde o primeiro dia.",
  "Mercado mundial desde o primeiro dia. Portugal é pequeno demais.",
  "Documentar tudo. SOPs — escalabilidade exige documentação.",
  "Contratar humanos só quando o cashflow cobre 3x o salário.",
  "Hustle com inteligência. Astúcia e dados, não apenas esforço.",
  "Re-investir o excedente. Lucro → infra → escala → mais lucro.",
  "A Quinta é gerida pela Kairos. O lucro líquido é transferido mensalmente. Registar tudo.",
];

const ACCELERATE_STEPS = [
  { l:"A", name:"Acquire",  desc:"Captar leads ou tráfego" },
  { l:"C", name:"Convert",  desc:"Transformar visitante em cliente" },
  { l:"E", name:"Execute",  desc:"Produção automática do entregável" },
  { l:"L", name:"Log",      desc:"Guardar em Supabase com timestamp" },
  { l:"E", name:"Evaluate", desc:"Analisar métricas e resultados" },
  { l:"R", name:"Refine",   desc:"Otimizar baseado em dados" },
  { l:"A", name:"Automate", desc:"Remover o humano do loop" },
  { l:"T", name:"Template", desc:"Tornar o processo replicável" },
  { l:"E", name:"Expand",   desc:"Escalar para mais mercados" },
];

/* ─── HELPERS ────────────────────────────────────────────────────────────────── */
const fmt  = (n: number | string): string => `€${Number(n).toLocaleString("pt-PT")}`;
const fmtK = (n: any): string => n >= 1000 ? `€${(n/1000).toFixed(0)}k` : fmt(n);

// Responsive breakpoint hook
function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return mobile;
}

// Tooltip style for recharts
const TTS = {
  background: "#0a0d18",
  border: `1px solid rgba(255,255,255,0.1)`,
  borderRadius: 10,
  fontSize: 12,
  color: T.text,
  boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
};

/* ─── BESTSELLER LOGIC ───────────────────────────────────────────────────────── */
function getBestseller(items: any[], keyFn: (i:any)=>any, valFn?: (i:any)=>number): string|null {
  if (!items?.length) return null;
  const counts: Record<string,number> = {};
  items.forEach(item => {
    const k = keyFn(item);
    if (k) counts[k] = (counts[k] || 0) + (valFn ? valFn(item) : 1);
  });
  if (!Object.keys(counts).length) return null;
  return Object.entries(counts).sort((a,b) => (b[1] as number)-(a[1] as number))[0][0];
}

/* ─── SHARED STYLE HELPERS ───────────────────────────────────────────────────── */
// Consistent input style used across all forms
const inputStyle = (focused = false): React.CSSProperties => ({
  width: "100%",
  padding: "9px 12px",
  borderRadius: 10,
  background: "rgba(255,255,255,0.04)",
  border: `1px solid ${focused ? T.borderFocus : T.border}`,
  color: T.text,
  fontSize: 13,
  fontFamily: "inherit",
  boxSizing: "border-box",
  outline: "none",
  transition: "border-color 0.2s",
});

const selectStyle = (): React.CSSProperties => ({
  ...inputStyle(),
  background: "#070a14",
  cursor: "pointer",
});

const btnPrimary = (color: string): React.CSSProperties => ({
  padding: "10px 18px",
  borderRadius: 10,
  border: "none",
  background: `linear-gradient(135deg, ${color}, ${color}cc)`,
  color: "white",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  letterSpacing: "0.01em",
  boxShadow: `0 4px 14px ${color}40`,
  transition: "opacity 0.2s, transform 0.1s",
});

const btnSecondary = (): React.CSSProperties => ({
  padding: "8px 14px",
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: "rgba(255,255,255,0.04)",
  color: T.textSub,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.15s",
});

/* ─── UI ATOMS ───────────────────────────────────────────────────────────────── */

// KPI Card — clean metric display with left accent bar
function KPI({ l, v, c, up, ac }: any) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={()=>setHov(true)}
      onMouseLeave={()=>setHov(false)}
      style={{
        background: hov ? T.panel2 : T.panel,
        border: `1px solid ${hov ? T.borderHi : T.border}`,
        borderLeft: `3px solid ${ac || T.blueL}`,
        borderRadius: 10,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        transition: "all 0.18s",
        cursor: "default",
      }}>
      <span style={{
        fontSize: 11,
        color: T.muted,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        fontWeight: 700,
      }}>{l}</span>
      <span style={{
        fontSize: 22,
        fontWeight: 700,
        color: ac || T.text,
        fontFamily: "'IBM Plex Mono', monospace",
        letterSpacing: "-0.03em",
        lineHeight: 1.1,
      }}>{v}</span>
      {c && (
        <span style={{
          fontSize: 11,
          color: up === true ? T.green : up === false ? T.red : T.muted,
          display: "flex",
          alignItems: "center",
          gap: 3,
        }}>
          {up === true ? "↑" : up === false ? "↓" : ""} {c}
        </span>
      )}
    </div>
  );
}

// Tag — compact label pill
function Tag({ label, color, bg }: any) {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      fontSize: 11,
      padding: "2px 8px",
      borderRadius: 99,
      background: bg || `${color || T.muted}18`,
      color: color || T.muted,
      fontWeight: 600,
      whiteSpace: "nowrap",
      lineHeight: 1.7,
      border: `1px solid ${color || T.muted}25`,
    }}>
      {label}
    </span>
  );
}

// BestsellerBadge
function BestsellerBadge({ label }: any) {
  if (!label) return null;
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 11,
      padding: "2px 9px",
      borderRadius: 99,
      background: `${T.gold}18`,
      color: T.goldL,
      fontWeight: 700,
      border: `1px solid ${T.gold}35`,
      whiteSpace: "nowrap",
    }}>
      ★ {label}
    </span>
  );
}

// Card — glass panel with optional header
function Card({ title, children, noPad, action, onAction, accent }: any) {
  return (
    <div style={{
      background: T.panel,
      border: `1px solid ${accent ? `${accent}28` : T.border}`,
      borderRadius: 14,
      overflow: "hidden",
      backdropFilter: "blur(8px)",
    }}>
      {title && (
        <div style={{
          padding: "12px 18px",
          borderBottom: `1px solid ${T.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: accent ? `${accent}07` : "rgba(255,255,255,0.01)",
        }}>
          <span style={{
            fontSize: 11,
            fontWeight: 700,
            color: accent || T.textSub,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
          }}>{title}</span>
          {action && (
            <button onClick={onAction} style={{
              fontSize: 12,
              color: accent || T.blueL,
              background: accent ? `${accent}15` : "rgba(59,157,232,0.12)",
              border: `1px solid ${accent || T.blueL}30`,
              borderRadius: 6,
              padding: "3px 10px",
              cursor: "pointer",
              fontWeight: 600,
            }}>{action}</button>
          )}
        </div>
      )}
      <div style={noPad ? {} : { padding: 18 }}>{children}</div>
    </div>
  );
}

// Empty state
function Empty({ icon, msg, sub }: any) {
  return (
    <div style={{ padding: "40px 20px", textAlign: "center" }}>
      <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.7 }}>{icon}</div>
      <div style={{ fontSize: 14, color: T.text, fontWeight: 600, marginBottom: 6 }}>{msg}</div>
      {sub && <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.7, maxWidth: 380, margin: "0 auto" }}>{sub}</div>}
    </div>
  );
}

// ── Skeleton loaders — para carregamento progressivo ──
function SkeletonBox({ width = "100%", height = 16, radius = 6 }: any) {
  return (
    <div style={{
      width, height,
      borderRadius: radius,
      background: `linear-gradient(90deg, ${T.panel2} 0%, ${T.border} 50%, ${T.panel2} 100%)`,
      backgroundSize: "200% 100%",
      animation: "kairos-skeleton 1.5s ease-in-out infinite"
    }}/>
  );
}

function SkeletonKPI() {
  return (
    <div style={{ padding: 16, borderRadius: 12, background: T.panel, border: `1px solid ${T.border}` }}>
      <SkeletonBox width={60} height={11} />
      <div style={{ marginTop: 10 }}><SkeletonBox width="70%" height={24} /></div>
      <div style={{ marginTop: 8 }}><SkeletonBox width="50%" height={10} /></div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div style={{ padding: "10px 0", display: "flex", gap: 12, alignItems: "center", borderBottom: `1px solid ${T.border}` }}>
      <SkeletonBox width={10} height={10} radius={5} />
      <div style={{ flex: 1 }}>
        <SkeletonBox width="60%" height={13} />
        <div style={{ marginTop: 6 }}><SkeletonBox width="40%" height={10} /></div>
      </div>
      <SkeletonBox width={60} height={20} radius={8} />
    </div>
  );
}

function SkeletonKPIRow({ count = 4 }: any) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit,minmax(140px,1fr))`, gap: 10 }}>
      {Array.from({ length: count }).map((_, i) => <SkeletonKPI key={i} />)}
    </div>
  );
}

function SkeletonList({ rows = 3 }: any) {
  return (
    <div>
      {Array.from({ length: rows }).map((_, i) => <SkeletonRow key={i} />)}
    </div>
  );
}

// StatusDot — semantic indicator
function StatusDot({ s }: any) {
  const c =
    ["ativo","confirmado","concluido","confirmada","concluída"].includes(s) ? T.green :
    ["em curso","negociação","limpeza","em progresso"].includes(s)          ? T.amber :
    T.muted;
  return (
    <div style={{
      width: 7, height: 7, borderRadius: "50%",
      background: c, flexShrink: 0,
      boxShadow: `0 0 6px ${c}80`,
    }}/>
  );
}

// Section divider for mobile-friendly lists
function Divider() {
  return <div style={{ height: 1, background: T.border, margin: "2px 0" }}/>;
}



/* ─── ROADMAP ────────────────────────────────────────────────────────────────── */
function Roadmap({ filter }: any) {
  const [tasks, setTasks] = useState(ROADMAP_INIT);
  const [faseF, setFaseF] = useState("all");

  const visible = tasks.filter(t =>
    (filter === "all" || t.co === filter) &&
    (faseF === "all" || t.fase === Number(faseF))
  );

  const done   = visible.filter(t=>t.status==="concluido").length;
  const inprog = visible.filter(t=>t.status==="em curso").length;
  const pct    = visible.length ? Math.round(done/visible.length*100) : 0;

  const cycle = (id: number) => setTasks(ts => ts.map(t => t.id !== id ? t : {
    ...t, status: t.status==="pendente"?"em curso":t.status==="em curso"?"concluido":"pendente"
  }));

  const tipoC = {
    Fundação:T.blue, Receita:T.green, Automação:T.teal, Marketing:T.pink,
    Vendas:T.amber, Passivo:T.purple, MRR:T.gold, Produto:T.red,
    Escala:T.gold, Equipa:T.green
  };
  const coLabel = { holdings:"Holdings", hw3d:"HW3D", kai_ai:"KAI AI",
    quinta:"Quinta", photobooth:"PhotoBooth", vj:"VJ", media:"Media",
    influencer:"Influencer", kids:"Kids" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12 }}>
        <KPI l="Total"      v={visible.length} c="tarefas"     ac={T.blue}/>
        <KPI l="Concluídas" v={done}  c={`${pct}%`} ac={T.green} up={done>0?true:null}/>
        <KPI l="Em Curso"   v={inprog} c="a decorrer" ac={T.amber}/>
        <KPI l="Progresso"  v={`${pct}%`} c="" ac={T.blueL}/>
      </div>
      <div style={{ height:5, borderRadius:99, background:"rgba(255,255,255,0.06)" }}>
        <div style={{ width:`${Math.max(pct,1)}%`, height:"100%", borderRadius:99,
          background:`linear-gradient(90deg,${T.blueDark},${T.blueL})`, transition:"width 0.5s" }}/>
      </div>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
        {[["all","Todas"],["1","Fase 1"],["2","Fase 2"],["3","Fase 3"],["4","Fase 4"]].map(([v,l]: any)=>(
          <button key={v} onClick={()=>setFaseF(v)} style={{ padding:"10px 14px", borderRadius:6,
            border:"none", cursor:"pointer", fontSize:11, fontWeight:600,
            background:faseF===v?`${T.blueL}28`:"rgba(255,255,255,0.04)",
            color:faseF===v?T.blueL:T.muted }}>
            {l}
          </button>
        ))}
      </div>
      <Card noPad>
        <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch" as any}}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, minWidth:520 }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${T.border}` }}>
              {(filter==="all"
                ? ["","#","Tarefa","Empresa","Horas","Semana","Tipo","Status"]
                : ["","#","Tarefa","Horas","Semana","Tipo","Status"]
              ).map((h,i)=>(
                <th key={i} style={{ padding:"9px 14px", textAlign:"left", color:T.muted,
                  fontSize:11, fontWeight:700, textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map(t=>(
              <tr key={t.id}
                style={{ borderBottom:`1px solid rgba(255,255,255,0.04)`, opacity:t.status==="concluido"?0.4:1 }}
                onMouseEnter={(e: any)=>e.currentTarget.style.background="rgba(255,255,255,0.025)"}
                onMouseLeave={(e: any)=>e.currentTarget.style.background="transparent"}>
                <td style={{ padding:"10px 14px" }}>
                  <div onClick={()=>cycle(t.id)} title="Clica para avançar"
                    style={{ width:15, height:15, borderRadius:6, cursor:"pointer",
                      border:`2px solid ${t.status==="concluido"?T.green:t.status==="em curso"?T.amber:"rgba(255,255,255,0.15)"}`,
                      background:t.status==="concluido"?T.green:t.status==="em curso"?"rgba(245,166,35,0.2)":"transparent",
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"white" }}>
                    {t.status==="concluido"?"✓":t.status==="em curso"?"●":""}
                  </div>
                </td>
                <td style={{ padding:"10px 14px", color:T.muted, fontFamily:"monospace", fontSize:11 }}>T{t.id}</td>
                <td style={{ padding:"10px 14px", color:T.text, maxWidth:280,
                  textDecoration:t.status==="concluido"?"line-through":"none" }}>{t.title}</td>
                {filter==="all" && (
                  <td style={{ padding:"10px 14px" }}><Tag label={coLabel[t.co]||t.co} color={CO[t.co]||T.muted}/></td>
                )}
                <td style={{ padding:"10px 14px", color:T.muted, fontFamily:"monospace", whiteSpace:"nowrap" }}>{t.horas}</td>
                <td style={{ padding:"10px 14px", color:T.muted, whiteSpace:"nowrap" }}>{t.semana}</td>
                <td style={{ padding:"10px 14px" }}><Tag label={t.tipo} color={tipoC[t.tipo]||T.muted}/></td>
                <td style={{ padding:"10px 14px" }}>
                  <Tag label={t.status}
                    color={t.status==="concluido"?T.green:t.status==="em curso"?T.amber:T.muted}/>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}

/* ─── VIEWS POR EMPRESA ──────────────────────────────────────────────────────── */

// ── HOLDINGS ──
function HoldingsView({ page, logs }: any) {
  if (page === "regras")     return <RegrasView/>;
  if (page === "accelerate") return <AccelerateView/>;
  if (page === "fin_global") return <FinGlobalView/>;
  if (page === "tesouraria") return <TesourariaView/>;
  return <HoldingsOverview logs={logs}/>;
}

function HoldingsOverview({ logs }: any) {
  const hoje = new Date();
  const mesAtual = hoje.toLocaleDateString("pt-PT",{month:"long",year:"numeric"});
  const anoAtual = hoje.getFullYear();
  const mesNum   = hoje.getMonth() + 1;

  // Dados reais do Supabase
  const { data: vjEventosDB }  = useVJEventos();
  const { data: alResDB }      = useALReservas();
  const { data: hw3dProjDB }   = useHW3DProjetos();
  const { data: kaiProjDB }    = useKaiProjetos();
  const { data: pbEventosDB }  = usePBEventos();

  // Calcular receitas por empresa (mês actual)
  const vjRec = (isDemoAccount() ? DEMO_VJ_EVENTS : vjEventosDB||[])
    .filter((e:any)=>e.status==="confirmado"||e.status==="concluido")
    .reduce((s:number,e:any)=>s+parseFloat(e.valor||0),0);

  const alRec = normResArr(isDemoAccount() ? DEMO_AL_RES : alResDB||[])
    .filter((r:any)=>r.status==="confirmada"&&r.valor>0)
    .reduce((s:number,r:any)=>s+r.valor,0);

  const hw3dRec = (isDemoAccount() ? DEMO_HW3D_PIPELINE : hw3dProjDB||[])
    .filter((p:any)=>p.etapa==="Concluído"||p.etapa==="Em progresso")
    .reduce((s:number,p:any)=>s+parseFloat(p.valor||p.orcamento||0),0);

  const kaiRec = (isDemoAccount() ? [] : kaiProjDB||[])
    .filter((p:any)=>p.status==="Em curso"||p.status==="Concluído")
    .reduce((s:number,p:any)=>s+parseFloat(p.valor||0),0);

  const pbRec = (isDemoAccount() ? [] : pbEventosDB||[])
    .filter((e:any)=>e.status==="confirmado"||e.status==="concluido")
    .reduce((s:number,e:any)=>s+parseFloat(e.valor||400),0);

  const empresas = [
    { id:"hw3d",       nome:"Homework 3D",      icon:"🧊", cor:CO.hw3d,       status:"Operacional",    rec:hw3dRec, desp:0 },
    { id:"vj",         nome:"VJ Casamentos",    icon:"🎤", cor:CO.vj,         status:"Sazonal",         rec:vjRec,   desp:0 },
    { id:"quinta",     nome:"Quinta da Regada", icon:"🏠", cor:CO.quinta,     status:"Operacional",    rec:alRec,   desp:0 },
    { id:"photobooth", nome:"321 PhotoBooth",   icon:"📸", cor:CO.photobooth, status:"Em Arranque",    rec:pbRec,   desp:0 },
    { id:"kai_ai",     nome:"Kairos AI",        icon:"🤖", cor:CO.kai_ai,     status:"Em Lançamento",  rec:kaiRec,  desp:0 },
  ];

  const totalRec   = empresas.reduce((s:number,e:any)=>s+e.rec,0);
  const totalDesp  = empresas.reduce((s:number,e:any)=>s+e.desp,0);
  const totalLucro = totalRec - totalDesp;
  const margem     = totalRec>0 ? Math.round(totalLucro/totalRec*100) : 0;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {/* KPIs Financeiros Reais */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Faturação Total" v={fmt(totalRec)} c={mesAtual} ac={T.green}/>
        <KPI l="Despesas Totais" v={fmt(totalDesp)} c={mesAtual} ac={T.red}/>
        <KPI l="Lucro Líquido"  v={fmt(totalLucro)} c={`${margem}% margem`} ac={T.blueL}/>
        <KPI l="Empresas Activas" v={empresas.filter((e:any)=>e.status==="Operacional"||e.status==="Sazonal").length} c="em operação" ac={T.amber}/>
      </div>

      {/* Dados operacionais por empresa */}
      <Card title="Empresas — Dados Operacionais" accent={T.blueL}>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {empresas.map((emp:any)=>(
            <div key={emp.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:10,
              background:T.panel,border:`1px solid ${T.border}`}}>
              <div style={{width:36,height:36,borderRadius:10,flexShrink:0,
                background:`${emp.cor}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>
                {emp.icon}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>{emp.nome}</div>
                <Tag label={emp.status} color={emp.status==="Operacional"?T.green:emp.status==="Sazonal"?T.amber:T.muted}/>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:13,color:T.green,fontFamily:"monospace",fontWeight:700}}>{fmt(emp.rec)}</div>
                <div style={{fontSize:11,color:T.muted}}>rec · desp {fmt(emp.desp)}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{marginTop:12,padding:"10px 14px",borderRadius:8,background:T.panel2,fontSize:11,color:T.muted}}>
          💡 Liga os dados reais via Supabase para ver faturação e despesas automáticas por empresa.
        </div>
      </Card>

      {/* Actividade Recente */}
      <Card title="Actividade Recente" accent={T.blueL}>
        {logs.slice(0,6).map((l:any,i:number)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",
            borderBottom:i<5?`1px solid ${T.border}`:"none"}}>
            <span style={{fontSize:14,flexShrink:0}}>{l.icon||"📋"}</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.desc}</div>
              <div style={{fontSize:11,color:T.muted}}>{l.empresa} · {l.hora||""}</div>
            </div>
            {l.valor>0&&<div style={{fontSize:11,color:T.green,fontFamily:"monospace",flexShrink:0}}>{fmt(l.valor)}</div>}
          </div>
        ))}
        {logs.length===0&&<Empty icon="📋" msg="Sem actividade" sub="As acções em todas as empresas aparecerão aqui"/>}
      </Card>
    </div>
  );
}


function FinGlobalView() {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12 }}>
        <KPI l="Net M3 (proj.)"  v="€5.450"   c="acumulado" up={true}  ac={T.blueL}/>
        <KPI l="Net M6 (proj.)"  v="€15.945"  c="5 empresas"           up={true}  ac={T.blueL}/>
        <KPI l="Net M12 (proj.)" v="€42.575+" c="consolidado"          up={true}  ac={T.gold}/>
        <KPI l="AL Lucro M12"   v="€1.432+"  c="lucro separado"        ac={T.amber}/>
      </div>
      <Card title="Net por Empresa — M1 / M3 / M6 / M9 / M12" accent={T.blueL}>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={PROJ_CHART} barGap={3}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
            <XAxis dataKey="m" tick={{fill:T.muted,fontSize:11}} axisLine={false} tickLine={false}/>
            <YAxis tick={{fill:T.muted,fontSize:11}} axisLine={false} tickLine={false} tickFormatter={fmtK}/>
            <Tooltip contentStyle={TTS} formatter={v=>fmt(v)}/>
            <Bar dataKey="hw3d"  fill={T.teal}   radius={[3,3,0,0]} name="HW3D"/>
            <Bar dataKey="kai"   fill={T.blueL}  radius={[3,3,0,0]} name="Kairos AI"/>
            <Bar dataKey="pb"    fill={T.pink}   radius={[3,3,0,0]} name="PhotoBooth"/>
            <Bar dataKey="vj"    fill={T.purple} radius={[3,3,0,0]} name="VJ"/>
            <Bar dataKey="med"   fill={T.red}    radius={[3,3,0,0]} name="Media+"/>
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card title="Tabela Consolidada — NET (excl. Quinta da Regada)" noPad>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${T.border}` }}>
              {["Empresa","M1","M3","M6","M9","M12"].map(h=>(
                <th key={h} style={{ padding:"9px 14px", textAlign:"right", color:T.muted,
                  fontSize:11, fontWeight:700, textTransform:"uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ["🧊 HW 3D Studio", T.teal,   [-20,1960,4760,7880,11250]],
              ["🤖 Kairos AI",    T.blueL,  [-90,3370,8940,15510,24780]],
              ["📸 PhotoBooth",   T.pink,   [0,250,900,1600,2500]],
              ["🎤 VJ",           T.purple, [0,0,1225,175,1225]],
              ["📺 Media+",       T.red,    [0,0,280,1000,3000]],
              ["🏠 AL (Quinta)",  T.amber,  ["—","—","—","—","—"]],
              ["⚡ TOTAL NET",    T.blueL,  [-240,5450,15945,25995,42575]],
            ].map(([name,color,vals]: any[],ri: number)=>(
              <tr key={ri}
                style={{ borderBottom:`1px solid rgba(255,255,255,0.04)`,
                  background:name.includes("TOTAL")?"rgba(59,157,232,0.05)":"transparent" }}
                onMouseEnter={(e: any)=>e.currentTarget.style.background="rgba(255,255,255,0.025)"}
                onMouseLeave={(e: any)=>e.currentTarget.style.background=name.includes("TOTAL")?"rgba(59,157,232,0.05)":"transparent"}>
                <td style={{ padding:"10px 14px", color, fontWeight:name.includes("TOTAL")?700:400 }}>{name}</td>
                {vals.map((v,ci)=>(
                  <td key={ci} style={{ padding:"10px 14px", textAlign:"right",
                    fontFamily:"monospace", fontWeight:name.includes("TOTAL")?700:400,
                    color:typeof v==="number"&&v<0?T.red:typeof v==="number"&&v>0?T.text:T.muted }}>
                    {typeof v==="number"?fmt(v):v}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function RegrasView() {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <div style={{ padding:"14px 18px", borderRadius:10,
        background:"rgba(30,111,196,0.08)", border:`1px solid rgba(30,111,196,0.25)` }}>
        <p style={{ fontSize:12, color:T.blueL, lineHeight:1.7 }}>
          <strong>15 Princípios Invioláveis da Kairos Holding.</strong><br/>
          Nenhuma decisão estratégica é tomada contra estes princípios.
        </p>
      </div>
      {REGRAS.map((r,i)=>(
        <div key={i} style={{ display:"flex", gap:14, padding:"13px 18px",
          borderRadius:10, background:T.panel, border:`1px solid ${T.border}`,
          alignItems:"flex-start" }}>
          <span style={{ fontSize:13, fontWeight:800, color:T.blueL,
            minWidth:28, fontFamily:"monospace" }}>{String(i+1).padStart(2,"0")}</span>
          <span style={{ fontSize:13, color:i%2===0?T.text:"#94a3b8", lineHeight:1.6 }}>{r}</span>
        </div>
      ))}
    </div>
  );
}

function AccelerateView() {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ padding:"14px 18px", borderRadius:10,
        background:"rgba(30,111,196,0.08)", border:`1px solid rgba(30,111,196,0.25)` }}>
        <p style={{ fontSize:12, color:T.blueL, fontWeight:600 }}>
          Framework Universal — nenhum projeto Kairos avança sem passar por este ciclo completo.
        </p>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12 }}>
        {ACCELERATE_STEPS.map((s,i)=>(
          <div key={i} style={{ background:T.panel, border:`1px solid ${T.border}`,
            borderRadius:10, padding:"16px 18px", display:"flex", gap:14 }}>
            <div style={{ width:36, height:36, borderRadius:10, flexShrink:0,
              background:"rgba(30,111,196,0.15)", border:`1px solid rgba(30,111,196,0.3)`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:17, fontWeight:900, color:T.blueL, fontFamily:"monospace" }}>{s.l}</div>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{s.name}</div>
              <div style={{ fontSize:11, color:T.muted, marginTop:3, lineHeight:1.5 }}>{s.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── HOLDINGS TESOURARIA ──
function TesourariaView() {
  const [tesouraria, setTesouraria] = useState(TESOURARIA_INIT);
  const [impostos, setImpostos] = useState(TESOURARIA_INIT.impostos);
  const { data: dividasDB, insert: insertDivida, update: updateDivida } = useHBDividas();
  const dividas = isDemoAccount() ? [] : (dividasDB || []);
  const setDividas = (_:any) => {};
  const [dividasDemo] = useState<any[]>([
    {id:1,de:"Quinta da Regada",para:"Nuno",valor:0,descricao:"Reposição adiantamento",pago:false,data:""},
  ]);
  const [addingD, setAddingD] = useState(false);
  const [newD, setNewD] = useState({de:"",para:"",valor:0,descricao:"",data:""});
  const [ordenadosData, setOrdenadosData] = useState({bruto:0,ss:0,irc:0,iva:0,valorOrdenados:0,valorOrdenadosUsado:0});

  const empresasIds = ["hw3d","kai_ai","vj","pb"] as const;
  const empresasLabel:Record<string,string> = {hw3d:"HW3D",kai_ai:"Kairos AI",vj:"VJ",pb:"PhotoBooth",quinta:"Quinta"};

  const totalImpostos = impostos.reduce((s,i)=>s+i.valor,0);
  const totalDividas = dividas.filter(d=>!d.pago).reduce((s,d)=>s+d.valor,0);
  const valorOrdenadosLivre = ordenadosData.valorOrdenados - ordenadosData.valorOrdenadosUsado;

  const toggleImposto = (i:number) => setImpostos(imp=>imp.map((x,j)=>j===i?{...x,pago:!x.pago}:x));
  const addDivida = () => {
    setDividas([...dividas,{...newD,id:dividas.length+1,pago:false,valor:+newD.valor}]);
    setAddingD(false);
    setNewD({de:"",para:"",valor:0,descricao:"",data:""});
  };
  const toggleDivida = (id:number) => setDividas(ds=>ds.map(d=>d.id===id?{...d,pago:!d.pago}:d));

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Impostos Pendentes" v={fmt(impostos.filter(i=>!i.pago).reduce((s,i)=>s+i.valor,0))} c="SS + IRC + IVA" ac={T.red}/>
        <KPI l="Dívidas Inter-Empresa" v={fmt(totalDividas)} c="a regularizar" ac={T.amber}/>
        <KPI l="Valor Ordenados Livre" v={fmt(valorOrdenadosLivre)} c="apoio governo disponível" ac={T.green}/>
        <KPI l="Total Impostos Pagos" v={fmt(impostos.filter(i=>i.pago).reduce((s,i)=>s+i.valor,0))} c="este ano" ac={T.muted}/>
      </div>

      {/* Ordenados e separação pessoal/empresa */}
      <Card title="Controlo de Ordenado e Separação Pessoal/Empresa" accent={T.blueL}>
        <div style={{fontSize:12,color:T.muted,marginBottom:14,lineHeight:1.7}}>
          O teu ordenado é calculado apenas com base no que faturaste no <Tag label="HW3D" color={T.teal}/> e <Tag label="Kairos AI" color={T.blueL}/> (sem IVA). O que é da empresa fica na empresa.
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:16}}>
          {([["Bruto Faturado (sem IVA)","bruto",T.amber],["SS a Pagar","ss",T.red],["IRC Estimado","irc",T.red],["IVA Liquidado","iva",T.red],["Valor Ordenados (Gov.)","valorOrdenados",T.green],["Já Usado dos Ordenados","valorOrdenadosUsado",T.amber]] as any).map(([l,k,c]:any)=>(
            <div key={k}>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>{l}</div>
              <input type="number" value={ordenadosData[k]} onChange={(e:any)=>setOrdenadosData({...ordenadosData,[k]:+e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:c,fontSize:13,fontWeight:700,fontFamily:"monospace",boxSizing:"border-box" as any}}/>
            </div>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
          <div style={{padding:"12px",borderRadius:10,background:"rgba(34,194,122,0.08)",border:`1px solid rgba(34,194,122,0.2)`}}>
            <div style={{fontSize:11,color:T.muted,marginBottom:4}}>NET PARA TI</div>
            <div style={{fontSize:18,fontWeight:700,color:T.green,fontFamily:"monospace"}}>{fmt(Math.max(0,ordenadosData.bruto-ordenadosData.ss-ordenadosData.irc))}</div>
          </div>
          <div style={{padding:"12px",borderRadius:10,background:"rgba(245,166,35,0.08)",border:`1px solid rgba(245,166,35,0.2)`}}>
            <div style={{fontSize:11,color:T.muted,marginBottom:4}}>FICA NA EMPRESA</div>
            <div style={{fontSize:18,fontWeight:700,color:T.amber,fontFamily:"monospace"}}>{fmt(Math.max(0,ordenadosData.iva))}</div>
            <div style={{fontSize:11,color:T.muted}}>IVA retido para entregar</div>
          </div>
          <div style={{padding:"12px",borderRadius:10,background:"rgba(232,82,82,0.08)",border:`1px solid rgba(232,82,82,0.2)`}}>
            <div style={{fontSize:11,color:T.muted,marginBottom:4}}>VALOR ORDENADOS LIVRE</div>
            <div style={{fontSize:18,fontWeight:700,color:valorOrdenadosLivre>=0?T.green:T.red,fontFamily:"monospace"}}>{fmt(valorOrdenadosLivre)}</div>
            <div style={{fontSize:11,color:T.muted}}>para repor na Quinta</div>
          </div>
        </div>
      </Card>

      {/* Impostos */}
      <Card title="Impostos — Controlo de Pagamentos" accent={T.red}>
        {impostos.map((imp,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:12,
            padding:"10px 0",borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13,color:T.text,fontWeight:600}}>{imp.tipo}</div>
              {imp.data&&<div style={{fontSize:11,color:T.muted}}>Vence: {imp.data}</div>}
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <input type="number" value={imp.valor}
                onChange={(e:any)=>setImpostos(im=>im.map((x,j)=>j===i?{...x,valor:+e.target.value}:x))}
                style={{width:90,padding:"5px 9px",borderRadius:6,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12,fontFamily:"monospace",textAlign:"right"}}/>
              <input type="date" value={imp.data}
                onChange={(e:any)=>setImpostos(im=>im.map((x,j)=>j===i?{...x,data:e.target.value}:x))}
                style={{padding:"5px 9px",borderRadius:6,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:11}}/>
            </div>
            <button onClick={()=>toggleImposto(i)}
              style={{padding:"5px 14px",borderRadius:6,border:"none",
                background:imp.pago?T.green:"rgba(255,255,255,0.06)",
                color:imp.pago?"white":T.muted,fontSize:11,fontWeight:700,cursor:"pointer"}}>
              {imp.pago?"✓ Pago":"Marcar Pago"}
            </button>
          </div>
        ))}
      </Card>

      {/* Dívidas inter-empresa */}
      <Card title="Dívidas Inter-Empresa" accent={T.amber}
        action="+ Registar Dívida" onAction={()=>setAddingD(!addingD)}>
        <div style={{fontSize:11,color:T.muted,marginBottom:12}}>
          Ex: Quinta deve à Elisabete/Nuno — Valor Ordenados usado para cobrir despesas
        </div>
        {addingD&&(
          <div style={{marginBottom:14,padding:14,borderRadius:10,background:"rgba(245,166,35,0.05)",
            border:`1px solid rgba(245,166,35,0.2)`,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
            {([["De (Empresa/Pessoa)","text","de","Ex: Quinta da Regada"],["Para (Empresa/Pessoa)","text","para","Ex: Nuno"],
               ["Valor €","number","valor","0"],["Data","date","data",""]]).map(([l,t,k,ph]:any)=>(
              <div key={k}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>{l}</div>
                <input type={t} placeholder={ph} value={newD[k]} onChange={(e:any)=>setNewD({...newD,[k]:e.target.value})}
                  style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
              </div>
            ))}
            <div style={{gridColumn:"1/-1"}}>
              <input placeholder="Descrição da dívida..." value={newD.descricao}
                onChange={(e:any)=>setNewD({...newD,descricao:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
            </div>
            <button onClick={addDivida} style={{gridColumn:"1/-1",padding:"9px",borderRadius:10,border:"none",
              background:T.amber,color:"white",fontSize:13,fontWeight:700,cursor:"pointer"}}>Guardar</button>
          </div>
        )}
        {dividas.map(d=>(
          <div key={d.id} style={{display:"flex",alignItems:"center",gap:12,
            padding:"10px 0",borderBottom:`1px solid rgba(255,255,255,0.04)`,
            opacity:d.pago?0.5:1}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13,color:T.text,fontWeight:600,
                textDecoration:d.pago?"line-through":"none"}}>
                <span style={{color:T.amber}}>{d.de}</span> → <span style={{color:T.green}}>{d.para}</span>
              </div>
              <div style={{fontSize:11,color:T.muted,marginTop:2}}>{d.descricao}{d.data?` · ${d.data}`:""}</div>
            </div>
            <span style={{fontSize:14,fontWeight:700,color:d.pago?T.muted:T.amber,fontFamily:"monospace"}}>{fmt(d.valor)}</span>
            <button onClick={()=>toggleDivida(d.id)}
              style={{padding:"10px 14px",borderRadius:6,border:"none",
                background:d.pago?T.green:"rgba(255,255,255,0.06)",
                color:d.pago?"white":T.muted,fontSize:11,fontWeight:700,cursor:"pointer"}}>
              {d.pago?"✓ Pago":"Marcar Pago"}
            </button>
          </div>
        ))}
        {dividas.filter(d=>!d.pago).length===0&&<Empty icon="✅" msg="Sem dívidas pendentes" sub=""/>}
      </Card>
    </div>
  );
}

// ── HOMEWORK 3D ──
function HW3DDespesas({ projects, addLog }: any) {
  const [despesas, setDespesas] = useState<any[]>([
    {id:1,data:"2026-01-15",desc:"Software 3D (licença anual)",valor:320,projeto:"",tipo:"software"},
    {id:2,data:"2026-02-01",desc:"Assets e texturas premium",valor:85,projeto:"",tipo:"assets"},
    {id:3,data:"2026-03-10",desc:"Curso de animação",valor:149,projeto:"",tipo:"formacao"},
  ]);
  const [adding, setAdding] = useState(false);
  const [newD, setNewD] = useState({data:"",desc:"",valor:0,projeto:"",tipo:"outros"});

  const totalDesp = despesas.reduce((s:number,d:any)=>s+d.valor,0);
  const tipos: Record<string,string> = {software:"Software",assets:"Assets",formacao:"Formação",marketing:"Marketing",outros:"Outros"};

  const addDesp = () => {
    if(!newD.desc||!newD.data) return;
    setDespesas(ds=>[...ds,{...newD,id:ds.length+1,valor:+newD.valor}]);
    addLog?.("hw3d","despesa",`Despesa: ${newD.desc} · ${fmt(+newD.valor)}`,"💸",+newD.valor);
    setAdding(false);
    setNewD({data:"",desc:"",valor:0,projeto:"",tipo:"outros"});
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Total Despesas" v={fmt(totalDesp)} c="acumulado" ac={T.red}/>
        <KPI l="Este Mês" v={fmt(despesas.filter(d=>d.data.startsWith(new Date().toISOString().slice(0,7))).reduce((s:number,d:any)=>s+d.valor,0))} c="mês actual" ac={T.amber}/>
        <KPI l="Nº Registos" v={despesas.length} c="despesas" ac={T.muted}/>
      </div>

      <Card title="Registo de Despesas" accent={T.red} action="+ Despesa" onAction={()=>setAdding(true)}>
        {adding && (
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,
            display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10}}>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Data</div>
              <input type="date" value={newD.data} onChange={(e:any)=>setNewD({...newD,data:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Descrição</div>
              <input value={newD.desc} onChange={(e:any)=>setNewD({...newD,desc:e.target.value})} placeholder="Ex: Licença software"
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Valor (€)</div>
              <input type="number" value={newD.valor} onChange={(e:any)=>setNewD({...newD,valor:+e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Tipo</div>
              <select value={newD.tipo} onChange={(e:any)=>setNewD({...newD,tipo:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                {Object.entries(tipos).map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Projecto</div>
              <select value={newD.projeto} onChange={(e:any)=>setNewD({...newD,projeto:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                <option value="">Geral</option>
                {projects?.map((p:any)=><option key={p.id} value={p.titulo}>{p.titulo}</option>)}
              </select>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
              <button onClick={addDesp} style={{padding:"8px 16px",borderRadius:8,border:"none",background:T.red,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}

        <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:500}}>
          <thead>
            <tr style={{borderBottom:`1px solid ${T.border}`}}>
              {["Data","Descrição","Tipo","Projecto","Valor"].map(h=>(
                <th key={h} style={{padding:"9px 12px",textAlign:"left",color:T.muted,fontSize:11,fontWeight:700,textTransform:"uppercase"}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {despesas.sort((a,b)=>b.data.localeCompare(a.data)).map((d:any)=>(
              <tr key={d.id} style={{borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
                <td style={{padding:"9px 12px",color:T.textSub,whiteSpace:"nowrap"}}>{d.data}</td>
                <td style={{padding:"9px 12px",color:T.text}}>{d.desc}</td>
                <td style={{padding:"9px 12px"}}><Tag label={tipos[d.tipo]||d.tipo} color={T.muted}/></td>
                <td style={{padding:"9px 12px",color:T.muted,fontSize:11}}>{d.projeto||"—"}</td>
                <td style={{padding:"9px 12px",color:T.red,fontFamily:"monospace",fontWeight:700}}>{fmt(d.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}

function HW3DView({ page, addLog, onProjChange }: any) {
  const { data: clientesDB, insert: insertCliente, update: updateCliente, remove: removeCliente } = useHW3DClientes();
  const { data: projectsDB, insert: insertProject, update: updateProject, remove: removeProject } = useHW3DProjetos();
  const { data: despesasDB, insert: insertDespesa } = useHW3DDespesas();
  const [clientesLocal] = useState(isDemoAccount() ? DEMO_HW3D_CLIENTES : []);
  const [projectsLocal] = useState(isDemoAccount() ? DEMO_HW3D_PIPELINE : []);
  const clientes = isDemoAccount() ? clientesLocal : clientesDB;
  const setClientes = (_: any) => {};
  const projects = isDemoAccount() ? projectsLocal : (projectsDB || []);
  const setProjects = (_: any) => {};
  const updateProjects = (fn: any) => {};
  const [gigs, setGigs]         = useState(FIVERR_GIGS_INIT);
  const { data: tarefasDB, insert: insertTarefa, update: updateTarefa } = useHW3DTarefas();
  const [tarefasLocal, setTarefasLocal] = useState<any[]>(HW3D_TAREFAS_INIT);
  const tarefas = isDemoAccount() ? tarefasLocal : (tarefasDB || []);
  const setTarefas = isDemoAccount() ? setTarefasLocal : (_:any) => {};

  if (page==="fiverr")   return <FiverrView gigs={gigs} setGigs={setGigs} bestseller={null}/>;
  if (page==="assets")   return <HW3DAssets/>;
  if (page==="clientes") return <HW3DClientes clientes={clientes} setClientes={setClientes} addLog={addLog}/>;
  if (page==="projetos") return <HW3DProjetos projects={projects} setProjects={setProjects} clientes={clientes} addLog={addLog}/>;
  if (page==="funil") return <HW3DPipeline projects={projects} setProjects={setProjects} addLog={addLog}/>;
  if (page==="fin")      return <HW3DFinDashboard projects={projects}/>;
  if (page==="kpi")      return <HW3DKPI projects={projects} clientes={clientes}/>;
  if (page==="tarefas")  return <HW3DTarefas tarefas={tarefas} setTarefas={setTarefas} projects={projects} addLog={addLog}/>;
  // overview
  const ativos = projects.filter(p=>["Em Produção","Adjudicado"].includes(p.etapa));
  const concl  = projects.filter(p=>p.etapa==="Concluído");
  const pendentes = projects.filter(p=>["Briefing","Orçamentação","Negociação"].includes(p.etapa));
  const receitaTotal = concl.reduce((s:number,p:any)=>s+(p.valorEst||0),0);
  const hoje = new Date().toISOString().split("T")[0];
  const deadlines = projects.filter((p:any)=>p.dataFim&&p.dataFim>=hoje&&p.etapa!=="Concluído")
    .sort((a:any,b:any)=>a.dataFim.localeCompare(b.dataFim)).slice(0,3);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <AlertPanelHW3D projects={projects}/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Projetos Ativos"  v={ativos.length}   c="em execução"      ac={T.teal}/>
        <KPI l="Concluídos"       v={concl.length}    c="entregues"        ac={T.green}/>
        <KPI l="Em Pipeline"      v={pendentes.length} c="a negociar"       ac={T.amber}/>
        <KPI l="Receita Fechada"  v={fmt(receitaTotal)} c="projetos concluídos" ac={T.teal}/>
      </div>

      {/* Deadlines a vencer */}
      {deadlines.length>0 && (
        <div style={{padding:"12px 16px",borderRadius:10,background:"rgba(232,82,82,0.07)",
          border:"1px solid rgba(232,82,82,0.25)"}}>
          <div style={{fontSize:11,fontWeight:700,color:T.red,marginBottom:8}}>⏰ DEADLINES A VENCER</div>
          {deadlines.map((p:any)=>{
            const dias = Math.ceil((new Date(p.dataFim).getTime()-new Date().getTime())/(86400000));
            return (
              <div key={p.id} style={{display:"flex",gap:12,alignItems:"center",marginBottom:4}}>
                <span style={{fontSize:12,color:T.text,fontWeight:600}}>{p.titulo}</span>
                <Tag label={`${dias}d restantes`} color={dias<=3?T.red:T.amber}/>
                <Tag label={p.etapa} color={T.teal}/>
              </div>
            );
          })}
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16}}>
        <Card title="Projetos em Andamento" accent={T.teal}>
          {ativos.length===0&&<Empty icon="🧊" msg="Sem projetos ativos" sub="Adiciona no menu Projetos"/>}
          {ativos.map((p:any)=>(
            <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,
              padding:"9px 0",borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
              <div style={{flex:1}}>
                <div style={{fontSize:12,color:T.text,fontWeight:600}}>{p.titulo}</div>
                <div style={{fontSize:11,color:T.muted}}>
                  {clientes.find((c:any)=>c.id===p.clienteId)?.nome||"—"} · {p.dataFim||"sem prazo"}
                </div>
              </div>
              <Tag label={p.etapa} color={T.teal}/>
              <span style={{fontSize:12,fontWeight:700,color:T.amber,fontFamily:"monospace"}}>{fmt(p.valorEst)}</span>
            </div>
          ))}
        </Card>
        <Card title="Tarefas da Semana" accent={T.teal}>
          {tarefas.filter((t:any)=>t.status!=="concluída").slice(0,5).map((t:any)=>(
            <div key={t.id} style={{display:"flex",gap:8,padding:"8px 0",
              borderBottom:`1px solid rgba(255,255,255,0.04)`,alignItems:"center"}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:T.teal,flexShrink:0}}/>
              <span style={{fontSize:12,color:T.text,flex:1}}>{t.titulo}</span>
              {t.dataFim&&<span style={{fontSize:11,color:T.muted}}>{t.dataFim}</span>}
            </div>
          ))}
          {tarefas.filter((t:any)=>t.status!=="concluída").length===0&&
            <div style={{fontSize:12,color:T.muted,textAlign:"center",padding:"12px"}}>Sem tarefas pendentes</div>}
        </Card>
      </div>
    </div>
  );
}

// ── HW3D — CLIENTES CRM ──
function HW3DClientes({clientes,setClientes,addLog}:any) {
  const [addingC,setAddingC]=useState(false);
  const [newC,setNewC]=useState({nome:"",empresa:"",email:"",nif:"",tipo:"Arquitetura",origem:"LinkedIn",contacto:"",notas:""});
  const tipos = ["Arquitetura","Imobiliário","Publicidade","Gaming","Outro"];
  const origens = ["Instagram","LinkedIn","Recomendação","Website","Fiverr","Outro"];

  const addCliente=()=>{
    setClientes([...clientes,{...newC,id:clientes.length+1,projetos:0,faturado:0}]);
    addLog?.("hw3d","cliente",`Novo cliente: ${newC.nome} · ${newC.empresa||"—"} · ${newC.tipo}`,"👥");
    setAddingC(false);
    setNewC({nome:"",empresa:"",email:"",nif:"",tipo:"Arquitetura",origem:"LinkedIn",contacto:"",notas:""});
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Total Clientes" v={clientes.length} c="" ac={T.teal}/>
        <KPI l="Arquitetura" v={clientes.filter((c:any)=>c.tipo==="Arquitetura").length} c="" ac={T.teal}/>
        <KPI l="Imobiliário" v={clientes.filter((c:any)=>c.tipo==="Imobiliário").length} c="" ac={T.teal}/>
        <KPI l="Faturado Total" v={fmt(clientes.reduce((s:number,c:any)=>s+c.faturado,0))} c="" ac={T.amber}/>
      </div>
      <Card title="CRM Clientes" accent={T.teal} action="+ Novo Cliente" onAction={()=>setAddingC(!addingC)}>
        {addingC&&(
          <div style={{marginBottom:14,padding:14,borderRadius:10,background:"rgba(45,212,191,0.05)",
            border:`1px solid rgba(45,212,191,0.2)`,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
            {([["Nome","text","nome","Nome completo"],["Empresa","text","empresa","Empresa"],
               ["Email","email","email","email@empresa.com"],["NIF","text","nif","NIF"],
               ["Contacto","text","contacto","Telefone"]] as any).map(([l,t,k,ph]:any)=>(
              <div key={k}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>{l}</div>
                <input type={t} placeholder={ph} value={newC[k]} onChange={(e:any)=>setNewC({...newC,[k]:e.target.value})}
                  style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
              </div>
            ))}
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Tipo</div>
              <select value={newC.tipo} onChange={(e:any)=>setNewC({...newC,tipo:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"#0c1322",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12}}>
                {tipos.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Origem do Lead</div>
              <select value={newC.origem} onChange={(e:any)=>setNewC({...newC,origem:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"#0c1322",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12}}>
                {origens.map(o=><option key={o}>{o}</option>)}
              </select>
            </div>
            <div style={{gridColumn:"1/-1"}}>
              <input placeholder="Notas..." value={newC.notas} onChange={(e:any)=>setNewC({...newC,notas:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
            </div>
            <button onClick={addCliente} style={{gridColumn:"1/-1",padding:"9px",borderRadius:10,border:"none",
              background:T.teal,color:"#05080f",fontSize:13,fontWeight:700,cursor:"pointer"}}>Guardar Cliente</button>
          </div>
        )}
        <div style={{overflowX:"auto", WebkitOverflowScrolling:"touch" as any as any}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:600}}>
            <thead>
              <tr style={{borderBottom:`1px solid ${T.border}`}}>
                {["Nome","Empresa","Email","Tipo","Origem","Projetos","Faturado","Contacto"].map(h=>(
                  <th key={h} style={{padding:"8px 12px",textAlign:"left",color:T.muted,fontSize:11,fontWeight:700,textTransform:"uppercase"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clientes.map((c:any)=>(
                <tr key={c.id} style={{borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
                  <td style={{padding:"9px 12px",fontWeight:600,color:T.text}}>{c.nome}</td>
                  <td style={{padding:"9px 12px",color:T.muted}}>{c.empresa||"—"}</td>
                  <td style={{padding:"9px 12px",color:T.muted}}>{c.email||"—"}</td>
                  <td style={{padding:"9px 12px"}}><Tag label={c.tipo} color={T.teal}/></td>
                  <td style={{padding:"9px 12px"}}><Tag label={c.origem} color={T.muted}/></td>
                  <td style={{padding:"9px 12px",color:T.text}}>{c.projetos}</td>
                  <td style={{padding:"9px 12px",fontWeight:700,color:T.amber,fontFamily:"monospace"}}>{fmt(c.faturado)}</td>
                  <td style={{padding:"9px 12px",color:T.muted}}>{c.contacto||"—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {clientes.length===0&&<Empty icon="👥" msg="Sem clientes" sub="Adiciona o primeiro cliente"/>}
      </Card>
    </div>
  );
}

// ── HW3D — PROJETOS ──
function HW3DProjetos({projects,setProjects,clientes,addLog}:any) {
  const [addingP,setAddingP]=useState(false);
  const [newP,setNewP]=useState({titulo:"",clienteId:1,etapa:"Briefing",prob:20,valorEst:0,dataInicio:"",dataFim:"",hh:0,custoRender:0,assets:0,revisoes:0,notas:""});
  const hoje = new Date().toISOString().split("T")[0];

  const addProjeto=()=>{
    setProjects([...projects,{...newP,id:projects.length+1,valorEst:+newP.valorEst,hh:+newP.hh}]);
    addLog?.("hw3d","projeto",`Novo projeto: ${newP.titulo} · ${fmt(+newP.valorEst)}`,"🧊",+newP.valorEst);
    setAddingP(false);
  };
  const iniciar=(id:number)=>{
    const p=projects.find((x:any)=>x.id===id);
    setProjects((ps:any[])=>ps.map(p=>p.id===id&&!p.dataInicio?{...p,dataInicio:hoje,etapa:"Em Produção"}:p));
    if(p) addLog?.("hw3d","projeto",`Projeto iniciado: ${p.titulo}`,"▶️");
  };
  const concluir=(id:number)=>{
    const p=projects.find((x:any)=>x.id===id);
    setProjects((ps:any[])=>ps.map(p=>p.id===id?{...p,dataFim:hoje,etapa:"Concluído"}:p));
    if(p) addLog?.("hw3d","projeto",`Projeto concluído: ${p.titulo} · ${fmt(p.valorEst||0)}`,"✅",p.valorEst||0);
  };

  const etapaColor:any={Briefing:T.muted,Orçamentação:T.blue,Negociação:T.amber,Adjudicado:T.purple,"Em Produção":T.teal,Concluído:T.green};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        {HW3D_PIPELINE_STAGES.map(e=>(
          <KPI key={e} l={e} v={projects.filter((p:any)=>p.etapa===e).length} c="" ac={etapaColor[e]||T.muted}/>
        ))}
      </div>
      <Card title="Projetos" accent={T.teal} action="+ Novo Projeto" onAction={()=>setAddingP(!addingP)}>
        {addingP&&(
          <div style={{marginBottom:14,padding:14,borderRadius:10,background:"rgba(45,212,191,0.05)",
            border:`1px solid rgba(45,212,191,0.2)`,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
            <div style={{gridColumn:"1/-1"}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Título do Projeto</div>
              <input value={newP.titulo} onChange={(e:any)=>setNewP({...newP,titulo:e.target.value})}
                placeholder="Ex: Render Exterior Moradia" style={{width:"100%",padding:"8px 11px",borderRadius:8,
                  background:"rgba(255,255,255,0.05)",border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Cliente</div>
              <select value={newP.clienteId} onChange={(e:any)=>setNewP({...newP,clienteId:+e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"#0c1322",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12}}>
                {clientes.map((c:any)=><option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Etapa</div>
              <select value={newP.etapa} onChange={(e:any)=>setNewP({...newP,etapa:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"#0c1322",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12}}>
                {HW3D_PIPELINE_STAGES.map(e=><option key={e}>{e}</option>)}
              </select>
            </div>
            {([["Valor Est. €","number","valorEst"],["Data Início","date","dataInicio"],["Data Fim","date","dataFim"],
               ["Horas HH","number","hh"],["Custo Render €","number","custoRender"],["Assets €","number","assets"]] as any).map(([l,t,k]:any)=>(
              <div key={k}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>{l}</div>
                <input type={t} value={newP[k]} onChange={(e:any)=>setNewP({...newP,[k]:e.target.value})}
                  style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
              </div>
            ))}
            <button onClick={addProjeto} style={{gridColumn:"1/-1",padding:"9px",borderRadius:10,border:"none",
              background:T.teal,color:"#05080f",fontSize:13,fontWeight:700,cursor:"pointer"}}>Guardar Projeto</button>
          </div>
        )}
        {projects.map((p:any)=>{
          const cl=clientes.find((c:any)=>c.id===p.clienteId);
          const hoje2=new Date().toISOString().split("T")[0];
          const diasFim=p.dataFim?Math.ceil((new Date(p.dataFim).getTime()-new Date().getTime())/(86400000)):null;
          const atrasado=p.dataFim&&p.dataFim<hoje2&&p.etapa!=="Concluído";
          const margem=p.valorEst>0?Math.round((p.valorEst-(p.hh*25+p.custoRender+p.assets))/p.valorEst*100):0;
          return (
            <div key={p.id} style={{padding:"12px 0",borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,color:atrasado?T.red:T.text,fontWeight:600}}>
                    {p.titulo} {atrasado&&<Tag label="⚠ Atrasado" color={T.red}/>}
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2,display:"flex",gap:8,flexWrap:"wrap" as any}}>
                    <span>{cl?.nome||"—"}</span>
                    {p.dataInicio&&<span>🟢 Início: {p.dataInicio}</span>}
                    {p.dataFim&&<span>🔴 Fim: {p.dataFim}</span>}
                    {diasFim!==null&&p.etapa!=="Concluído"&&<Tag label={`${diasFim}d`} color={diasFim<=3?T.red:T.amber}/>}
                    {p.hh>0&&<span>⏱ {p.hh}h</span>}
                    {margem>0&&<span style={{color:T.green}}>Margem: {margem}%</span>}
                  </div>
                </div>
                <span style={{fontSize:14,fontWeight:700,color:T.amber,fontFamily:"monospace"}}>{fmt(p.valorEst)}</span>
                <Tag label={p.etapa} color={etapaColor[p.etapa]||T.muted}/>
                {!p.dataInicio&&p.etapa!=="Concluído"&&(
                  <button onClick={()=>iniciar(p.id)} style={{padding:"4px 10px",borderRadius:6,border:"none",
                    background:T.teal,color:"#05080f",fontSize:11,fontWeight:700,cursor:"pointer"}}>▶ Iniciar</button>
                )}
                {p.dataInicio&&p.etapa!=="Concluído"&&(
                  <button onClick={()=>concluir(p.id)} style={{padding:"4px 10px",borderRadius:6,border:"none",
                    background:T.green,color:"white",fontSize:11,fontWeight:700,cursor:"pointer"}}>✓ Concluir</button>
                )}
              </div>
            </div>
          );
        })}
        {projects.length===0&&<Empty icon="🧊" msg="Sem projetos" sub="Adiciona o primeiro projeto"/>}
      </Card>
    </div>
  );
}

// ── HW3D — PIPELINE ──
function HW3DPipeline({projects,setProjects,addLog}:any) {
  const etapaColor:any={Briefing:T.muted,Orçamentação:T.blue,Negociação:T.amber,Adjudicado:T.purple,"Em Produção":T.teal,Concluído:T.green};
  const avancar=(id:number)=>{
    const p=projects.find((x:any)=>x.id===id);
    const idx=HW3D_PIPELINE_STAGES.indexOf(p?.etapa||"");
    if(idx<HW3D_PIPELINE_STAGES.length-1){
      const novaEtapa=HW3D_PIPELINE_STAGES[idx+1];
      setProjects((ps:any[])=>ps.map(x=>x.id===id?{...x,etapa:novaEtapa}:x));
      if(p) addLog?.("hw3d","projeto",`Pipeline: ${p.titulo} → ${novaEtapa}`,"🔄");
    }
  };
  const pipelineTotal=projects.filter((p:any)=>p.etapa!=="Concluído").reduce((s:number,p:any)=>s+(p.valorEst||0),0);
  const ponderado=projects.filter((p:any)=>p.etapa!=="Concluído").reduce((s:number,p:any)=>s+(p.valorEst||0)*(p.prob||0)/100,0);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <KPI l="Pipeline Total" v={fmt(pipelineTotal)} c="valor bruto" ac={T.teal}/>
        <KPI l="Pipeline Pond." v={fmt(Math.round(ponderado))} c="ajustado probabilidade" ac={T.teal}/>
        <KPI l="Negócios Abertos" v={projects.filter((p:any)=>p.etapa!=="Concluído").length} c="" ac={T.teal}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(80px,1fr))",gap:8}}>
        {HW3D_PIPELINE_STAGES.map(etapa=>{
          const ps=projects.filter((p:any)=>p.etapa===etapa);
          return (
            <div key={etapa} style={{background:T.panel,border:`1px solid ${etapaColor[etapa]}30`,borderRadius:10,padding:"10px"}}>
              <div style={{fontSize:11,fontWeight:700,color:etapaColor[etapa],textTransform:"uppercase",marginBottom:8,letterSpacing:"0.08em"}}>{etapa}</div>
              <div style={{fontSize:11,color:T.muted,marginBottom:8}}>{ps.length} proj. · {fmt(ps.reduce((s:number,p:any)=>s+(p.valorEst||0),0))}</div>
              {ps.map((p:any)=>(
                <div key={p.id} style={{padding:"8px 10px",borderRadius:6,marginBottom:6,
                  background:`${etapaColor[etapa]}10`,border:`1px solid ${etapaColor[etapa]}25`}}>
                  <div style={{fontSize:11,color:T.text,fontWeight:600,marginBottom:4}}>{p.titulo}</div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontSize:11,color:T.amber,fontFamily:"monospace"}}>{fmt(p.valorEst)}</span>
                    {etapa!=="Concluído"&&(
                      <button onClick={()=>avancar(p.id)}
                        style={{fontSize:11,padding:"2px 7px",borderRadius:6,border:"none",
                          background:etapaColor[etapa],color:"white",cursor:"pointer",fontWeight:700}}>→</button>
                    )}
                  </div>
                  {p.prob>0&&(
                    <div style={{marginTop:4,height:3,borderRadius:99,background:"rgba(255,255,255,0.06)"}}>
                      <div style={{width:`${p.prob}%`,height:"100%",borderRadius:99,background:etapaColor[etapa]}}/>
                    </div>
                  )}
                </div>
              ))}
              {ps.length===0&&<div style={{fontSize:11,color:T.muted,textAlign:"center",padding:"8px 0"}}>—</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── HW3D — DASHBOARD FINANCEIRO ──
function HW3DFinDashboard({projects}:any) {
  const concl=projects.filter((p:any)=>p.etapa==="Concluído");
  const receitaTotal=concl.reduce((s:number,p:any)=>s+(p.valorEst||0),0);
  const custoHH=projects.reduce((s:number,p:any)=>s+(p.hh||0)*25,0); // €25/hora
  const custoRender=projects.reduce((s:number,p:any)=>s+(p.custoRender||0),0);
  const custoAssets=projects.reduce((s:number,p:any)=>s+(p.assets||0),0);
  const custoTotal=custoHH+custoRender+custoAssets;
  const margem=receitaTotal>0?Math.round((receitaTotal-custoTotal)/receitaTotal*100):0;
  const ticketMedio=concl.length>0?Math.round(receitaTotal/concl.length):0;

  const tipoStats = HW3D_SERVICES.map(s=>({
    tipo:s,
    projetos:projects.filter((p:any)=>p.tipo===s).length,
    receita:projects.filter((p:any)=>p.tipo===s&&p.etapa==="Concluído").reduce((sum:number,p:any)=>sum+(p.valorEst||0),0),
  })).filter(s=>s.projetos>0);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Receita Total" v={fmt(receitaTotal)} c={`${concl.length} projetos concluídos`} ac={T.teal}/>
        <KPI l="Margem Bruta" v={`${margem}%`} c="(receita - custos)" ac={margem>=50?T.green:T.amber}/>
        <KPI l="Ticket Médio" v={fmt(ticketMedio)} c="por projeto" ac={T.teal}/>
        <KPI l="Custo Total" v={fmt(custoTotal)} c="HH + Render + Assets" ac={T.red}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16}}>
        <Card title="Custos Detalhados" accent={T.teal}>
          {[["Custo HH (€25/hora)",fmt(custoHH),T.red],["Custo Render Farm",fmt(custoRender),T.red],
            ["Assets / Plugins",fmt(custoAssets),T.red],["TOTAL CUSTOS",fmt(custoTotal),T.red],
            ["LUCRO BRUTO",fmt(receitaTotal-custoTotal),T.green]].map(([k,v,c]:any,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",
              borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
              <span style={{fontSize:12,color:T.muted}}>{k}</span>
              <span style={{fontSize:12,fontWeight:700,color:c,fontFamily:"monospace"}}>{v}</span>
            </div>
          ))}
        </Card>
        <Card title="Por Tipo de Serviço" accent={T.teal}>
          {tipoStats.length===0&&<Empty icon="📊" msg="Sem dados" sub="Adiciona projetos concluídos"/>}
          {tipoStats.sort((a,b)=>b.receita-a.receita).map(s=>(
            <div key={s.tipo} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
              padding:"8px 0",borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
              <div>
                <div style={{fontSize:12,color:T.text,fontWeight:600}}>{s.tipo}</div>
                <div style={{fontSize:11,color:T.muted}}>{s.projetos} projetos</div>
              </div>
              <span style={{fontSize:12,fontWeight:700,color:T.amber,fontFamily:"monospace"}}>{fmt(s.receita)}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ── HW3D — KPIs & ANÁLISE ──
function HW3DKPI({projects,clientes}:any) {
  const concl=projects.filter((p:any)=>p.etapa==="Concluído");
  const receitaTotal=concl.reduce((s:number,p:any)=>s+(p.valorEst||0),0);
  const revisoes=projects.reduce((s:number,p:any)=>s+(p.revisoes||0),0);
  const horas=projects.reduce((s:number,p:any)=>s+(p.hh||0),0);

  // Melhor cliente
  const clienteStats=clientes.map((c:any)=>({
    ...c,
    nProj:projects.filter((p:any)=>p.clienteId===c.id).length,
    receita:projects.filter((p:any)=>p.clienteId===c.id&&p.etapa==="Concluído").reduce((s:number,p:any)=>s+(p.valorEst||0),0)
  }));
  const melhorCliente=clienteStats.sort((a:any,b:any)=>b.receita-a.receita)[0];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Taxa de Ocupação" v={horas>0?`${Math.min(100,Math.round(horas/160*100))}%`:"—"} c="horas/mês disponível" ac={T.teal}/>
        <KPI l="Revisões Médias" v={projects.length>0?(revisoes/projects.length).toFixed(1):"—"} c="por projeto" ac={T.amber}/>
        <KPI l="Melhor Cliente" v={melhorCliente?.nome||"—"} c={fmt(melhorCliente?.receita||0)} ac={T.gold}/>
        <KPI l="Revenue/Hora" v={horas>0?fmt(Math.round(receitaTotal/horas)):"—"} c="eficiência" ac={T.teal}/>
      </div>
      <Card title="Análise de Clientes" accent={T.teal}>
        {clienteStats.map((c:any)=>(
          <div key={c.id} style={{display:"flex",alignItems:"center",gap:12,
            padding:"10px 0",borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
            <div style={{flex:1}}>
              <div style={{fontSize:12,color:T.text,fontWeight:600}}>{c.nome}
                {melhorCliente?.id===c.id&&<Tag label="★ Top" color={T.gold}/>}
              </div>
              <div style={{fontSize:11,color:T.muted}}>{c.empresa} · {c.tipo} · {c.origem}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:12,fontWeight:700,color:T.amber,fontFamily:"monospace"}}>{fmt(c.receita)}</div>
              <div style={{fontSize:11,color:T.muted}}>{c.nProj} projetos</div>
            </div>
          </div>
        ))}
        {clientes.length===0&&<Empty icon="👥" msg="Sem dados" sub="Adiciona clientes no CRM"/>}
      </Card>
      <Card title="Previsão Preditiva (próx. 3 meses)" accent={T.teal}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
          {["M+1","M+2","M+3"].map((m,i)=>(
            <div key={m} style={{padding:"14px",borderRadius:10,background:"rgba(45,212,191,0.06)",
              border:`1px solid rgba(45,212,191,0.2)`,textAlign:"center"}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:6}}>{m}</div>
              <div style={{fontSize:16,fontWeight:700,color:T.teal,fontFamily:"monospace"}}>
                {fmt(Math.round(receitaTotal/Math.max(concl.length,1)*(1+i*0.1)))}
              </div>
              <div style={{fontSize:11,color:T.muted,marginTop:4}}>baseado em média</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── HW3D — TAREFAS ──
function HW3DTarefas({tarefas,setTarefas,projects,addLog}:any) {
  const [addingT,setAddingT]=useState(false);
  const [newT,setNewT]=useState({titulo:"",projectoId:"",responsavel:"",dataInicio:"",dataFim:"",status:"por fazer",categoria:"geral"});
  const hoje=new Date().toISOString().split("T")[0];
  const addTarefa=()=>{
    setTarefas([...tarefas,{...newT,id:tarefas.length+1}]);
    addLog?.("hw3d","tarefa",`Nova tarefa: ${newT.titulo}`,"✅");
    setAddingT(false);
    setNewT({titulo:"",projectoId:"",responsavel:"",dataInicio:"",dataFim:"",status:"por fazer",categoria:"geral"});
  };
  const cycleStatus=(id:number)=>setTarefas((ts:any[])=>ts.map(t=>t.id!==id?t:{...t,
    status:t.status==="por fazer"?"em progresso":t.status==="em progresso"?"concluída":"por fazer"}));
  const statusColor:any={"por fazer":T.muted,"em progresso":T.amber,"concluída":T.green,"atrasada":T.red};
  const cats=["geral","projeto","marketing","admin","técnico"];
  const atrasadas=tarefas.filter((t:any)=>t.dataFim&&t.dataFim<hoje&&t.status!=="concluída");

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Por Fazer" v={tarefas.filter((t:any)=>t.status==="por fazer").length} c="" ac={T.muted}/>
        <KPI l="Em Progresso" v={tarefas.filter((t:any)=>t.status==="em progresso").length} c="" ac={T.amber}/>
        <KPI l="Concluídas" v={tarefas.filter((t:any)=>t.status==="concluída").length} c="" ac={T.green}/>
        <KPI l="Atrasadas" v={atrasadas.length} c="" ac={atrasadas.length>0?T.red:T.muted}/>
      </div>
      <Card title="Tarefas" accent={T.teal} action="+ Nova Tarefa" onAction={()=>setAddingT(!addingT)}>
        {addingT&&(
          <div style={{marginBottom:14,padding:14,borderRadius:10,background:"rgba(45,212,191,0.05)",
            border:`1px solid rgba(45,212,191,0.2)`,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
            <div style={{gridColumn:"1/-1"}}>
              <input placeholder="Título da tarefa..." value={newT.titulo}
                onChange={(e:any)=>setNewT({...newT,titulo:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
            </div>
            {([["Responsável","text","responsavel","Nome"],["Data Início","date","dataInicio",""],["Data Fim","date","dataFim",""]] as any).map(([l,t,k,ph]:any)=>(
              <div key={k}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>{l}</div>
                <input type={t} placeholder={ph} value={newT[k]} onChange={(e:any)=>setNewT({...newT,[k]:e.target.value})}
                  style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
              </div>
            ))}
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Categoria</div>
              <select value={newT.categoria} onChange={(e:any)=>setNewT({...newT,categoria:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"#0c1322",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12}}>
                {cats.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Projeto (opcional)</div>
              <select value={newT.projectoId} onChange={(e:any)=>setNewT({...newT,projectoId:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"#0c1322",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12}}>
                <option value="">— Geral —</option>
                {projects.map((p:any)=><option key={p.id} value={String(p.id)}>{p.titulo}</option>)}
              </select>
            </div>
            <button onClick={addTarefa} style={{gridColumn:"1/-1",padding:"9px",borderRadius:10,border:"none",
              background:T.teal,color:"#05080f",fontSize:13,fontWeight:700,cursor:"pointer"}}>Guardar</button>
          </div>
        )}
        {tarefas.length===0&&<Empty icon="✅" msg="Sem tarefas" sub="Clica em + Nova Tarefa"/>}
        {tarefas.map((t:any)=>{
          const atrasada=t.dataFim&&t.dataFim<hoje&&t.status!=="concluída";
          const st=atrasada?"atrasada":t.status;
          return (
            <div key={t.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",
              borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
              <div onClick={()=>cycleStatus(t.id)} style={{width:20,height:20,borderRadius:6,flexShrink:0,
                border:`2px solid ${statusColor[st]||T.muted}`,
                background:t.status==="concluída"?T.green:t.status==="em progresso"?T.amber:"transparent",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:11,color:"white",cursor:"pointer"}}>
                {t.status==="concluída"?"✓":t.status==="em progresso"?"…":""}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,color:atrasada?T.red:t.status==="concluída"?T.muted:T.text,
                  fontWeight:600,textDecoration:t.status==="concluída"?"line-through":"none"}}>{t.titulo}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:2,display:"flex",gap:8}}>
                  {t.responsavel&&<span>👤 {t.responsavel}</span>}
                  {t.dataFim&&<span>📅 {t.dataFim}</span>}
                  {t.categoria&&<span>#{t.categoria}</span>}
                </div>
              </div>
              <Tag label={st} color={statusColor[st]||T.muted}/>
              {atrasada&&<Tag label="⚠ Atrasada" color={T.red}/>}
            </div>
          );
        })}
      </Card>
    </div>
  );
}

function HW3DAssets() {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Assets Publicados" v="0" c="meta M12: 50+" ac={T.teal}/>
        <KPI l="Receita Passiva" v="€0" c="meta M12: €900+" ac={T.teal}/>
        <KPI l="Custo/Asset" v="<€8" c="meta" ac={T.teal}/>
        <KPI l="Plataformas" v="5" c="CGTrader · TurboSquid · Fab" ac={T.teal}/>
      </div>
      <Empty icon="📦" msg="Assets 3D — a configurar"
        sub="Pipeline: Meshy.ai / Tripo3D → finish 3DS Max → auto-publicar CGTrader + TurboSquid + Fab"/>
    </div>
  );
}

function FiverrView({ gigs, setGigs, bestseller }: any) {
  const toggleStatus = (id:number) => setGigs((gs:any[]) => gs.map(g =>
    g.id===id ? { ...g, status: g.status==="ativo"?"rascunho":"ativo" } : g
  ));
  const totalOrders = gigs.reduce((s:number,g:any)=>s+(g.orders||0),0);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Gigs Ativos" v={gigs.filter((g:any)=>g.status==="ativo").length} c="meta M3: 10" ac={T.teal}/>
        <KPI l="Total Pedidos" v={totalOrders} c="meta M3: 8/semana" ac={T.teal}/>
        <KPI l="Rating Médio" v="—" c="meta: 4.8+" ac={T.teal}/>
        <KPI l="Custo/Gig" v="€2–8" c="Narva.ai / Rendair" ac={T.teal}/>
      </div>
      {bestseller && (
        <div style={{padding:"10px 16px",borderRadius:10,background:"rgba(201,168,76,0.07)",
          border:"1px solid rgba(201,168,76,0.25)",display:"flex",alignItems:"center",gap:10}}>
          <BestsellerBadge label={bestseller}/>
          <span style={{fontSize:12,color:T.muted}}>— calculado por volume de pedidos</span>
        </div>
      )}
      <Card title="Fiverr Gigs" accent={T.teal} noPad>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead>
            <tr style={{borderBottom:`1px solid ${T.border}`}}>
              {["Gig","Serviço","Preço","Pedidos","Entrega","Status"].map(h=>(
                <th key={h} style={{padding:"9px 14px",textAlign:"left",color:T.muted,
                  fontSize:11,fontWeight:700,textTransform:"uppercase"}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gigs.map((g:any)=>(
              <tr key={g.id} style={{borderBottom:`1px solid rgba(255,255,255,0.04)`}}
                onMouseEnter={(e:any)=>e.currentTarget.style.background="rgba(255,255,255,0.025)"}
                onMouseLeave={(e:any)=>e.currentTarget.style.background="transparent"}>
                <td style={{padding:"10px 14px",color:T.text}}>{g.title}</td>
                <td style={{padding:"10px 14px"}}><Tag label={g.servico} color={T.teal}/></td>
                <td style={{padding:"10px 14px",color:T.amber,fontFamily:"monospace",fontWeight:700}}>{g.preco}</td>
                <td style={{padding:"10px 14px",color:T.text}}>{g.orders}</td>
                <td style={{padding:"10px 14px",color:T.teal,fontFamily:"monospace"}}>{g.delivery}</td>
                <td style={{padding:"10px 14px"}}>
                  <div onClick={()=>toggleStatus(g.id)} style={{cursor:"pointer"}}>
                    <Tag label={g.status} color={g.status==="ativo"?T.green:T.muted}/>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


// ── KAIROS AI ──
function KaiView({ page, addLog }: any) {
  const { data: clientes, insert: insertCliente, update: updateCliente, remove: removeCliente } = useKaiClientes();
  const { data: projetos, insert: insertProjeto, update: updateProjeto, remove: removeProjeto } = useKaiProjetos();
  const { data: retainers, insert: insertRetainer, update: updateRetainer } = useKaiRetainers();
  const { data: despesas, insert: insertDespesa } = useKaiDespesas();
  const { data: calEntries, insert: insertCalEntry } = useKaiCalendario();
  // Setters locais para compatibilidade com sub-componentes (passamos as funções de insert/update)
  const setProjetos = (_: any) => {};
  const setRetainers = (_: any) => {};
  const setDespesas = (_: any) => {};
  const setCalEntries = (_: any) => {};
  // (dados vêm do Supabase via hooks acima)

  if (page==="clientes")  return <KaiCRM clientes={clientes} onAddCliente={insertCliente} projetos={projetos} addLog={addLog}/>;
  if (page==="projetos")  return <KaiProjetos projetos={projetos} setProjetos={setProjetos} addLog={addLog}/>;
  if (page==="retainers") return <KaiRetainers retainers={retainers} setRetainers={setRetainers} addLog={addLog}/>;
  if (page==="funil")     return <KaiFunil projetos={projetos}/>;
  if (page==="despesas")  return <KaiDespesas despesas={despesas} setDespesas={setDespesas} projetos={projetos} addLog={addLog}/>;
  if (page==="kpi")       return <KaiKPI projetos={projetos} retainers={retainers} despesas={despesas}/>;
  if (page==="tarefas")   return <KaiTarefas projetos={projetos}/>;
  if (page==="calendario") return <KaiCalendario entries={calEntries} setEntries={setCalEntries}/>;
  return <KaiOverview projetos={projetos} retainers={retainers} despesas={despesas}/>;
}

// ─── KAI: Overview ───────────────────────────────────────────
function KaiOverview({ projetos, retainers, despesas }: any) {
  const totalFaturado = projetos.reduce((s:number,p:any)=>s+(p.faturado||0),0);
  const totalDesp = despesas.reduce((s:number,d:any)=>s+(d.valor||0),0) +
    projetos.reduce((s:number,p:any)=>s+(p.despesas||0),0);
  const lucro = totalFaturado - totalDesp;
  const mrr = retainers.filter((r:any)=>r.status==="Activo").reduce((s:number,r:any)=>s+(r.valor||0),0);
  const emCurso = projetos.filter((p:any)=>p.status==="Em curso").length;
  const concluidos = projetos.filter((p:any)=>p.status==="Concluído").length;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Faturação Total" v={fmt(totalFaturado)} c="todos os projetos" ac={T.green}/>
        <KPI l="Despesas" v={fmt(totalDesp)} c="infra + projetos" ac={T.red}/>
        <KPI l="Lucro Líquido" v={fmt(lucro)} c={`${totalFaturado>0?Math.round(lucro/totalFaturado*100):0}% margem`} ac={T.blueL}/>
        <KPI l="MRR Retainers" v={fmt(mrr)} c="receita recorrente" ac={T.purple}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Projetos em Curso" v={emCurso} c="activos" ac={T.amber}/>
        <KPI l="Projetos Concluídos" v={concluidos} c="total" ac={T.green}/>
        <KPI l="Total Projetos" v={projetos.length} c="histórico" ac={T.muted}/>
      </div>
      <Card title="Projetos Recentes" accent={T.blueL}>
        {projetos.length===0&&<Empty icon="🤖" msg="Sem projetos" sub="Adiciona projetos no menu Projetos"/>}
        {projetos.slice(0,5).map((p:any)=>(
          <div key={p.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.titulo}</div>
              <div style={{fontSize:11,color:T.muted}}>{p.dataInicio} → {p.deadline||"—"}</div>
            </div>
            <Tag label={p.status} color={p.status==="Concluído"?T.green:p.status==="Em curso"?T.amber:T.muted}/>
            <div style={{fontSize:13,color:T.green,fontFamily:"monospace",fontWeight:700,flexShrink:0}}>{fmt(p.faturado||0)}</div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── KAI: CRM Clientes ──────────────────────────────────────
function KaiCRM({ clientes, onAddCliente, projetos, addLog }: any) {
  const [adding, setAdding] = useState(false);
  const [newC, setNewC] = useState({nome:"",email:"",empresa:"",telefone:"",notas:""});

  const addCliente = async () => {
    if(!newC.nome) return;
    if (isDemoAccount() || !onAddCliente) {
      console.log("Demo: cliente não guardado no Supabase");
    } else {
      await onAddCliente({...newC});
    }
    addLog?.("kai_ai","cliente",`Novo cliente: ${newC.nome}`,"👥",0);
    setAdding(false);
    setNewC({nome:"",email:"",empresa:"",telefone:"",notas:""});
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <KPI l="Total Clientes" v={clientes.length} c="CRM" ac={T.blueL}/>
      <Card title="Clientes (CRM)" accent={T.blueL} action="+ Cliente" onAction={()=>setAdding(true)}>
        {adding&&(
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,
            display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10}}>
            {[["nome","Nome *"],["email","Email"],["empresa","Empresa"],["telefone","Telefone"],["notas","Notas"]].map(([k,l])=>(
              <div key={k}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>{l}</div>
                <input value={(newC as any)[k]} onChange={(e:any)=>setNewC({...newC,[k]:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
              </div>
            ))}
            <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
              <button onClick={addCliente} style={{padding:"8px 16px",borderRadius:8,border:"none",background:T.blueL,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}
        {clientes.length===0&&<Empty icon="👥" msg="Sem clientes" sub="Adiciona o primeiro cliente"/>}
        {clientes.map((c:any)=>(
          <div key={c.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
            <div style={{width:34,height:34,borderRadius:10,background:`${T.blueL}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>👤</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>{c.nome}</div>
              <div style={{fontSize:11,color:T.muted}}>{c.empresa||""}{c.email?` · ${c.email}`:""}</div>
            </div>
            <div style={{fontSize:11,color:T.muted}}>{projetos.filter((p:any)=>p.cliente===c.nome).length} proj.</div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── KAI: Projetos ──────────────────────────────────────────
function KaiProjetos({ projetos, setProjetos, addLog }: any) {
  const [adding, setAdding] = useState(false);
  const [newP, setNewP] = useState({titulo:"",cliente:"",dataInicio:"",deadline:"",conclusao:"",faturado:0,despesas:0,status:"Em curso",notas:""});

  const addProjeto = () => {
    if(!newP.titulo) return;
    setProjetos((ps:any)=>[...ps,{...newP,id:ps.length+1,faturado:+newP.faturado,despesas:+newP.despesas}]);
    addLog?.("kai_ai","projeto",`Novo projecto: ${newP.titulo}`,"◉",+newP.faturado);
    setAdding(false);
    setNewP({titulo:"",cliente:"",dataInicio:"",deadline:"",conclusao:"",faturado:0,despesas:0,status:"Em curso",notas:""});
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <KPI l="Em Curso" v={projetos.filter((p:any)=>p.status==="Em curso").length} c="activos" ac={T.amber}/>
        <KPI l="Concluídos" v={projetos.filter((p:any)=>p.status==="Concluído").length} c="total" ac={T.green}/>
        <KPI l="Faturado Total" v={fmt(projetos.reduce((s:number,p:any)=>s+(p.faturado||0),0))} c="todos" ac={T.green}/>
        <KPI l="Despesas Total" v={fmt(projetos.reduce((s:number,p:any)=>s+(p.despesas||0),0))} c="todos" ac={T.red}/>
      </div>
      <Card title="Projetos" accent={T.blueL} action="+ Projecto" onAction={()=>setAdding(true)}>
        {adding&&(
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,
            display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10}}>
            {[["titulo","Título *"],["cliente","Cliente"],["dataInicio","Data Início"],["deadline","Deadline"],["conclusao","Conclusão"]].map(([k,l])=>(
              <div key={k}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>{l}</div>
                <input type={k.includes("data")||k==="deadline"||k==="conclusao"?"date":"text"}
                  value={(newP as any)[k]} onChange={(e:any)=>setNewP({...newP,[k]:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
              </div>
            ))}
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Faturado (€)</div>
              <input type="number" value={newP.faturado} onChange={(e:any)=>setNewP({...newP,faturado:+e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Despesas (€)</div>
              <input type="number" value={newP.despesas} onChange={(e:any)=>setNewP({...newP,despesas:+e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Status</div>
              <select value={newP.status} onChange={(e:any)=>setNewP({...newP,status:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                <option>Em curso</option><option>Concluído</option><option>Pausado</option><option>Cancelado</option>
              </select>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:8,gridColumn:"1/-1"}}>
              <button onClick={addProjeto} style={{padding:"8px 16px",borderRadius:8,border:"none",background:T.blueL,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}
        {projetos.length===0&&<Empty icon="◉" msg="Sem projetos" sub="Adiciona o primeiro projecto"/>}
        {projetos.map((p:any)=>(
          <div key={p.id} style={{padding:"12px 0",borderBottom:`1px solid ${T.border}`}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
              <div style={{flex:1,minWidth:0}}>
                <span style={{fontSize:13,fontWeight:700,color:T.text}}>{p.titulo}</span>
                {p.cliente&&<span style={{fontSize:11,color:T.muted}}> · {p.cliente}</span>}
              </div>
              <Tag label={p.status} color={p.status==="Concluído"?T.green:p.status==="Em curso"?T.amber:T.muted}/>
            </div>
            <div style={{display:"flex",gap:16,flexWrap:"wrap",fontSize:11,color:T.muted}}>
              {p.dataInicio&&<span>📅 Início: {p.dataInicio}</span>}
              {p.deadline&&<span>⏰ Deadline: {p.deadline}</span>}
              {p.conclusao&&<span>✅ Conclusão: {p.conclusao}</span>}
              <span style={{color:T.green}}>💰 {fmt(p.faturado||0)}</span>
              {p.despesas>0&&<span style={{color:T.red}}>💸 -{fmt(p.despesas)}</span>}
              {(p.faturado||0)>0&&<span style={{color:T.blueL}}>📊 Lucro: {fmt((p.faturado||0)-(p.despesas||0))}</span>}
            </div>
            {p.notas&&<div style={{fontSize:11,color:T.muted,marginTop:4}}>{p.notas}</div>}
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── KAI: Retainers ─────────────────────────────────────────
function KaiRetainers({ retainers, setRetainers, addLog }: any) {
  const [adding, setAdding] = useState(false);
  const [newR, setNewR] = useState({cliente:"",valor:0,periodicidade:"Mensal",inicio:"",status:"Activo",pago:false,notas:""});

  const mrr = retainers.filter((r:any)=>r.status==="Activo").reduce((s:number,r:any)=>s+(r.valor||0),0);

  const addRetainer = () => {
    if(!newR.cliente) return;
    setRetainers((rs:any)=>[...rs,{...newR,id:rs.length+1,valor:+newR.valor}]);
    addLog?.("kai_ai","retainer",`Retainer: ${newR.cliente} · ${fmt(+newR.valor)}/mês`,"🔄",+newR.valor);
    setAdding(false);
    setNewR({cliente:"",valor:0,periodicidade:"Mensal",inicio:"",status:"Activo",pago:false,notas:""});
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="MRR Total" v={fmt(mrr)} c="retainers activos" ac={T.purple}/>
        <KPI l="Activos" v={retainers.filter((r:any)=>r.status==="Activo").length} c="contratos" ac={T.green}/>
        <KPI l="Total" v={retainers.length} c="histórico" ac={T.muted}/>
      </div>
      <Card title="Retainers" accent={T.purple} action="+ Retainer" onAction={()=>setAdding(true)}>
        {adding&&(
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,
            display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10}}>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Cliente <span style={{color:T.red}}>*</span></div>
              <input value={newR.cliente} onChange={(e:any)=>setNewR({...newR,cliente:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Valor (€/mês)</div>
              <input type="number" value={newR.valor} onChange={(e:any)=>setNewR({...newR,valor:+e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Início</div>
              <input type="date" value={newR.inicio} onChange={(e:any)=>setNewR({...newR,inicio:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Status</div>
              <select value={newR.status} onChange={(e:any)=>setNewR({...newR,status:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                <option>Activo</option><option>Cancelado</option><option>Pausado</option>
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Notas</div>
              <input value={newR.notas} onChange={(e:any)=>setNewR({...newR,notas:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
              <button onClick={addRetainer} style={{padding:"8px 16px",borderRadius:8,border:"none",background:T.purple,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}
        {retainers.length===0&&<Empty icon="🔄" msg="Sem retainers" sub="Adiciona o primeiro retainer"/>}
        {retainers.map((r:any)=>(
          <div key={r.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:`1px solid ${T.border}`}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>{r.cliente}</div>
              <div style={{fontSize:11,color:T.muted}}>{r.periodicidade||"Mensal"}{r.inicio?` · desde ${r.inicio}`:""}</div>
              {r.notas&&<div style={{fontSize:11,color:T.muted}}>{r.notas}</div>}
            </div>
            <Tag label={r.status} color={r.status==="Activo"?T.green:r.status==="Cancelado"?T.red:T.amber}/>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{fontSize:14,fontWeight:700,color:T.purple,fontFamily:"monospace"}}>{fmt(r.valor||0)}<span style={{fontSize:11,color:T.muted}}>/mês</span></div>
              <div style={{fontSize:11,color:r.pago?T.green:T.amber,marginTop:2}}>{r.pago?"✓ Pago":"● Pendente"}</div>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── KAI: Funil de Projetos ─────────────────────────────────
function KaiFunil({ projetos }: any) {
  const etapas = ["Contacto","Proposta","Em curso","Revisão","Concluído","Cancelado"];
  const etapaCores: Record<string,string> = {
    "Contacto":T.muted,"Proposta":T.amber,"Em curso":T.blueL,
    "Revisão":T.purple,"Concluído":T.green,"Cancelado":T.red
  };
  // Map project status to funnel stages
  const getEtapa = (p:any) => {
    if(p.status==="Concluído") return "Concluído";
    if(p.status==="Cancelado") return "Cancelado";
    if(p.status==="Em curso") return "Em curso";
    return "Proposta";
  };
  const totalValor = projetos.filter((p:any)=>p.status==="Em curso").reduce((s:number,p:any)=>s+(p.faturado||0),0);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <KPI l="Em Curso" v={projetos.filter((p:any)=>p.status==="Em curso").length} c="projectos" ac={T.blueL}/>
        <KPI l="Valor em Pipeline" v={fmt(totalValor)} c="em curso" ac={T.amber}/>
        <KPI l="Taxa Conclusão" v={`${projetos.length>0?Math.round(projetos.filter((p:any)=>p.status==="Concluído").length/projetos.length*100):0}%`} c="projectos" ac={T.green}/>
      </div>
      <Card title="Funil de Projetos" accent={T.blueL}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {etapas.map(etapa=>{
            const ps = projetos.filter((p:any)=>getEtapa(p)===etapa);
            const valorEtapa = ps.reduce((s:number,p:any)=>s+(p.faturado||0),0);
            if(ps.length===0&&etapa!=="Em curso") return null;
            return (
              <div key={etapa}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <div style={{width:10,height:10,borderRadius:"50%",background:etapaCores[etapa],flexShrink:0}}/>
                  <span style={{fontSize:12,fontWeight:700,color:etapaCores[etapa]}}>{etapa}</span>
                  <span style={{fontSize:11,color:T.muted}}>({ps.length})</span>
                  {valorEtapa>0&&<span style={{fontSize:11,color:T.muted,marginLeft:"auto"}}>{fmt(valorEtapa)}</span>}
                </div>
                {ps.map((p:any)=>(
                  <div key={p.id} style={{marginLeft:18,padding:"6px 10px",borderRadius:6,background:T.panel,border:`1px solid ${T.border}`,marginBottom:4}}>
                    <div style={{fontSize:12,color:T.text,fontWeight:600}}>{p.titulo}</div>
                    {p.cliente&&<div style={{fontSize:11,color:T.muted}}>{p.cliente}</div>}
                  </div>
                ))}
                {ps.length===0&&<div style={{marginLeft:18,fontSize:11,color:T.muted}}>Sem projectos nesta fase</div>}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ─── KAI: Despesas ──────────────────────────────────────────
function KaiDespesas({ despesas, setDespesas, projetos, addLog }: any) {
  const [adding, setAdding] = useState(false);
  const [newD, setNewD] = useState({data:"",desc:"",valor:0,tipo:"infra",projeto:""});
  const tipos: Record<string,string> = {infra:"Infraestrutura",software:"Software",marketing:"Marketing",formacao:"Formação",outros:"Outros"};

  const totalDesp = despesas.reduce((s:number,d:any)=>s+(d.valor||0),0);
  const addDesp = () => {
    if(!newD.desc||!newD.data) return;
    setDespesas((ds:any)=>[...ds,{...newD,id:ds.length+1,valor:+newD.valor}]);
    addLog?.("kai_ai","despesa",`Despesa KAI: ${newD.desc} · ${fmt(+newD.valor)}`,"💸",+newD.valor);
    setAdding(false);
    setNewD({data:"",desc:"",valor:0,tipo:"infra",projeto:""});
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Total Despesas" v={fmt(totalDesp)} c="acumulado" ac={T.red}/>
        <KPI l="Este Mês" v={fmt(despesas.filter((d:any)=>d.data.startsWith(new Date().toISOString().slice(0,7))).reduce((s:number,d:any)=>s+(d.valor||0),0))} c="mês actual" ac={T.amber}/>
      </div>
      <Card title="Despesas Kairos AI" accent={T.red} action="+ Despesa" onAction={()=>setAdding(true)}>
        {adding&&(
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,
            display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10}}>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Data</div>
              <input type="date" value={newD.data} onChange={(e:any)=>setNewD({...newD,data:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Descrição</div>
              <input value={newD.desc} onChange={(e:any)=>setNewD({...newD,desc:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Valor (€)</div>
              <input type="number" value={newD.valor} onChange={(e:any)=>setNewD({...newD,valor:+e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Tipo</div>
              <select value={newD.tipo} onChange={(e:any)=>setNewD({...newD,tipo:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                {Object.entries(tipos).map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Projecto</div>
              <select value={newD.projeto} onChange={(e:any)=>setNewD({...newD,projeto:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                <option value="">Geral</option>
                {projetos?.map((p:any)=><option key={p.id} value={p.titulo}>{p.titulo}</option>)}
              </select>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
              <button onClick={addDesp} style={{padding:"8px 16px",borderRadius:8,border:"none",background:T.red,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}
        {despesas.length===0&&<Empty icon="💸" msg="Sem despesas" sub="Regista as despesas da Kairos AI"/>}
        <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:400}}>
          <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
            {["Data","Descrição","Tipo","Projecto","Valor"].map(h=>(
              <th key={h} style={{padding:"9px 12px",textAlign:"left",color:T.muted,fontSize:11,fontWeight:700,textTransform:"uppercase"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {[...despesas].sort((a,b)=>b.data.localeCompare(a.data)).map((d:any)=>(
              <tr key={d.id} style={{borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
                <td style={{padding:"9px 12px",color:T.textSub,whiteSpace:"nowrap"}}>{d.data}</td>
                <td style={{padding:"9px 12px",color:T.text}}>{d.desc}</td>
                <td style={{padding:"9px 12px"}}><Tag label={tipos[d.tipo]||d.tipo} color={T.muted}/></td>
                <td style={{padding:"9px 12px",color:T.muted,fontSize:11}}>{d.projeto||"—"}</td>
                <td style={{padding:"9px 12px",color:T.red,fontFamily:"monospace",fontWeight:700}}>{fmt(d.valor||0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}

// ─── KAI: KPIs & Análise ────────────────────────────────────
function KaiKPI({ projetos, retainers, despesas }: any) {
  const totalFat = projetos.reduce((s:number,p:any)=>s+(p.faturado||0),0);
  const totalDesp = despesas.reduce((s:number,d:any)=>s+(d.valor||0),0) + projetos.reduce((s:number,p:any)=>s+(p.despesas||0),0);
  const mrr = retainers.filter((r:any)=>r.status==="Activo").reduce((s:number,r:any)=>s+(r.valor||0),0);
  const margem = totalFat>0?Math.round((totalFat-totalDesp)/totalFat*100):0;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <Card title="KPIs & Análise" accent={T.blueL}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12,marginBottom:18}}>
          <KPI l="MRR" v={fmt(mrr)} c="recorrente/mês" ac={T.purple}/>
          <KPI l="Margem Líquida" v={`${margem}%`} c="após despesas" ac={margem>50?T.green:margem>30?T.amber:T.red}/>
          <KPI l="Ticket Médio" v={fmt(projetos.length>0?Math.round(totalFat/projetos.length):0)} c="por projecto" ac={T.blueL}/>
          <KPI l="Lucro Líquido" v={fmt(totalFat-totalDesp)} c="total" ac={T.green}/>
        </div>
        <div style={{padding:"12px 14px",borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`}}>
          <div style={{fontSize:12,fontWeight:700,color:T.muted,marginBottom:10,textTransform:"uppercase",letterSpacing:"0.05em"}}>Definir Objectivos</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
            {[["Meta MRR","€ /mês"],["Meta Faturação Anual","€ /ano"],["Meta Margem","%"],["Nº Clientes Alvo","clientes"]].map(([l,ph])=>(
              <div key={l}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>{l}</div>
                <input placeholder={ph} style={{width:"100%",padding:"7px 10px",borderRadius:6,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

// ─── KAI: Tarefas ───────────────────────────────────────────
function KaiTarefas({ projetos }: any) {
  const [tarefas, setTarefas] = useState<any[]>([
    {id:1,titulo:"Configurar domínio kairosai.pt",tipo:"empresa",projeto:"",status:"por fazer",prioridade:"alta"},
    {id:2,titulo:"Criar landing page",tipo:"empresa",projeto:"",status:"por fazer",prioridade:"alta"},
  ]);
  const [adding, setAdding] = useState(false);
  const [newT, setNewT] = useState({titulo:"",tipo:"empresa",projeto:"",status:"por fazer",prioridade:"media"});

  const addTarefa = () => {
    if(!newT.titulo) return;
    setTarefas(ts=>[...ts,{...newT,id:ts.length+1}]);
    setAdding(false);
    setNewT({titulo:"",tipo:"empresa",projeto:"",status:"por fazer",prioridade:"media"});
  };

  const toggle = (id:number) => setTarefas(ts=>ts.map(t=>t.id===id?{...t,status:t.status==="concluído"?"por fazer":"concluído"}:t));
  const priCor:Record<string,string>={alta:T.red,media:T.amber,baixa:T.muted};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <KPI l="Por Fazer" v={tarefas.filter(t=>t.status!=="concluído").length} c="tarefas" ac={T.amber}/>
        <KPI l="Concluídas" v={tarefas.filter(t=>t.status==="concluído").length} c="tarefas" ac={T.green}/>
      </div>
      <Card title="Tarefas — Empresa & Projetos" accent={T.blueL} action="+ Tarefa" onAction={()=>setAdding(true)}>
        {adding&&(
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,
            display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10}}>
            <div style={{gridColumn:"1/-1"}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Título <span style={{color:T.red}}>*</span></div>
              <input value={newT.titulo} onChange={(e:any)=>setNewT({...newT,titulo:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Tipo</div>
              <select value={newT.tipo} onChange={(e:any)=>setNewT({...newT,tipo:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                <option value="empresa">Empresa</option>
                {projetos?.map((p:any)=><option key={p.id} value={p.titulo}>{p.titulo}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Prioridade</div>
              <select value={newT.prioridade} onChange={(e:any)=>setNewT({...newT,prioridade:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                <option value="alta">Alta</option><option value="media">Média</option><option value="baixa">Baixa</option>
              </select>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
              <button onClick={addTarefa} style={{padding:"8px 16px",borderRadius:8,border:"none",background:T.blueL,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}
        {tarefas.length===0&&<Empty icon="✅" msg="Sem tarefas" sub="Adiciona tarefas da empresa ou por projecto"/>}
        {tarefas.map(t=>(
          <div key={t.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:`1px solid ${T.border}`,opacity:t.status==="concluído"?0.5:1}}>
            <button onClick={()=>toggle(t.id)} style={{width:20,height:20,borderRadius:6,border:`1px solid ${priCor[t.prioridade]||T.muted}`,background:t.status==="concluído"?T.green:"transparent",cursor:"pointer",flexShrink:0,fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",color:"white"}}>
              {t.status==="concluído"?"✓":""}
            </button>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,color:T.text,textDecoration:t.status==="concluído"?"line-through":"none"}}>{t.titulo}</div>
              <div style={{fontSize:11,color:T.muted}}>{t.tipo==="empresa"?"Empresa":t.tipo}</div>
            </div>
            <Tag label={t.prioridade} color={priCor[t.prioridade]||T.muted}/>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── KAI: Calendário ────────────────────────────────────────
function KaiCalendario({ entries, setEntries }: any) {
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth());
  const [ano, setAno] = useState(hoje.getFullYear());
  const [sel, setSel] = useState<number|null>(null);
  const [adding, setAdding] = useState(false);
  const [newE, setNewE] = useState({titulo:"",data:"",hora:"09:00",tipo:"reuniao",descricao:""});

  const meses = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const diasNoMes = new Date(ano,mes+1,0).getDate();
  const primeiroDia = new Date(ano,mes,1).getDay();
  const hojeStr = hoje.toISOString().split("T")[0];
  const tipoCor: Record<string,string> = {reuniao:T.blueL,deadline:T.red,chamada:T.purple,entrega:T.green,outro:T.muted};

  const eventosPorDia: Record<number,any[]> = {};
  entries.forEach((e:any)=>{
    const d=new Date(e.data);
    if(d.getFullYear()===ano&&d.getMonth()===mes){
      const dia=d.getDate();
      if(!eventosPorDia[dia]) eventosPorDia[dia]=[];
      eventosPorDia[dia].push(e);
    }
  });

  const addEntry = () => {
    if(!newE.titulo||!newE.data) return;
    setEntries((es:any)=>[...es,{...newE,id:es.length+1}]);
    setAdding(false);
    setNewE({titulo:"",data:"",hora:"09:00",tipo:"reuniao",descricao:""});
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <Card title="Calendário — Reuniões & Deadlines" accent={T.blueL} action="+ Evento" onAction={()=>setAdding(true)}>
        {adding&&(
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,
            display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10}}>
            {[["titulo","Título *","text"],["data","Data","date"],["hora","Hora","time"],["descricao","Descrição","text"]].map(([k,l,type])=>(
              <div key={k}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>{l}</div>
                <input type={type} value={(newE as any)[k]} onChange={(e:any)=>setNewE({...newE,[k]:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
              </div>
            ))}
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Tipo</div>
              <select value={newE.tipo} onChange={(e:any)=>setNewE({...newE,tipo:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                <option value="reuniao">Reunião</option><option value="deadline">Deadline</option>
                <option value="chamada">Chamada</option><option value="entrega">Entrega</option><option value="outro">Outro</option>
              </select>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
              <button onClick={addEntry} style={{padding:"8px 16px",borderRadius:8,border:"none",background:T.blueL,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <button onClick={()=>{if(mes===0){setMes(11);setAno(ano-1);}else setMes(mes-1);setSel(null);}}
            style={{background:"none",border:`1px solid ${T.border}`,color:T.text,padding:"10px 14px",borderRadius:6,cursor:"pointer",fontSize:14}}>‹</button>
          <span style={{fontSize:14,fontWeight:700,color:T.text}}>{meses[mes]} {ano}</span>
          <button onClick={()=>{if(mes===11){setMes(0);setAno(ano+1);}else setMes(mes+1);setSel(null);}}
            style={{background:"none",border:`1px solid ${T.border}`,color:T.text,padding:"10px 14px",borderRadius:6,cursor:"pointer",fontSize:14}}>›</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:4}}>
          {["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"].map(d=>(
            <div key={d} style={{fontSize:11,color:T.muted,textAlign:"center",padding:"4px 0",fontWeight:700}}>{d}</div>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
          {Array.from({length:primeiroDia}).map((_,i)=><div key={`e${i}`}/>)}
          {Array.from({length:diasNoMes}).map((_,i)=>{
            const dia=i+1;
            const dataStr=`${ano}-${String(mes+1).padStart(2,"0")}-${String(dia).padStart(2,"0")}`;
            const isHoje=dataStr===hojeStr;
            const evs=eventosPorDia[dia]||[];
            const isSel=sel===dia;
            return (
              <div key={dia} onClick={()=>setSel(isSel?null:dia)}
                style={{minHeight:36,padding:"2px 1px",borderRadius:5,
                  background:isSel?`${T.blueL}20`:isHoje?`${T.blueL}12`:"transparent",
                  border:isSel?`1px solid ${T.blueL}`:isHoje?`1px solid ${T.blueL}40`:`1px solid transparent`,
                  cursor:evs.length>0||isHoje?"pointer":"default"}}>
                <div style={{fontSize:10,color:isHoje?T.blueL:T.textSub,fontWeight:isHoje?700:400,textAlign:"center",marginBottom:1}}>{dia}</div>
                {evs.slice(0,2).map((ev:any,ei:number)=>(
                  <div key={ei} style={{fontSize:8,borderRadius:3,padding:"1px 2px",marginBottom:1,
                    background:`${tipoCor[ev.tipo]||T.muted}25`,color:tipoCor[ev.tipo]||T.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>
                    {ev.hora} {ev.titulo}
                  </div>
                ))}
                {evs.length>2&&<div style={{fontSize:8,color:T.muted,textAlign:"center"}}>+{evs.length-2}</div>}
              </div>
            );
          })}
        </div>
        {sel&&eventosPorDia[sel]&&(
          <div style={{marginTop:14,padding:12,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`}}>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:8,textTransform:"uppercase"}}>{sel} de {meses[mes]}</div>
            {eventosPorDia[sel].map((ev:any,i:number)=>(
              <div key={i} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"8px 0",borderBottom:i<eventosPorDia[sel].length-1?`1px solid ${T.border}`:"none"}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:tipoCor[ev.tipo]||T.muted,marginTop:4,flexShrink:0}}/>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:T.text}}>{ev.titulo}</div>
                  <div style={{fontSize:11,color:T.muted}}>🕐 {ev.hora} · {ev.tipo}</div>
                  {ev.descricao&&<div style={{fontSize:11,color:T.textSub}}>{ev.descricao}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}


function KaiPipeline({ projects, bestseller, getPacote }: any) {
  const totalPipe = projects.filter(p=>["proposta","negociação","qualificação"].includes(p.status)).reduce((s: number, p: any)=>s+(p.valor||0),0);
  const statusProb = { negociação:75, proposta:50, qualificação:30 };
  const ponderado  = projects.filter(p=>statusProb[p.status]).reduce((s: number, p: any)=>s+(p.valor||0)*(statusProb[p.status]||0)/100,0);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12 }}>
        <KPI l="Pipeline Total"   v={fmt(totalPipe)} c="negócios em aberto" ac={T.blueL}/>
        <KPI l="Pipeline Pond."   v={fmt(ponderado)} c="valor ajustado prob." ac={T.blueL}/>
        <KPI l="Taxa Fecho Meta"  v="20%"            c="meta M3 · 35% M12"  ac={T.blueL}/>
        <KPI l="Negócios Abertos" v={projects.filter(p=>statusProb[p.status]).length} c="" ac={T.blueL}/>
      </div>
      <Card title="Pipeline Kairos AI" accent={T.blueL}>
        {projects.map(p=>{
          const pac = getPacote(p.pacoteId);
          const prob = statusProb[p.status]||0;
          return (
            <div key={p.id} style={{ padding:"13px 0", borderBottom:`1px solid ${T.border}`,
              display:"flex", alignItems:"center", gap:14 }}>
              <StatusDot s={p.status}/>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:T.text }}>{p.client}</div>
                <div style={{ display:"flex", gap:6, marginTop:4, alignItems:"center" }}>
                  <Tag label={pac.label} color={pac.cor}/>
                  {bestseller && p.pacoteId===bestseller && <BestsellerBadge label=""/>}
                  <span style={{ fontSize:11, color:T.muted }}>{p.entregavel}</span>
                </div>
              </div>
              <div style={{ textAlign:"right", minWidth:90 }}>
                <div style={{ fontSize:14, fontWeight:700, color:T.amber, fontFamily:"monospace" }}>{fmt(p.valor)}</div>
                <div style={{ fontSize:11, color:T.muted }}>+{fmt(pac.mrr)}/mês</div>
              </div>
              {prob>0 && (
                <div style={{ width:80 }}>
                  <div style={{ height:5, borderRadius:99, background:"rgba(255,255,255,0.06)", marginBottom:3 }}>
                    <div style={{ width:`${prob}%`, height:"100%", borderRadius:99,
                      background:prob>=70?T.green:prob>=50?T.amber:T.red }}/>
                  </div>
                  <div style={{ fontSize:11, color:T.muted, textAlign:"right" }}>{prob}%</div>
                </div>
              )}
              <Tag label={p.status}
                color={p.status==="negociação"?T.green:p.status==="proposta"?T.amber:T.muted}/>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

function KaiPacotesTable({ bestseller, metrica }: any) {
  return (
    <Card title="Pacotes Kairos AI — Pricing Real 2025" noPad accent={T.blueL}>
      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
        <thead>
          <tr style={{ borderBottom:`1px solid ${T.border}` }}>
            {["Pacote","Setup Fee","Retainer/Mês","Inclui",""].map(h=>(
              <th key={h} style={{ padding:"9px 14px", textAlign:"left", color:T.muted,
                fontSize:11, fontWeight:700, textTransform:"uppercase" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {KAI_PACOTES.map(p=>(
            <tr key={p.id} style={{ borderBottom:`1px solid rgba(255,255,255,0.04)` }}
              onMouseEnter={(e: any)=>e.currentTarget.style.background="rgba(255,255,255,0.025)"}
              onMouseLeave={(e: any)=>e.currentTarget.style.background="transparent"}>
              <td style={{ padding:"10px 14px" }}>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <Tag label={p.label} color={p.cor} bold/>
                  {bestseller===p.id && <BestsellerBadge label=""/>}
                </div>
              </td>
              <td style={{ padding:"10px 14px", color:T.amber, fontFamily:"monospace", fontWeight:700 }}>
                {p.setup?fmt(p.setup):"—"}
              </td>
              <td style={{ padding:"10px 14px", color:T.green, fontFamily:"monospace", fontWeight:700 }}>
                {p.id==="hora"?`€${p.mrr}/hora`:`${fmt(p.mrr)}/mês`}
              </td>
              <td style={{ padding:"10px 14px", color:T.muted, fontSize:11 }}>
                {p.id==="starter"?"1 workflow n8n, dashboard, 1 mês suporte":
                 p.id==="growth"?"3 workflows, CRM, LinkedIn auto, 3 meses suporte":
                 p.id==="full"?"Sistema completo, agentes, Kairos OS, 6 meses":
                 p.id==="macmini"?"Mac Mini configurado, agente exclusivo, 24/7":
                 "Análise, estratégia, revisão de sistemas AI"}
              </td>
              <td style={{ padding:"10px 14px" }}>
                <Tag label={bestseller===p.id?"Popular":""}
                  color={bestseller===p.id?T.gold:T.muted}/>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/* ─── INLINE ALERT PANEL ───────────────────────────────────────────────────── */
function AlertPanel({ alRes, limpezas }: { alRes: any[]; limpezas?: any[] }) {
  const hoje = new Date();
  hoje.setHours(0,0,0,0);

  const alertas: Array<{tipo:string; titulo:string; detalhe:string; nivel:string; icon:string; dias:number}> = [];

  // Reservas
  alRes.filter(r=>r.status!=="cancelada"&&r.status!=="limpeza").forEach(r=>{
    const ci = new Date(r.ci); ci.setHours(0,0,0,0);
    const co = new Date(r.co); co.setHours(0,0,0,0);
    const diasCI = Math.ceil((ci.getTime()-hoje.getTime())/86400000);
    const diasCO = Math.ceil((co.getTime()-hoje.getTime())/86400000);
    const casaLabel = CASAS.find((c:any)=>c.id===r.casa)?.label||r.casa;
    if(diasCI===0) alertas.push({tipo:"ci",titulo:`🏠 Check-in hoje: ${r.guest}`,detalhe:`${casaLabel} · ${r.ciHora||"15:00"} · ${r.hospedes} pax`,nivel:"urgente",icon:"🏠",dias:0});
    else if(diasCI===1) alertas.push({tipo:"ci",titulo:`🏠 Check-in amanhã: ${r.guest}`,detalhe:`${casaLabel} · ${r.ciHora||"15:00"}`,nivel:"aviso",icon:"🏠",dias:1});
    else if(diasCI<=3&&diasCI>1) alertas.push({tipo:"ci",titulo:`🏠 Check-in em ${diasCI} dias: ${r.guest}`,detalhe:`${casaLabel} · ${r.hospedes} pax`,nivel:"info",icon:"📅",dias:diasCI});
    if(diasCO===0&&diasCI!==0) alertas.push({tipo:"co",titulo:`🧹 Check-out hoje: ${r.guest}`,detalhe:`${casaLabel} · ${r.coHora||"11:00"} · preparar limpeza`,nivel:"urgente",icon:"🧹",dias:0});
    else if(diasCO===1&&diasCI!==1) alertas.push({tipo:"co",titulo:`🔔 Check-out amanhã: ${r.guest}`,detalhe:`${casaLabel} · ${r.coHora||"11:00"}`,nivel:"aviso",icon:"🏠",dias:1});
  });

  // Limpezas
  (limpezas||[]).filter((l:any)=>!l.feita).forEach((l:any)=>{
    const d = new Date(l.data); d.setHours(0,0,0,0);
    const dias = Math.ceil((d.getTime()-hoje.getTime())/86400000);
    const casaLabel = CASAS.find((c:any)=>c.id===l.casa)?.label||l.casa;
    if(dias===0) alertas.push({tipo:"limp",titulo:`🧹 Limpeza HOJE: ${casaLabel}`,detalhe:`${l.hora||""}`,nivel:"urgente",icon:"🧹",dias:0});
    else if(dias===1) alertas.push({tipo:"limp",titulo:`🧹 Limpeza amanhã: ${casaLabel}`,detalhe:"Avisar a empregada",nivel:"aviso",icon:"🧹",dias:1});
    else if(dias<=3&&dias>1) alertas.push({tipo:"limp",titulo:`🧹 Limpeza em ${dias} dias: ${casaLabel}`,detalhe:"",nivel:"info",icon:"🧹",dias:dias});
  });

  // Ordenar por urgência e dias
  alertas.sort((a,b)=>{
    const order:Record<string,number>={urgente:0,aviso:1,info:2};
    if(order[a.nivel]!==order[b.nivel]) return order[a.nivel]-order[b.nivel];
    return a.dias-b.dias;
  });

  if(alertas.length===0) return null;

  const nivelColor:Record<string,string>={urgente:T.red,aviso:T.amber,info:T.blueL};
  const nivelBg:Record<string,string>={urgente:`${T.red}12`,aviso:`${T.amber}10`,info:`${T.blueL}08`};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      <div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase",
        letterSpacing:"0.1em",marginBottom:2}}>
        🔔 {alertas.length} Alerta{alertas.length!==1?"s":""} Activo{alertas.length!==1?"s":""}
      </div>
      {alertas.map((a,i)=>(
        <div key={i} style={{
          display:"flex",alignItems:"flex-start",gap:10,padding:"10px 14px",
          borderRadius:10,background:nivelBg[a.nivel],
          border:`1px solid ${nivelColor[a.nivel]}25`,
        }}>
          <div style={{
            width:30,height:30,borderRadius:8,flexShrink:0,
            background:`${nivelColor[a.nivel]}18`,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,
          }}>{a.icon}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12,fontWeight:700,color:a.nivel==="urgente"?nivelColor[a.nivel]:T.text}}>
              {a.titulo}
            </div>
            {a.detalhe&&<div style={{fontSize:11,color:T.textSub,marginTop:2}}>{a.detalhe}</div>}
          </div>
          <Tag label={a.nivel==="urgente"?"URGENTE":a.nivel==="aviso"?"AVISO":"INFO"} color={nivelColor[a.nivel]}/>
        </div>
      ))}
    </div>
  );
}

/* ─── ALERT PANELS — VJ, PhotoBooth, HW3D ─────────────────────────────────── */

function AlertPanelVJ({ events }: { events: any[] }) {
  const hoje = new Date(); hoje.setHours(0,0,0,0);
  const nivelColor:Record<string,string>={urgente:T.red,aviso:T.amber,info:T.blueL};
  const nivelBg:Record<string,string>={urgente:`${T.red}12`,aviso:`${T.amber}10`,info:`${T.blueL}08`};
  const alertas: Array<{titulo:string;detalhe:string;nivel:string;icon:string;dias:number}> = [];

  events.filter((e:any)=>e.status!=="concluido").forEach((e:any)=>{
    const ev=new Date(e.data); ev.setHours(0,0,0,0);
    const dias=Math.ceil((ev.getTime()-hoje.getTime())/86400000);
    const local=e.local?.split(",")[0]||"—";
    const chDone=Object.values(e.checklist||{}).filter(Boolean).length;
    const chTotal=Object.keys(e.checklist||{}).length;
    const ck=chTotal>0?` · Checklist ${chDone}/${chTotal}`:"";
    if(dias<0) return;
    if(dias===0) alertas.push({titulo:`🎤 Evento HOJE: ${local}`,detalhe:`${e.horario||""}${ck}`,nivel:"urgente",icon:"🎤",dias:0});
    else if(dias===1) alertas.push({titulo:`🎤 Evento amanhã: ${local}`,detalhe:`${e.horario||""}${ck}`,nivel:"urgente",icon:"🎤",dias:1});
    else if(dias<=3) alertas.push({titulo:`🎤 Em ${dias} dias: ${local}`,detalhe:`${e.horario||""}${ck}`,nivel:"aviso",icon:"🎤",dias});
    else if(dias<=7) alertas.push({titulo:`📅 Em ${dias} dias: ${local}`,detalhe:`${e.status}${ck}`,nivel:"info",icon:"📅",dias});
  });

  if(!alertas.length) return null;
  alertas.sort((a,b)=>a.dias-b.dias);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      <div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:2}}>🔔 {alertas.length} Alerta{alertas.length!==1?"s":""} VJ</div>
      {alertas.map((a,i)=>(
        <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,background:nivelBg[a.nivel],border:`1px solid ${nivelColor[a.nivel]}25`}}>
          <div style={{width:30,height:30,borderRadius:8,flexShrink:0,background:`${nivelColor[a.nivel]}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>{a.icon}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:12,fontWeight:700,color:a.nivel==="urgente"?nivelColor[a.nivel]:T.text}}>{a.titulo}</div>
            {a.detalhe&&<div style={{fontSize:11,color:T.textSub,marginTop:1}}>{a.detalhe}</div>}
          </div>
          <Tag label={a.nivel==="urgente"?"URGENTE":a.nivel==="aviso"?"AVISO":"INFO"} color={nivelColor[a.nivel]}/>
        </div>
      ))}
    </div>
  );
}

function AlertPanelPB({ events }: { events: any[] }) {
  const hoje=new Date(); hoje.setHours(0,0,0,0);
  const nivelColor:Record<string,string>={urgente:T.red,aviso:T.amber,info:T.pink};
  const nivelBg:Record<string,string>={urgente:`${T.red}12`,aviso:`${T.amber}10`,info:`${T.pink}08`};
  const alertas: Array<{titulo:string;detalhe:string;nivel:string;icon:string;dias:number}> = [];

  events.filter((e:any)=>e.status!=="concluido").forEach((e:any)=>{
    const ev=new Date(e.data); ev.setHours(0,0,0,0);
    const dias=Math.ceil((ev.getTime()-hoje.getTime())/86400000);
    const local=e.local?.split(",")[0]||"—";
    const chDone=Object.values(e.checklist||{}).filter(Boolean).length;
    const chTotal=Object.keys(e.checklist||{}).length;
    const ck=chTotal>0?` · Checklist ${chDone}/${chTotal}`:"";
    if(dias<0) return;
    if(dias===0) alertas.push({titulo:`📸 Evento HOJE: ${local}`,detalhe:`${e.horario||""}${ck}`,nivel:"urgente",icon:"📸",dias:0});
    else if(dias===1) alertas.push({titulo:`📸 Evento amanhã: ${local}`,detalhe:`${e.horario||""}${ck}`,nivel:"urgente",icon:"📸",dias:1});
    else if(dias<=3) alertas.push({titulo:`📸 Em ${dias} dias: ${local}`,detalhe:`${e.horario||""}${ck}`,nivel:"aviso",icon:"📸",dias});
    else if(dias<=7) alertas.push({titulo:`📅 Em ${dias} dias: ${local}`,detalhe:`${e.horario||""}`,nivel:"info",icon:"📅",dias});
  });

  if(!alertas.length) return null;
  alertas.sort((a,b)=>a.dias-b.dias);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      <div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:2}}>🔔 {alertas.length} Alerta{alertas.length!==1?"s":""} PhotoBooth</div>
      {alertas.map((a,i)=>(
        <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,background:nivelBg[a.nivel],border:`1px solid ${nivelColor[a.nivel]}25`}}>
          <div style={{width:30,height:30,borderRadius:8,flexShrink:0,background:`${nivelColor[a.nivel]}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>{a.icon}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:12,fontWeight:700,color:a.nivel==="urgente"?nivelColor[a.nivel]:T.text}}>{a.titulo}</div>
            {a.detalhe&&<div style={{fontSize:11,color:T.textSub,marginTop:1}}>{a.detalhe}</div>}
          </div>
          <Tag label={a.nivel==="urgente"?"URGENTE":a.nivel==="aviso"?"AVISO":"INFO"} color={nivelColor[a.nivel]}/>
        </div>
      ))}
    </div>
  );
}

function AlertPanelHW3D({ projects }: { projects: any[] }) {
  const hoje=new Date(); hoje.setHours(0,0,0,0);
  const nivelColor:Record<string,string>={urgente:T.red,aviso:T.amber,info:T.teal};
  const nivelBg:Record<string,string>={urgente:`${T.red}12`,aviso:`${T.amber}10`,info:`${T.teal}08`};
  const alertas: Array<{titulo:string;detalhe:string;nivel:string;icon:string;dias:number}> = [];

  projects.filter((p:any)=>p.etapa!=="Concluído"&&p.dataFim).forEach((p:any)=>{
    const prazo=new Date(p.dataFim); prazo.setHours(0,0,0,0);
    const dias=Math.ceil((prazo.getTime()-hoje.getTime())/86400000);
    if(dias<0) alertas.push({titulo:`⚠️ Atrasado ${Math.abs(dias)}d: ${p.titulo}`,detalhe:`Etapa: ${p.etapa}`,nivel:"urgente",icon:"🧊",dias});
    else if(dias===0) alertas.push({titulo:`⏰ Deadline HOJE: ${p.titulo}`,detalhe:`Etapa: ${p.etapa}`,nivel:"urgente",icon:"🧊",dias:0});
    else if(dias===1) alertas.push({titulo:`⏰ Deadline amanhã: ${p.titulo}`,detalhe:`Etapa: ${p.etapa}`,nivel:"urgente",icon:"🧊",dias:1});
    else if(dias<=3) alertas.push({titulo:`⏰ Deadline em ${dias}d: ${p.titulo}`,detalhe:`Etapa: ${p.etapa}`,nivel:"aviso",icon:"⏰",dias});
    else if(dias<=7) alertas.push({titulo:`📋 Deadline em ${dias}d: ${p.titulo}`,detalhe:`Etapa: ${p.etapa}`,nivel:"info",icon:"📋",dias});
  });

  if(!alertas.length) return null;
  alertas.sort((a,b)=>a.dias-b.dias);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      <div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:2}}>🔔 {alertas.length} Alerta{alertas.length!==1?"s":""} HW3D</div>
      {alertas.map((a,i)=>(
        <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,background:nivelBg[a.nivel],border:`1px solid ${nivelColor[a.nivel]}25`}}>
          <div style={{width:30,height:30,borderRadius:8,flexShrink:0,background:`${nivelColor[a.nivel]}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>{a.icon}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:12,fontWeight:700,color:a.nivel==="urgente"?nivelColor[a.nivel]:T.text}}>{a.titulo}</div>
            {a.detalhe&&<div style={{fontSize:11,color:T.textSub,marginTop:1}}>{a.detalhe}</div>}
          </div>
          <Tag label={a.nivel==="urgente"?"URGENTE":a.nivel==="aviso"?"AVISO":"INFO"} color={nivelColor[a.nivel]}/>
        </div>
      ))}
    </div>
  );
}


// ── QUINTA DA REGADA ──
function QuintaManutencao({ addLog }: any) {
  const [items, setItems] = useState<any[]>(isDemoAccount() ? DEMO_MANUTENCAO : []);
  const [adding, setAdding] = useState(false);
  const [newI, setNewI] = useState({tipo:"manutencao",titulo:"",prioridade:"media",status:"pendente",data:"",valor:0,notas:""});
  const [filtro, setFiltro] = useState("todos");

  const tipoC:Record<string,string>={avaria:T.red,manutencao:T.amber,investimento:T.blueL,melhoria:T.green};
  const priC:Record<string,string>={alta:T.red,media:T.amber,baixa:T.muted};

  const addItem = () => {
    if(!newI.titulo) return;
    setItems([...items,{...newI,id:items.length+1,valor:+newI.valor}]);
    addLog?.("quinta","manutencao",`${newI.tipo==="investimento"?"Investimento":"Manutenção"}: ${newI.titulo}`,"🔧",+newI.valor);
    setAdding(false);
    setNewI({tipo:"manutencao",titulo:"",prioridade:"media",status:"pendente",data:"",valor:0,notas:""});
  };

  const toggleStatus = (id:number) => {
    setItems(its=>its.map(i=>i.id===id?{...i,status:i.status==="concluido"?"pendente":"concluido"}:i));
  };

  const visible = filtro==="todos" ? items : items.filter(i=>i.tipo===filtro||i.status===filtro);
  const totalInvestido = items.filter(i=>i.status==="concluido").reduce((s:number,i:any)=>s+(i.valor||0),0);
  const totalPlaneado = items.filter(i=>i.status!=="concluido").reduce((s:number,i:any)=>s+(i.valor||0),0);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Itens Activos" v={items.filter(i=>i.status!=="concluido").length} c="por resolver" ac={T.amber}/>
        <KPI l="Avarias" v={items.filter(i=>i.tipo==="avaria"&&i.status!=="concluido").length} c="urgentes" ac={T.red}/>
        <KPI l="Total Investido" v={fmt(totalInvestido)} c="concluídos" ac={T.green}/>
        <KPI l="Planeado" v={fmt(totalPlaneado)} c="por gastar" ac={T.blueL}/>
      </div>

      <Card title="Registos de Manutenção & Investimentos" accent={T.amber}
        action="+ Adicionar" onAction={()=>setAdding(true)}>

        {adding && (
          <div style={{background:T.panel2,borderRadius:10,padding:16,marginBottom:16,display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Tipo</div>
                <select value={newI.tipo} onChange={e=>setNewI({...newI,tipo:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                  <option value="avaria">🔴 Avaria</option>
                  <option value="manutencao">🟡 Manutenção</option>
                  <option value="melhoria">🟢 Melhoria</option>
                  <option value="investimento">🔵 Investimento Futuro</option>
                </select>
              </div>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Título</div>
                <input value={newI.titulo} onChange={e=>setNewI({...newI,titulo:e.target.value})} placeholder="Ex: Trocar janelas sala"
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
              </div>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Prioridade</div>
                <select value={newI.prioridade} onChange={e=>setNewI({...newI,prioridade:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                  <option value="alta">Alta</option>
                  <option value="media">Média</option>
                  <option value="baixa">Baixa</option>
                </select>
              </div>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Data Prevista</div>
                <input type="date" value={newI.data} onChange={e=>setNewI({...newI,data:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
              </div>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Valor Est. (€)</div>
                <input type="number" value={newI.valor} onChange={e=>setNewI({...newI,valor:+e.target.value})} placeholder="0"
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
              </div>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Notas</div>
                <input value={newI.notas} onChange={e=>setNewI({...newI,notas:e.target.value})} placeholder="Detalhes..."
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={addItem} style={{padding:"8px 16px",borderRadius:8,border:"none",background:T.amber,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}}>Cancelar</button>
            </div>
          </div>
        )}

        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
          {[["todos","Todos"],["avaria","Avarias"],["manutencao","Manutenção"],["investimento","Investimentos"],["concluido","Concluídos"]].map(([v,l])=>(
            <button key={v} onClick={()=>setFiltro(v)} style={{padding:"4px 12px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,
              background:filtro===v?`${T.amber}25`:"rgba(255,255,255,0.04)",color:filtro===v?T.amber:T.muted}}>
              {l}
            </button>
          ))}
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {visible.length===0 && <Empty icon="🔧" msg="Sem registos" sub="Adiciona avarias, manutenções ou investimentos futuros"/>}
          {visible.map(item=>(
            <div key={item.id} style={{
              display:"flex",alignItems:"flex-start",gap:12,padding:"12px 14px",borderRadius:10,
              background:item.status==="concluido"?"rgba(255,255,255,0.01)":T.panel,
              border:`1px solid ${item.status==="concluido"?T.border:tipoC[item.tipo]+"25"}`,
              opacity:item.status==="concluido"?0.55:1,
            }}>
              <div style={{width:34,height:34,borderRadius:10,flexShrink:0,
                background:`${tipoC[item.tipo]}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>
                {item.tipo==="avaria"?"🔴":item.tipo==="investimento"?"🔵":item.tipo==="melhoria"?"🟢":"🟡"}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <span style={{fontSize:13,fontWeight:700,color:item.status==="concluido"?T.muted:T.text,
                    textDecoration:item.status==="concluido"?"line-through":"none"}}>{item.titulo}</span>
                  <Tag label={item.tipo} color={tipoC[item.tipo]}/>
                  <Tag label={item.prioridade} color={priC[item.prioridade]}/>
                  {item.valor>0 && <Tag label={fmt(item.valor)} color={T.blueL}/>}
                </div>
                {item.notas && <div style={{fontSize:11,color:T.muted,marginTop:3}}>{item.notas}</div>}
                {item.data && <div style={{fontSize:11,color:T.textSub,marginTop:2}}>📅 {new Date(item.data).toLocaleDateString("pt-PT")}</div>}
              </div>
              <button onClick={()=>toggleStatus(item.id)}
                style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${T.border}`,
                  background:item.status==="concluido"?T.green+"30":"transparent",
                  color:item.status==="concluido"?T.green:T.muted,fontSize:11,cursor:"pointer",fontWeight:600,flexShrink:0}}>
                {item.status==="concluido"?"✓ Feito":"Marcar feito"}
              </button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function QuintaView({ page, addLog, onResChange }: any) {
  if (page==="fin")        return <QuintaFin addLog={addLog}/>;
  if (page==="reservas")   return <QuintaReservas addLog={addLog} onResChange={onResChange}/>;
  if (page==="previsoes")  return <QuintaPrevisoes/>;
  if (page==="limpezas")   return <QuintaLimpezas addLog={addLog}/>;
  if (page==="manutencao") return <QuintaManutencao addLog={addLog}/>;
  if (page==="casas")      return <QuintaCasas/>;
  if (page==="tarefas")    return <QuintaTarefas addLog={addLog}/>;
  return <QuintaOverview/>;
}

// ── QUINTA DA REGADA — OVERVIEW ──
function QuintaCasas() {
  const { data: casasDB, insert: insertCasa, update: updateCasaDB } = useALCasas();
  const [casasLocal, setCasasLocal] = useState<any[]>(loadCasas());
  const casas = isDemoAccount() ? casasLocal : (casasDB || []).map((c:any) => ({
    ...c, label: c.nome, icon: c.icone||"🏠", maxHospedes: c.capacidade||2, precoPorNoite: parseFloat(c.preco_noite)||100
  }));
  const setCasas = isDemoAccount() ? setCasasLocal : (_:any) => {};
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string|null>(null);
  const [newC, setNewC] = useState({label:"",icon:"🏠",maxHospedes:2,precoPorNoite:100});

  const save = (updated: any[]) => {
    setCasas(updated);
    localStorage.setItem("kairos_casas", JSON.stringify(updated));
    // Also update in-memory CASAS
    CASAS.length = 0;
    updated.forEach(c => CASAS.push(c));
  };

  const addCasa = async () => {
    if(!newC.label.trim()) return;
    if (isDemoAccount()) {
      const updated = [...casas, {...newC, id: newC.label.toLowerCase().replace(/\s+/g,"_")+"_"+Date.now()}];
      save(updated);
    } else {
      await insertCasa({ nome: newC.label, icone: newC.icon, capacidade: newC.maxHospedes, preco_noite: newC.precoPorNoite, ativa: true });
    }
    setAdding(false);
    setNewC({label:"",icon:"🏠",maxHospedes:2,precoPorNoite:100});
  };

  const removeCasa = async (id: string) => {
    if(casas.length <= 1) return;
    if (isDemoAccount()) { save(casas.filter(c=>c.id!==id)); }
    else { await updateCasaDB(id, { ativa: false }); }
  };

  const updateCasa = async (id: string, field: string, value: any) => {
    if (isDemoAccount()) { save(casas.map(c=>c.id===id?{...c,[field]:value}:c)); }
    else {
      const fieldMap: any = { label:'nome', icon:'icone', maxHospedes:'capacidade', precoPorNoite:'preco_noite' };
      await updateCasaDB(id, { [fieldMap[field]||field]: value });
    }
  };

  const inp = (style={}) => ({
    padding:"8px 10px",borderRadius:8,background:T.surface,
    border:`1px solid ${T.border}`,color:T.text,fontSize:13,
    width:"100%",...style
  } as any);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <KPI l="Unidades" v={casas.length} c="alojamentos" ac={T.amber}/>
        <KPI l="Capacidade" v={casas.reduce((s:number,c:any)=>s+c.maxHospedes,0)} c="hóspedes máx" ac={T.blueL}/>
      </div>

      <Card title="Gerir Alojamentos" accent={T.amber} action="+ Adicionar Alojamento" onAction={()=>setAdding(true)}>
        {adding && (
          <div style={{padding:16,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:16}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10,marginBottom:12}}>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Nome</div>
                <input value={newC.label} onChange={e=>setNewC({...newC,label:e.target.value})}
                  placeholder="Ex: Suite Principal" style={inp()}/>
              </div>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Ícone</div>
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  {CASAS_ICONS.map(ic=>(
                    <button key={ic} onClick={()=>setNewC({...newC,icon:ic})}
                      style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${newC.icon===ic?T.amber:T.border}`,
                        background:newC.icon===ic?`${T.amber}20`:"transparent",cursor:"pointer",fontSize:16}}>
                      {ic}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Hóspedes Máx</div>
                <input type="number" min={1} max={20} value={newC.maxHospedes}
                  onChange={e=>setNewC({...newC,maxHospedes:+e.target.value})} style={inp()}/>
              </div>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Preço/Noite (€)</div>
                <input type="number" min={0} value={newC.precoPorNoite}
                  onChange={e=>setNewC({...newC,precoPorNoite:+e.target.value})} style={inp()}/>
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={addCasa} style={{padding:"8px 16px",borderRadius:8,border:"none",
                background:T.amber,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"8px 12px",borderRadius:8,
                border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}}>Cancelar</button>
            </div>
          </div>
        )}

        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {casas.map((casa:any)=>(
            <div key={casa.id} style={{padding:"14px 16px",borderRadius:10,background:T.panel,
              border:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
              <div style={{fontSize:28}}>{casa.icon}</div>
              {editing===casa.id ? (
                <div style={{flex:1,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:8}}>
                  <input value={casa.label} onChange={e=>updateCasa(casa.id,"label",e.target.value)}
                    style={inp({fontSize:12})} placeholder="Nome"/>
                  <input type="number" value={casa.maxHospedes} min={1} max={20}
                    onChange={e=>updateCasa(casa.id,"maxHospedes",+e.target.value)}
                    style={inp({fontSize:12})} placeholder="Hóspedes"/>
                  <input type="number" value={casa.precoPorNoite} min={0}
                    onChange={e=>updateCasa(casa.id,"precoPorNoite",+e.target.value)}
                    style={inp({fontSize:12})} placeholder="€/noite"/>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
                    {CASAS_ICONS.map(ic=>(
                      <button key={ic} onClick={()=>updateCasa(casa.id,"icon",ic)}
                        style={{padding:"2px 6px",borderRadius:6,border:`1px solid ${casa.icon===ic?T.amber:T.border}`,
                          background:casa.icon===ic?`${T.amber}20`:"transparent",cursor:"pointer",fontSize:14}}>
                        {ic}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:700,color:T.text}}>{casa.label}</div>
                  <div style={{fontSize:12,color:T.muted,marginTop:2}}>
                    👥 Máx {casa.maxHospedes} hóspedes · 💶 €{casa.precoPorNoite}/noite
                  </div>
                </div>
              )}
              <div style={{display:"flex",gap:6,flexShrink:0}}>
                <button onClick={()=>setEditing(editing===casa.id?null:casa.id)}
                  style={{padding:"10px 14px",borderRadius:6,border:`1px solid ${T.border}`,
                    background:"transparent",color:editing===casa.id?T.amber:T.muted,cursor:"pointer",fontSize:12,fontWeight:600}}>
                  {editing===casa.id?"✓ Feito":"✏️ Editar"}
                </button>
                {casas.length>1 && (
                  <button onClick={()=>removeCasa(casa.id)}
                    style={{padding:"10px 14px",borderRadius:6,border:`1px solid ${T.border}`,
                      background:"transparent",color:T.red,cursor:"pointer",fontSize:12,fontWeight:600}}>
                    🗑️
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div style={{marginTop:12,padding:"10px 14px",borderRadius:8,background:T.panel2,fontSize:12,color:T.muted}}>
          💡 As alterações aos alojamentos aplicam-se a toda a app (Reservas, Limpezas, Previsões, etc.)
        </div>
      </Card>

      {/* ── Configurações de Horários ── */}
      <ALConfigCard/>
    </div>
  );
}

function ALConfigCard() {
  const LS_KEY = "kairos_al_config";
  const defaults = { horaCI:"16:00", horaCO:"10:00", horaLimpeza:"10:00", limpezaAuto:true, notifLimpeza:true };
  const [cfg, setCfg] = useState<any>(() => {
    try { return { ...defaults, ...JSON.parse(localStorage.getItem(LS_KEY)||"{}") }; }
    catch { return defaults; }
  });
  const [saved, setSaved] = useState(false);

  const save = () => {
    localStorage.setItem(LS_KEY, JSON.stringify(cfg));
    setSaved(true); setTimeout(()=>setSaved(false), 2500);
  };

  const inp = {padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13};
  const lbl = {fontSize:11,color:T.muted,marginBottom:5,fontWeight:700 as any,textTransform:"uppercase" as any};

  const Row = ({label,sub,children}:{label:string,sub?:string,children:any}) => (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
      padding:"12px 0",borderBottom:`1px solid ${T.border}`,gap:12,flexWrap:"wrap" as any}}>
      <div>
        <div style={{fontSize:13,color:T.text}}>{label}</div>
        {sub&&<div style={{fontSize:11,color:T.muted,marginTop:2}}>{sub}</div>}
      </div>
      {children}
    </div>
  );

  const Toggle = ({val,k}:{val:boolean,k:string}) => (
    <button onClick={()=>setCfg((c:any)=>({...c,[k]:!c[k]}))}
      style={{padding:"6px 16px",borderRadius:8,border:"none",cursor:"pointer",fontWeight:700,fontSize:12,
        flexShrink:0,background:val?T.green:T.panel2,color:val?"white":T.muted,transition:"all 0.15s"}}>
      {val?"✓ Activo":"Desligado"}
    </button>
  );

  return (
    <Card title="⚙️ Configurações de Horários" accent={T.amber}>
      <Row label="🕐 Hora de Check-in" sub="Hora predefinida para todos os check-ins">
        <input type="time" value={cfg.horaCI}
          onChange={e=>setCfg((c:any)=>({...c,horaCI:e.target.value}))} style={{...inp,width:120}}/>
      </Row>
      <Row label="🕐 Hora de Check-out" sub="Hora predefinida para todos os check-outs">
        <input type="time" value={cfg.horaCO}
          onChange={e=>setCfg((c:any)=>({...c,horaCO:e.target.value}))} style={{...inp,width:120}}/>
      </Row>
      <Row label="🧹 Hora de Limpeza" sub="Hora predefinida para limpezas pós checkout">
        <input type="time" value={cfg.horaLimpeza}
          onChange={e=>setCfg((c:any)=>({...c,horaLimpeza:e.target.value}))} style={{...inp,width:120}}/>
      </Row>
      <Row label="🤖 Agendar limpeza automaticamente"
        sub="Cria limpeza automaticamente no dia do checkout">
        <Toggle val={cfg.limpezaAuto} k="limpezaAuto"/>
      </Row>
      <Row label="📲 Notificar responsável de limpeza"
        sub="Envia aviso quando há limpeza agendada">
        <Toggle val={cfg.notifLimpeza} k="notifLimpeza"/>
      </Row>

      <div style={{marginTop:14,padding:"10px 14px",borderRadius:8,background:`${T.blueL}10`,
        border:`1px solid ${T.blueL}30`,fontSize:12,color:T.muted}}>
        💡 Podes alterar o horário de cada reserva individualmente. Estes são os valores predefinidos para novas reservas.
      </div>

      {saved&&(
        <div style={{marginTop:10,padding:"9px 14px",borderRadius:8,
          background:`${T.green}15`,border:`1px solid ${T.green}40`,color:T.green,fontSize:13}}>
          ✅ Configurações guardadas
        </div>
      )}
      <div style={{marginTop:14,display:"flex",justifyContent:"flex-end"}}>
        <button onClick={save} style={{padding:"10px 24px",borderRadius:10,border:"none",
          background:T.amber,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>
          💾 Guardar
        </button>
      </div>
    </Card>
  );
}

function QuintaOverview() {
  const { data: resDB, loading: resLoading } = useALReservas();
  const [res] = useState(isDemoAccount() ? DEMO_AL_RES : []);
  const allRes = normResArr(isDemoAccount() ? res : (resDB || []));
  const showSkeleton = !isDemoAccount() && resLoading && allRes.length === 0;
  const [desp] = useState(AL_DESPESAS_INIT);
  const limpezasSimuladas = isDemoAccount() ? DEMO_LIMPEZAS : [];
  const [casasConfig, setCasasConfig] = useState<any[]>(CASAS);
  const [editPrecos, setEditPrecos] = useState(false);
  const [tmpPrecos, setTmpPrecos] = useState<Record<string,number>>(
    Object.fromEntries(CASAS.map((c:any)=>[c.id, c.precoPorNoite||100]))
  );
  const [historicoPrecos, setHistoricoPrecos] = useState<any[]>(isDemoAccount() ? DEMO_PRECOS_HIST : []);
  // precos helper — map from casa.id to price
  const precos: Record<string,number> = Object.fromEntries(casasConfig.map((c:any)=>[c.id, c.precoPorNoite||100]));

  const conf = allRes.filter((r:any)=>r.status==="confirmada");
  const receitaTotal = conf.reduce((s:number,r:any)=>s+r.valor,0);
  const despTotal = desp.reduce((s:number,d:any)=>s+d.valor,0);
  const totalComissoes = conf.reduce((s:number,r:any)=>s+r.valor*(COMISSAO_PLAT[r.plat]||0),0);
  const totalIVA = conf.reduce((s:number,r:any)=>s+r.valor*IVA_TAXA,0);
  const netLiquido = receitaTotal - despTotal - totalComissoes - totalIVA;
  const totalNoites = conf.reduce((s:number,r:any)=>s+r.noites,0);
  const totalDisp = 3 * 30;
  const ocupacao = totalNoites > 0 ? Math.round(totalNoites/totalDisp*100) : 0;
  const revpar = totalDisp > 0 ? Math.round(receitaTotal/totalDisp) : 0;

  // Por casa
  const casaStats = CASAS.map(c=>{
    const reservasCasa = conf.filter((r:any)=>
      r.casa===c.id||r.casa===c.label||r.casa===c.nome||
      r.casa_nome===c.label||r.casa_nome===c.nome||r.casa_nome===c.id);
    const receitaCasa = reservasCasa.reduce((s:number,r:any)=>s+r.valor,0);
    const noitesCasa = reservasCasa.reduce((s:number,r:any)=>s+r.noites,0);
    const dispCasa = 30;
    return { ...c, receita:receitaCasa, noites:noitesCasa, ocupacao:Math.round(noitesCasa/dispCasa*100), nReservas:reservasCasa.length };
  });
  const melhorCasa = [...casaStats].sort((a,b)=>b.receita-a.receita)[0];
  const menosCasa = [...casaStats].sort((a,b)=>a.ocupacao-b.ocupacao)[0];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
      {showSkeleton ? (
        <>
          <SkeletonKPIRow count={4} />
          <SkeletonKPIRow count={4} />
          <Card title="A carregar..." accent={T.amber}>
            <SkeletonList rows={3} />
          </Card>
        </>
      ) : (<>
      {/* Alertas inline */}
      <AlertPanel alRes={res} limpezas={limpezasSimuladas}/>
      {/* KPIs principais */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12 }}>
        <KPI l="Receita Bruta" v={fmt(receitaTotal)} c={`${conf.length} reservas`} ac={T.amber}/>
        <KPI l="Lucro Líquido" v={fmt(Math.round(netLiquido))} c="após comissões, IVA e despesas" ac={T.green}/>
        <KPI l="Taxa de Ocupação" v={`${ocupacao}%`} c="meta: 75%" ac={ocupacao>=75?T.green:T.amber}/>
        <KPI l="RevPAR" v={fmt(revpar)} c="receita/quarto disponível" ac={T.amber}/>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12 }}>
        <KPI l="Comissões Plat." v={fmt(Math.round(totalComissoes))} c="Airbnb 3% · Booking 15%" ac={T.red}/>
        <KPI l="IVA a Pagar" v={fmt(Math.round(totalIVA))} c="taxa turismo 6%" ac={T.red}/>
        <KPI l="Despesas" v={fmt(despTotal)} c="operacionais" ac={T.red}/>
        <KPI l="Fundo Emergência" v={fmt(Math.round(netLiquido*0.1))} c="10% do lucro" ac={T.muted}/>
      </div>

      {/* Preços por noite editáveis */}
      <Card title="Preços por Noite" accent={T.amber}
        action={editPrecos?"Guardar":"Editar Preços"}
        onAction={()=>{
          if(editPrecos) {
            // Save prices back to CASAS
            const updatedCasas = CASAS.map((c:any)=>({...c,precoPorNoite:tmpPrecos[c.id]||c.precoPorNoite}));
            CASAS.length=0; updatedCasas.forEach(c=>CASAS.push(c));
            localStorage.setItem("kairos_casas",JSON.stringify(updatedCasas));
            setCasasConfig([...updatedCasas]);
            const changed = Object.keys(tmpPrecos).some(k=>tmpPrecos[k]!==precos[k]);
            if(changed) {
              const hist:any = { data:new Date().toISOString().split("T")[0], nota:"Actualização manual" };
              updatedCasas.forEach((c:any)=>{ hist[c.id]=c.precoPorNoite; });
              setHistoricoPrecos((h:any[])=>[...h,hist]);
            }
          } else setTmpPrecos(Object.fromEntries(casasConfig.map((c:any)=>[c.id,c.precoPorNoite||100])));
          setEditPrecos(!editPrecos);
        }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12 }}>
          {CASAS.map(c=>(
            <div key={c.id} style={{ textAlign:"center", padding:"12px", background:"rgba(255,255,255,0.02)", borderRadius:9 }}>
              <div style={{ fontSize:18, marginBottom:6 }}>{c.icon}</div>
              <div style={{ fontSize:12, fontWeight:700, color:T.text, marginBottom:4 }}>{c.label}</div>
              <div style={{ fontSize:11, color:T.muted, marginBottom:8 }}>{c.maxPessoas} pessoas max</div>
              {editPrecos ? (
                <input type="number" value={tmpPrecos[c.id]}
                  onChange={(e:any)=>setTmpPrecos({...tmpPrecos,[c.id]:+e.target.value})}
                  style={{ width:"90%", padding:"6px 10px", borderRadius:6, background:"rgba(255,255,255,0.07)",
                    border:`1px solid ${T.amber}`, color:T.amber, fontSize:16, fontWeight:700,
                    textAlign:"center", fontFamily:"monospace" }}/>
              ) : (
                <div style={{ fontSize:18, fontWeight:700, color:T.amber, fontFamily:"monospace" }}>€{precos[c.id]}/noite</div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Por casa + resumo financeiro */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:16 }}>
        <Card title="Análise por Casa" accent={T.amber}>
          {casaStats.map((c,i)=>(
            <div key={c.id} style={{ padding:"10px 0", borderBottom:`1px solid rgba(255,255,255,0.04)` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span>{c.icon}</span>
                  <span style={{ fontSize:12, fontWeight:600, color:T.text }}>{c.label}</span>
                  {melhorCasa?.id===c.id && <Tag label="★ Top" color={T.gold}/>}
                  {menosCasa?.id===c.id && c.ocupacao<30 && <Tag label="⚠ Baixa" color={T.red}/>}
                </div>
                <span style={{ fontSize:13, fontWeight:700, color:T.amber, fontFamily:"monospace" }}>{fmt(c.receita)}</span>
              </div>
              <div style={{ display:"flex", gap:12, marginBottom:6 }}>
                <span style={{ fontSize:11, color:T.muted }}>{c.nReservas} reservas · {c.noites} noites</span>
                <span style={{ fontSize:11, color:c.ocupacao>=60?T.green:c.ocupacao>=30?T.amber:T.red, fontWeight:600 }}>{c.ocupacao}% ocup.</span>
              </div>
              <div style={{ height:4, borderRadius:99, background:"rgba(255,255,255,0.06)" }}>
                <div style={{ width:`${c.ocupacao}%`, height:"100%", borderRadius:99,
                  background:c.ocupacao>=60?T.green:c.ocupacao>=30?T.amber:T.red }}/>
              </div>
            </div>
          ))}
        </Card>
        <Card title="Resumo Financeiro" accent={T.amber}>
          {[
            ["Receita Bruta", fmt(receitaTotal), T.amber],
            [`Comissão Airbnb (3%)`, `-${fmt(Math.round(conf.filter((r:any)=>r.plat==="Airbnb").reduce((s:number,r:any)=>s+r.valor*0.03,0)))}`, T.red],
            [`Comissão Booking (15%)`, `-${fmt(Math.round(conf.filter((r:any)=>r.plat==="Booking").reduce((s:number,r:any)=>s+r.valor*0.15,0)))}`, T.red],
            ["IVA Turismo (6%)", `-${fmt(Math.round(totalIVA))}`, T.red],
            ["Despesas Operacionais", `-${fmt(despTotal)}`, T.red],
            ["Lucro Líquido", fmt(Math.round(netLiquido)), T.green],
            ["Custo médio/noite", totalNoites>0?fmt(Math.round(receitaTotal/totalNoites)):"—", T.muted],
            ["Preço médio/noite", totalNoites>0?fmt(Math.round(receitaTotal/totalNoites)):"—", T.amber],
          ].map(([k,v,c]:any,i)=>(
            <div key={i} style={{ display:"flex", justifyContent:"space-between",
              padding:"7px 0", borderBottom:`1px solid rgba(255,255,255,0.04)` }}>
              <span style={{ fontSize:11, color:T.muted }}>{k}</span>
              <span style={{ fontSize:12, fontWeight:700, color:c, fontFamily:"monospace" }}>{v}</span>
            </div>
          ))}
        </Card>
      </div>

      {/* Próximas reservas */}
      <Card title="Próximas Reservas" accent={T.amber}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8 }}>
          {res.filter(r=>r.status!=="limpeza"&&r.status!=="cancelada").slice(0,4).map(r=>{
            const casa = CASAS.find(c=>c.id===r.casa);
            return (
              <div key={r.id} style={{ padding:"10px 12px", borderRadius:10,
                background:"rgba(255,255,255,0.025)", border:`1px solid ${T.border}` }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:T.text }}>{r.guest}</span>
                  <Tag label={r.plat} color={r.plat==="Airbnb"?T.red:r.plat==="Booking"?T.blueL:T.green}/>
                </div>
                <div style={{ fontSize:11, color:T.muted }}>
                  {casa?.icon} {casa?.label} · {r.hospedes} pax
                </div>
                <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>
                  {new Date(r.ci).toLocaleDateString("pt-PT",{day:"numeric",month:"short"})} →{" "}
                  {new Date(r.co).toLocaleDateString("pt-PT",{day:"numeric",month:"short"})}
                </div>
                {r.valor>0 && <div style={{ fontSize:13, fontWeight:700, color:T.amber, fontFamily:"monospace", marginTop:4 }}>{fmt(r.valor)}</div>}
              </div>
            );
          })}
        </div>
      </Card>
      </>)}
    </div>
  );
}

// ── QUINTA — RESERVAS ──
function QuintaReservas({ addLog, onResChange }: any) {
  const { data: res, loading: resLoading, insert: insertRes, update: updateRes, remove: removeRes, reload: reloadRes } = useALReservas();
  const { data: casas } = useALCasas();
  // Compatibilidade: usar setter local para demo account
  const [resLocal, setResLocal] = useState(isDemoAccount() ? DEMO_AL_RES : []);
  const allRes = normResArr(isDemoAccount() ? resLocal : (res || []));

  // ── Sync iCal manual ──────────────────────────────────────
  const [syncing, setSyncing]   = useState(false);
  const [syncMsg, setSyncMsg]   = useState("");

  const syncICal = async () => {
    if (isDemoAccount()) { setSyncMsg("Demo — sync desactivado"); setTimeout(()=>setSyncMsg(""),3000); return; }
    setSyncing(true); setSyncMsg("");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Sessão expirada — faz login novamente");

      const resp = await fetch(
        "https://rchyocvcskswoudqrjrf.supabase.co/functions/v1/sync-ical",
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        }
      );

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Erro ${resp.status}: ${txt.slice(0,100)}`);
      }

      const data = await resp.json();
      const total = (data.results||[]).reduce((s:number,r:any)=>s+(r.synced||0),0);
      setSyncMsg(total > 0 ? `✅ ${total} reserva(s) importada(s)!` : "✅ Sincronizado — sem novas reservas");
      setTimeout(()=>setSyncMsg(""), 5000);
      // Recarregar dados do Supabase para mostrar as novas reservas
      reloadRes();
    } catch(e: any) {
      setSyncMsg(`⚠️ ${e.message}`);
      setTimeout(()=>setSyncMsg(""), 10000);
    } finally {
      setSyncing(false);
    }
  };
  const [mes, setMes] = useState(new Date().getMonth());
  const [ano, setAno] = useState(2026);
  const [addingR, setAddingR] = useState(false);
  const [expandedR, setExpandedR] = useState<any>(null);
  const alCfgDefaults = getALConfig();
  const [newR, setNewR] = useState({guest:"",ci:"",co:"",ciHora:alCfgDefaults.horaCI,coHora:alCfgDefaults.horaCO,plat:"Airbnb",casa:"girassol",hospedes:2,criancas:false,numCriancas:0,animais:false,valor:0,notas:""});

  const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const diasNoMes = new Date(ano,mes+1,0).getDate();
  const primeiroDia = new Date(ano,mes,1).getDay();

  // Cores por reserva (cycling)
  const resCores = ["#3b9de8","#9b72f7","#22c27a","#f5a623","#f472b6","#2dd4bf","#e85252","#c9a84c"];
  const resColorMap:Record<number,string> = {};
  res.filter(r=>r.status!=="limpeza").forEach((r:any,i:number)=>{ resColorMap[r.id]=resCores[i%resCores.length]; });

  // Dias com eventos para o calendário
  const diaEventos: Record<number,{tipo:string,cor:string,label:string}[]> = {};
  res.forEach((r:any)=>{
    const start = new Date(r.ci), end = new Date(r.co);
    for(let d=new Date(start); d<=end; d.setDate(d.getDate()+1)) {
      if(d.getFullYear()===ano && d.getMonth()===mes) {
        const dia = d.getDate();
        if(!diaEventos[dia]) diaEventos[dia]=[];
        if(r.status==="limpeza") {
          diaEventos[dia].push({tipo:"limpeza",cor:T.teal,label:"🧹"});
        } else if(r.status!=="cancelada") {
          const casa = CASAS.find(c=>c.id===r.casa);
          diaEventos[dia].push({tipo:"reserva",cor:resColorMap[r.id]||T.amber,label:`${casa?.icon||""} ${r.guest?.split(" ")[0]||""}`});
        }
      }
    }
  });

  const addReserva = async () => {
    const noites = Math.max(0,Math.round((new Date(newR.co).getTime()-new Date(newR.ci).getTime())/(86400000)));
    const alCfg = getALConfig();
    const horaCI = newR.ciHora || alCfg.horaCI;
    const horaCO = newR.coHora || alCfg.horaCO;
    if (isDemoAccount()) {
      const nova = {...newR,id:allRes.length+1,noites,status:"confirmada",ciHora:horaCI,coHora:horaCO};
      setResLocal((r:any)=>[...r,nova]);
      // Limpeza automática demo
      if (alCfg.limpezaAuto) {
        setResLocal((r:any)=>[...r, {id:allRes.length+2,guest:"🧹 Limpeza",ci:newR.co,co:newR.co,noites:0,plat:"auto",status:"limpeza",valor:0,casa:newR.casa,hospedes:0,ciHora:alCfg.horaLimpeza,coHora:"",notas:"Limpeza automática"}]);
      }
    } else {
      await insertRes({
        guest:newR.guest, check_in:newR.ci, check_out:newR.co, noites,
        status:"confirmada", valor_total:+newR.valor,
        casa_nome: (CASAS.find((c:any)=>c.id===newR.casa)?.label?.replace("Casa ","").replace("dos ","").replace("Catequese","Catequese") || newR.casa),
        plataforma: (newR.plat||"airbnb").toLowerCase(),
        hora_ci:horaCI, hora_co:horaCO, notas:newR.notas||"",
        num_pessoas: +newR.hospedes||0,
        num_adultos: Math.max(0,(+newR.hospedes||0)-(+newR.numCriancas||0)),
        num_criancas: +newR.numCriancas||0,
        tem_animal: !!newR.animais,
      });
      // Limpeza automática
      if (alCfg.limpezaAuto) {
        await insertRes({guest:"🧹 Limpeza", check_in:newR.co, check_out:newR.co, noites:0, plataforma:"auto", status:"limpeza", valor_total:0, casa_nome:newR.casa, hora_ci:alCfg.horaLimpeza, hora_co:"", notas:"Limpeza automática pós checkout"});
      }
    }
    addLog?.("quinta","reserva",`Nova reserva: ${newR.guest} · ${fmt(+newR.valor)}`,"🏠",+newR.valor);
    setAddingR(false);
    setNewR({guest:"",ci:"",co:"",ciHora:"15:00",coHora:"11:00",plat:"Airbnb",casa:"girassol",hospedes:2,criancas:false,numCriancas:0,animais:false,valor:0,notas:""});
  };
  const cancelarReserva = async (id:any) => {
    const r = allRes.find((x:any)=>x.id===id);
    if (isDemoAccount()) {
      setResLocal((rs:any)=>rs.map((rv:any)=>rv.id===id?{...rv,status:"cancelada"}:rv));
    } else {
      await updateRes(id, {status:"cancelada"});
    }
    if(r) addLog?.("quinta","reserva",`Reserva cancelada: ${r.guest}`,"❌");
  };

  const conf = allRes.filter((r:any)=>r.status==="confirmada");
  const limps = allRes.filter((r:any)=>r.status==="limpeza");
  const canceladas = res.filter(r=>r.status==="cancelada");

  const statusColors = { confirmada:T.green, pendente:T.amber, limpeza:T.teal, cancelada:T.red };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {/* Alertas inline */}
      <AlertPanel alRes={res}/>

      {/* Botão Sync iCal */}
      <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" as any}}>
        <button onClick={syncICal} disabled={syncing}
          style={{padding:"10px 20px",borderRadius:10,border:`1px solid ${T.amber}`,
            background:`${T.amber}12`,color:T.amber,cursor:syncing?"wait":"pointer",
            fontSize:13,fontWeight:700,display:"flex",alignItems:"center",gap:8,
            opacity:syncing?0.7:1,transition:"all 0.15s"}}>
          <span style={{fontSize:16}}>{syncing?"⏳":"🔄"}</span>
          {syncing?"A sincronizar...":"Sincronizar Airbnb & Booking"}
        </button>
        {syncMsg&&(
          <span style={{fontSize:13,color:syncMsg.startsWith("✅")?T.green:T.red,fontWeight:500}}>
            {syncMsg}
          </span>
        )}
      </div>

      {/* Calendário de Reservas — único, com cores por casa e limpezas */}
      <ReservasCalendario reservas={allRes}/>

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:12}}>
        <KPI l="Ativas" v={conf.filter(r=>new Date(r.co)>=new Date()).length} c="" ac={T.green}/>
        <KPI l="Próximas" v={conf.filter(r=>new Date(r.ci)>new Date()).length} c="" ac={T.amber}/>
        <KPI l="Concluídas" v={conf.filter(r=>new Date(r.co)<new Date()).length} c="" ac={T.blueL}/>
        <KPI l="Limpezas" v={limps.length} c="agendadas" ac={T.teal}/>
        <KPI l="Canceladas" v={canceladas.length} c="" ac={T.red}/>
      </div>

      {/* Form nova reserva */}
      {addingR && (
        <Card title="Nova Reserva" accent={T.amber}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
            <div style={{gridColumn:"1/-1"}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Hóspede</div>
              <input placeholder="Nome do hóspede" value={newR.guest}
                onChange={(e:any)=>setNewR({...newR,guest:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
            </div>
            {([["Check-in","date","ci"],["Check-out","date","co"],["Hora CI","time","ciHora"],["Hora CO","time","coHora"]] as any).map(([l,t,k]:any)=>(
              <div key={k}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>{l}</div>
                <input type={t} value={newR[k]} onChange={(e:any)=>setNewR({...newR,[k]:e.target.value})}
                  style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
              </div>
            ))}
            {([["Casa","select","casa"],["Plataforma","select","plat"]] as any).map(([l,t,k]:any)=>(
              <div key={k}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>{l}</div>
                <select value={newR[k]} onChange={(e:any)=>setNewR({...newR,[k]:e.target.value})}
                  style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"#0c1322",
                    border:`1px solid ${T.border}`,color:T.text,fontSize:12}}>
                  {k==="casa" ? CASAS.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)
                    : ["Airbnb","Booking","Direto"].map(p=><option key={p}>{p}</option>)}
                </select>
              </div>
            ))}
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Valor €</div>
              <input type="number" value={newR.valor} onChange={(e:any)=>setNewR({...newR,valor:+e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Hóspedes</div>
              <input type="number" value={newR.hospedes} onChange={(e:any)=>setNewR({...newR,hospedes:+e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>👶 Crianças</div>
              <input type="number" min="0" value={newR.criancas?1:newR.numCriancas||0}
                onChange={(e:any)=>setNewR({...newR,numCriancas:+e.target.value,criancas:+e.target.value>0})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
            </div>
            <div style={{display:"flex",alignItems:"flex-end"}}>
              <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,color:T.text,paddingBottom:8}}>
                <input type="checkbox" checked={newR.animais} onChange={(e:any)=>setNewR({...newR,animais:e.target.checked})}/>🐾 Animal de estimação
              </label>
            </div>
            <div style={{gridColumn:"1/-1"}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Notas</div>
              <input placeholder="Notas..." value={newR.notas} onChange={(e:any)=>setNewR({...newR,notas:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
            </div>
            <button onClick={addReserva} style={{gridColumn:"1/-1",padding:"9px",borderRadius:10,border:"none",
              background:T.amber,color:"white",fontSize:13,fontWeight:700,cursor:"pointer"}}>
              Guardar Reserva
            </button>
          </div>
        </Card>
      )}

      {/* Lista de reservas e limpezas */}
      <Card title="Todas as Reservas & Limpezas" accent={T.amber}>
        {allRes.sort((a:any,b:any)=>new Date(a.ci).getTime()-new Date(b.ci).getTime()).map((r:any)=>{
          const casa = (casas||[]).find((c:any)=>c.nome===r.casa||c.nome===r.casa_nome||c.id===r.casa) || CASAS.find((c:any)=>c.id===r.casa);
          const isLimp = r.status==="limpeza";
          const isCanc = r.status==="cancelada";
          const cor = statusColors[r.status]||T.muted;
          const resColor = resColorMap[r.id]||T.amber;
          const isExpanded = expandedR===r.id;
          const incompleta = !isLimp && (!r.guest || r.valor===0);

          return (
            <div key={r.id}>
              <div style={{padding:"11px 0",borderBottom:`1px solid rgba(255,255,255,0.04)`,
                display:"flex",alignItems:"center",gap:12,opacity:isCanc?0.5:1}}>
                <div style={{width:8,height:8,borderRadius:"50%",flexShrink:0,
                  background:isLimp?T.teal:incompleta?T.amber:isCanc?T.red:resColor}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,color:isLimp?T.teal:isCanc?T.muted:T.text,fontWeight:600,
                    textDecoration:isCanc?"line-through":"none",display:"flex",alignItems:"center",gap:6}}>
                    {isLimp?"🧹 Limpeza":(r.guest||<span style={{color:T.amber,fontStyle:"italic"}}>Sem nome</span>)}
                    {incompleta&&<span style={{fontSize:10,background:`${T.amber}25`,color:T.amber,
                      padding:"1px 6px",borderRadius:4,fontWeight:700}}>⚠️ Incompleto</span>}
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2,display:"flex",gap:6,flexWrap:"wrap" as any}}>
                    <span>{new Date(r.ci).toLocaleDateString("pt-PT",{day:"numeric",month:"short"})}</span>
                    {r.ciHora&&!isLimp&&<span>🕐 {r.ciHora}</span>}
                    <span>→ {new Date(r.co).toLocaleDateString("pt-PT",{day:"numeric",month:"short"})}</span>
                    {r.coHora&&!isLimp&&<span>{r.coHora}</span>}
                    <span>· {casa?.icon||"🏠"} {casa?.nome||casa?.label||r.casa_nome||r.casa}</span>
                    {r.noites>0&&<span>· {r.noites}n</span>}
                    {r.plataforma&&<span>· {r.plataforma}</span>}
                    {!isLimp&&r.num_pessoas>0&&<span>· 👥 {r.num_pessoas}</span>}
                    {!isLimp&&r.num_criancas>0&&<span style={{color:T.blueL}}>· 👶 {r.num_criancas}</span>}
                    {!isLimp&&r.tem_animal&&<span style={{color:T.amber}}>· 🐾</span>}
                  </div>
                </div>
                {r.valor>0&&!isCanc&&<span style={{fontSize:13,fontWeight:700,color:T.amber,fontFamily:"monospace",flexShrink:0}}>{fmt(r.valor)}</span>}
                {!isLimp&&!isCanc&&(
                  <button onClick={()=>setExpandedR(isExpanded?null:r.id)}
                    style={{background:incompleta?`${T.amber}20`:"transparent",
                      border:`1px solid ${incompleta?T.amber:T.border}`,
                      color:incompleta?T.amber:T.muted,padding:"4px 10px",
                      borderRadius:6,fontSize:11,cursor:"pointer",flexShrink:0,fontWeight:incompleta?700:400}}>
                    {isExpanded?"Fechar":"✏️ Editar"}
                  </button>
                )}
                {!isCanc&&!isLimp&&(
                  <button onClick={()=>cancelarReserva(r.id)}
                    style={{background:"none",border:`1px solid ${T.red}30`,color:T.red,padding:"4px 8px",
                      borderRadius:6,fontSize:11,cursor:"pointer",flexShrink:0}} aria-label="Cancelar">✕</button>
                )}
              </div>

              {/* Painel de edição inline */}
              {isExpanded&&(
                <EditarReserva r={r} casas={casas} onSave={async (changes:any)=>{
                  if (!isDemoAccount()) {
                    await updateRes(r.id, {
                      guest:      changes.guest,
                      valor_total: parseFloat(changes.valor)||0,
                      notas:      changes.notas,
                      hora_ci:    changes.ciHora,
                      hora_co:    changes.coHora,
                      check_in:   changes.ci,
                      check_out:  changes.co,
                      casa_nome:  changes.casa_nome,
                      plataforma: changes.plataforma,
                      noites:     parseInt(changes.noites)||1,
                      num_pessoas:  parseInt(changes.num_pessoas)||0,
                      num_criancas: parseInt(changes.num_criancas)||0,
                      tem_animal:   !!changes.tem_animal,
                    });
                  }
                  setExpandedR(null);
                }} onClose={()=>setExpandedR(null)}/>
              )}
            </div>
          );
        })}
        {allRes.filter((r:any)=>!r.guest||r.valor===0).length>0&&(
          <div style={{marginTop:12,padding:"10px 14px",borderRadius:8,
            background:`${T.amber}12`,border:`1px solid ${T.amber}30`,
            fontSize:12,color:T.amber}}>
            ⚠️ {allRes.filter((r:any)=>!r.guest||r.valor===0).length} reserva(s) com dados em falta — clica em "✏️ Editar" para completar
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Calendário de Reservas AL ──────────────────────────────
// Cores por casa — consistentes em toda a app
const CASA_CAL_CORES: Record<string,string> = {
  "Girassol":  "#F59E0B", // âmbar
  "Avós":      "#3B82F6", // azul
  "Catequese": "#10B981", // verde
  "limpeza":   "#EF4444", // vermelho
};
function casaCorCal(nome:string) {
  if(!nome) return "#6B7280";
  const n = nome.toLowerCase();
  if(n.includes("girassol"))  return CASA_CAL_CORES["Girassol"];
  if(n.includes("avós")||n.includes("avos")) return CASA_CAL_CORES["Avós"];
  if(n.includes("cateques"))  return CASA_CAL_CORES["Catequese"];
  if(n.includes("limpeza"))   return CASA_CAL_CORES["limpeza"];
  return "#F59E0B";
}
function casaNomeCurto(nome:string) {
  if(!nome) return "?";
  const n = nome.toLowerCase();
  if(n.includes("girassol"))  return "Girassol";
  if(n.includes("avós")||n.includes("avos")) return "Avós";
  if(n.includes("cateques"))  return "Cat.";
  return nome.split(" ")[0];
}

function ReservasCalendario({ reservas }: any) {
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth());
  const [ano, setAno] = useState(hoje.getFullYear());
  const [sel, setSel] = useState<number|null>(null);

  const meses = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const diasNoMes  = new Date(ano,mes+1,0).getDate();
  const primeiroDia = new Date(ano,mes,1).getDay();
  const hojeStr = hoje.toISOString().split("T")[0];

  const res = normResArr(reservas||[]);

  // Indexar por dia: reservas + limpezas automáticas no dia de checkout
  const porDia: Record<number,any[]> = {};

  res.filter((r:any)=>r.status!=="cancelada").forEach((r:any)=>{
    if(!r.ci||!r.co) return;
    const casa = r.casa_nome||r.casa||"";
    const cor = casaCorCal(casa);
    const isLimp = r.status==="limpeza";

    if(isLimp) {
      // Limpeza manual — só no dia
      const d = new Date(r.ci);
      if(d.getFullYear()===ano && d.getMonth()===mes) {
        const dia = d.getDate();
        if(!porDia[dia]) porDia[dia]=[];
        porDia[dia].push({...r, isCI:false, isCO:false, isLimp:true, cor:CASA_CAL_CORES["limpeza"]});
      }
      return;
    }

    // Reserva normal — marcar todos os dias
    const start = new Date(r.ci);
    const end   = new Date(r.co);
    for(let d=new Date(start); d<end; d.setDate(d.getDate()+1)) {
      if(d.getFullYear()===ano && d.getMonth()===mes) {
        const dia = d.getDate();
        if(!porDia[dia]) porDia[dia]=[];
        const ds = d.toISOString().split("T")[0];
        const isCI = ds===r.ci;
        if(!porDia[dia].find((x:any)=>x.id===r.id&&!x.isLimp)) {
          porDia[dia].push({...r, isCI, isCO:false, isLimp:false, cor});
        }
      }
    }
    // Check-out day
    const coDate = new Date(r.co);
    if(coDate.getFullYear()===ano && coDate.getMonth()===mes) {
      const dia = coDate.getDate();
      if(!porDia[dia]) porDia[dia]=[];
      // Marcar checkout
      const existing = porDia[dia].find((x:any)=>x.id===r.id&&!x.isLimp);
      if(existing) { existing.isCO=true; }
      else { porDia[dia].push({...r, isCI:false, isCO:true, isLimp:false, cor}); }
      // Limpeza automática no dia de checkout (se não houver já)
      const jaTemLimp = porDia[dia].some((x:any)=>x.isLimp&&(x.casa_nome||x.casa)===casa);
      if(!jaTemLimp) {
        porDia[dia].push({
          id:`limp-${r.id}`, isCI:false, isCO:false, isLimp:true,
          cor:CASA_CAL_CORES["limpeza"],
          casa_nome:casa, guest:"Limpeza",
          ci:r.co, co:r.co,
        });
      }
    }

    // Limpeza ANTES do check-in se não houve check-out na mesma casa nos 2 dias anteriores
    const ciDate = new Date(r.ci);
    const houveCheckoutAntes = res.filter((o:any)=>o.status!=="cancelada"&&o.status!=="limpeza").some((outra:any)=>{
      if(outra.id===r.id) return false;
      if((outra.casa_nome||outra.casa||"")!==casa) return false;
      if(!outra.co) return false;
      const diff = (ciDate.getTime()-new Date(outra.co).getTime())/(1000*60*60*24);
      return diff>=0 && diff<=2;
    });
    if(!houveCheckoutAntes) {
      const prep = new Date(ciDate);
      prep.setDate(prep.getDate()-1);
      if(prep.getFullYear()===ano && prep.getMonth()===mes) {
        const diaPrep = prep.getDate();
        if(!porDia[diaPrep]) porDia[diaPrep]=[];
        const jaTemPrep = porDia[diaPrep].some((x:any)=>x.isLimp&&(x.casa_nome||x.casa)===casa);
        if(!jaTemPrep) {
          porDia[diaPrep].push({
            id:`prep-${r.id}`, isCI:false, isCO:false, isLimp:true,
            cor:CASA_CAL_CORES["limpeza"],
            casa_nome:casa, guest:"Limpeza (prep.)",
            ci:prep.toISOString().split("T")[0], co:prep.toISOString().split("T")[0],
          });
        }
      }
    }
  });

  const selEvents = sel ? (porDia[sel]||[]) : [];

  return (
    <Card title="📅 Calendário de Reservas" accent={T.amber}
      action="+ Nova Reserva" onAction={()=>{
        // scroll to form nova reserva
        document.getElementById("nova-reserva-btn")?.click();
      }}>
      {/* Legenda por casa */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap" as any,marginBottom:12}}>
        {[
          {cor:CASA_CAL_CORES["Girassol"], l:"Girassol"},
          {cor:CASA_CAL_CORES["Avós"],     l:"Avós"},
          {cor:CASA_CAL_CORES["Catequese"],l:"Catequese"},
          {cor:CASA_CAL_CORES["limpeza"],  l:"Limpeza"},
        ].map(({cor,l})=>(
          <div key={l} style={{display:"flex",alignItems:"center",gap:5,padding:"2px 10px",
            borderRadius:6,background:`${cor}15`,border:`1px solid ${cor}40`}}>
            <div style={{width:7,height:7,borderRadius:"50%",background:cor}}/>
            <span style={{fontSize:11,color:cor,fontWeight:600}}>{l}</span>
          </div>
        ))}
      </div>

      {/* Navegação */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <button onClick={()=>{if(mes===0){setMes(11);setAno(ano-1);}else setMes(mes-1);setSel(null);}}
          style={{background:"none",border:`1px solid ${T.border}`,color:T.text,padding:"5px 14px",borderRadius:8,cursor:"pointer",fontSize:15}}>‹</button>
        <span style={{fontSize:13,fontWeight:700,color:T.text}}>{meses[mes]} {ano}</span>
        <button onClick={()=>{if(mes===11){setMes(0);setAno(ano+1);}else setMes(mes+1);setSel(null);}}
          style={{background:"none",border:`1px solid ${T.border}`,color:T.text,padding:"5px 14px",borderRadius:8,cursor:"pointer",fontSize:15}}>›</button>
      </div>

      {/* Cabeçalho dias */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
        {["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"].map(d=>(
          <div key={d} style={{fontSize:10,color:T.muted,textAlign:"center",fontWeight:700}}>{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
        {Array.from({length:primeiroDia}).map((_,i)=><div key={`e${i}`}/>)}
        {Array.from({length:diasNoMes}).map((_,i)=>{
          const dia=i+1;
          const ds=`${ano}-${String(mes+1).padStart(2,"0")}-${String(dia).padStart(2,"0")}`;
          const isHoje=ds===hojeStr;
          const evs=porDia[dia]||[];
          const isSel=sel===dia;
          // Cor do dia: se só limpeza=vermelho, se reserva=cor da casa, se misto=primeira reserva
          const reservaEvs = evs.filter((e:any)=>!e.isLimp);
          const limpEvs    = evs.filter((e:any)=>e.isLimp);
          const temReserva = reservaEvs.length>0;
          const temLimp    = limpEvs.length>0;
          const corFundo   = temReserva ? reservaEvs[0].cor : temLimp ? CASA_CAL_CORES["limpeza"] : null;

          return (
            <div key={dia} onClick={()=>evs.length>0&&setSel(isSel?null:dia)}
              style={{
                minHeight:36,padding:"2px 1px",borderRadius:5,
                cursor:evs.length>0?"pointer":"default",
                background:isSel?`${T.amber}25`:isHoje?`${T.blueL}10`:corFundo?`${corFundo}18`:"transparent",
                border:isSel?`1px solid ${T.amber}`:isHoje?`1px solid ${T.blueL}40`:corFundo?`1px solid ${corFundo}50`:`1px solid transparent`,
                position:"relative" as any,
              }}>
              <div style={{fontSize:10,textAlign:"center",fontWeight:isHoje?700:400,
                color:isHoje?T.blueL:corFundo||T.textSub}}>{dia}</div>
              {/* Mostrar até 2 eventos com nome da casa */}
              {evs.slice(0,2).map((e:any,ei:number)=>(
                <div key={ei} style={{
                  fontSize:8,padding:"1px 2px",borderRadius:3,marginBottom:1,
                  background:`${e.cor}30`,color:e.cor,
                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any,
                }}>
                  {e.isLimp?"🧹":e.isCI?"▶":e.isCO?"◀":"▪"} {e.isLimp?casaNomeCurto(e.casa_nome):casaNomeCurto(e.casa_nome||e.casa)}
                </div>
              ))}
              {evs.length>2&&<div style={{fontSize:8,color:T.muted,textAlign:"center"}}>+{evs.length-2}</div>}
            </div>
          );
        })}
      </div>

      {/* Detalhe do dia seleccionado */}
      {sel&&selEvents.length>0&&(
        <div style={{marginTop:12,padding:12,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`}}>
          <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:8,textTransform:"uppercase" as any}}>
            {sel} de {meses[mes]}
          </div>
          {selEvents.map((r:any,i:number)=>(
            <div key={i} style={{padding:"8px 0",borderBottom:i<selEvents.length-1?`1px solid ${T.border}`:"none",
              display:"flex",alignItems:"flex-start",gap:8}}>
              <div style={{width:8,height:8,borderRadius:"50%",flexShrink:0,marginTop:3,background:r.cor}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>
                  {r.isLimp?"🧹 Limpeza":r.isCI?"▶ Check-in":r.isCO?"◀ Check-out":"● Ocupado"}
                  {!r.isLimp&&<span style={{color:T.muted,fontWeight:400}}> — {r.guest||"Hóspede"}</span>}
                </div>
                <div style={{fontSize:11,color:T.muted,marginTop:2,display:"flex",gap:8,flexWrap:"wrap" as any}}>
                  <span style={{color:r.cor,fontWeight:600}}>🏠 {casaNomeCurto(r.casa_nome||r.casa)}</span>
                  {!r.isLimp&&<span>{r.ci} → {r.co}</span>}
                  {!r.isLimp&&r.isCI&&<span>🕐 CI: {r.hora_ci||r.ciHora||"16:00"}</span>}
                  {!r.isLimp&&r.isCO&&<span>🕐 CO: {r.hora_co||r.coHora||"10:00"}</span>}
                  {!r.isLimp&&r.valor>0&&<span style={{color:T.amber,fontWeight:700}}>{fmt(r.valor)}</span>}
                  {!r.isLimp&&<span>{r.plataforma||r.plat}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Editar Reserva — painel inline completo ──────────────────────────
function EditarReserva({ r, casas, onSave, onClose }: any) {
  const [form, setForm] = useState({
    guest:      r.guest      || "",
    valor:      r.valor      || r.valor_total || 0,
    notas:      r.notas      || "",
    ciHora:     r.ciHora     || r.hora_ci     || "16:00",
    coHora:     r.coHora     || r.hora_co     || "10:00",
    ci:         r.ci         || r.check_in    || "",
    co:         r.co         || r.check_out   || "",
    casa_nome:  r.casa_nome  || r.casa        || "Girassol",
    plataforma: r.plataforma || r.plat        || "airbnb",
    noites:     r.noites     || 1,
    num_pessoas:  r.num_pessoas  || 0,
    num_criancas: r.num_criancas || 0,
    tem_animal:   r.tem_animal    || false,
  });
  const [saving, setSaving] = useState(false);
  const inp = {width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13};
  const lbl = {fontSize:11,color:T.muted,marginBottom:4,fontWeight:700 as any,textTransform:"uppercase" as any};

  const save = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div style={{padding:"14px",borderRadius:10,background:T.panel2,
      border:`1px solid ${T.amber}30`,marginBottom:8}}>
      <div style={{fontSize:12,fontWeight:700,color:T.amber,marginBottom:12}}>✏️ Editar Reserva</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
        <div style={{gridColumn:"1/-1"}}>
          <div style={lbl}>Nome do Hóspede</div>
          <input value={form.guest} onChange={e=>setForm(f=>({...f,guest:e.target.value}))}
            placeholder="Nome completo" style={inp}/>
        </div>
        <div>
          <div style={lbl}>Check-in</div>
          <input type="date" value={form.ci} onChange={e=>setForm(f=>({...f,ci:e.target.value}))} style={inp}/>
        </div>
        <div>
          <div style={lbl}>Check-out</div>
          <input type="date" value={form.co} onChange={e=>setForm(f=>({...f,co:e.target.value}))} style={inp}/>
        </div>
        <div>
          <div style={lbl}>Hora Check-in</div>
          <input type="time" value={form.ciHora} onChange={e=>setForm(f=>({...f,ciHora:e.target.value}))} style={inp}/>
        </div>
        <div>
          <div style={lbl}>Hora Check-out</div>
          <input type="time" value={form.coHora} onChange={e=>setForm(f=>({...f,coHora:e.target.value}))} style={inp}/>
        </div>
        <div>
          <div style={lbl}>Casa</div>
          <select value={form.casa_nome} onChange={e=>setForm(f=>({...f,casa_nome:e.target.value}))} style={inp}>
            <option value="Girassol">🌻 Girassol</option>
            <option value="Avós">🏡 Avós</option>
            <option value="Catequese">⛪ Catequese</option>
          </select>
        </div>
        <div>
          <div style={lbl}>Plataforma</div>
          <select value={form.plataforma} onChange={e=>setForm(f=>({...f,plataforma:e.target.value}))} style={inp}>
            <option value="airbnb">Airbnb</option>
            <option value="booking">Booking</option>
            <option value="direto">Direto</option>
          </select>
        </div>
        <div>
          <div style={lbl}>Valor Total (€)</div>
          <input type="number" value={form.valor} onChange={e=>setForm(f=>({...f,valor:+e.target.value}))} style={inp}/>
        </div>
        <div>
          <div style={lbl}>Noites</div>
          <input type="number" value={form.noites} onChange={e=>setForm(f=>({...f,noites:+e.target.value}))} style={inp}/>
        </div>
        <div>
          <div style={lbl}>👥 Nº Pessoas</div>
          <input type="number" min="0" value={form.num_pessoas} onChange={e=>setForm(f=>({...f,num_pessoas:+e.target.value}))} style={inp}/>
        </div>
        <div>
          <div style={lbl}>👶 Crianças</div>
          <input type="number" min="0" value={form.num_criancas} onChange={e=>setForm(f=>({...f,num_criancas:+e.target.value}))} style={inp}/>
        </div>
        <div style={{display:"flex",alignItems:"flex-end"}}>
          <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13,color:T.text,paddingBottom:8}}>
            <input type="checkbox" checked={form.tem_animal} onChange={e=>setForm(f=>({...f,tem_animal:e.target.checked}))}/>🐾 Animal de estimação
          </label>
        </div>
        <div style={{gridColumn:"1/-1"}}>
          <div style={lbl}>Notas</div>
          <input value={form.notas} onChange={e=>setForm(f=>({...f,notas:e.target.value}))}
            placeholder="Observações" style={inp}/>
        </div>
      </div>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button onClick={onClose}
          style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${T.border}`,
            background:"transparent",color:T.muted,cursor:"pointer",fontSize:12}}>Cancelar</button>
        <button onClick={save} disabled={saving}
          style={{padding:"7px 16px",borderRadius:8,border:"none",
            background:T.amber,color:"white",cursor:"pointer",fontSize:12,fontWeight:700,
            opacity:saving?0.7:1}}>
          {saving?"A guardar...":"💾 Guardar"}
        </button>
      </div>
    </div>
  );
}

// ── QUINTA — GESTÃO FINANCEIRA ──
function QuintaFin({ addLog }: any) {
  const { data: resDB } = useALReservas();
  const [resLocal] = useState(isDemoAccount() ? DEMO_AL_RES : []);
  const res: any[] = normResArr(isDemoAccount() ? resLocal : (resDB || []));
  const { data: despesasDB, insert: insertDespesa } = useTable('al_financas', { orderBy:'data', ascending:false });
  const [despesasLocal] = useState(AL_DESPESAS_INIT);
  const despesas = isDemoAccount() ? despesasLocal : (despesasDB || []);
  const setDespesas = (_:any) => {};
  const [limpezas, setLimpezas] = useState<any[]>([
    {id:1,casa:"girassol",data:"2026-03-07",horas:2,feita:true},
    {id:2,casa:"avos",data:"2026-03-12",horas:3,feita:true},
  ]);
  const [addingD, setAddingD] = useState(false);
  const [newD, setNewD] = useState({descricao:"",valor:0,data:"",label:"contas",casa:"todas"});

  const conf = res.filter(r=>r.status==="confirmada"&&r.valor>0);
  const receitaTotal = conf.reduce((s:number,r:any)=>s+r.valor,0);
  const despTotal = despesas.reduce((s:number,d:any)=>s+d.valor,0);
  const custoLimpeza = limpezas.reduce((s:number,l:any)=>s+(l.horas||0)*8,0);
  const totalComissoes = conf.reduce((s:number,r:any)=>s+r.valor*(COMISSAO_PLAT[r.plat]||0),0);
  const totalIVA = conf.reduce((s:number,r:any)=>s+r.valor*IVA_TAXA,0);
  const netLiquido = receitaTotal - despTotal - custoLimpeza - totalComissoes - totalIVA;

  const labels = ["contas","limpeza","manutenção","outro"];
  const labelColor:any = {contas:T.blueL,limpeza:T.teal,"manutenção":T.amber,outro:T.muted};

  const addDespesa = () => {
    setDespesas([...despesas,{...newD,id:despesas.length+1,valor:+newD.valor}]);
    addLog?.("quinta","despesa",`Nova despesa: ${newD.descricao} · ${fmt(+newD.valor)}`,"💸",+newD.valor);
    setAddingD(false);
    setNewD({descricao:"",valor:0,data:"",label:"contas",casa:"todas"});
  };
  const updateLimpHoras = (id:number,h:number) =>
    setLimpezas(ls=>ls.map(l=>l.id===id?{...l,horas:h}:l));

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Receita Bruta" v={fmt(receitaTotal)} c="todas as reservas" ac={T.amber}/>
        <KPI l="Comissões + IVA" v={fmt(Math.round(totalComissoes+totalIVA))} c="plataformas + estado" ac={T.red}/>
        <KPI l="Despesas + Limpeza" v={fmt(despTotal+custoLimpeza)} c={`€8/h · ${limpezas.reduce((s:number,l:any)=>s+(l.horas||0),0)}h total`} ac={T.red}/>
        <KPI l="Lucro Líquido" v={fmt(Math.round(netLiquido))} c="após tudo" ac={T.green}/>
      </div>

      {/* Reservas com detalhe financeiro */}
      <Card title="Reservas — Detalhe Financeiro" accent={T.amber}>
        <div style={{overflowX:"auto", WebkitOverflowScrolling:"touch" as any as any}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:600}}>
            <thead>
              <tr style={{borderBottom:`1px solid ${T.border}`}}>
                {["Hóspede","Casa","Plataforma","Bruto","Comissão","IVA 6%","Net"].map(h=>(
                  <th key={h} style={{padding:"8px 10px",textAlign:"left",color:T.muted,fontSize:11,fontWeight:700,textTransform:"uppercase"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {conf.map((r:any)=>{
                const casa=CASAS.find(c=>c.id===r.casa);
                const comissao=Math.round(r.valor*(COMISSAO_PLAT[r.plat]||0));
                const iva=Math.round(r.valor*IVA_TAXA);
                const net=r.valor-comissao-iva;
                return (
                  <tr key={r.id} style={{borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
                    <td style={{padding:"9px 10px",fontWeight:600,color:T.text}}>{r.guest}</td>
                    <td style={{padding:"9px 10px",color:T.muted}}>{casa?.icon} {casa?.label}</td>
                    <td style={{padding:"9px 10px"}}><Tag label={r.plat} color={r.plat==="Airbnb"?T.red:T.blueL}/></td>
                    <td style={{padding:"9px 10px",fontWeight:700,color:T.amber,fontFamily:"monospace"}}>{fmt(r.valor)}</td>
                    <td style={{padding:"9px 10px",color:T.red,fontFamily:"monospace"}}>-{fmt(comissao)} <span style={{fontSize:11,color:T.muted}}>({Math.round((COMISSAO_PLAT[r.plat]||0)*100)}%)</span></td>
                    <td style={{padding:"9px 10px",color:T.red,fontFamily:"monospace"}}>-{fmt(iva)}</td>
                    <td style={{padding:"9px 10px",fontWeight:700,color:T.green,fontFamily:"monospace"}}>{fmt(net)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Limpezas com horas */}
      <Card title="Limpezas — Custo (€8/hora)" accent={T.teal}>
        {limpezas.map(l=>{
          const casa=CASAS.find(c=>c.id===l.casa);
          return (
            <div key={l.id} style={{display:"flex",alignItems:"center",gap:12,
              padding:"10px 0",borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
              <div style={{flex:1}}>
                <div style={{fontSize:12,color:T.text,fontWeight:600}}>{casa?.icon} {casa?.label}</div>
                <div style={{fontSize:11,color:T.muted}}>{l.data}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:11,color:T.muted}}>Horas:</span>
                <input type="number" value={l.horas} min={0} max={12}
                  onChange={(e:any)=>updateLimpHoras(l.id,+e.target.value)}
                  style={{width:52,padding:"4px 8px",borderRadius:6,background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${T.border}`,color:T.text,fontSize:12,textAlign:"center"}}/>
              </div>
              <span style={{fontSize:13,fontWeight:700,color:T.teal,fontFamily:"monospace"}}>-{fmt(l.horas*8)}</span>
              <Tag label={l.feita?"Feita":"Pendente"} color={l.feita?T.green:T.amber}/>
            </div>
          );
        })}
        <div style={{display:"flex",justifyContent:"flex-end",paddingTop:10,borderTop:`1px solid ${T.border}`}}>
          <span style={{fontSize:13,fontWeight:700,color:T.teal,fontFamily:"monospace"}}>
            Total Limpeza: -{fmt(custoLimpeza)}
          </span>
        </div>
      </Card>

      {/* Despesas */}
      <Card title="Outras Despesas" accent={T.red} action="+ Despesa" onAction={()=>setAddingD(!addingD)}>
        {addingD && (
          <div style={{marginBottom:16,padding:14,borderRadius:10,background:"rgba(255,255,255,0.03)",
            border:`1px solid ${T.border}`,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
            {([["Descrição","text","descricao","Ex: Electricidade"],["Valor €","number","valor","0"],["Data","date","data",""]] as any).map(([l,t,k,ph]:any)=>(
              <div key={k}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>{l}</div>
                <input type={t} placeholder={ph} value={newD[k]} onChange={(e:any)=>setNewD({...newD,[k]:e.target.value})}
                  style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
              </div>
            ))}
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Categoria</div>
              <select value={newD.label} onChange={(e:any)=>setNewD({...newD,label:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"#0c1322",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12}}>
                {labels.map(l=><option key={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Casa</div>
              <select value={newD.casa} onChange={(e:any)=>setNewD({...newD,casa:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"#0c1322",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12}}>
                <option value="todas">Todas</option>
                {CASAS.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <button onClick={addDespesa} style={{gridColumn:"1/-1",padding:"9px",borderRadius:10,border:"none",
              background:T.red,color:"white",fontSize:13,fontWeight:700,cursor:"pointer"}}>
              Guardar
            </button>
          </div>
        )}
        {despesas.map((d:any)=>(
          <div key={d.id} style={{display:"flex",alignItems:"center",gap:12,
            padding:"10px 0",borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13,color:T.text,fontWeight:600}}>{d.descricao}</div>
              <div style={{fontSize:11,color:T.muted}}>{d.data} · {d.casa==="todas"?"Todas":CASAS.find(c=>c.id===d.casa)?.label}</div>
            </div>
            <Tag label={d.label} color={labelColor[d.label]||T.muted}/>
            <span style={{fontSize:13,fontWeight:700,color:T.red,fontFamily:"monospace"}}>-{fmt(d.valor)}</span>
          </div>
        ))}
        <div style={{display:"flex",justifyContent:"flex-end",paddingTop:10,borderTop:`1px solid ${T.border}`}}>
          <span style={{fontSize:13,fontWeight:700,color:T.red,fontFamily:"monospace"}}>Total: -{fmt(despTotal)}</span>
        </div>
      </Card>
    </div>
  );
}

// ── QUINTA — PREVISÕES ──
function QuintaPrevisoes() {
  const previsoes = [
    {m:"Jan",ocupacao:20,receita:1200,despesas:900,net:300},
    {m:"Fev",ocupacao:25,receita:1500,despesas:900,net:600},
    {m:"Mar",ocupacao:40,receita:2400,despesas:950,net:1450},
    {m:"Abr",ocupacao:55,receita:3300,despesas:950,net:2350},
    {m:"Mai",ocupacao:70,receita:4200,despesas:1000,net:3200},
    {m:"Jun",ocupacao:85,receita:5100,despesas:1000,net:4100},
    {m:"Jul",ocupacao:95,receita:5700,despesas:1100,net:4600},
    {m:"Ago",ocupacao:95,receita:5700,despesas:1100,net:4600},
    {m:"Set",ocupacao:75,receita:4500,despesas:1000,net:3500},
    {m:"Out",ocupacao:45,receita:2700,despesas:950,net:1750},
    {m:"Nov",ocupacao:25,receita:1500,despesas:900,net:600},
    {m:"Dez",ocupacao:30,receita:1800,despesas:950,net:850},
  ];
  // Comparação ano anterior (estimativa)
  const prev2025 = previsoes.map(m=>({...m,receita:Math.round(m.receita*0.75),net:Math.round(m.net*0.7)}));

  const totalAnual = previsoes.reduce((s,m)=>s+m.net,0);
  const totalReceita = previsoes.reduce((s,m)=>s+m.receita,0);
  const mediaOcup = Math.round(previsoes.reduce((s,m)=>s+m.ocupacao,0)/12);
  const totalAnual2025 = prev2025.reduce((s,m)=>s+m.net,0);
  const crescimento = Math.round((totalAnual-totalAnual2025)/totalAnual2025*100);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Net Anual 2026" v={fmt(totalAnual)} c={`+${crescimento}% vs 2025`} up={true} ac={T.amber}/>
        <KPI l="Receita Bruta 2026" v={fmt(totalReceita)} c="projeção anual" ac={T.amber}/>
        <KPI l="Ocupação Média" v={`${mediaOcup}%`} c="anual" ac={T.amber}/>
        <KPI l="Net Anual 2025 (est.)" v={fmt(totalAnual2025)} c="ano anterior" ac={T.muted}/>
      </div>

      {/* Gráfico projeção */}
      <Card title="Projeção Mensal 2026 vs 2025" accent={T.amber}>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={previsoes.map((m,i)=>({...m,net2025:prev2025[i].net}))}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
            <XAxis dataKey="m" tick={{fill:T.muted,fontSize:11}} axisLine={false} tickLine={false}/>
            <YAxis tick={{fill:T.muted,fontSize:11}} axisLine={false} tickLine={false} tickFormatter={fmtK}/>
            <Tooltip contentStyle={TTS} formatter={(v:any)=>fmt(v)}/>
            <Bar dataKey="receita" fill={T.amber} radius={[3,3,0,0]} opacity={0.3} name="Receita Bruta 2026"/>
            <Bar dataKey="net" fill={T.green} radius={[3,3,0,0]} name="Net 2026"/>
            <Bar dataKey="net2025" fill={T.muted} radius={[3,3,0,0]} opacity={0.5} name="Net 2025 (est.)"/>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Taxa de ocupação mensal com detalhes */}
      <Card title="Taxa de Ocupação Mensal — 2026" accent={T.amber}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(80px,1fr))",gap:8}}>
          {previsoes.map(m=>(
            <div key={m.m} style={{textAlign:"center",padding:"8px 4px",borderRadius:8,
              background:"rgba(255,255,255,0.02)",border:`1px solid rgba(255,255,255,0.04)`}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:6,fontWeight:600}}>{m.m}</div>
              <div style={{height:50,background:"rgba(255,255,255,0.04)",borderRadius:6,position:"relative",overflow:"hidden",marginBottom:6}}>
                <div style={{position:"absolute",bottom:0,left:0,right:0,
                  height:`${m.ocupacao}%`,borderRadius:"4px 4px 0 0",
                  background:m.ocupacao>=75?T.green:m.ocupacao>=50?T.amber:T.red,opacity:0.7}}/>
              </div>
              <div style={{fontSize:12,fontWeight:700,color:m.ocupacao>=75?T.green:m.ocupacao>=50?T.amber:T.red}}>{m.ocupacao}%</div>
              <div style={{fontSize:11,color:T.muted,marginTop:2}}>{fmt(m.receita)}</div>
              <div style={{fontSize:11,color:T.green}}>{fmt(m.net)} net</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Tabela detalhada */}
      <Card title="Detalhe Mensal Comparativo" accent={T.amber}>
        <div style={{overflowX:"auto", WebkitOverflowScrolling:"touch" as any as any}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:500}}>
            <thead>
              <tr style={{borderBottom:`1px solid ${T.border}`}}>
                {["Mês","Ocupação","Receita 2026","Net 2026","Net 2025","Crescimento"].map(h=>(
                  <th key={h} style={{padding:"7px 10px",textAlign:"left",color:T.muted,fontSize:11,fontWeight:700,textTransform:"uppercase"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previsoes.map((m,i)=>{
                const cr=prev2025[i].net>0?Math.round((m.net-prev2025[i].net)/prev2025[i].net*100):0;
                return (
                  <tr key={m.m} style={{borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
                    <td style={{padding:"8px 10px",fontWeight:600,color:T.text}}>{m.m}</td>
                    <td style={{padding:"8px 10px"}}>
                      <span style={{color:m.ocupacao>=75?T.green:m.ocupacao>=50?T.amber:T.red,fontWeight:700}}>{m.ocupacao}%</span>
                    </td>
                    <td style={{padding:"8px 10px",color:T.amber,fontFamily:"monospace"}}>{fmt(m.receita)}</td>
                    <td style={{padding:"8px 10px",fontWeight:700,color:T.green,fontFamily:"monospace"}}>{fmt(m.net)}</td>
                    <td style={{padding:"8px 10px",color:T.muted,fontFamily:"monospace"}}>{fmt(prev2025[i].net)}</td>
                    <td style={{padding:"8px 10px"}}>
                      <Tag label={`+${cr}%`} color={cr>0?T.green:T.red}/>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── QUINTA — LIMPEZAS (Mission Control) ──
// ─── Calendário de Limpezas ──────────────────────────────────
function LimpezasCalendario({ allRes: allResRaw, limpezas, alCfg }: any) {
  const allRes = normResArr(allResRaw || []);
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth());
  const [ano, setAno] = useState(hoje.getFullYear());
  const [selDia, setSelDia] = useState<number|null>(null);

  const meses = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const diasNoMes  = new Date(ano,mes+1,0).getDate();
  const primeiroDia = new Date(ano,mes,1).getDay(); // 0=Dom ... 6=Sáb
  const hojeStr    = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,"0")}-${String(hoje.getDate()).padStart(2,"0")}`;
  // Parse "YYYY-MM-DD" como data LOCAL (evita deslize de fuso do new Date(string) que é UTC)
  const parseLocal = (s:string) => { const [y,m,d]=(s||"").split("T")[0].split("-").map(Number); return {y,m:m-1,d}; };

  const eventosPorDia: Record<number,Array<{tipo:string,label:string,casa:string,hora:string,cor:string}>> = {};
  const addEv = (data:string,tipo:string,label:string,casa:string,hora:string,cor:string) => {
    if(!data) return;
    const {y,m,d} = parseLocal(data);
    if(y===ano && m===mes) {
      if(!eventosPorDia[d]) eventosPorDia[d]=[];
      eventosPorDia[d].push({tipo,label,casa,hora,cor});
    }
  };

  // Reservas confirmadas — só casa, sem nomes de hóspedes (check-in e check-out)
  const reservasConf = (allRes||[]).filter((r:any)=>r.status==="confirmada"||r.status==="concluida");
  reservasConf.forEach((r:any)=>{
    const casaNome = r.casa_nome||r.casa||"";
    const curto = casaNome.toLowerCase().includes("cateques")?"Catequese":casaNome.toLowerCase().includes("avós")||casaNome.toLowerCase().includes("avos")?"Avós":casaNome.toLowerCase().includes("girassol")?"Girassol":casaNome;
    const ci = r.ci||r.check_in||"";
    const co = r.co||r.check_out||"";
    addEv(ci,"checkin",`▶ CI ${curto}`,casaNome,r.ciHora||r.hora_ci||alCfg?.horaCI||"16:00",T.green);
    addEv(co,"checkout",`◀ CO ${curto}`,casaNome,r.coHora||r.hora_co||alCfg?.horaCO||"10:00",T.amber);
  });

  // Limpezas — vêm da tabela al_limpezas (fonte de verdade, já com datas/agrupamentos corretos)
  (limpezas||[]).forEach((l:any)=>{
    const casaNome = l.casaNome || l.casa_nome || l.casa || "";
    const curto = casaNome.toLowerCase().includes("cateques")?"Catequese":casaNome.toLowerCase().includes("avós")||casaNome.toLowerCase().includes("avos")?"Avós":casaNome.toLowerCase().includes("girassol")?"Girassol":casaNome;
    const nota = l.notas || l.tipo || "";
    const isPrep = (nota+"").includes("prep");
    const label = isPrep ? `🧹 ${curto} (prep.)` : `🧹 ${curto}`;
    addEv(l.data, "limpeza", label, casaNome, l.hora||"10:00", T.red);
  });

  return (
    <Card title="📅 Calendário de Reservas e Limpezas" accent={T.red}>
      {/* Legenda */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap" as any,marginBottom:12}}>
        {[{cor:T.green,l:"Check-in"},{cor:T.amber,l:"Check-out"},{cor:T.red,l:"🧹 Limpeza"}].map(({cor,l})=>(
          <div key={l} style={{display:"flex",alignItems:"center",gap:5,padding:"2px 10px",
            borderRadius:6,background:`${cor}15`,border:`1px solid ${cor}40`}}>
            <div style={{width:7,height:7,borderRadius:"50%",background:cor}}/>
            <span style={{fontSize:11,color:cor,fontWeight:600}}>{l}</span>
          </div>
        ))}
      </div>
      {/* Navegação */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <button onClick={()=>{if(mes===0){setMes(11);setAno(ano-1);}else setMes(mes-1);setSelDia(null);}}
          style={{background:"none",border:`1px solid ${T.border}`,color:T.text,padding:"5px 14px",borderRadius:8,cursor:"pointer",fontSize:15}}>‹</button>
        <span style={{fontSize:13,fontWeight:700,color:T.text}}>{meses[mes]} {ano}</span>
        <button onClick={()=>{if(mes===11){setMes(0);setAno(ano+1);}else setMes(mes+1);setSelDia(null);}}
          style={{background:"none",border:`1px solid ${T.border}`,color:T.text,padding:"5px 14px",borderRadius:8,cursor:"pointer",fontSize:15}}>›</button>
      </div>
      {/* Cabeçalho */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
        {["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"].map(d=>(
          <div key={d} style={{fontSize:10,color:T.muted,textAlign:"center",padding:"3px 0",fontWeight:700}}>{d}</div>
        ))}
      </div>
      {/* Grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,width:"100%",overflow:"hidden"}}>
        {Array.from({length:primeiroDia}).map((_,i)=><div key={`e${i}`} style={{minWidth:0}}/>)}
        {Array.from({length:diasNoMes}).map((_,i)=>{
          const dia = i+1;
          const ds  = `${ano}-${String(mes+1).padStart(2,"0")}-${String(dia).padStart(2,"0")}`;
          const isHoje = ds===hojeStr;
          const evs = eventosPorDia[dia]||[];
          const temLimpeza = evs.some(e=>e.tipo==="limpeza");
          const isSel = selDia===dia;
          return (
            <div key={dia} onClick={()=>setSelDia(isSel?null:dia)}
              style={{minHeight:36,minWidth:0,padding:"2px 1px",borderRadius:5,cursor:evs.length>0?"pointer":"default",overflow:"hidden",
                background:isSel?`${T.red}18`:isHoje?`${T.blueL}10`:temLimpeza?`${T.red}06`:"transparent",
                border:isSel?`1px solid ${T.red}`:isHoje?`1px solid ${T.blueL}40`:temLimpeza?`1px solid ${T.red}25`:`1px solid transparent`}}>
              <div style={{fontSize:10,textAlign:"center",marginBottom:1,fontWeight:isHoje?700:400,color:isHoje?T.blueL:T.textSub}}>{dia}</div>
              {evs.slice(0,2).map((ev,ei)=>(
                <div key={ei} style={{fontSize:8,borderRadius:3,padding:"1px 2px",marginBottom:1,
                  background:`${ev.cor}28`,color:ev.cor,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>
                  {ev.label}
                </div>
              ))}
              {evs.length>2&&<div style={{fontSize:8,color:T.muted,textAlign:"center"}}>+{evs.length-2}</div>}
            </div>
          );
        })}
      </div>
      {/* Detalhe dia */}
      {selDia&&eventosPorDia[selDia]&&(
        <div style={{marginTop:12,padding:12,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`}}>
          <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:8,textTransform:"uppercase"}}>{selDia} de {meses[mes]}</div>
          {eventosPorDia[selDia].map((ev,i)=>(
            <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"8px 0",
              borderBottom:i<eventosPorDia[selDia].length-1?`1px solid ${T.border}`:"none"}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:ev.cor,marginTop:4,flexShrink:0}}/>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:ev.tipo==="limpeza"?T.red:T.text}}>{ev.label}</div>
                {ev.casa&&<div style={{fontSize:11,color:T.muted}}>🏠 {ev.casa}</div>}
                <div style={{fontSize:11,color:T.muted}}>🕐 {ev.hora}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Painel Financeiro de Limpezas ───────────────────────────
function LimpezasFinanceiro({ allRes, alCfg }: any) {
  const { data: limpezasDB, update: updateLimpeza } = useALLimpezas();
  const [limpezasLocal, setLimpezasLocal] = useState<any[]>([]);
  const limpezas = isDemoAccount() ? limpezasLocal : (limpezasDB || []);

  // Lê SÓ da tabela al_limpezas (fonte de verdade, com custos reais 33/38/43€ + deslocação)
  const hoje = new Date().toISOString().split("T")[0];
  const parseNota = (l:any) => {
    const n = (l.notas||"");
    const tipo = n.includes("preparacao") ? "prep" : "checkout";
    const berco = n.includes("BERCO");
    const animal = n.includes("ANIMAL");
    return { tipo, berco, animal };
  };
  const valorDe = (l:any) => Number(l.custo || l.valor_pago || 0);

  const feitas   = limpezas.filter((l:any) => l.data && l.data <= hoje);
  const futuras  = limpezas.filter((l:any) => l.data && l.data > hoje);
  const pagas    = limpezas.filter((l:any) => l.pago);
  const porPagar = limpezas.filter((l:any) => !l.pago && l.data && l.data <= hoje);

  const valorFeito  = feitas.reduce((s:number,l:any) => s + valorDe(l), 0);
  const valorPago   = pagas.reduce((s:number,l:any) => s + valorDe(l), 0);
  const valorFalta  = porPagar.reduce((s:number,l:any) => s + valorDe(l), 0);
  const valorFuturo = futuras.reduce((s:number,l:any) => s + valorDe(l), 0);

  const ordenar = (arr:any[]) => [...arr].sort((a:any,b:any)=>(a.data||"").localeCompare(b.data||""));

  return (
    <Card title="💰 Financeiro de Limpezas" accent={T.teal}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:14}}>
        <KPI l="Total Realizado" v={fmt(valorFeito)} c={`${feitas.length} limpezas`} ac={T.teal}/>
        <KPI l="Já Recebido" v={fmt(valorPago)} c={`${pagas.length} pagas`} ac={T.green}/>
        <KPI l="Por Receber" v={fmt(valorFalta)} c={`${porPagar.length} em falta`} ac={T.amber}/>
        <KPI l="Previsão Futura" v={fmt(valorFuturo)} c={`${futuras.length} agendadas`} ac={T.blueL}/>
      </div>

      {/* Lista de limpezas por pagar */}
      {porPagar.length > 0 && (
        <div>
          <div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase",marginBottom:8}}>Por receber</div>
          {ordenar(porPagar).map((l:any, i:number) => {
            const info = parseNota(l);
            return (
            <div key={l.id||i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",
              borderBottom:`1px solid ${T.border}`}}>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:600,color:T.text}}>🧹 {l.casa_nome||l.casa} · {l.data}</div>
                <div style={{fontSize:11,color:T.muted}}>
                  {info.tipo==="prep"?"Preparação":"Saída"}
                  {info.berco?" · 👶 berço":""}{info.animal?" · 🐾 animal":""}
                </div>
              </div>
              <span style={{fontSize:13,fontWeight:700,color:T.amber,fontFamily:"monospace"}}>{fmt(valorDe(l))}</span>
              <button onClick={async()=>{
                if(!isDemoAccount()) await updateLimpeza(l.id,{pago:true});
                else setLimpezasLocal((arr:any)=>arr.map((x:any)=>x.id===l.id?{...x,pago:true}:x));
              }} style={{padding:"4px 10px",borderRadius:6,border:"none",
                background:T.green,color:"white",cursor:"pointer",fontSize:11,fontWeight:700}}>
                ✓ Recebido
              </button>
            </div>
          );})}
        </div>
      )}
    </Card>
  );
}

// ─── Resumo em texto do Calendário Holdings ───────────────────
function ResumoCalendarioHoldings({ vjEvents, alRes, hw3dProjects, kaiEvents, pbEvents }: any) {
  const hoje = new Date();
  const hojeStr = hoje.toISOString().split("T")[0];
  const [mostrarTodos, setMostrarTodos] = useState(false);

  const eventos: Array<{data:string,label:string,cor:string,empresa:string,detalhe?:string,valor?:number}> = [];

  // VJ Casamentos
  (vjEvents||[]).forEach((e:any) => {
    if(!e.data) return;
    eventos.push({
      data: e.data,
      label: `🎤 VJ Casamentos`,
      cor: T.purple,
      empresa: "VJ",
      detalhe: `${e.local||""} · ${e.horario||""} · ${e.status||""}`,
      valor: parseFloat(e.valor||0),
    });
  });

  // AL — Reservas (check-in e check-out)
  normResArr(alRes||[]).filter((r:any)=>r.status==="confirmada").forEach((r:any) => {
    const ci = r.ci||r.check_in||"";
    const co = r.co||r.check_out||"";
    const casa = r.casa||r.casa_nome||"";
    const guest = r.guest||"Hóspede";
    if(ci) eventos.push({
      data: ci, label: `🟢 Check-in Quinta`, cor: T.green, empresa: "AL",
      detalhe: `${casa} · ${guest} · ${r.ciHora||"16:00"}`, valor: r.valor,
    });
    if(co) eventos.push({
      data: co, label: `🔴 Check-out Quinta`, cor: T.red, empresa: "AL",
      detalhe: `${casa} · ${guest} · ${r.coHora||"10:00"}`,
    });
    // Limpeza automática no dia do checkout
    if(co) eventos.push({
      data: co, label: `🧹 Limpeza`, cor: T.teal, empresa: "AL",
      detalhe: `${casa} · após checkout`,
    });
  });

  // HW3D — Deadlines
  (hw3dProjects||[]).filter((p:any)=>p.dataFim&&p.etapa!=="Concluído").forEach((p:any) => {
    eventos.push({
      data: p.dataFim, label: `🧊 Deadline HW3D`, cor: T.teal, empresa: "HW3D",
      detalhe: p.titulo||p.nome||"Projecto",
      valor: parseFloat(p.valor||p.orcamento||0),
    });
  });

  // Kairos AI
  (kaiEvents||[]).forEach((e:any) => {
    if(!e.data) return;
    eventos.push({
      data: e.data, label: `🤖 Kairos AI`, cor:"#a855f7", empresa: "KAI",
      detalhe: e.titulo||e.descricao||"",
    });
  });

  // PhotoBooth
  (pbEvents||[]).forEach((e:any) => {
    if(!e.data) return;
    eventos.push({
      data: e.data, label: `📸 PhotoBooth`, cor: T.pink, empresa: "PB",
      detalhe: `${e.local||""} · ${e.horario||""}`,
      valor: parseFloat(e.valor||400),
    });
  });

  // Ordenar por data
  eventos.sort((a,b) => a.data.localeCompare(b.data));

  // Separar passados e futuros
  const futuros  = eventos.filter(e => e.data >= hojeStr);
  const passados = eventos.filter(e => e.data < hojeStr).reverse(); // mais recentes primeiro
  const visiveis = mostrarTodos ? [...futuros, ...passados] : futuros.slice(0, 20);

  if(eventos.length === 0) return (
    <Card title="📋 Resumo de Marcações" accent={T.blueL}>
      <Empty icon="📅" msg="Sem marcações" sub="Os eventos das tuas empresas aparecem aqui"/>
    </Card>
  );

  return (
    <Card title={`📋 Todas as Marcações (${futuros.length} futuras · ${passados.length} passadas)`} accent={T.blueL}>
      {visiveis.map((e,i) => {
        const d = new Date(e.data);
        const isHoje = e.data === hojeStr;
        const isPast = e.data < hojeStr;
        const labelData = d.toLocaleDateString("pt-PT",{weekday:"short",day:"numeric",month:"short",year:"numeric"});
        return (
          <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 0",
            borderBottom:i<visiveis.length-1?`1px solid ${T.border}`:"none",
            opacity:isPast?0.6:1}}>
            {/* Cor da empresa */}
            <div style={{width:3,minHeight:36,borderRadius:2,background:e.cor,flexShrink:0,marginTop:2}}/>
            {/* Data */}
            <div style={{minWidth:110,flexShrink:0}}>
              <div style={{fontSize:12,fontWeight:isHoje?800:600,
                color:isHoje?T.amber:isPast?T.muted:T.text}}>
                {isHoje?"📍 Hoje":labelData}
              </div>
              <div style={{fontSize:10,color:T.muted,marginTop:1}}>{e.empresa}</div>
            </div>
            {/* Evento */}
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,color:isPast?T.muted:T.text,
                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {e.label}
              </div>
              {e.detalhe&&<div style={{fontSize:11,color:T.muted,marginTop:2,
                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.detalhe}</div>}
            </div>
            {/* Valor */}
            {e.valor&&e.valor>0&&(
              <div style={{fontSize:13,fontWeight:700,color:isPast?T.muted:T.green,
                fontFamily:"monospace",flexShrink:0}}>
                {fmt(e.valor)}
              </div>
            )}
          </div>
        );
      })}
      {/* Ver mais / Ver menos */}
      <button onClick={()=>setMostrarTodos(v=>!v)}
        style={{marginTop:10,padding:"7px 14px",borderRadius:8,border:`1px solid ${T.border}`,
          background:"transparent",color:T.muted,cursor:"pointer",fontSize:12,width:"100%"}}>
        {mostrarTodos?`▲ Mostrar só futuros`:`▼ Ver todos (${passados.length} passados)`}
      </button>
    </Card>
  );
}

// ─── Limpezas — função principal ─────────────────────────────
function QuintaLimpezas({ addLog }: any) {
  // Dados para o calendário
  const { data: resDB } = useALReservas();
  const { data: limpezasDB, insert: insertLimpeza, update: updateLimpeza } = useALLimpezas();
  const [resLocal] = useState(isDemoAccount() ? DEMO_AL_RES : []);
  const allRes: any[] = normResArr(isDemoAccount() ? resLocal : (resDB || []));
  const alCfg = getALConfig();

  const checklistBase = [
    "Mudar roupa de cama","Limpar casa de banho","Aspirar e varrer","Lavar loiça/louça",
    "Repor amenities","Verificar electrodomésticos","Limpar janelas","Repor papel higiénico",
    "Verificar toalhas","Limpar cozinha","Verificar inventário","Tirar lixo",
  ];
  const [checks, setChecks] = useState<Record<string,Record<string,boolean>>>(
    Object.fromEntries(CASAS.map(c=>[c.id,Object.fromEntries(checklistBase.map(t=>[t,false]))]))
  );
  const [extraChecklist, setExtraChecklist] = useState<Record<string,string[]>>(
    Object.fromEntries(CASAS.map(c=>[c.id,[]]))
  );
  const [notas, setNotas] = useState<Record<string,string>>(Object.fromEntries(CASAS.map(c=>[c.id,""])));
  const [notasCheck, setNotasCheck] = useState<Record<string,Record<string,boolean>>>(
    Object.fromEntries(CASAS.map(c=>[c.id,{}]))
  );
  const [casaAtiva, setCasaAtiva] = useState("girassol");
  const [newItem, setNewItem] = useState("");
  const [tab, setTab] = useState<"casas"|"tarefas">("casas");

  // Estado das limpezas vem da tabela al_limpezas (persistente)
  // Mapa: chave determinística → registo guardado
  const limpezaGravada = (reservaId:string, tipo:string) => {
    return (limpezasDB||[]).find((l:any)=>
      l.reserva_id===reservaId && (l.notas||"").includes(tipo)
    );
  };
  const horasPorCasa = (casaNome:string):number => {
    const n = (casaNome||"").toLowerCase();
    if(n.includes("cateques")) return 3;
    if(n.includes("girassol")) return 4;
    if(n.includes("avós")||n.includes("avos")) return 5;
    return 3;
  };
  // Preço real da limpeza por casa (tabela Raquel Maravalhas):
  // Catequese T0 = 33€ · Girassol T2 = 38€ · Avós T3 = 43€ (lavandaria incluída). Deslocação 10€/ida.
  const TAXA_DESLOCACAO = 10;
  const precoLimpezaCasa = (casaNome:string):number => {
    const n = (casaNome||"").toLowerCase();
    if(n.includes("cateques")) return 33;
    if(n.includes("girassol")) return 38;
    if(n.includes("avós")||n.includes("avos")) return 75;
    return 33;
  };
  const reservasParaLimpeza = (allRes||[]).filter((r:any)=>r.status==="confirmada"||r.status==="concluida");
  const casaIdOf = (casaNome:string) => casaNome.toLowerCase().includes("cateques")?"catequese":(casaNome.toLowerCase().includes("avós")||casaNome.toLowerCase().includes("avos"))?"avos":"girassol";
  const diasEntre = (a:string,b:string) => Math.round((new Date(a).getTime()-new Date(b).getTime())/(1000*60*60*24));
  const hojeLimp = new Date().toISOString().split("T")[0];

  // Agrupar reservas por casa e ordenar por check-in
  const porCasa: Record<string, any[]> = {};
  reservasParaLimpeza.forEach((r:any)=>{
    const casaNome = r.casa_nome||r.casa||"";
    (porCasa[casaNome] = porCasa[casaNome]||[]).push({
      ...r, _ci: r.ci||r.check_in||"", _co: r.co||r.check_out||"", _casaNome: casaNome,
    });
  });
  Object.keys(porCasa).forEach(c=>porCasa[c].sort((a:any,b:any)=>(a._ci||"").localeCompare(b._ci||"")));

  // Gerar limpezas (regras acordadas):
  // - PREPARAÇÃO só na PRIMEIRA reserva de cada casa (antes dela ninguém limpou)
  // - CHECK-OUT: uma limpeza por saída; a casa fica pronta até ao próximo hóspede
  // - Cada limpeza tem uma JANELA [janIni, janFim] em que pode ser feita:
  //     janIni = dia do check-out (o mais cedo possível)
  //     janFim = véspera do próximo check-in da mesma casa (ou o próprio dia se transição mesmo-dia)
  // - AGRUPAR casas diferentes cujas janelas se cruzam, para poupar deslocação (opção B)
  // - Transição mesmo-dia (sai e entra na mesma casa no mesmo dia): limpeza obrigatória
  //     nesse dia, sozinha, sem agrupar
  const diaAntes = (s:string)=>{ const dt=new Date(s+"T12:00:00"); dt.setDate(dt.getDate()-1); return dt.toISOString().split("T")[0]; };
  const limpezasRaw: any[] = [];
  Object.values(porCasa).forEach((lst:any[])=>{
    lst.forEach((r:any, i:number)=>{
      const casaNome = r._casaNome;
      const ci = r._ci, co = r._co;
      if(i===0 && ci && ci>=hojeLimp) {
        limpezasRaw.push({casa:casaIdOf(casaNome), casaNome, reservaId:r.id, tipo:"preparacao",
          janIni:ci, janFim:ci, mesmo:false, nc:r.num_criancas||0, an:r.tem_animal||false});
      }
      if(co && co>=hojeLimp) {
        const prox = lst[i+1] || null;
        const mesmo = prox ? prox._ci===co : false;
        const janFim = prox ? (mesmo ? co : diaAntes(prox._ci)) : co;
        limpezasRaw.push({casa:casaIdOf(casaNome), casaNome, reservaId:r.id, tipo:"checkout",
          janIni:co, janFim, mesmo,
          nc:prox?(prox.num_criancas||0):0, an:prox?(prox.tem_animal||false):false});
      }
    });
  });

  // Atribuir datas com AGRUPAMENTO (maximiza casas por ida, respeita janelas)
  limpezasRaw.forEach((l:any)=>{ l.data = null; });
  // 1) Transições mesmo-dia: data fixa, sozinhas
  limpezasRaw.forEach((l:any)=>{ if(l.mesmo) l.data = l.janIni; });
  // 2) Resto: agrupar casas diferentes cujas janelas cruzam; data = mais cedo do overlap
  const livres = limpezasRaw.filter((l:any)=>!l.mesmo && !l.data)
    .sort((a:any,b:any)=> (a.janIni||"").localeCompare(b.janIni||"") || a.casaNome.localeCompare(b.casaNome));
  livres.forEach((l:any)=>{
    if(l.data) return;
    const grupo=[l];
    livres.forEach((l2:any)=>{
      if(l2===l || l2.data) return;
      if(grupo.some((g:any)=>g.casa===l2.casa)) return;
      const ini=grupo.concat([l2]).reduce((m:string,g:any)=> g.janIni>m?g.janIni:m, l2.janIni);
      const fim=grupo.concat([l2]).reduce((m:string,g:any)=> g.janFim<m?g.janFim:m, l2.janFim);
      if(ini<=fim) grupo.push(l2);
    });
    const ini=grupo.reduce((m:string,g:any)=> g.janIni>m?g.janIni:m, grupo[0].janIni);
    const fim=grupo.reduce((m:string,g:any)=> g.janFim<m?g.janFim:m, grupo[0].janFim);
    // Preferir dia útil (seg-sex) dentro da janela comum; só usar sáb/dom se for a única opção
    const ehFDS = (s:string) => { const wd=new Date(s+"T12:00:00").getDay(); return wd===0||wd===6; };
    let data = ini;
    const candidatos:string[] = [];
    { let dt=new Date(ini+"T12:00:00"); const fimDt=new Date(fim+"T12:00:00");
      while(dt<=fimDt){ candidatos.push(dt.toISOString().split("T")[0]); dt.setDate(dt.getDate()+1); } }
    const uteis = candidatos.filter(c=>!ehFDS(c));
    const sabados = candidatos.filter(c=>{ const wd=new Date(c+"T12:00:00").getDay(); return wd===6; });
    if(uteis.length) data = uteis[0];
    else if(sabados.length) data = sabados[0];
    else if(candidatos.length) data = candidatos[0];
    grupo.forEach((g:any)=>{ g.data = data; });
  });

  // Deslocação 10€ uma vez por dia; cruzar com registos gravados
  limpezasRaw.sort((a,b)=> (a.data||"").localeCompare(b.data||"") || a.casaNome.localeCompare(b.casaNome));
  const diasComDeslocacao = new Set<string>();
  const limpezas = limpezasRaw.filter((l:any)=>l.data).map((l:any)=>{
    const grav = (limpezasDB||[]).find((db:any)=> db.reserva_id===l.reservaId && (db.notas||"").includes(l.tipo));
    let desloc = 0;
    if(!diasComDeslocacao.has(l.data)) { diasComDeslocacao.add(l.data); desloc = TAXA_DESLOCACAO; }
    const precoBase = precoLimpezaCasa(l.casaNome);
    const custoTotal = precoBase + desloc;
    return {
      id: `${l.reservaId}_${l.tipo==="preparacao"?"prep":"co"}`,
      reservaId: l.reservaId, casa: l.casa, casaNome: l.casaNome, data: l.data,
      tipo: l.tipo, hora: "10:00", horas: horasPorCasa(l.casaNome),
      nc: l.nc, an: l.an, mesmo: l.mesmo,
      feita: grav?.status==="feita"||false, dbId: grav?.id||null,
      precoBase, deslocacao: desloc, custoTotal,
      valorReceber: grav?.status==="feita" ? (grav?.valor_pago||custoTotal) : custoTotal,
    };
  });

  // Sincronizar limpezas calculadas → Supabase (inserir as que ainda não existem)
  const [syncFeito, setSyncFeito] = useState(false);
  useEffect(() => {
    if (isDemoAccount()) return;
    if (!limpezasDB) return; // esperar que a tabela carregue
    if (syncFeito) return;   // correr apenas uma vez por montagem
    // Notas tal como ficam gravadas, para comparar com a BD
    const notaDe = (l:any) => l.tipo + (l.nc?" | BERCO":"") + (l.an?" | ANIMAL":"");
    const existeNaBD = (l:any) => (limpezasDB||[]).some((db:any) =>
      db.data===l.data && (db.casa_nome||"")===l.casaNome && (db.notas||"")===notaDe(l)
    );
    const paraInserir = limpezas.filter((l:any) => !existeNaBD(l));
    setSyncFeito(true);
    if (paraInserir.length === 0) return;
    (async () => {
      const jaTentados = new Set<string>();
      for (const l of paraInserir) {
        const chave = `${l.data}_${l.casaNome}_${l.tipo}`;
        if (jaTentados.has(chave)) continue;
        jaTentados.add(chave);
        try {
          // O constraint UNIQUE (user_id, reserva_id, data, notas) impede duplicados na BD
          await insertLimpeza({
            casa_nome: l.casaNome,
            data: l.data,
            hora: l.hora,
            tipo: "casa",
            duracao_min: Math.round(l.horas*60),
            custo: l.custoTotal,
            valor_pago: 0,
            status: "pendente",
            pago: false,
            reserva_id: l.reservaId,
            notas: notaDe(l),
          });
        } catch(e){ /* duplicado rejeitado pela BD — ignorar */ }
      }
    })();
  }, [limpezasDB, syncFeito]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tarefas independentes — lavandaria e exterior (não ligadas às casas)
  const [tarefasIndep, setTarefasIndep] = useState<any[]>([]);
  const [addingTarefa, setAddingTarefa] = useState(false);
  const [newTarefa, setNewTarefa] = useState({tipo:"lavandaria",descricao:"",data:""});

  const toggleCheck = (casa:string,item:string) =>
    setChecks(c=>({...c,[casa]:{...c[casa],[item]:!c[casa][item]}}));
  const toggleNotaCheck = (casa:string,item:string) =>
    setNotasCheck(c=>({...c,[casa]:{...c[casa],[item]:!c[casa]?.[item]}}));
  const addExtraItem = (casa:string) => {
    if(!newItem.trim()) return;
    setExtraChecklist(ec=>({...ec,[casa]:[...(ec[casa]||[]),newItem.trim()]}));
    setNewItem("");
  };
  const addNotasChecklist = (casa:string) => {
    if(!notas[casa]?.trim()) return;
    const items = notas[casa].split(/[\n.]+/).map((s:string)=>s.trim()).filter((s:string)=>s.length>3);
    setExtraChecklist(ec=>({...ec,[casa]:[...(ec[casa]||[]),...items]}));
  };

  // Timer functions for casa limpezas
  const [timers, setTimers] = useState<Record<string,string>>({});
  const iniciarLimpeza = (id:any) => {
    setTimers(s=>({...s,[id]:new Date().toISOString()}));
  };
  // Gravar limpeza como feita na tabela al_limpezas
  const gravarLimpezaFeita = async (l:any, horas:number) => {
    if(isDemoAccount()) return;
    const custo = l.custoTotal || (l.precoBase||precoLimpezaCasa(l.casaNome));
    const registo:any = {
      casa_nome: l.casaNome,
      data: l.data,
      hora: l.hora,
      tipo: "casa",
      duracao_min: Math.round(horas*60),
      custo: custo,
      valor_pago: custo,
      status: "feita",
      pago: false,
      reserva_id: l.reservaId,
      notas: l.tipo + (l.nc?" | BERCO":"") + (l.an?" | ANIMAL":""),
    };
    try {
      if(l.dbId) {
        await updateLimpeza(l.dbId, {status:"feita", duracao_min:Math.round(horas*60), valor_pago:custo, custo});
      } else {
        await insertLimpeza(registo);
      }
    } catch(e){ console.error("Erro ao gravar limpeza:", e); }
  };
  const terminarLimpeza = (id:any) => {
    const now = new Date().toISOString();
    const l = limpezas.find((x:any)=>x.id===id);
    if(!l) return;
    const inicio = timers[id];
    const durMin = inicio ? Math.round((new Date(now).getTime()-new Date(inicio).getTime())/60000) : 0;
    const horas = Math.max(0.5, Math.round(durMin/60*10)/10);
    const casaLabel = l.casaNome||l.casa;
    const custo = l.custoTotal || precoLimpezaCasa(l.casaNome);
    addLog?.("quinta","limpeza",`Limpeza concluída: ${casaLabel} · ${fmt(custo)}`,"🧹",custo);
    gravarLimpezaFeita(l, horas);
    setTimers(s=>{ const n={...s}; delete n[id]; return n; });
  };
  const marcarFeita = (id:any, horas:number) => {
    const l = limpezas.find((x:any)=>x.id===id);
    if(!l) return;
    const casaLabel = l.casaNome||l.casa;
    const custo = l.custoTotal || precoLimpezaCasa(l.casaNome);
    addLog?.("quinta","limpeza",`Limpeza concluída: ${casaLabel} · ${fmt(custo)}`,"🧹",custo);
    gravarLimpezaFeita(l, horas);
  };

  // Timer functions for tarefas independentes
  const iniciarTarefa = (id:number) => {
    setTarefasIndep(ts=>ts.map(t=>t.id===id&&!t.inicio?{...t,inicio:new Date().toISOString()}:t));
  };
  const terminarTarefa = (id:number) => {
    const now = new Date().toISOString();
    setTarefasIndep(ts=>ts.map(t=>{
      if(t.id!==id||t.feita) return t;
      const durMin = t.inicio ? Math.round((new Date(now).getTime()-new Date(t.inicio).getTime())/60000) : 0;
      const horas = Math.round(durMin/60*10)/10;
      addLog?.("quinta","limpeza",`${t.tipo==="lavandaria"?"Lavandaria":"Exterior"}: ${t.descricao} · ${horas}h`,"🧺",horas*8);
      return {...t,fim:now,feita:true,durMin};
    }));
  };
  const addTarefaIndep = () => {
    if(!newTarefa.descricao) return;
    setTarefasIndep(ts=>[...ts,{...newTarefa,id:ts.length+1,inicio:null,fim:null,durMin:0,feita:false}]);
    setAddingTarefa(false);
    setNewTarefa({tipo:"lavandaria",descricao:"",data:""});
  };

  const fmtDur = (min:number) => min<60?`${min}min`:`${Math.floor(min/60)}h${min%60>0?` ${min%60}min`:""}`;
  const fmtElapsed = (inicio:string|null) => {
    if(!inicio) return "";
    return fmtDur(Math.round((Date.now()-new Date(inicio).getTime())/60000));
  };

  const concluidas = Object.values(checks[casaAtiva]||{}).filter(Boolean).length;
  const total = checklistBase.length + (extraChecklist[casaAtiva]||[]).length;
  const [custoHora, setCustoHora] = useState<number>(()=>+(localStorage.getItem("kairos_al_custo_hora")||8));
  const saveCustoHora = (v:number) => { setCustoHora(v); localStorage.setItem("kairos_al_custo_hora",String(v)); };
  const totalRecebido = limpezas.filter(l=>l.feita).reduce((s:number,l:any)=>s+(l.horas||0)*custoHora,0);
  const totalAReceber = limpezas.filter(l=>!l.feita).reduce((s:number,l:any)=>s+(l.horas||0)*custoHora,0);
  const totalHorasTarefas = tarefasIndep.filter(t=>t.feita).reduce((s:number,t:any)=>s+(t.durMin||0)/60,0);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>

      {/* ── CALENDÁRIO (sempre visível) ── */}
      <LimpezasCalendario allRes={allRes} limpezas={limpezas} alCfg={alCfg}/>

      {/* ── PAINEL FINANCEIRO ── */}
      <LimpezasFinanceiro allRes={allRes} alCfg={alCfg}/>

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <KPI l="Limpezas Feitas" v={limpezas.filter(l=>l.feita).length} c={`de ${limpezas.length} total`} ac={T.green}/>
        <KPI l="Valor Recebido" v={fmt(totalRecebido)} c="€8/hora" ac={T.green}/>
        <KPI l="A Receber" v={fmt(totalAReceber)} c="pendentes" ac={T.amber}/>
        <KPI l="Tarefas Extras" v={`${Math.round(totalHorasTarefas*10)/10}h`} c="lavandaria+exterior" ac={T.teal}/>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:6}}>
        {[["casas","🏠 Limpezas por Casa"],["tarefas","🧺 Lavandaria & Exterior"]].map(([v,l])=>(
          <button key={v} onClick={()=>setTab(v as any)}
            style={{padding:"7px 16px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,
              background:tab===v?`${T.teal}25`:"rgba(255,255,255,0.04)",color:tab===v?T.teal:T.muted}}>
            {l}
          </button>
        ))}
      </div>

      {tab==="tarefas" && (
        <Card title="Lavandaria & Exterior (tarefas independentes)" accent={T.teal}
          action="+ Adicionar" onAction={()=>setAddingTarefa(true)}>
          {addingTarefa && (
            <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10}}>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Tipo</div>
                <select value={newTarefa.tipo} onChange={e=>setNewTarefa({...newTarefa,tipo:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                  <option value="lavandaria">🧺 Lavandaria</option>
                  <option value="exterior">🌿 Exterior</option>
                </select>
              </div>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Descrição</div>
                <input value={newTarefa.descricao} onChange={e=>setNewTarefa({...newTarefa,descricao:e.target.value})}
                  placeholder="Ex: Lavar roupa cama Girassol"
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
              </div>
              <div>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Data</div>
                <input type="date" value={newTarefa.data} onChange={e=>setNewTarefa({...newTarefa,data:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
              </div>
              <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
                <button onClick={addTarefaIndep} style={{padding:"8px 16px",borderRadius:8,border:"none",background:T.teal,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
                <button onClick={()=>setAddingTarefa(false)} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
              </div>
            </div>
          )}
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {tarefasIndep.length===0&&<Empty icon="🧺" msg="Sem tarefas" sub="Adiciona lavandaria ou tarefas de exterior"/>}
            {tarefasIndep.map(t=>(
              <div key={t.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:10,
                background:t.feita?"rgba(255,255,255,0.01)":T.panel,border:`1px solid ${t.feita?T.border:T.teal+"25"}`,
                opacity:t.feita?0.6:1}}>
                <div style={{fontSize:22}}>{t.tipo==="lavandaria"?"🧺":"🌿"}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:T.text}}>{t.descricao}</div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2}}>
                    {t.data&&`📅 ${new Date(t.data).toLocaleDateString("pt-PT")} · `}
                    {t.feita&&t.durMin>0?`⏱ ${fmtDur(t.durMin)}`:t.inicio?`▶ A decorrer: ${fmtElapsed(t.inicio)}`:""}
                  </div>
                </div>
                {t.feita?(
                  <Tag label={`✓ ${t.durMin>0?fmtDur(t.durMin):"Feito"}`} color={T.green}/>
                ):t.inicio?(
                  <button onClick={()=>terminarTarefa(t.id)}
                    style={{padding:"6px 14px",borderRadius:8,border:"none",background:T.red,color:"white",fontWeight:700,cursor:"pointer",fontSize:12}}>
                    ■ Terminar
                  </button>
                ):(
                  <button onClick={()=>iniciarTarefa(t.id)}
                    style={{padding:"6px 14px",borderRadius:8,border:"none",background:T.teal,color:"white",fontWeight:700,cursor:"pointer",fontSize:12}}>
                    ▶ Iniciar
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab==="casas" && (
      <>
      {/* Agenda de limpezas com timer */}
      <Card title="Agenda de Limpezas" accent={T.teal}>
        {limpezas.length===0&&<div style={{fontSize:12,color:T.muted,padding:"8px 0"}}>Sem limpezas agendadas — aparecem automaticamente com cada reserva</div>}
        {limpezas.sort((a:any,b:any)=>a.data.localeCompare(b.data)).map((l:any)=>{
          const casa=CASAS.find((c:any)=>c.id===l.casa);
          const ehPrep = l.tipo==="preparacao";
          return (
            <div key={l.id} style={{padding:"12px 0",borderBottom:`1px solid rgba(255,255,255,0.04)`,
              display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" as any}}>
              <div style={{flex:1,minWidth:140}}>
                <div style={{fontSize:13,color:T.text,fontWeight:600}}>
                  {casa?.icon} {casa?.label}
                  <span style={{fontSize:10,marginLeft:6,padding:"1px 6px",borderRadius:4,
                    background:ehPrep?`${T.blueL}20`:`${T.red}20`,color:ehPrep?T.blueL:T.red}}>
                    {ehPrep?"antes do check-in":"após check-out"}
                  </span>
                </div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>
                  📅 {new Date(l.data).toLocaleDateString("pt-PT",{weekday:"long",day:"numeric",month:"long"})}
                  {" · "} 🕐 {l.hora}
                  {" · "} 💶 {fmt(l.custoTotal)}{l.deslocacao>0?" (inc. desloc.)":" (mesma ida)"}
                  {timers[l.id]&&!l.feita&&<span style={{color:T.amber}}> · ▶ {fmtElapsed(timers[l.id])}</span>}
                </div>
              </div>
              {l.feita ? (
                <div style={{textAlign:"right" as any}}>
                  <Tag label="✓ Feita" color={T.green}/>
                  <div style={{fontSize:11,color:T.green,fontFamily:"monospace",marginTop:4}}>{fmt(l.valorReceber)}</div>
                </div>
              ) : timers[l.id] ? (
                <button onClick={()=>terminarLimpeza(l.id)}
                  style={{padding:"6px 14px",borderRadius:8,border:"none",background:T.red,color:"white",fontWeight:700,cursor:"pointer",fontSize:12}}>
                  ■ Terminar
                </button>
              ) : (
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <button onClick={()=>iniciarLimpeza(l.id)}
                    style={{padding:"6px 12px",borderRadius:8,border:"none",background:T.teal,color:"white",fontWeight:700,cursor:"pointer",fontSize:12}}>
                    ▶ Iniciar
                  </button>
                  <input type="number" placeholder="h" defaultValue={l.horas} min={0} max={12}
                    style={{width:52,padding:"4px 8px",borderRadius:6,background:"rgba(255,255,255,0.05)",
                      border:`1px solid ${T.border}`,color:T.text,fontSize:12,textAlign:"center" as any}}
                    id={`limp_${l.id}`}/>
                  <button onClick={()=>{
                    const el=document.getElementById(`limp_${l.id}`) as any;
                    marcarFeita(l.id,+(el?.value||l.horas));
                  }} style={{padding:"10px 14px",borderRadius:6,border:"none",
                    background:T.teal,color:"white",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                    Marcar Feita
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </Card>

      {/* Seleção de casa para checklist */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        {CASAS.map(c=>{
          const done=Object.values(checks[c.id]||{}).filter(Boolean).length+
            (extraChecklist[c.id]||[]).filter((_:any,i:number)=>notasCheck[c.id]?.[`extra_${i}`]).length;
          const tot=checklistBase.length+(extraChecklist[c.id]||[]).length;
          const isAtiva=casaAtiva===c.id;
          return (
            <div key={c.id} onClick={()=>setCasaAtiva(c.id)} style={{
              background:isAtiva?`rgba(45,212,191,0.1)`:T.panel,
              border:`1px solid ${isAtiva?T.teal:T.border}`,borderRadius:10,
              padding:"14px",cursor:"pointer",transition:"all 0.2s"}}>
              <div style={{fontSize:22,marginBottom:6}}>{c.icon}</div>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>{c.label}</div>
              <div style={{fontSize:11,color:T.muted,marginTop:2}}>{done}/{tot} itens</div>
              <div style={{marginTop:8,height:4,borderRadius:99,background:"rgba(255,255,255,0.06)"}}>
                <div style={{width:`${tot>0?Math.round(done/tot*100):0}%`,height:"100%",borderRadius:99,
                  background:done===tot?T.green:done>0?T.amber:T.muted}}/>
              </div>
            </div>
          );
        })}
      </div>

      {/* Checklist */}
      <Card title={`Checklist — ${CASAS.find((c:any)=>c.id===casaAtiva)?.label} (${concluidas}/${total})`} accent={T.teal}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,marginBottom:16}}>
          {checklistBase.map(item=>{
            const done=checks[casaAtiva]?.[item];
            return (
              <div key={item} onClick={()=>toggleCheck(casaAtiva,item)}
                style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",
                  borderRadius:6,cursor:"pointer",
                  background:done?"rgba(34,194,122,0.07)":"transparent",
                  border:`1px solid ${done?"rgba(34,194,122,0.2)":"rgba(255,255,255,0.04)"}`}}>
                <div style={{width:18,height:18,borderRadius:6,flexShrink:0,
                  border:`2px solid ${done?T.green:"rgba(255,255,255,0.2)"}`,
                  background:done?T.green:"transparent",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:11,color:"white"}}>{done?"✓":""}</div>
                <span style={{fontSize:12,color:done?T.muted:T.text,
                  textDecoration:done?"line-through":"none"}}>{item}</span>
              </div>
            );
          })}
          {(extraChecklist[casaAtiva]||[]).map((item:string,i:number)=>{
            const key=`extra_${i}`;
            const done=notasCheck[casaAtiva]?.[key];
            return (
              <div key={key} onClick={()=>toggleNotaCheck(casaAtiva,key)}
                style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",
                  borderRadius:6,cursor:"pointer",
                  background:done?"rgba(34,194,122,0.07)":"rgba(155,114,247,0.05)",
                  border:`1px solid ${done?"rgba(34,194,122,0.2)":"rgba(155,114,247,0.15)"}`}}>
                <div style={{width:18,height:18,borderRadius:6,flexShrink:0,
                  border:`2px solid ${done?T.green:T.purple}`,
                  background:done?T.green:"transparent",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:11,color:"white"}}>{done?"✓":""}</div>
                <span style={{fontSize:12,color:done?T.muted:T.text,
                  textDecoration:done?"line-through":"none"}}>{item}</span>
                <span style={{fontSize:11,color:T.purple,marginLeft:"auto"}}>extra</span>
              </div>
            );
          })}
        </div>

        {/* Adicionar item à checklist */}
        <div style={{display:"flex",gap:8,marginBottom:16}}>
          <input placeholder="Adicionar item à checklist..." value={newItem}
            onChange={(e:any)=>setNewItem(e.target.value)}
            onKeyDown={(e:any)=>e.key==="Enter"&&addExtraItem(casaAtiva)}
            style={{flex:1,padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
              border:`1px solid ${T.border}`,color:T.text,fontSize:12}}/>
          <button onClick={()=>addExtraItem(casaAtiva)}
            style={{padding:"8px 14px",borderRadius:8,border:"none",
              background:T.teal,color:"white",fontSize:12,fontWeight:700,cursor:"pointer"}}>+</button>
        </div>

        {/* Notas */}
        <div>
          <div style={{fontSize:11,color:T.muted,marginBottom:6,fontWeight:700,textTransform:"uppercase"}}>
            📝 Notas / Reporte (gera checklist automaticamente)
          </div>
          <textarea value={notas[casaAtiva]||""} onChange={(e:any)=>setNotas({...notas,[casaAtiva]:e.target.value})}
            placeholder="Ex: Torneira a pingar. Mancha no sofá. Falta produto de limpeza."
            style={{width:"100%",padding:"10px 12px",borderRadius:8,background:"rgba(255,255,255,0.05)",
              border:`1px solid ${T.border}`,color:T.text,fontSize:12,fontFamily:"inherit",
              minHeight:70,resize:"vertical" as any,boxSizing:"border-box" as any}}/>
          <button onClick={()=>addNotasChecklist(casaAtiva)}
            style={{marginTop:6,padding:"6px 14px",borderRadius:6,border:"none",
              background:T.purple,color:"white",fontSize:11,fontWeight:700,cursor:"pointer"}}>
            ➜ Criar Checklist das Notas
          </button>
        </div>
      </Card>

      </>
      )}
    </div>
  );
}

// ── QUINTA — TAREFAS ──
// ── QUINTA — TAREFAS ──
function QuintaTarefas({ addLog }: any) {
  const { data: tarefasDB, insert: insertTarefa, update: updateTarefa, remove: removeTarefa } = useALTarefas();
  const [tarefasLocal, setTarefasLocal] = useState<any[]>(AL_TAREFAS_INIT);
  const tarefas = isDemoAccount() ? tarefasLocal : (tarefasDB || []);
  const setTarefas = isDemoAccount() ? setTarefasLocal : (_:any) => {};
  const [addingT, setAddingT] = useState(false);
  const [newT, setNewT] = useState({ titulo:"", categoria:"geral", responsavel:"", dataInicio:"", dataFim:"", status:"por fazer" });

  const hoje = new Date().toISOString().split("T")[0];
  const addTarefa = () => {
    setTarefas([...tarefas,{...newT,id:tarefas.length+1}]);
    addLog?.("quinta","tarefa",`Nova tarefa: ${newT.titulo}`,"✅");
    setAddingT(false);
    setNewT({ titulo:"", categoria:"geral", responsavel:"", dataInicio:"", dataFim:"", status:"por fazer" });
  };
  const cycleStatus = (id:number) => setTarefas(ts=>ts.map(t=>t.id!==id?t:{...t,
    status:t.status==="por fazer"?"em progresso":t.status==="em progresso"?"concluída":"por fazer"}));

  const statusColor = { "por fazer":T.muted, "em progresso":T.amber, "concluída":T.green, "atrasada":T.red };
  const cats = ["geral","reservas","manutenção","limpeza","exterior","financeiro","outro"];
  const atrasadas = tarefas.filter(t=>t.dataFim && t.dataFim < hoje && t.status!=="concluída");

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12 }}>
        <KPI l="Por Fazer"    v={tarefas.filter(t=>t.status==="por fazer").length}    c="" ac={T.muted}/>
        <KPI l="Em Progresso" v={tarefas.filter(t=>t.status==="em progresso").length} c="" ac={T.amber}/>
        <KPI l="Concluídas"   v={tarefas.filter(t=>t.status==="concluída").length}    c="" ac={T.green}/>
        <KPI l="Atrasadas"    v={atrasadas.length} c="" ac={atrasadas.length>0?T.red:T.muted}/>
      </div>

      <Card title="Tarefas" accent={T.amber} action="+ Nova Tarefa" onAction={()=>setAddingT(!addingT)}>
        {addingT && (
          <div style={{ marginBottom:16, padding:16, borderRadius:10, background:"rgba(255,255,255,0.03)",
            border:`1px solid ${T.border}`, display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:10 }}>
            <div style={{ gridColumn:"1/-1" }}>
              <div style={{ fontSize:11, color:T.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Título</div>
              <input placeholder="O que fazer..." value={newT.titulo}
                onChange={(e:any)=>setNewT({...newT,titulo:e.target.value})}
                style={{ width:"100%", padding:"8px 11px", borderRadius:8, background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`, color:T.text, fontSize:12, boxSizing:"border-box" as any }}/>
            </div>
            {[["Responsável","text","responsavel","Nome"],
              ["Data Início","date","dataInicio",""],["Data Fim","date","dataFim",""]
            ].map(([l,type,key,ph]:any)=>(
              <div key={key}>
                <div style={{ fontSize:11, color:T.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>{l}</div>
                <input type={type} placeholder={ph} value={newT[key]}
                  onChange={(e:any)=>setNewT({...newT,[key]:e.target.value})}
                  style={{ width:"100%", padding:"8px 11px", borderRadius:8, background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${T.border}`, color:T.text, fontSize:12, boxSizing:"border-box" as any }}/>
              </div>
            ))}
            <div>
              <div style={{ fontSize:11, color:T.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Categoria</div>
              <select value={newT.categoria} onChange={(e:any)=>setNewT({...newT,categoria:e.target.value})}
                style={{ width:"100%", padding:"8px 11px", borderRadius:8, background:"#0c1322",
                  border:`1px solid ${T.border}`, color:T.text, fontSize:12 }}>
                {cats.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <button onClick={addTarefa} style={{ gridColumn:"1/-1", padding:"9px", borderRadius:10, border:"none",
              background:T.amber, color:"white", fontSize:13, fontWeight:700, cursor:"pointer" }}>
              Guardar Tarefa
            </button>
          </div>
        )}
        {tarefas.length===0 && <Empty icon="✅" msg="Sem tarefas" sub="Clica em + Nova Tarefa para começar"/>}
        {tarefas.map(t=>{
          const atrasada = t.dataFim && t.dataFim < hoje && t.status!=="concluída";
          const st = atrasada?"atrasada":t.status;
          return (
            <div key={t.id} style={{ display:"flex", alignItems:"center", gap:12,
              padding:"11px 0", borderBottom:`1px solid rgba(255,255,255,0.04)` }}>
              <div onClick={()=>cycleStatus(t.id)} style={{ width:20, height:20, borderRadius:6, flexShrink:0,
                border:`2px solid ${statusColor[st]||T.muted}`,
                background:t.status==="concluída"?T.green:"transparent",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:11, color:"white", cursor:"pointer" }}>
                {t.status==="concluída"?"✓":t.status==="em progresso"?"…":""}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, color:t.status==="concluída"?T.muted:T.text,
                  fontWeight:600, textDecoration:t.status==="concluída"?"line-through":"none" }}>{t.titulo}</div>
                <div style={{ fontSize:11, color:T.muted, marginTop:2, display:"flex", gap:8 }}>
                  {t.responsavel && <span>👤 {t.responsavel}</span>}
                  {t.dataFim && <span>📅 {t.dataFim}</span>}
                </div>
              </div>
              <Tag label={t.categoria} color={T.muted}/>
              <Tag label={st} color={statusColor[st]||T.muted}/>
              {atrasada && <Tag label="⚠️ Atrasada" color={T.red}/>}
            </div>
          );
        })}
      </Card>
    </div>
  );
}

// ── PHOTOBOOTH ──
const PB_EVENTS_INIT = [
  {id:1,data:"2026-05-17",local:"Quinta das Oliveiras, Porto",horario:"18:00",valor:400,status:"confirmado",noiteFora:false,checklist:{equip:false,transport:false,setup:false,sound:false}},
  {id:2,data:"2026-06-21",local:"Herdade da Serra, Sintra",horario:"17:00",valor:400,status:"pendente",noiteFora:true,checklist:{equip:false,transport:false,setup:false,sound:false}},
];
const PB_TAREFAS_INIT:any[]=[];
const PB_DESPESAS_INIT:any[]=[];

function PhotoboothView({ page }: any) {
  const [events,setEvents]=useState<any[]>([]);
  const [tarefas,setTarefas]=useState<any[]>([]);
  const [despesas,setDespesas]=useState<any[]>([]);
  const [active,setActive]=useState<any>(null);
  if(page==="tarefas") return <PBTarefas events={events} tarefas={tarefas} setTarefas={setTarefas}/>;
  if(page==="despesas") return <PBDespesas events={events} despesas={despesas} setDespesas={setDespesas}/>;
  return <PBOverview events={events} setEvents={setEvents} active={active} setActive={setActive}/>;
}

function PBOverview({events,setEvents,active,setActive}:any) {
  const [addingE,setAddingE]=useState(false);
  const [newE,setNewE]=useState({data:"",local:"",horario:"18:00",noiteFora:false});

  const toggle=(id:number,key:string)=>setEvents((es:any[])=>es.map(e=>
    e.id===id?{...e,checklist:{...e.checklist,[key]:!e.checklist[key]}}:e));
  const toggleField=(id:number,key:string)=>setEvents((es:any[])=>es.map(e=>
    e.id!==id?e:{...e,[key]:!e[key]}));


  const conf=events.filter((e:any)=>e.status==="confirmado");
  const concl=events.filter((e:any)=>e.status==="concluido");
  const totalBruto    = events.filter((e:any)=>e.status!=="pendente").reduce((s:number,e:any)=>s+parseFloat(e.valor||0),0);
  const epocaMeta=12;

  const getDiaSemana=(d:string)=>["Domingo","Segunda","Terça","Quarta","Quinta","Sexta","Sábado"][new Date(d).getDay()];

  const hoje=new Date();
  const [calMes,setCalMes]=useState(hoje.getMonth());
  const [calAno,setCalAno]=useState(hoje.getFullYear());
  const meses=["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const diasNoMes=new Date(calAno,calMes+1,0).getDate();
  const primeiroDia=new Date(calAno,calMes,1).getDay();
  const diasComEvento=new Set(events
    .filter((e:any)=>{const d=new Date(e.data);return d.getFullYear()===calAno&&d.getMonth()===calMes;})
    .map((e:any)=>new Date(e.data).getDate()));

  const addEvento = () => {
    setEvents([...events,{...newE,id:events.length+1,valor:400,status:"pendente",
      checklist:{equip:false,transport:false,setup:false,sound:false}}]);
    setAddingE(false);
    setNewE({data:"",local:"",horario:"18:00",noiteFora:false});
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <AlertPanelPB events={events}/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Eventos Confirmados" v={conf.length} c={`meta: ${epocaMeta}`} ac={T.pink}/>
        <KPI l="Receita Bruta" v={fmt(totalBruto)} c={`${conf.length+concl.length} eventos`} ac={T.pink}/>
        <KPI l="Por Evento" v="€400" c="valor base" ac={T.pink}/>
        <KPI l="Época" v="Abr–Out" c="7 meses" ac={T.pink}/>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <Card title={`${meses[calMes]} ${calAno}`} accent={T.pink}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <button onClick={()=>{if(calMes===0){setCalMes(11);setCalAno(calAno-1);}else setCalMes(calMes-1);}}
              style={{background:"none",border:`1px solid ${T.border}`,color:T.text,padding:"3px 8px",borderRadius:6,cursor:"pointer"}}>‹</button>
            <span style={{fontSize:12,fontWeight:700,color:T.text}}>{meses[calMes]} {calAno}</span>
            <button onClick={()=>{if(calMes===11){setCalMes(0);setCalAno(calAno+1);}else setCalMes(calMes+1);}}
              style={{background:"none",border:`1px solid ${T.border}`,color:T.text,padding:"3px 8px",borderRadius:6,cursor:"pointer"}}>›</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1,textAlign:"center"}}>
            {["D","S","T","Q","Q","S","S"].map((d,i)=>(
              <div key={i} style={{fontSize:11,color:T.muted,padding:"3px 0",fontWeight:700}}>{d}</div>
            ))}
            {Array.from({length:primeiroDia}).map((_,i)=><div key={`e${i}`}/>)}
            {Array.from({length:diasNoMes}).map((_,i)=>{
              const dia=i+1;
              const h=new Date();
              const isHoje=dia===h.getDate()&&calMes===h.getMonth()&&calAno===h.getFullYear();
              const tem=diasComEvento.has(dia);
              return (
                <div key={dia} style={{
                  padding:"4px 1px",borderRadius:6,fontSize:11,
                  background:isHoje?"rgba(244,114,182,0.15)":tem?"rgba(244,114,182,0.2)":"transparent",
                  color:isHoje?T.pink:tem?T.pink:T.muted,fontWeight:tem||isHoje?700:400,
                  border:isHoje?`1px solid ${T.pink}`:tem?`1px solid rgba(244,114,182,0.4)`:"1px solid transparent"
                }}>{dia}</div>
              );
            })}
          </div>
        </Card>

        <Card title="Eventos & Checklist" accent={T.pink} action="+ Novo Evento" onAction={()=>setAddingE(!addingE)}>

          {addingE&&(
            <div style={{marginBottom:14,padding:14,borderRadius:10,background:"rgba(244,114,182,0.05)",
              border:`1px solid rgba(244,114,182,0.2)`,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
              {([["Data","date","data",""],["Horário","time","horario","18:00"],["Local","text","local","Nome da Quinta, Cidade"]] as any).map(([l,t,k,ph]:any)=>(
                <div key={k} style={k==="local"?{gridColumn:"1/-1"}:{}}>
                  <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>{l}</div>
                  <input type={t} placeholder={ph} value={newE[k]}
                    onChange={(e:any)=>setNewE({...newE,[k]:e.target.value})}
                    style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                      border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
                </div>
              ))}
              <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,color:T.text}}>
                <input type="checkbox" checked={newE.noiteFora} onChange={(e:any)=>setNewE({...newE,noiteFora:e.target.checked})}/>
                🌙 Noite Fora
              </label>
              <button onClick={addEvento} style={{gridColumn:"1/-1",padding:"9px",borderRadius:10,border:"none",
                background:T.pink,color:"white",fontSize:13,fontWeight:700,cursor:"pointer"}}>Guardar</button>
            </div>
          )}
          {events.sort((a:any,b:any)=>new Date(a.data).getTime()-new Date(b.data).getTime()).map((ev:any)=>(
            <div key={ev.id}>
              <div onClick={()=>setActive(active===ev.id?null:ev.id)}
                style={{display:"flex",alignItems:"center",gap:10,padding:"12px 0",
                  borderBottom:`1px solid rgba(255,255,255,0.04)`,cursor:"pointer"}}>
                <div onClick={(e:any)=>{e.stopPropagation();setEvents((es:any[])=>es.map((ev2:any)=>ev2.id!==ev.id?ev2:{...ev2,status:ev2.status==="pendente"?"confirmado":ev2.status==="confirmado"?"concluido":"pendente"}));}}  
                  style={{width:10,height:10,borderRadius:"50%",flexShrink:0,cursor:"pointer",
                    background:ev.status==="confirmado"?T.amber:ev.status==="concluido"?T.green:T.muted}}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:T.text,display:"flex",gap:8,alignItems:"center"}}>
                    {new Date(ev.data).toLocaleDateString("pt-PT",{day:"numeric",month:"long"})}
                    <span style={{fontSize:11,color:T.muted}}>({getDiaSemana(ev.data)}) · {ev.horario}</span>
                    {ev.noiteFora&&<Tag label="🌙 Fora" color={T.blue}/>}
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2}}>{ev.local}</div>
                </div>
                <span style={{fontSize:14,fontWeight:700,color:T.pink,fontFamily:"monospace"}}>{fmt(ev.valor)}</span>
                <Tag label={ev.status} color={ev.status==="confirmado"?T.amber:ev.status==="concluido"?T.green:T.muted}/>
                {ev.status==="confirmado"&&(
                  <button onClick={(e:any)=>{e.stopPropagation();
                    setEvents((arr:any)=>arr.map((x:any)=>x.id===ev.id?{...x,status:"concluido"}:x));
                  }} style={{padding:"3px 8px",borderRadius:6,border:"none",background:T.green,
                    color:"white",cursor:"pointer",fontSize:10,fontWeight:700,flexShrink:0}}>
                    ✓ Concluir
                  </button>
                )}
                <span style={{fontSize:11,color:T.muted}}>{active===ev.id?"▲":"▼"}</span>
              </div>
              {active===ev.id&&(
                <div style={{padding:"12px 14px",marginBottom:8,borderRadius:10,
                  background:"rgba(244,114,182,0.05)",border:"1px solid rgba(244,114,182,0.2)"}}>
                  <div style={{fontSize:11,fontWeight:700,color:T.pink,marginBottom:10}}>Checklist Pré-Evento</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                    {([["equip","📸 Equipamento verificado"],["transport","🚗 Transporte confirmado"],
                      ["setup","⚙️ Setup testado"],["sound","🔊 Som verificado"]] as any).map(([k,l]:any)=>(
                      <div key={k} onClick={e=>{e.stopPropagation();toggle(ev.id,k);}}
                        style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",
                          borderRadius:6,cursor:"pointer",
                          background:ev.checklist[k]?"rgba(34,194,122,0.07)":"transparent",
                          border:`1px solid ${ev.checklist[k]?"rgba(34,194,122,0.2)":"rgba(255,255,255,0.04)"}`}}>
                        <div style={{width:16,height:16,borderRadius:6,flexShrink:0,
                          border:`2px solid ${ev.checklist[k]?T.green:"rgba(255,255,255,0.15)"}`,
                          background:ev.checklist[k]?T.green:"transparent",
                          display:"flex",alignItems:"center",justifyContent:"center",
                          fontSize:11,color:"white"}}>{ev.checklist[k]?"✓":""}</div>
                        <span style={{fontSize:11,color:ev.checklist[k]?T.muted:T.text,
                          textDecoration:ev.checklist[k]?"line-through":"none"}}>{l}</span>
                      </div>
                    ))}
                  </div>
                  <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,color:T.text,marginTop:12}}>
                    <input type="checkbox" checked={ev.noiteFora} onChange={()=>toggleField(ev.id,"noiteFora")}/>
                    🌙 Noite fora
                  </label>
                </div>
              )}
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

function PBTarefas({events,tarefas,setTarefas}:any) {
  const [addingT,setAddingT]=useState(false);
  const [newT,setNewT]=useState({titulo:"",eventoId:"",status:"por fazer"});
  const addTarefa=()=>{
    setTarefas([...tarefas,{...newT,id:tarefas.length+1}]);
    setAddingT(false);
    setNewT({titulo:"",eventoId:"",status:"por fazer"});
  };
  const cycleStatus=(id:number)=>setTarefas((ts:any[])=>ts.map(t=>t.id!==id?t:{...t,
    status:t.status==="por fazer"?"concluída":"por fazer"}));
  const statusColor:any={"por fazer":T.muted,"concluída":T.green};
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <KPI l="Por Fazer" v={tarefas.filter((t:any)=>t.status==="por fazer").length} c="" ac={T.muted}/>
        <KPI l="Concluídas" v={tarefas.filter((t:any)=>t.status==="concluída").length} c="" ac={T.green}/>
        <KPI l="Total" v={tarefas.length} c="" ac={T.pink}/>
      </div>
      <Card title="Tarefas" accent={T.pink} action="+ Nova Tarefa" onAction={()=>setAddingT(!addingT)}>
        {addingT&&(
          <div style={{marginBottom:14,padding:14,borderRadius:10,background:"rgba(244,114,182,0.05)",
            border:`1px solid rgba(244,114,182,0.2)`,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
            <div style={{gridColumn:"1/-1"}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Tarefa</div>
              <input placeholder="O que fazer..." value={newT.titulo}
                onChange={(e:any)=>setNewT({...newT,titulo:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Evento (opcional)</div>
              <select value={newT.eventoId} onChange={(e:any)=>setNewT({...newT,eventoId:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"#0c1322",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12}}>
                <option value="">— Geral —</option>
                {events.map((ev:any)=>(
                  <option key={ev.id} value={String(ev.id)}>
                    {new Date(ev.data).toLocaleDateString("pt-PT",{day:"numeric",month:"short"})} — {(ev.local||"Local por definir").split(",")[0]}
                  </option>
                ))}
              </select>
            </div>
            <button onClick={addTarefa} style={{gridColumn:"1/-1",padding:"9px",borderRadius:10,border:"none",
              background:T.pink,color:"white",fontSize:13,fontWeight:700,cursor:"pointer"}}>Guardar</button>
          </div>
        )}
        {tarefas.length===0&&<Empty icon="✅" msg="Sem tarefas" sub="Clica em + Nova Tarefa"/>}
        {tarefas.map((t:any)=>(
          <div key={t.id} onClick={()=>cycleStatus(t.id)}
            style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",
              borderBottom:`1px solid rgba(255,255,255,0.04)`,cursor:"pointer"}}>
            <div style={{width:18,height:18,borderRadius:6,flexShrink:0,
              border:`2px solid ${statusColor[t.status]||T.muted}`,
              background:t.status==="concluída"?T.green:"transparent",
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"white"}}>
              {t.status==="concluída"?"✓":""}
            </div>
            <span style={{flex:1,fontSize:13,color:t.status==="concluída"?T.muted:T.text,
              textDecoration:t.status==="concluída"?"line-through":"none"}}>{t.titulo}</span>
            <Tag label={t.status} color={statusColor[t.status]||T.muted}/>
          </div>
        ))}
      </Card>
    </div>
  );
}

function PBDespesas({events,despesas,setDespesas}:any) {
  const [addingD,setAddingD]=useState(false);
  const [newD,setNewD]=useState({descricao:"",valor:0,data:"",tipo:"outro",eventoId:"",reembolsavel:false});
  const addDespesa=()=>{
    setDespesas([...despesas,{...newD,id:despesas.length+1,valor:+newD.valor}]);
    setAddingD(false);
    setNewD({descricao:"",valor:0,data:"",tipo:"outro",eventoId:"",reembolsavel:false});
  };
  const totalGeral=despesas.filter((d:any)=>!d.reembolsavel).reduce((s:number,d:any)=>s+d.valor,0);
  const totalReemb=despesas.filter((d:any)=>d.reembolsavel).reduce((s:number,d:any)=>s+d.valor,0);
  const tipoColor:any={gasolina:T.amber,portagens:T.blue,alimentacao:T.green,hotel:T.purple,outro:T.muted};
  const tipos=["gasolina","portagens","alimentacao","hotel","material","outro"];
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <KPI l="Despesas Totais" v={fmt(totalGeral+totalReemb)} c="" ac={T.pink}/>
        <KPI l="Reembolsáveis" v={fmt(totalReemb)} c="pela empresa" ac={T.green}/>
        <KPI l="Próprias" v={fmt(totalGeral)} c="" ac={T.red}/>
      </div>
      <Card title="Despesas" accent={T.pink} action="+ Despesa" onAction={()=>setAddingD(!addingD)}>
        {addingD&&(
          <div style={{marginBottom:14,padding:14,borderRadius:10,background:"rgba(244,114,182,0.05)",
            border:`1px solid rgba(244,114,182,0.2)`,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
            <div style={{gridColumn:"1/-1"}}>
              <input placeholder="Descrição..." value={newD.descricao}
                onChange={(e:any)=>setNewD({...newD,descricao:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
            </div>
            {([["Valor €","number","valor"],["Data","date","data"]] as any).map(([l,t,k]:any)=>(
              <div key={k}>
                <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>{l}</div>
                <input type={t} value={newD[k]} onChange={(e:any)=>setNewD({...newD,[k]:e.target.value})}
                  style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${T.border}`,color:T.text,fontSize:12,boxSizing:"border-box" as any}}/>
              </div>
            ))}
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:600,textTransform:"uppercase"}}>Tipo</div>
              <select value={newD.tipo} onChange={(e:any)=>setNewD({...newD,tipo:e.target.value})}
                style={{width:"100%",padding:"8px 11px",borderRadius:8,background:"#0c1322",
                  border:`1px solid ${T.border}`,color:T.text,fontSize:12}}>
                {tipos.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,color:T.text}}>
              <input type="checkbox" checked={newD.reembolsavel}
                onChange={(e:any)=>setNewD({...newD,reembolsavel:e.target.checked})}/>
              💼 Reembolsável
            </label>
            <button onClick={addDespesa} style={{gridColumn:"1/-1",padding:"9px",borderRadius:10,border:"none",
              background:T.pink,color:"white",fontSize:13,fontWeight:700,cursor:"pointer"}}>Guardar</button>
          </div>
        )}
        {despesas.length===0&&<Empty icon="💸" msg="Sem despesas" sub="Clica em + Despesa"/>}
        {despesas.map((d:any)=>(
          <div key={d.id} style={{display:"flex",alignItems:"center",gap:12,
            padding:"9px 0",borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13,color:T.text,fontWeight:600}}>{d.descricao}</div>
              <div style={{fontSize:11,color:T.muted}}>{d.data}</div>
            </div>
            <Tag label={d.tipo} color={tipoColor[d.tipo]||T.muted}/>
            {d.reembolsavel&&<Tag label="Reembolsável" color={T.green}/>}
            <span style={{fontSize:13,fontWeight:700,
              color:d.reembolsavel?T.green:T.red,fontFamily:"monospace"}}>
              {d.reembolsavel?"":"-"}{fmt(d.valor)}
            </span>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── VJ CASAMENTOS ──
// ─── VJ Equipa ───────────────────────────────────────────────
function VJEquipa() {
  const { data: perfilDB, upsert: upsertPerfil } = useVJPerfil();
  const { data: equipaDB, insert: insertMembro,  update: updateMembro } = useVJEquipa();
  const { data: eventosDB } = useVJEventos();
  const { data: atribuicoesDB, insert: insertAtrib, update: updateAtrib } = useVJAtribuicoes();

  const perfil     = (perfilDB||[])[0] || {};
  const equipa     = equipaDB || [];
  const eventos    = eventosDB || [];
  const atribuicoes = atribuicoesDB || [];

  const [tab, setTab]   = useState<"perfil"|"equipa"|"atribuicoes">("perfil");
  const [editPerfil, setEditPerfil] = useState(false);
  const [perfilForm, setPerfilForm] = useState({ papel:"colaborador", nome:"", valor_base:0, iban:"", telefone:"" });
  const [addingMembro, setAddingMembro] = useState(false);
  const [novoMembro, setNovoMembro] = useState({ colaborador_nome:"", colaborador_email:"", valor_base:0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if(perfil.id) setPerfilForm({
      papel:      perfil.papel      || "colaborador",
      nome:       perfil.nome       || "",
      valor_base: perfil.valor_base || 0,
      iban:       perfil.iban       || "",
      telefone:   perfil.telefone   || "",
    });
  }, [perfil.id]);

  const savePerfil = async () => {
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if(user) await upsertPerfil({ ...perfilForm, user_id: user.id });
    setSaving(false);
    setEditPerfil(false);
  };

  const addMembro = async () => {
    if(!novoMembro.colaborador_nome) return;
    const { data: { user } } = await supabase.auth.getUser();
    if(!user) return;
    await insertMembro({ ...novoMembro, gestor_id: user.id, activo: true });
    setNovoMembro({ colaborador_nome:"", colaborador_email:"", valor_base:0 });
    setAddingMembro(false);
  };

  const inp = {width:"100%",padding:"9px 12px",borderRadius:10,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13};
  const lbl = {fontSize:11,color:T.muted,marginBottom:5,fontWeight:700 as any,textTransform:"uppercase" as any};

  // Estatísticas por colaborador
  const statsColaborador = equipa.map((m:any) => {
    const atribs = atribuicoes.filter((a:any) => a.colaborador_nome===m.colaborador_nome||a.colaborador_id===m.colaborador_id);
    const pagas  = atribs.filter((a:any) => a.pago);
    const emFalta= atribs.filter((a:any) => !a.pago);
    return {
      ...m,
      totalEventos:   atribs.length,
      totalRecebido:  pagas.reduce((s:number,a:any)=>s+parseFloat(a.comissao_gestor||0),0),
      totalEmFalta:   emFalta.reduce((s:number,a:any)=>s+parseFloat(a.comissao_gestor||0),0),
    };
  });

  // Eventos sem colaborador atribuído
  const eventosSemColaborador = eventos.filter((e:any) =>
    e.status==="confirmado" && !atribuicoes.find((a:any)=>a.evento_id===e.id)
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>

      {/* Aviso eventos sem colaborador */}
      {eventosSemColaborador.length > 0 && (
        <div style={{padding:"12px 16px",borderRadius:12,background:`${T.amber}15`,
          border:`1px solid ${T.amber}40`,display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:20}}>⚠️</span>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:T.amber}}>
              {eventosSemColaborador.length} evento(s) sem colaborador atribuído
            </div>
            <div style={{fontSize:11,color:T.muted}}>
              {eventosSemColaborador.map((e:any)=>e.local).join(", ")}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{display:"flex",gap:6}}>
        {([["perfil","👤 O meu Perfil"],["equipa","👥 Equipa"],["atribuicoes","📋 Atribuições"]] as const).map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t)}
            style={{padding:"8px 16px",borderRadius:10,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,
              background:tab===t?`${T.purple}25`:"transparent",color:tab===t?T.purple:T.muted}}>
            {l}
          </button>
        ))}
      </div>

      {/* ── PERFIL ── */}
      {tab==="perfil" && (
        <Card title="O meu Perfil VJ" accent={T.purple}
          action={editPerfil?"Cancelar":"✏️ Editar"} onAction={()=>setEditPerfil(v=>!v)}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div style={{gridColumn:"1/-1"}}>
              <div style={lbl}>Papel na Equipa</div>
              {editPerfil ? (
                <select value={perfilForm.papel} onChange={e=>setPerfilForm({...perfilForm,papel:e.target.value})} style={inp}>
                  <option value="colaborador">🎤 Colaborador — faço parte de uma equipa</option>
                  <option value="gestor">🎯 Gestor — giro a minha própria equipa</option>
                </select>
              ) : (
                <div style={{fontSize:14,fontWeight:700,color:T.purple,padding:"8px 0"}}>
                  {perfil.papel==="gestor"?"🎯 Gestor de Equipa":"🎤 Colaborador"}
                </div>
              )}
            </div>
            {[["nome","Nome","Nuno Soares"],["telefone","Telefone","+351 9XX XXX XXX"],["iban","IBAN","PT50 XXXX XXXX XXXX XXXX XXXX X"]].map(([k,label,ph])=>(
              <div key={k}>
                <div style={lbl}>{label}</div>
                {editPerfil ? (
                  <input value={(perfilForm as any)[k]} placeholder={ph}
                    onChange={e=>setPerfilForm({...perfilForm,[k]:e.target.value})} style={inp}/>
                ) : (
                  <div style={{fontSize:13,color:T.text,padding:"8px 0"}}>{(perfil as any)[k]||<span style={{color:T.muted}}>Não definido</span>}</div>
                )}
              </div>
            ))}
            <div>
              <div style={lbl}>Valor Base por Evento (€)</div>
              {editPerfil ? (
                <input type="number" value={perfilForm.valor_base}
                  onChange={e=>setPerfilForm({...perfilForm,valor_base:+e.target.value})} style={inp}/>
              ) : (
                <div style={{fontSize:14,fontWeight:700,color:T.green,fontFamily:"monospace",padding:"8px 0"}}>{fmt(perfil.valor_base||0)}</div>
              )}
            </div>
          </div>
          {editPerfil && (
            <button onClick={savePerfil} disabled={saving}
              style={{marginTop:14,padding:"10px 24px",borderRadius:10,border:"none",
                background:T.purple,color:"white",fontWeight:700,cursor:"pointer",fontSize:13,opacity:saving?0.7:1}}>
              {saving?"A guardar...":"💾 Guardar Perfil"}
            </button>
          )}
          {perfil.papel==="colaborador" && (
            <div style={{marginTop:14,padding:"10px 14px",borderRadius:10,
              background:`${T.blueL}10`,border:`1px solid ${T.blueL}30`,fontSize:12,color:T.muted}}>
              💡 Se o teu gestor criar conta no Kairos OS, podes ser adicionado como colaborador da equipa dele. As tuas despesas e lucro individual permanecem privados — o gestor só vê o valor bruto e as comissões.
            </div>
          )}
        </Card>
      )}

      {/* ── EQUIPA ── */}
      {tab==="equipa" && (
        <Card title="Equipa VJ" accent={T.purple}
          action="+ Colaborador" onAction={()=>setAddingMembro(true)}>
          {addingMembro && (
            <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,
              display:"grid",gridTemplateColumns:"1fr 1fr 120px auto",gap:10,alignItems:"flex-end"}}>
              <div><div style={lbl}>Nome</div><input value={novoMembro.colaborador_nome} onChange={e=>setNovoMembro({...novoMembro,colaborador_nome:e.target.value})} placeholder="Nome do colaborador" style={inp}/></div>
              <div><div style={lbl}>Email</div><input value={novoMembro.colaborador_email} onChange={e=>setNovoMembro({...novoMembro,colaborador_email:e.target.value})} placeholder="email@exemplo.pt" style={inp}/></div>
              <div><div style={lbl}>Valor Base €</div><input type="number" value={novoMembro.valor_base} onChange={e=>setNovoMembro({...novoMembro,valor_base:+e.target.value})} style={inp}/></div>
              <div style={{display:"flex",gap:6}}>
                <button onClick={addMembro} style={{padding:"9px 14px",borderRadius:8,border:"none",background:T.purple,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
                <button onClick={()=>setAddingMembro(false)} style={{padding:"9px 10px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer"}}>✕</button>
              </div>
            </div>
          )}
          {equipa.length===0 && <Empty icon="👥" msg="Sem colaboradores" sub="Adiciona membros da tua equipa VJ"/>}
          {statsColaborador.map((m:any,i:number) => (
            <div key={i} style={{padding:"12px 0",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:40,height:40,borderRadius:"50%",background:`${T.purple}20`,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🎤</div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text}}>{m.colaborador_nome}</div>
                <div style={{fontSize:11,color:T.muted}}>{m.colaborador_email||"Sem email"} · {m.totalEventos} evento(s)</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:12,color:T.green,fontWeight:700}}>{fmt(m.totalRecebido)} recebido</div>
                {m.totalEmFalta>0&&<div style={{fontSize:11,color:T.amber}}>{fmt(m.totalEmFalta)} em falta</div>}
              </div>
              <button onClick={async()=>{ if(!isDemoAccount()) await updateMembro(m.id,{activo:!m.activo}); }}
                style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${m.activo?T.red:T.green}`,
                  background:"transparent",color:m.activo?T.red:T.green,cursor:"pointer",fontSize:11}}>
                {m.activo?"Remover":"Reactivar"}
              </button>
            </div>
          ))}
        </Card>
      )}

      {/* ── ATRIBUIÇÕES ── */}
      {tab==="atribuicoes" && (
        <Card title="Atribuição de Eventos" accent={T.purple}>
          <div style={{fontSize:12,color:T.muted,marginBottom:12}}>
            Atribui cada evento a um colaborador ou a ti próprio. O gestor vê as comissões, o colaborador vê o seu valor líquido.
          </div>
          {eventos.filter((e:any)=>e.status==="confirmado"||e.status==="concluido").map((ev:any) => {
            const atrib = atribuicoes.find((a:any)=>a.evento_id===ev.id);
            return (
              <VJAtribuicaoItem key={ev.id} ev={ev} atrib={atrib} equipa={equipa}
                insertAtrib={insertAtrib} updateAtrib={updateAtrib} inp={inp} lbl={lbl}/>
            );
          })}
          {eventos.filter((e:any)=>e.status==="confirmado"||e.status==="concluido").length===0&&(
            <Empty icon="📋" msg="Sem eventos" sub="Adiciona eventos confirmados para atribuir colaboradores"/>
          )}

          {/* Resumo financeiro de comissões */}
          {atribuicoes.length > 0 && (
            <div style={{marginTop:16,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10}}>
              <KPI l="Total Comissões" v={fmt(atribuicoes.reduce((s:number,a:any)=>s+parseFloat(a.comissao_gestor||0),0))} c="a receber/recebidas" ac={T.purple}/>
              <KPI l="Já Pagas" v={fmt(atribuicoes.filter((a:any)=>a.pago).reduce((s:number,a:any)=>s+parseFloat(a.comissao_gestor||0),0))} c="comissões pagas" ac={T.green}/>
              <KPI l="Por Pagar" v={fmt(atribuicoes.filter((a:any)=>!a.pago).reduce((s:number,a:any)=>s+parseFloat(a.comissao_gestor||0),0))} c="em falta" ac={T.amber}/>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// Sub-componente separado para evitar hooks dentro de map()
function VJAtribuicaoItem({ ev, atrib, equipa, insertAtrib, updateAtrib, inp, lbl }: any) {
  const [editAtrib, setEditAtrib] = useState(false);
  const [atribForm, setAtribForm] = useState({
    colaborador_nome:  atrib?.colaborador_nome  || "",
    valor_colaborador: atrib?.valor_colaborador || parseFloat(ev.valor||0),
    comissao_gestor:   atrib?.comissao_gestor   || parseFloat(ev.colega_valor||0),
    pago:              atrib?.pago              || false,
  });

  return (
    <div style={{padding:"12px 0",borderBottom:`1px solid ${T.border}`}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:editAtrib?10:0}}>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>{ev.data} · {ev.local}</div>
          <div style={{fontSize:11,color:T.muted}}>
            {atrib
              ? `👤 ${atrib.colaborador_nome} · ${fmt(atrib.valor_colaborador)} · comissão ${fmt(atrib.comissao_gestor)}`
              : <span style={{color:T.amber}}>⚠️ Sem colaborador atribuído</span>
            }
          </div>
        </div>
        {atrib && (
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:11,fontWeight:700,color:atrib.pago?T.green:T.red}}>
              {atrib.pago?"✓ Pago":"Por pagar"}
            </span>
            <button onClick={async()=>{
              if(!isDemoAccount()) await updateAtrib(atrib.id,{pago:!atrib.pago,
                data_pagamento:!atrib.pago?new Date().toISOString().split("T")[0]:null});
            }} style={{padding:"3px 8px",borderRadius:6,
              border:`1px solid ${atrib.pago?T.red:T.green}`,
              background:"transparent",color:atrib.pago?T.red:T.green,cursor:"pointer",fontSize:11}}>
              {atrib.pago?"Desfazer":"Marcar Pago"}
            </button>
          </div>
        )}
        <button onClick={()=>setEditAtrib(v=>!v)}
          style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${T.border}`,
            background:"transparent",color:T.muted,cursor:"pointer",fontSize:11}}>
          {editAtrib?"Fechar":"✏️ Atribuir"}
        </button>
      </div>
      {editAtrib && (
        <div style={{padding:12,borderRadius:10,background:T.panel2,border:`1px solid ${T.purple}30`,
          display:"grid",gridTemplateColumns:"1fr 110px 110px auto",gap:10,alignItems:"flex-end"}}>
          <div>
            <div style={lbl}>Colaborador</div>
            <select value={atribForm.colaborador_nome}
              onChange={e=>setAtribForm({...atribForm,colaborador_nome:e.target.value})} style={inp}>
              <option value="">— Seleccionar —</option>
              <option value="Eu próprio">Eu próprio</option>
              {(equipa||[]).map((m:any)=>(
                <option key={m.id} value={m.colaborador_nome}>{m.colaborador_nome}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={lbl}>Valor (€)</div>
            <input type="number" value={atribForm.valor_colaborador}
              onChange={e=>setAtribForm({...atribForm,valor_colaborador:+e.target.value})} style={inp}/>
          </div>
          <div>
            <div style={lbl}>Comissão (€)</div>
            <input type="number" value={atribForm.comissao_gestor}
              onChange={e=>setAtribForm({...atribForm,comissao_gestor:+e.target.value})} style={inp}/>
          </div>
          <button onClick={async()=>{
            if(!isDemoAccount()) {
              if(atrib) await updateAtrib(atrib.id, atribForm);
              else await insertAtrib({ evento_id:ev.id, ...atribForm });
            }
            setEditAtrib(false);
          }} style={{padding:"9px 14px",borderRadius:8,border:"none",
            background:T.purple,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>
            💾
          </button>
        </div>
      )}
    </div>
  );
}

function VJCalendario({ events: eventsRaw }: any) {
  const events = (eventsRaw||[]).map((e:any)=>({...e, data: e.data||e.check_in||""}));
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth());
  const [ano, setAno] = useState(hoje.getFullYear());
  const [sel, setSel] = useState<number|null>(null);

  const meses = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const diasNoMes  = new Date(ano,mes+1,0).getDate();
  const primeiroDia = new Date(ano,mes,1).getDay();
  const hojeStr = hoje.toISOString().split("T")[0];

  const eventosPorDia: Record<number,any[]> = {};
  (events||[]).forEach((e:any) => {
    const d = new Date(e.data);
    if(d.getFullYear()===ano && d.getMonth()===mes) {
      const dia = d.getDate();
      if(!eventosPorDia[dia]) eventosPorDia[dia]=[];
      eventosPorDia[dia].push(e);
    }
  });

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <Card title="📅 Calendário VJ Casamentos" accent={T.purple}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <button onClick={()=>{if(mes===0){setMes(11);setAno(ano-1);}else setMes(mes-1);setSel(null);}}
            style={{background:"none",border:`1px solid ${T.border}`,color:T.text,padding:"5px 14px",borderRadius:8,cursor:"pointer",fontSize:15}}>‹</button>
          <span style={{fontSize:14,fontWeight:700,color:T.text}}>{meses[mes]} {ano}</span>
          <button onClick={()=>{if(mes===11){setMes(0);setAno(ano+1);}else setMes(mes+1);setSel(null);}}
            style={{background:"none",border:`1px solid ${T.border}`,color:T.text,padding:"5px 14px",borderRadius:8,cursor:"pointer",fontSize:15}}>›</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
          {["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"].map(d=>(
            <div key={d} style={{fontSize:10,color:T.muted,textAlign:"center",fontWeight:700}}>{d}</div>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
          {Array.from({length:primeiroDia}).map((_,i)=><div key={`e${i}`}/>)}
          {Array.from({length:diasNoMes}).map((_,i)=>{
            const dia=i+1;
            const ds=`${ano}-${String(mes+1).padStart(2,"0")}-${String(dia).padStart(2,"0")}`;
            const evs=eventosPorDia[dia]||[];
            const isHoje=ds===hojeStr;
            const isSel=sel===dia;
            return (
              <div key={dia} onClick={()=>setSel(isSel?null:dia)}
                style={{minHeight:36,padding:"2px 1px",borderRadius:5,
                  cursor:evs.length>0?"pointer":"default",
                  background:isSel?`${T.purple}20`:isHoje?`${T.blueL}10`:evs.length>0?`${T.purple}08`:"transparent",
                  border:isSel?`1px solid ${T.purple}`:isHoje?`1px solid ${T.blueL}40`:evs.length>0?`1px solid ${T.purple}30`:`1px solid transparent`}}>
                <div style={{fontSize:10,textAlign:"center",marginBottom:1,fontWeight:isHoje?700:400,color:isHoje?T.blueL:T.textSub}}>{dia}</div>
                {evs.slice(0,2).map((ev,ei)=>(
                  <div key={ei} style={{fontSize:8,borderRadius:3,padding:"1px 2px",marginBottom:1,
                    background:`${T.purple}28`,color:T.purple,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>
                    🎤 {ev.local?.split(",")[0]||"VJ"}
                  </div>
                ))}
                {evs.length>2&&<div style={{fontSize:8,color:T.muted,textAlign:"center"}}>+{evs.length-2}</div>}
              </div>
            );
          })}
        </div>
        {sel&&eventosPorDia[sel]&&(
          <div style={{marginTop:12,padding:12,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`}}>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:8,textTransform:"uppercase"}}>{sel} de {meses[mes]}</div>
            {eventosPorDia[sel].map((ev,i)=>(
              <div key={i} style={{padding:"8px 0",borderBottom:`1px solid ${T.border}`}}>
                <div style={{fontSize:13,fontWeight:700,color:T.purple}}>🎤 {ev.local}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:3}}>🕐 {ev.horario} · 💶 {fmt(parseFloat(ev.valor||0))} · {ev.status}</div>
                {ev.notas&&<div style={{fontSize:11,color:T.muted}}>📝 {ev.notas}</div>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Lista de eventos do mês */}
      <Card title="Eventos" accent={T.purple}>
        {(events||[]).filter((e:any)=>{
          const d=new Date(e.data);
          return d.getFullYear()===ano && d.getMonth()===mes;
        }).length===0&&<Empty icon="🎤" msg="Sem eventos neste mês" sub=""/>}
        {(events||[]).filter((e:any)=>{
          const d=new Date(e.data);
          return d.getFullYear()===ano && d.getMonth()===mes;
        }).sort((a:any,b:any)=>a.data.localeCompare(b.data)).map((e:any,i:number)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
            <div style={{width:36,height:36,borderRadius:8,background:`${T.purple}18`,
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:16}}>🎤</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.local}</div>
              <div style={{fontSize:11,color:T.muted}}>{e.data} · {e.horario} · {e.tipo}</div>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{fontSize:13,fontWeight:700,color:T.purple,fontFamily:"monospace"}}>{fmt(parseFloat(e.valor||0))}</div>
              <div style={{display:"flex",gap:4,alignItems:"center"}}>
                <Tag label={e.status} color={e.status==="confirmado"?T.green:e.status==="concluido"?T.blueL:T.amber}/>
  
              </div>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function VJHistorico({ events }: any) {
  const passados = [...events].filter((e:any)=>e.status==="concluido"||new Date(e.data)<new Date()).sort((a:any,b:any)=>b.data.localeCompare(a.data));
  const totalGanho = passados.reduce((s:number,e:any)=>s+(e.valor||0),0);
  const getDiaSemana = (d:string)=>["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"][new Date(d).getDay()];
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
        <KPI l="Eventos Passados" v={passados.length} c="histórico total" ac={T.purple}/>
        <KPI l="Total Ganho" v={fmt(totalGanho)} c="receita histórica" ac={T.green}/>
      </div>
      <Card title="Histórico de Eventos" accent={T.purple} noPad>
        {passados.length===0&&<Empty icon="📚" msg="Sem histórico" sub="Os eventos concluídos aparecerão aqui"/>}
        <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:500}}>
          <thead>
            <tr style={{borderBottom:`1px solid ${T.border}`}}>
              {["Data","Local","Horário","Valor","Colega","Status"].map(h=>(
                <th key={h} style={{padding:"10px 14px",textAlign:"left",color:T.muted,fontSize:11,fontWeight:700,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {passados.map((e:any)=>(
              <tr key={e.id} style={{borderBottom:`1px solid rgba(255,255,255,0.04)`}}>
                <td style={{padding:"10px 14px",color:T.textSub,whiteSpace:"nowrap"}}>{getDiaSemana(e.data)} {new Date(e.data).toLocaleDateString("pt-PT")}</td>
                <td style={{padding:"10px 14px",color:T.text,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.local}</td>
                <td style={{padding:"10px 14px",color:T.textSub}}>{e.horario}</td>
                <td style={{padding:"10px 14px",color:T.green,fontFamily:"monospace"}}>{fmt(e.valor||0)}</td>
                <td style={{padding:"10px 14px"}}><Tag label={e.colega?"Sim":"Não"} color={e.colega?T.amber:T.muted}/></td>
                <td style={{padding:"10px 14px"}}><Tag label={e.status} color={e.status==="concluido"?T.green:T.muted}/></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}

function VJView({ page, addLog, onEventsChange }: any) {
  const { data: eventsDB, loading: evLoading, insert: insertEvent, update: updateEvent, remove: removeEvent, reload: reloadEvents } = useVJEventos();
  const { data: despesasDB } = useVJDespesas();
  const [eventsLocal, setEventsLocal] = useState<any[]>(isDemoAccount() ? DEMO_VJ_EVENTS : []);
  const events   = isDemoAccount() ? eventsLocal : (eventsDB || []);
  const despesas = isDemoAccount() ? VJ_DESPESAS_INIT : (despesasDB || []);
  const setEvents = isDemoAccount() ? setEventsLocal : (_: any) => {};
  const [tarefas, setTarefas] = useState(VJ_TAREFAS_INIT);
  const [active, setActive] = useState<any>(null);

  if (page==="equipa")      return <VJEquipa/>;
  if (page==="historico")   return <VJHistorico events={events}/>;
  if (page==="tarefas")     return <VJTarefas events={events} tarefas={tarefas} setTarefas={setTarefas} addLog={addLog}/>;
  if (page==="despesas")    return <VJDespesas events={events} addLog={addLog}/>;
  if (page==="calendario")  return <VJCalendario events={events}/>;
  return <VJOverview events={events} setEvents={setEvents} insertEvent={insertEvent} setEventsLocal={setEventsLocal} updateEvent={updateEvent} active={active} setActive={setActive} addLog={addLog} despesas={despesas} loading={evLoading} reloadEvents={reloadEvents}/>;
}

function VJOverview({ events, setEvents, insertEvent, setEventsLocal, updateEvent, active, setActive, addLog, despesas: despesasProps, loading, reloadEvents }:any) {
  const despesas = despesasProps || [];
  const showSkeleton = !isDemoAccount() && loading && events.length === 0;
  // Sincronização Groovy
  const [syncingG, setSyncingG] = useState(false);
  const [syncMsgG, setSyncMsgG] = useState("");
  const syncGroovy = async () => {
    if (isDemoAccount()) { setSyncMsgG("Demo — sync desactivado"); setTimeout(()=>setSyncMsgG(""),3000); return; }
    setSyncingG(true); setSyncMsgG("");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(
        "https://rchyocvcskswoudqrjrf.supabase.co/functions/v1/sync-groovy",
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${session?.access_token||""}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ user_id: session?.user?.id }),
        }
      );
      if (!resp.ok) { const txt = await resp.text(); throw new Error(`Erro ${resp.status}: ${txt.slice(0,80)}`); }
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error||"Erro desconhecido");
      setSyncMsgG(data.inseridas > 0 ? `✅ ${data.inseridas} nova(s) data(s)!` : "✅ Sincronizado — sem novas datas");
      setTimeout(()=>setSyncMsgG(""), 5000);
      reloadEvents?.();
    } catch(e: any) {
      setSyncMsgG(`⚠️ ${e.message}`);
      setTimeout(()=>setSyncMsgG(""), 8000);
    } finally {
      setSyncingG(false);
    }
  };
  const [addingE, setAddingE] = useState(false);
  const [newE, setNewE] = useState({ data:"", local:"", horario:"18:00", noiteFora:false, colega:true, valor:0 });
  const [pagamentos, setPagamentos] = useState<Record<number,boolean>>({});
  // Configurações VJ — valor por data e comissão colega
  const [vjConfig, setVjConfig] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kairos_vj_config")||"{}"); } catch { return {}; }
  });
  const [editConfig, setEditConfig] = useState(false);
  const [tmpConfig, setTmpConfig] = useState({valorData:200, comissaoColega:25});
  const valorPadrao = vjConfig.valorData || 200;
  const comissaoPadrao = vjConfig.comissaoColega || 25;
  const saveConfig = (v:number, c:number) => {
    const cfg = { valorData:v, comissaoColega:c };
    setVjConfig(cfg);
    localStorage.setItem("kairos_vj_config", JSON.stringify(cfg));
    setEditConfig(false);
  };

  // Tempo estimado de viagem de Vila do Conde por distrito
  const tempoViagem = (local:string):string => {
    const l = (local||"").toLowerCase();
    if(l.includes("alferes") || l.includes("vairão")) return "~15min";
    if(l.includes("amores") || l.includes("felicidade") || l.includes("paredes") || l.includes("vandoma") || l.includes("mouriz")) return "~35min";
    if(l.includes("lago dos cisnes") || l.includes("solar da levada") || l.includes("amares")) return "~55min";
    if(l.includes("calheiros") || l.includes("ponte de lima")) return "~1h";
    if(l.includes("roseiral") || l.includes("ericeira")) return "~3h15";
    if(l.includes("condes de valadares") || l.includes("loures")) return "~3h30";
    if(l.includes("porto") || l.includes("matosinhos") || l.includes("vila nova de gaia") || l.includes("maia")) return "~30min";
    if(l.includes("braga") || l.includes("barcelos") || l.includes("guimarães") || l.includes("viana")) return "~55min";
    if(l.includes("aveiro") || l.includes("coimbra")) return "~1h15";
    if(l.includes("viseu") || l.includes("bragança") || l.includes("vila real")) return "~1h30";
    if(l.includes("lisboa") || l.includes("sintra") || l.includes("cascais") || l.includes("setúbal")) return "~3h30";
    if(l.includes("évora") || l.includes("alentejo") || l.includes("beja")) return "~4h";
    if(l.includes("faro") || l.includes("algarve")) return "~5h";
    if(l.includes("salamanca") || l.includes("espanha")) return "~2h30";
    return "~1h (estimativa)";
  };

  const horaSaida = (horarioEvento:string, local:string):string => {
    if(!horarioEvento || !horarioEvento.includes(":")) return "—";
    const tempo = tempoViagem(local);
    const match = tempo.match(/~?(\d+)h?(?::?(\d+))?/);
    if(!match) return "—";
    let minTotais = parseInt(match[1])*60 + (match[2]?parseInt(match[2]):0) + 30; // +30min margem
    const [h,m] = horarioEvento.split(":").map(Number);
    if(isNaN(h)||isNaN(m)) return "—";
    const saida = new Date(2000,0,1,h,m);
    saida.setMinutes(saida.getMinutes() - minTotais);
    return `${String(saida.getHours()).padStart(2,"0")}:${String(saida.getMinutes()).padStart(2,"0")}`;
  };

  const toggle = (id:number, key:string) => setEvents((es:any[]) => es.map(e =>
    e.id===id ? { ...e, checklist:{...e.checklist,[key]:!e.checklist[key]} } : e));
  const toggleField = (id:number, key:string) => setEvents((es:any[]) => es.map(e =>
    e.id===id ? { ...e, [key]:!e[key] } : e));
  const cycleStatus = async (id: any) => {
    try {
      const ev = events.find((e:any)=>e.id===id);
      if(!ev) return;
      const nextStatus = ev.status==="pendente"?"confirmado":ev.status==="confirmado"?"concluido":"pendente";
      if(isDemoAccount()) {
        setEventsLocal((es:any[])=>es.map(e=>e.id!==id?e:{...e,status:nextStatus}));
      } else {
        await updateEvent(id, {status: nextStatus});
      }
    } catch(e) { console.error("cycleStatus error",e); }
  };
  const togglePago = async (id:any) => {
    const ev = events.find((e:any)=>e.id===id);
    if(!ev) return;
    const novoPago = !ev.colega_pago;
    const valor = parseFloat(ev.colega_valor||ev.comissao||comissaoPadrao);
    if(isDemoAccount()) {
      setEventsLocal((es:any[])=>es.map(e=>e.id===id?{...e,colega_pago:novoPago}:e));
    } else {
      await updateEvent(id, {colega_pago: novoPago});
    }
    if(novoPago) addLog?.("vj","pagamento",`Comissão paga: ${ev.local} · ${ev.data}`,"💚",valor);
  };

  const conf    = events.filter((e:any)=>e.status==="confirmado");
  const concl   = events.filter((e:any)=>e.status==="concluido");
  const ativos  = [...conf,...concl];

  // Cálculos financeiros correctos
  const totalBrutoEst    = ativos.reduce((s:number,e:any)=>s+parseFloat(e.valor||0),0);         // todos activos
  const totalFacturado   = concl.reduce((s:number,e:any)=>s+parseFloat(e.valor||0),0);          // só concluídos
  const totalPrevisao    = conf.reduce((s:number,e:any)=>s+parseFloat(e.valor||0),0);           // só confirmados
  const totalDespesas    = despesas.filter((d:any)=>!d.reembolsavel).reduce((s:number,d:any)=>s+parseFloat(d.valor||0),0);
  const totalComissoesOv = ativos.reduce((s:number,e:any)=>s+parseFloat(e.colega_valor||e.comissao||comissaoPadrao||0),0);
  const comissoesPagasOv = ativos.filter((e:any)=>e.colega_pago===true).reduce((s:number,e:any)=>s+parseFloat(e.colega_valor||e.comissao||comissaoPadrao||0),0);
  // Lucro Líquido = Facturado − comissões JÁ PAGAS − despesas
  // (comissões por pagar são uma obrigação futura, não um custo realizado)
  const lucroLiquido     = totalFacturado - comissoesPagasOv - totalDespesas;

  const colegas     = ativos.filter((e:any)=>(e.colega_valor||0)>0||e.colega).length;
  const colegasPago = ativos.filter((e:any)=>e.colega_pago===true).length;
  const epocaMeta   = 16;
  const getDiaSemana = (d:string) => ["Domingo","Segunda","Terça","Quarta","Quinta","Sexta","Sábado"][new Date(d).getDay()];

  const hoje = new Date();
  const [calMes, setCalMes] = useState(hoje.getMonth());
  const [calAno, setCalAno] = useState(hoje.getFullYear());
  const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const diasNoMes = new Date(calAno, calMes+1, 0).getDate();
  const primeiroDia = new Date(calAno, calMes, 1).getDay();

  // Eventos do mês por dia
  const eventosPorDia: Record<number,any[]> = {};
  events.forEach((e:any)=>{
    const d=new Date(e.data);
    if(d.getFullYear()===calAno && d.getMonth()===calMes) {
      const dia=d.getDate();
      if(!eventosPorDia[dia]) eventosPorDia[dia]=[];
      eventosPorDia[dia].push(e);
    }
  });
  const [calDiaSel, setCalDiaSel] = useState<number|null>(null);
  const eventoSel = calDiaSel ? eventosPorDia[calDiaSel]?.[0] : null;

  const addEvento = async () => {
    if(!newE.data || !newE.local) return;
    const v = newE.valor || valorPadrao;
    if (isDemoAccount()) {
      const novoEvento = {...newE, valor:v, status:"pendente",
        checklist:{setlist:false,acesso:false,equip:false,som:false,alinhamento:false}};
      setEventsLocal((e:any)=>[...e,{...novoEvento,id:e.length+1}]);
    } else {
      // Só colunas que existem na tabela vj_eventos
      const eventoDB:any = {
        data: newE.data,
        local: newE.local,
        horario: newE.horario || "18:00",
        tipo: "casamento",
        valor: v,
        status: "confirmado",
        colaborador_nome: "Nuno",
        colega_valor: newE.colega ? 25 : 0,
        colega_pago: false,
        noite_fora: !!newE.noiteFora,
      };
      try {
        await insertEvent(eventoDB);
      } catch(e) {
        console.error("Erro ao inserir evento VJ:", e);
        return;
      }
    }
    addLog?.("vj","evento",`Nova data VJ: ${(newE.local||"Local por definir").split(",")[0]} · ${newE.data}`,"🎤",v);
    setAddingE(false);
    setNewE({ data:"", local:"", horario:"18:00", noiteFora:false, colega:true, valor:0 });
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
      {/* Botão Sincronizar Groovy */}
      {!isDemoAccount() && (
        <div>
          <button onClick={syncGroovy} disabled={syncingG}
            style={{width:"100%",padding:"14px",borderRadius:12,border:`1px solid ${T.purple}40`,
              background:syncingG?`${T.purple}10`:`${T.purple}18`,color:T.purple,
              fontSize:14,fontWeight:700,cursor:syncingG?"wait":"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            {syncingG ? "🔄 A sincronizar..." : "🔄 Sincronizar Groovy"}
          </button>
          {syncMsgG && (
            <div style={{marginTop:8,padding:"8px 12px",borderRadius:8,fontSize:12,textAlign:"center" as any,
              background:syncMsgG.startsWith("⚠️")?`${T.red}15`:`${T.green}15`,
              color:syncMsgG.startsWith("⚠️")?T.red:T.green}}>
              {syncMsgG}
            </div>
          )}
        </div>
      )}
      {showSkeleton ? (
        <>
          <SkeletonKPIRow count={6} />
          <SkeletonKPIRow count={2} />
          <Card title="A carregar eventos..." accent={T.purple}>
            <SkeletonList rows={4} />
          </Card>
        </>
      ) : (<>
      <AlertPanelVJ events={events}/>
      {/* KPIs financeiros */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10 }}>
        <KPI l="Estimativa Bruta"  v={fmt(totalBrutoEst)}    c="todos os eventos activos"        ac={T.purple}/>
        <KPI l="Facturado"         v={fmt(totalFacturado)}   c="eventos concluídos"              ac={T.green}/>
        <KPI l="Previsão"          v={fmt(totalPrevisao)}    c="eventos confirmados"             ac={T.blueL}/>
        <KPI l="Comissões"         v={fmt(totalComissoesOv)} c={`${fmt(comissoesPagasOv)} pagas`} ac={T.amber}/>
        <KPI l="Despesas"          v={fmt(totalDespesas)}    c="não reembolsáveis"               ac={T.red}/>
        <KPI l="Lucro Líquido"     v={fmt(lucroLiquido)}     c="facturado − comissão − despesas" ac={lucroLiquido>=0?T.green:T.red}/>
      </div>
      {/* KPIs de datas */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10 }}>
        <KPI l="Confirmadas" v={conf.length}  c={`meta: ${epocaMeta}`} ac={T.purple}/>
        <KPI l="Concluídas"  v={concl.length} c="realizadas"           ac={T.green}/>
      </div>

      {/* Config VJ */}
      {editConfig ? (
        <Card title="⚙️ Configurações VJ" accent={T.purple}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Valor por Data (€)</div>
              <input type="number" defaultValue={valorPadrao} id="vj_valor_data"
                style={{width:"100%",padding:"9px 12px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:14}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Comissão Colega (€)</div>
              <input type="number" defaultValue={comissaoPadrao} id="vj_comissao"
                style={{width:"100%",padding:"9px 12px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:14}}/>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>{
              const v=+(document.getElementById("vj_valor_data") as any).value||200;
              const c=+(document.getElementById("vj_comissao") as any).value||25;
              saveConfig(v,c);
            }} style={{padding:"8px 18px",borderRadius:8,border:"none",background:T.purple,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>
              Guardar
            </button>
            <button onClick={()=>setEditConfig(false)} style={{padding:"8px 14px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}}>
              Cancelar
            </button>
          </div>
        </Card>
      ) : (
        <div style={{display:"flex",justifyContent:"flex-end"}}>
          <button onClick={()=>setEditConfig(true)} style={{padding:"10px 14px",borderRadius:6,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:11,fontWeight:600}}>
            ⚙️ Valor/Comissão padrão · €{valorPadrao}/data · Colega €{comissaoPadrao}
          </button>
        </div>
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
        {/* Mini Calendar com clique */}
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <Card title={`${meses[calMes]} ${calAno}`} accent={T.purple}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
              <button onClick={()=>{if(calMes===0){setCalMes(11);setCalAno(calAno-1);}else setCalMes(calMes-1);setCalDiaSel(null);}}
                style={{ background:"none", border:`1px solid ${T.border}`, color:T.text, padding:"3px 8px", borderRadius:6, cursor:"pointer" }}>‹</button>
              <span style={{ fontSize:12, fontWeight:700, color:T.text }}>{meses[calMes]} {calAno}</span>
              <button onClick={()=>{if(calMes===11){setCalMes(0);setCalAno(calAno+1);}else setCalMes(calMes+1);setCalDiaSel(null);}}
                style={{ background:"none", border:`1px solid ${T.border}`, color:T.text, padding:"3px 8px", borderRadius:6, cursor:"pointer" }}>›</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
              {["D","S","T","Q","Q","S","S"].map((d,i)=>(
                <div key={i} style={{ fontSize:11, color:T.muted, padding:"3px 0", textAlign:"center", fontWeight:700 }}>{d}</div>
              ))}
              {Array.from({length:primeiroDia}).map((_,i)=><div key={`e${i}`}/>)}
              {Array.from({length:diasNoMes}).map((_,i)=>{
                const dia=i+1;
                const isHoje=dia===hoje.getDate()&&calMes===hoje.getMonth()&&calAno===hoje.getFullYear();
                const temEv=eventosPorDia[dia]?.length>0;
                const isSelec=calDiaSel===dia;
                const ev=eventosPorDia[dia]?.[0];
                return (
                  <div key={dia} onClick={()=>setCalDiaSel(isSelec?null:dia)}
                    style={{ padding:"4px 2px", borderRadius:6, fontSize:11, textAlign:"center",
                      background:isSelec?"rgba(155,114,247,0.35)":isHoje?"rgba(155,114,247,0.15)":temEv?"rgba(155,114,247,0.2)":"transparent",
                      color:isHoje||temEv?T.purple:T.muted, fontWeight:temEv||isHoje?700:400,
                      border:isSelec?`2px solid ${T.purple}`:isHoje?`1px solid ${T.purple}`:temEv?`1px solid rgba(155,114,247,0.4)`:"1px solid transparent",
                      cursor:temEv?"pointer":"default" }}>
                    {dia}
                    {temEv&&ev&&<div style={{ fontSize:11, color:T.purple, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", lineHeight:1.2 }}>📅</div>}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Detalhe do evento seleccionado no calendário */}
          {eventoSel && (
            <div style={{ padding:"14px", borderRadius:10, background:"rgba(155,114,247,0.08)",
              border:`1px solid rgba(155,114,247,0.3)` }}>
              <div style={{ fontSize:11, fontWeight:700, color:T.purple, marginBottom:8 }}>
                📅 {new Date(eventoSel.data).toLocaleDateString("pt-PT",{day:"numeric",month:"long"})} — {getDiaSemana(eventoSel.data)}
              </div>
              <div style={{ fontSize:12, color:T.text, fontWeight:600, marginBottom:4 }}>{eventoSel.local}</div>
              <div style={{ fontSize:11, color:T.muted, marginBottom:6 }}>🕐 {eventoSel.horario} · 🚗 Saída: {horaSaida(eventoSel.horario,eventoSel.local)} ({tempoViagem(eventoSel.local)})</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" as any }}>
                <Tag label={eventoSel.status} color={eventoSel.status==="confirmado"?T.amber:eventoSel.status==="concluido"?T.green:T.muted}/>
                <Tag label={fmt(eventoSel.valor)} color={T.purple}/>
                {eventoSel.noiteFora&&<Tag label="🌙 Fora" color={T.blue}/>}
                {eventoSel.colega&&<Tag label="👥 Colega" color={T.amber}/>}
              </div>
            </div>
          )}
        </div>

        {/* Datas & Checklist */}
        <Card title="Datas & Checklist" accent={T.purple} action="+ Nova Data" onAction={()=>setAddingE(!addingE)}>
          {addingE && (
            <div style={{ marginBottom:14, padding:14, borderRadius:10, background:"rgba(155,114,247,0.06)",
              border:`1px solid rgba(155,114,247,0.2)`, display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:10 }}>
              {[["Data","date","data",""],["Horário","time","horario","18:00"],["Local","text","local","Nome da Quinta, Cidade"]
              ].map(([l,t,k,ph]:any)=>(
                <div key={k} style={k==="local"?{gridColumn:"1/-1"}:{}}>
                  <div style={{ fontSize:11, color:T.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>{l}</div>
                  <input type={t} placeholder={ph} value={newE[k]}
                    onChange={(e:any)=>setNewE({...newE,[k]:e.target.value})}
                    style={{ width:"100%", padding:"8px 11px", borderRadius:8, background:"rgba(255,255,255,0.05)",
                      border:`1px solid ${T.border}`, color:T.text, fontSize:12, boxSizing:"border-box" as any }}/>
                </div>
              ))}
              <div style={{ display:"flex", gap:16 }}>
                {[["noiteFora","🌙 Noite Fora"],["colega","👥 Colega Equipa"]].map(([k,l]:any)=>(
                  <label key={k} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", fontSize:12, color:T.text }}>
                    <input type="checkbox" checked={newE[k]} onChange={(e:any)=>setNewE({...newE,[k]:e.target.checked})}/>{l}
                  </label>
                ))}
              </div>
              <button onClick={addEvento} style={{ gridColumn:"1/-1", padding:"9px", borderRadius:10, border:"none",
                background:T.purple, color:"white", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                Guardar Data
              </button>
            </div>
          )}

          {events.sort((a:any,b:any)=>new Date(a.data).getTime()-new Date(b.data).getTime()).map((ev:any)=>(
            <div key={ev.id}>
              <div onClick={()=>setActive(active===ev.id?null:ev.id)}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 0",
                  borderBottom:`1px solid rgba(255,255,255,0.04)`, cursor:"pointer" }}>
                <div onClick={(e)=>{e.stopPropagation();cycleStatus(ev.id);}}
                  style={{ width:10, height:10, borderRadius:"50%", flexShrink:0, cursor:"pointer",
                    background:ev.status==="confirmado"?T.amber:ev.status==="concluido"?T.green:T.muted }}/>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:T.text, display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" as any }}>
                    {new Date(ev.data).toLocaleDateString("pt-PT",{day:"numeric",month:"long"})}
                    <span style={{ fontSize:11, color:T.muted }}>({getDiaSemana(ev.data)})</span>
                    <span style={{ fontSize:11, color:T.muted }}>· {ev.horario}</span>
                    {(ev.noite_fora||ev.noiteFora) && <Tag label="🌙 Fora" color={T.blue}/>}
                    {(ev.colega_valor>0||ev.colega) && <Tag label="👥 Colega" color={T.amber}/>}
                  </div>
                  <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>
                    {ev.local}
                    {" · "}
                    <span style={{ color:T.purple }}>🚗 {tempoViagem(ev.local)} · Sair: {horaSaida(ev.horario,ev.local)}</span>
                  </div>
                </div>
                <span style={{ fontSize:14, fontWeight:700, color:T.purple, fontFamily:"monospace" }}>{fmt(ev.valor)}</span>
                {(ev.colega_valor>0||ev.colega) && <span style={{ fontSize:11, color:T.amber, fontFamily:"monospace" }}>-€{getVJComissao()}</span>}
                {/* Status selector — clicável */}
                <select
                  value={ev.status}
                  onClick={(e:any)=>e.stopPropagation()}
                  onChange={async(e:any)=>{
                    e.stopPropagation();
                    const novoStatus = e.target.value;
                    try {
                      if(isDemoAccount()) setEventsLocal((es:any[])=>es.map(x=>x.id!==ev.id?x:{...x,status:novoStatus}));
                      else await updateEvent(ev.id,{status:novoStatus});
                    } catch(err){ console.error(err); }
                  }}
                  style={{
                    padding:"3px 6px", borderRadius:6, fontSize:10, fontWeight:700,
                    border:`1px solid ${ev.status==="confirmado"?T.amber:ev.status==="concluido"?T.green:ev.status==="cancelado"?T.red:T.muted}40`,
                    background:ev.status==="confirmado"?`${T.amber}20`:ev.status==="concluido"?`${T.green}20`:ev.status==="cancelado"?`${T.red}20`:`${T.muted}10`,
                    color:ev.status==="confirmado"?T.amber:ev.status==="concluido"?T.green:ev.status==="cancelado"?T.red:T.muted,
                    cursor:"pointer", outline:"none", appearance:"none" as any,
                  }}>
                  <option value="confirmado">✓ Confirmado</option>
                  <option value="concluido">✅ Concluído</option>
                  <option value="cancelado">✕ Cancelado</option>
                  <option value="pendente">⏳ Pendente</option>
                </select>
                {(ev.colega_valor>0||ev.colega) && (
                  <button onClick={async(e:any)=>{e.stopPropagation();await togglePago(ev.id);}}
                    style={{padding:"3px 8px",borderRadius:5,border:"none",fontSize:10,fontWeight:700,cursor:"pointer",
                      background:ev.colega_pago?T.green:"rgba(255,255,255,0.08)",
                      color:ev.colega_pago?"white":T.amber}}>
                    {ev.colega_pago?"✓ Pago":"Pagar €"+parseFloat(ev.colega_valor||25).toFixed(0)}
                  </button>
                )}
                <span style={{ fontSize:11, color:T.muted }}>{active===ev.id?"▲":"▼"}</span>
              </div>
              {active===ev.id && (
                <div style={{ padding:"12px 14px", marginBottom:8, borderRadius:10,
                  background:"rgba(155,114,247,0.06)", border:"1px solid rgba(155,114,247,0.2)" }}>
                  <div style={{ fontSize:11, fontWeight:700, color:T.purple, marginBottom:8 }}>Checklist Pré-Evento</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:4, marginBottom:10 }}>
                    {([["setlist","🎵 Setlist preparado"],["equip","🎛️ Equipamento verificado"]] as any).map(([k,l]:any)=>(
                      <div key={k} onClick={e=>{e.stopPropagation();toggle(ev.id,k);}}
                        style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px",
                          borderRadius:6, cursor:"pointer",
                          background:(ev.checklist||{})[k]?"rgba(34,194,122,0.07)":"transparent",
                          border:`1px solid ${(ev.checklist||{})[k]?"rgba(34,194,122,0.2)":"rgba(255,255,255,0.04)"}`}}>
                        <div style={{ width:16, height:16, borderRadius:6, flexShrink:0,
                          border:`2px solid ${(ev.checklist||{})[k]?T.green:"rgba(255,255,255,0.15)"}`,
                          background:(ev.checklist||{})[k]?T.green:"transparent",
                          display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"white" }}>
                          {(ev.checklist||{})[k]?"✓":""}
                        </div>
                        <span style={{ fontSize:11, color:(ev.checklist||{})[k]?T.muted:T.text,
                          textDecoration:(ev.checklist||{})[k]?"line-through":"none" }}>{l}</span>
                      </div>
                    ))}
                  </div>
                  {/* Viagem */}
                  <div style={{ padding:"8px 10px", borderRadius:8, background:"rgba(155,114,247,0.08)",
                    border:`1px solid rgba(155,114,247,0.15)`, marginBottom:10 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:T.purple, marginBottom:4 }}>🚗 VIAGEM DE VILA DO CONDE</div>
                    <div style={{ display:"flex", gap:16 }}>
                      <span style={{ fontSize:12, color:T.text }}>Tempo: <strong>{tempoViagem(ev.local)}</strong></span>
                      <span style={{ fontSize:12, color:T.text }}>Sair até: <strong style={{ color:T.amber }}>{horaSaida(ev.horario,ev.local)}</strong></span>
                    </div>
                    <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>Inclui +30min de margem</div>
                  </div>
                  {/* Valor e Comissão editáveis por evento */}
                  <div style={{ display:"flex", gap:10, marginBottom:10 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:10, color:T.muted, marginBottom:3, fontWeight:700, textTransform:"uppercase" as any }}>Valor do evento (€)</div>
                      <input type="number" defaultValue={parseFloat(ev.valor||valorPadrao)} id={`vj_val_${ev.id}`}
                        onClick={(e:any)=>e.stopPropagation()}
                        style={{ width:"100%", padding:"7px 10px", borderRadius:7, background:T.surface,
                          border:`1px solid ${T.border}`, color:T.text, fontSize:13, boxSizing:"border-box" as any }}/>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:10, color:T.muted, marginBottom:3, fontWeight:700, textTransform:"uppercase" as any }}>Comissão colega (€)</div>
                      <input type="number" defaultValue={parseFloat(ev.colega_valor||comissaoPadrao)} id={`vj_com_${ev.id}`}
                        onClick={(e:any)=>e.stopPropagation()}
                        style={{ width:"100%", padding:"7px 10px", borderRadius:7, background:T.surface,
                          border:`1px solid ${T.border}`, color:T.text, fontSize:13, boxSizing:"border-box" as any }}/>
                    </div>
                    <button onClick={async(e:any)=>{
                      e.stopPropagation();
                      const novoVal = +((document.getElementById(`vj_val_${ev.id}`) as any)?.value)||valorPadrao;
                      const novaCom = +((document.getElementById(`vj_com_${ev.id}`) as any)?.value)||0;
                      try {
                        if(isDemoAccount()) setEventsLocal((es:any[])=>es.map(x=>x.id!==ev.id?x:{...x,valor:novoVal,colega_valor:novaCom}));
                        else await updateEvent(ev.id,{valor:novoVal,colega_valor:novaCom});
                      } catch(err){ console.error(err); }
                    }} style={{ alignSelf:"flex-end", padding:"7px 14px", borderRadius:7, border:"none",
                      background:T.purple, color:"white", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                      💾
                    </button>
                  </div>
                  <div style={{ display:"flex", gap:16, flexWrap:"wrap" as any }}>
                    <label style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", fontSize:12, color:T.text }}>
                      <input type="checkbox" checked={!!(ev.noite_fora||ev.noiteFora)} onClick={(e:any)=>e.stopPropagation()}
                        onChange={async()=>{ try{ if(isDemoAccount()) setEventsLocal((es:any[])=>es.map(x=>x.id!==ev.id?x:{...x,noite_fora:!ev.noite_fora})); else await updateEvent(ev.id,{noite_fora:!(ev.noite_fora||ev.noiteFora)});}catch(err){console.error(err);} }}/>
                      🌙 Noite fora
                    </label>
                    <label style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", fontSize:12, color:T.text }}>
                      <input type="checkbox" checked={(ev.colega_valor>0)} onClick={(e:any)=>e.stopPropagation()}
                        onChange={async()=>{
                          const novaCom = (ev.colega_valor>0) ? 0 : comissaoPadrao;
                          try{ if(isDemoAccount()) setEventsLocal((es:any[])=>es.map(x=>x.id!==ev.id?x:{...x,colega_valor:novaCom})); else await updateEvent(ev.id,{colega_valor:novaCom});}catch(err){console.error(err);}
                        }}/>
                      👥 Dá comissão a colega
                    </label>
                  </div>
                </div>
              )}
            </div>
          ))}
        </Card>
      </div>

      {/* Comissão Colega — Controlo Pagamentos */}
      <Card title="Comissão Colega" accent={T.amber}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12, marginBottom:16 }}>
          <KPI l="Total a Pagar"  v={fmt(totalComissoesOv)}  c={`${colegas} datas`}        ac={T.amber}/>
          <KPI l="Já Pago"       v={fmt(comissoesPagasOv)}  c={`${colegasPago} pagas`}     ac={T.green}/>
          <KPI l="Em Falta"      v={fmt(totalComissoesOv-comissoesPagasOv)} c="por pagar" ac={(totalComissoesOv-comissoesPagasOv)>0?T.red:T.green}/>
        </div>
        {ativos.filter((e:any)=>e.colega_valor>0||e.colega).map((ev:any)=>{
          const pago = ev.colega_pago===true;
          const valorComissao = parseFloat(ev.colega_valor||ev.comissao||comissaoPadrao);
          return (
            <div key={ev.id} style={{ display:"flex", alignItems:"center", gap:12,
              padding:"9px 0", borderBottom:`1px solid rgba(255,255,255,0.04)` }}>
              <StatusDot s={ev.status}/>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, color:T.text, fontWeight:600 }}>
                  {new Date(ev.data).toLocaleDateString("pt-PT",{day:"numeric",month:"long"})} — {(ev.local||"Local por definir").split(",")[0]}
                </div>
              </div>
              <Tag label={fmt(valorComissao)} color={T.amber}/>
              <Tag label={ev.status} color={ev.status==="confirmado"?T.amber:T.green}/>
              <button onClick={()=>togglePago(ev.id)}
                style={{ padding:"4px 12px", borderRadius:6, border:"none",
                  background:pago?T.green:"rgba(255,255,255,0.06)",
                  color:pago?"white":T.muted, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                {pago?"✓ Pago":"Marcar Pago"}
              </button>
            </div>
          );
        })}
      </Card>
      </>)}
    </div>
  );
}

// ── VJ — TAREFAS ──
function VJTarefas({ events, tarefas, setTarefas, addLog }:any) {
  const [addingT, setAddingT] = useState(false);
  const [newT, setNewT] = useState({ titulo:"", eventoId:"", status:"por fazer" });
  const hoje = new Date().toISOString().split("T")[0];

  const addTarefa = () => {
    setTarefas([...tarefas,{...newT,id:tarefas.length+1}]);
    addLog?.("vj","tarefa",`Nova tarefa VJ: ${newT.titulo}`,"✅");
    setAddingT(false);
    setNewT({ titulo:"", eventoId:"", status:"por fazer" });
  };
  const cycleStatus = (id:number) => setTarefas((ts:any[])=>ts.map(t=>t.id!==id?t:{...t,
    status:t.status==="por fazer"?"concluída":"por fazer"}));
  const statusColor = { "por fazer":T.muted, "concluída":T.green };

  const tarefasGerais = tarefas.filter((t:any)=>!t.eventoId);
  const tarefasEvento = events.map((ev:any)=>({
    ev, ts: tarefas.filter((t:any)=>t.eventoId===String(ev.id))
  })).filter((g:any)=>g.ts.length>0);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12 }}>
        <KPI l="Por Fazer"  v={tarefas.filter((t:any)=>t.status==="por fazer").length}  c="" ac={T.muted}/>
        <KPI l="Concluídas" v={tarefas.filter((t:any)=>t.status==="concluída").length}  c="" ac={T.green}/>
        <KPI l="Total"      v={tarefas.length}                                           c="" ac={T.purple}/>
      </div>

      <Card title="Tarefas" accent={T.purple} action="+ Nova Tarefa" onAction={()=>setAddingT(!addingT)}>
        {addingT && (
          <div style={{ marginBottom:14, padding:14, borderRadius:10, background:"rgba(155,114,247,0.06)",
            border:`1px solid rgba(155,114,247,0.2)`, display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:10 }}>
            <div style={{ gridColumn:"1/-1" }}>
              <div style={{ fontSize:11, color:T.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Tarefa</div>
              <input placeholder="O que fazer..." value={newT.titulo}
                onChange={(e:any)=>setNewT({...newT,titulo:e.target.value})}
                style={{ width:"100%", padding:"8px 11px", borderRadius:8, background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`, color:T.text, fontSize:12, boxSizing:"border-box" as any }}/>
            </div>
            <div>
              <div style={{ fontSize:11, color:T.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Associar a Evento (opcional)</div>
              <select value={newT.eventoId} onChange={(e:any)=>setNewT({...newT,eventoId:e.target.value})}
                style={{ width:"100%", padding:"8px 11px", borderRadius:8, background:"#0c1322",
                  border:`1px solid ${T.border}`, color:T.text, fontSize:12 }}>
                <option value="">— Geral (sem evento) —</option>
                {events.map((ev:any)=>(
                  <option key={ev.id} value={String(ev.id)}>
                    {new Date(ev.data).toLocaleDateString("pt-PT",{day:"numeric",month:"short"})} — {(ev.local||"Local por definir").split(",")[0]}
                  </option>
                ))}
              </select>
            </div>
            <button onClick={addTarefa} style={{ gridColumn:"1/-1", padding:"9px", borderRadius:10, border:"none",
              background:T.purple, color:"white", fontSize:13, fontWeight:700, cursor:"pointer" }}>
              Guardar
            </button>
          </div>
        )}

        {tarefas.length===0 && <Empty icon="✅" msg="Sem tarefas" sub="Clica em + Nova Tarefa"/>}

        {tarefasGerais.length>0 && (
          <>
            <div style={{ fontSize:11, color:T.muted, fontWeight:700, textTransform:"uppercase", marginBottom:6, marginTop:4 }}>Tarefas Gerais</div>
            {tarefasGerais.map((t:any)=>(
              <div key={t.id} onClick={()=>cycleStatus(t.id)}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 0",
                  borderBottom:`1px solid rgba(255,255,255,0.04)`, cursor:"pointer" }}>
                <div style={{ width:18, height:18, borderRadius:6, flexShrink:0,
                  border:`2px solid ${statusColor[t.status]||T.muted}`,
                  background:t.status==="concluída"?T.green:"transparent",
                  display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"white" }}>
                  {t.status==="concluída"?"✓":""}
                </div>
                <span style={{ fontSize:13, color:t.status==="concluída"?T.muted:T.text,
                  textDecoration:t.status==="concluída"?"line-through":"none", flex:1 }}>{t.titulo}</span>
                <Tag label={t.status} color={statusColor[t.status]||T.muted}/>
              </div>
            ))}
          </>
        )}

        {tarefasEvento.map(({ev,ts}:any)=>(
          <div key={ev.id} style={{ marginTop:12 }}>
            <div style={{ fontSize:11, color:T.purple, fontWeight:700, textTransform:"uppercase",
              marginBottom:6, padding:"4px 0", borderTop:`1px solid rgba(155,114,247,0.2)` }}>
              📅 {new Date(ev.data).toLocaleDateString("pt-PT",{day:"numeric",month:"long"})} — {(ev.local||"Local por definir").split(",")[0]}
            </div>
            {ts.map((t:any)=>(
              <div key={t.id} onClick={()=>cycleStatus(t.id)}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0",
                  borderBottom:`1px solid rgba(255,255,255,0.03)`, cursor:"pointer" }}>
                <div style={{ width:16, height:16, borderRadius:6, flexShrink:0,
                  border:`2px solid ${statusColor[t.status]||T.muted}`,
                  background:t.status==="concluída"?T.green:"transparent",
                  display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"white" }}>
                  {t.status==="concluída"?"✓":""}
                </div>
                <span style={{ fontSize:12, color:t.status==="concluída"?T.muted:T.text,
                  textDecoration:t.status==="concluída"?"line-through":"none", flex:1 }}>{t.titulo}</span>
              </div>
            ))}
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── VJ — DESPESAS ──
function VJDespesas({ events, addLog }:any) {
  const { data: despesasDB, insert: insertDespesa } = useVJDespesas();
  const [despesasLocal, setDespesasLocal] = useState<any[]>(VJ_DESPESAS_INIT);
  const despesas = isDemoAccount() ? despesasLocal : (despesasDB || []);
  const setDespesas = isDemoAccount() ? setDespesasLocal : (_:any) => {};
  const [addingD, setAddingD] = useState(false);
  const [newD, setNewD] = useState({ descricao:"", valor:0, data:"", tipo:"geral", eventoId:"", reembolsavel:false });

  const addDespesa = async () => {
    if(!newD.descricao || !newD.valor) return;
    if (isDemoAccount()) {
      setDespesasLocal([...despesasLocal,{...newD,id:despesasLocal.length+1,valor:+newD.valor}]);
    } else {
      const despesaDB:any = {
        descricao: newD.descricao,
        valor: parseFloat(String(newD.valor))||0,
        data: newD.data || new Date().toISOString().split("T")[0],
        tipo: newD.tipo || "geral",
        reembolsavel: !!newD.reembolsavel,
      };
      // evento_id só se for um uuid válido (não vazio)
      if(newD.eventoId && newD.eventoId.length > 10) despesaDB.evento_id = newD.eventoId;
      try {
        await insertDespesa(despesaDB);
      } catch(e) {
        console.error("Erro ao inserir despesa VJ:", e);
        return;
      }
    }
    addLog?.("vj","despesa",`Despesa VJ: ${newD.descricao} · ${fmt(+newD.valor)}`,"💸",+newD.valor);
    setAddingD(false);
    setNewD({ descricao:"", valor:0, data:"", tipo:"geral", eventoId:"", reembolsavel:false });
  };

  const totalGeral = despesas.filter((d:any)=>!d.reembolsavel).reduce((s:number,d:any)=>s+parseFloat(d.valor||0),0);
  const totalReemb  = despesas.filter((d:any)=>d.reembolsavel).reduce((s:number,d:any)=>s+parseFloat(d.valor||0),0);
  const tipoColor:any = { gasolina:T.amber, portagens:T.blue, alimentacao:T.green, hotel:T.purple, outro:T.muted };
  const tipos = ["gasolina","portagens","alimentacao","hotel","outro","geral"];

  // Calcular receita e lucro — usar campos reais do Supabase
  const ativos = events.filter((e:any)=>e.status!=="pendente");
  const concluidos = events.filter((e:any)=>e.status==="concluido");
  const confirmados = events.filter((e:any)=>e.status==="confirmado");
  const receitaBruta   = ativos.reduce((s:number,e:any)=>s+parseFloat(e.valor||0),0);
  const receitaFact    = concluidos.reduce((s:number,e:any)=>s+parseFloat(e.valor||0),0);
  const receitaPrevisao= confirmados.reduce((s:number,e:any)=>s+parseFloat(e.valor||0),0);
  // Comissão: usar colega_valor do Supabase, ou comissao, ou 0
  const totalComissoes = ativos.reduce((s:number,e:any)=>s+parseFloat(e.colega_valor||e.comissao||0),0);
  const comissoesPagas = ativos.filter((e:any)=>e.colega_pago===true).reduce((s:number,e:any)=>s+parseFloat(e.colega_valor||e.comissao||0),0);
  const comissoesPorPagar = totalComissoes - comissoesPagas;
  const lucroLiquido = receitaFact - totalComissoes - totalGeral;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12 }}>
        <KPI l="Receita Bruta"      v={fmt(receitaBruta)}      c={`${ativos.length} eventos`}       ac={T.purple}/>
        <KPI l="Facturado"          v={fmt(receitaFact)}       c="eventos concluídos"               ac={T.green}/>
        <KPI l="Previsão"           v={fmt(receitaPrevisao)}   c="eventos confirmados"              ac={T.blueL}/>
        <KPI l="Despesas Próprias"  v={fmt(totalGeral)}        c="não reembolsáveis"                ac={T.red}/>
        <KPI l="Comissões Total"    v={fmt(totalComissoes)}    c={`${fmt(comissoesPagas)} pagas`}   ac={T.amber}/>
        <KPI l="Comissões em Falta" v={fmt(comissoesPorPagar)} c="por pagar ao gestor"             ac={comissoesPorPagar>0?T.red:T.green}/>
        <KPI l="Lucro Líquido"      v={fmt(lucroLiquido)}      c="facturado − comissão − despesas"  ac={lucroLiquido>=0?T.green:T.red}/>
      </div>

      <Card title="Despesas" accent={T.purple} action="+ Despesa" onAction={()=>setAddingD(!addingD)}>
        {addingD && (
          <div style={{ marginBottom:14, padding:14, borderRadius:10, background:"rgba(155,114,247,0.06)",
            border:`1px solid rgba(155,114,247,0.2)`, display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:10 }}>
            <div style={{ gridColumn:"1/-1" }}>
              <div style={{ fontSize:11, color:T.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Descrição</div>
              <input placeholder="Ex: Gasolina A1..." value={newD.descricao}
                onChange={(e:any)=>setNewD({...newD,descricao:e.target.value})}
                style={{ width:"100%", padding:"8px 11px", borderRadius:8, background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${T.border}`, color:T.text, fontSize:12, boxSizing:"border-box" as any }}/>
            </div>
            {[["Valor €","number","valor","0"],["Data","date","data",""]].map(([l,type,key,ph]:any)=>(
              <div key={key}>
                <div style={{ fontSize:11, color:T.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>{l}</div>
                <input type={type} placeholder={ph} value={newD[key]}
                  onChange={(e:any)=>setNewD({...newD,[key]:e.target.value})}
                  style={{ width:"100%", padding:"8px 11px", borderRadius:8, background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${T.border}`, color:T.text, fontSize:12, boxSizing:"border-box" as any }}/>
              </div>
            ))}
            <div>
              <div style={{ fontSize:11, color:T.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Tipo</div>
              <select value={newD.tipo} onChange={(e:any)=>setNewD({...newD,tipo:e.target.value})}
                style={{ width:"100%", padding:"8px 11px", borderRadius:8, background:"#0c1322",
                  border:`1px solid ${T.border}`, color:T.text, fontSize:12 }}>
                {tipos.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:11, color:T.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Evento (opcional)</div>
              <select value={newD.eventoId} onChange={(e:any)=>setNewD({...newD,eventoId:e.target.value})}
                style={{ width:"100%", padding:"8px 11px", borderRadius:8, background:"#0c1322",
                  border:`1px solid ${T.border}`, color:T.text, fontSize:12 }}>
                <option value="">— Geral —</option>
                {events.map((ev:any)=>(
                  <option key={ev.id} value={String(ev.id)}>
                    {new Date(ev.data).toLocaleDateString("pt-PT",{day:"numeric",month:"short"})} — {(ev.local||"Local por definir").split(",")[0]}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <label style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", fontSize:12, color:T.text }}>
                <input type="checkbox" checked={newD.reembolsavel}
                  onChange={(e:any)=>setNewD({...newD,reembolsavel:e.target.checked})}/>
                💼 Reembolsável pela empresa
              </label>
            </div>
            <button onClick={addDespesa} style={{ gridColumn:"1/-1", padding:"9px", borderRadius:10, border:"none",
              background:T.purple, color:"white", fontSize:13, fontWeight:700, cursor:"pointer" }}>
              Guardar
            </button>
          </div>
        )}

        {despesas.length===0 && <Empty icon="💸" msg="Sem despesas" sub="Clica em + Despesa para registar"/>}

        {/* Reembolsáveis */}
        {despesas.filter((d:any)=>d.reembolsavel).length>0 && (
          <>
            <div style={{ fontSize:11, color:T.green, fontWeight:700, textTransform:"uppercase", margin:"4px 0 8px" }}>
              💼 Deslocações Reembolsáveis
            </div>
            {despesas.filter((d:any)=>d.reembolsavel).map((d:any)=>{
              const ev = events.find((e:any)=>String(e.id)===d.eventoId);
              return (
                <div key={d.id} style={{ display:"flex", alignItems:"center", gap:12,
                  padding:"9px 0", borderBottom:`1px solid rgba(255,255,255,0.04)` }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, color:T.text, fontWeight:600 }}>{d.descricao}</div>
                    <div style={{ fontSize:11, color:T.muted }}>{d.data}{ev?` · ${(ev.local||"Local por definir").split(",")[0]}`:""}</div>
                  </div>
                  <Tag label={d.tipo} color={tipoColor[d.tipo]||T.muted}/>
                  <Tag label="Reembolsável" color={T.green}/>
                  <span style={{ fontSize:13, fontWeight:700, color:T.green, fontFamily:"monospace" }}>{fmt(d.valor)}</span>
                </div>
              );
            })}
          </>
        )}

        {/* Próprias */}
        {despesas.filter((d:any)=>!d.reembolsavel).length>0 && (
          <>
            <div style={{ fontSize:11, color:T.red, fontWeight:700, textTransform:"uppercase", margin:"12px 0 8px" }}>
              💸 Despesas Próprias
            </div>
            {despesas.filter((d:any)=>!d.reembolsavel).map((d:any)=>{
              const ev = events.find((e:any)=>String(e.id)===d.eventoId);
              return (
                <div key={d.id} style={{ display:"flex", alignItems:"center", gap:12,
                  padding:"9px 0", borderBottom:`1px solid rgba(255,255,255,0.04)` }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, color:T.text, fontWeight:600 }}>{d.descricao}</div>
                    <div style={{ fontSize:11, color:T.muted }}>{d.data}{ev?` · ${(ev.local||"Local por definir").split(",")[0]}`:""}</div>
                  </div>
                  <Tag label={d.tipo} color={tipoColor[d.tipo]||T.muted}/>
                  <span style={{ fontSize:13, fontWeight:700, color:T.red, fontFamily:"monospace" }}>-{fmt(d.valor)}</span>
                </div>
              );
            })}
          </>
        )}
      </Card>
    </div>
  );
}

// ── MEDIA / INFLUENCER / KIDS (placeholders) ──
function PlaceholderView({ empresa, page, icon, color }: any) {
  if (page==="roadmap") return <Roadmap filter={empresa}/>;
  const msgs = {
    media:      { msg:"Kairos Media — Em Planeamento", sub:"Canal Séries BR + Canal Humor BR. Meta M12: €3.000+ net. Fase 3 do roadmap." },
    series:     { msg:"Canal Séries BR", sub:"T20 roadmap M4: 10 vídeos + SEO. Pipeline: Claude script → ElevenLabs voz → Krea visuals → YouTube." },
    humor:      { msg:"Canal Humor BR", sub:"T19 roadmap M3-4: 20 vídeos setup. Pipeline: DB anedotas → HeyGen avatar → CapCut → TikTok/YouTube/Reels." },
    influencer: { msg:"Kairos Influencer — Em Planeamento", sub:"17 músicas no Spotify. T21 roadmap M3-4: 17 videoclipes pipeline automático." },
    artista1:   { msg:"Artista Principal", sub:"17 músicas publicadas. Pipeline: Claude analisa letra → Higgsfield/Krea cenas → CapCut montagem → YouTube + socials." },
    kids:       { msg:"Kairos Kids — Em Planeamento", sub:"Etsy + Amazon KDP. T18 roadmap M3: Pipeline KDP + Etsy v1. Meta M12: 300+ produtos, €700+ net." },
    etsy:       { msg:"Loja Etsy", sub:"Produtos digitais: livros colorir, labirintos, fichas educativas, posters, convites festa. Pipeline automático Midjourney → PDF → SEO → publicar." },
    kdp:        { msg:"Amazon KDP", sub:"Livros impressão on demand. Pipeline: Midjourney páginas → Python PDF → Claude SEO → auto-publicar KDP." },
  };
  const info = msgs[page]||msgs[empresa]||{ msg:"Em Planeamento", sub:"" };
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12 }}>
        <KPI l="Status" v="Planeamento" c="" ac={color}/>
        <KPI l="Meta M12" v={empresa==="media"?"€3.000+":empresa==="influencer"?"€400+":"€700+"} c="net/mês" ac={color}/>
        <KPI l="Fase Roadmap" v="Fase 3" c="M3–M5" ac={color}/>
        <KPI l="Prioridade" v={empresa==="media"?"Média":empresa==="influencer"?"Média":"Baixa"} c="" ac={color}/>
      </div>
      <Empty icon={icon} msg={info.msg} sub={info.sub}/>
    </div>
  );
}

/* ─── GLOBAL LOG + NOTIFICATION SYSTEM ────────────────────────────────────── */

// Log entry type
interface LogEntry {
  id: string;
  ts: string;       // ISO timestamp
  empresa: string;  // company id
  tipo: string;     // "reserva" | "evento" | "despesa" | "tarefa" | "projeto" | "sistema" | "limpeza"
  descricao: string;
  valor?: number;
  icon: string;
}

// Notification type
interface Notif {
  id: string;
  ts: string;
  empresa: string;
  titulo: string;
  detalhe: string;
  nivel: "info" | "aviso" | "urgente";
  lida: boolean;
  icon: string;
}

// Global log store — shared across app via context pattern (prop drilling for simplicity)
const COMPANY_ICONS: Record<string,string> = {
  holdings:"⚡",hw3d:"🧊",kai_ai:"🤖",quinta:"🏠",vj:"🎤",photobooth:"📸",
  media:"📺",influencer:"🎵",kids:"👶",
};
const COMPANY_NAMES: Record<string,string> = {
  holdings:"Kairos Holdings",hw3d:"Homework 3D",kai_ai:"Kairos AI",quinta:"Quinta da Regada",
  vj:"VJ Casamentos",photobooth:"321 PhotoBooth",media:"Kairos Media",influencer:"Kairos Influencer",kids:"Kairos Kids",
};

function makeId() { return Math.random().toString(36).slice(2,9); }
function nowISO() { return new Date().toISOString(); }

// Format relative time: "há 2min", "há 3h", "ontem", etc.
function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff/60000);
  if(m < 1)  return "agora";
  if(m < 60) return `há ${m}min`;
  const h = Math.floor(m/60);
  if(h < 24) return `há ${h}h`;
  const d = Math.floor(h/24);
  if(d === 1) return "ontem";
  return `há ${d} dias`;
}

// Generate smart notifications from app state
function generateNotifs(
  vjEvents: any[], alRes: any[], hw3dProjects: any[], impostos: any[]
): Notif[] {
  const notifs: Notif[] = [];
  const hoje = new Date();

  // VJ — eventos próximos
  vjEvents.forEach(ev => {
    if(ev.status === "concluido") return;
    const evDate = new Date(ev.data);
    const diasAte = Math.ceil((evDate.getTime() - hoje.getTime()) / 86400000);
    if(diasAte === 1) {
      notifs.push({
        id: makeId(), ts: nowISO(), empresa:"vj",
        titulo: "Evento amanhã!",
        detalhe: `${(ev.local||"Local por definir").split(",")[0]} · Sair cedo de Vila do Conde`,
        nivel: "urgente", lida: false, icon:"🎤"
      });
    } else if(diasAte <= 7 && diasAte > 1) {
      const chDone = Object.values(ev.checklist||{}).filter(Boolean).length;
      const chTotal = Object.keys(ev.checklist||{}).length;
      notifs.push({
        id: makeId(), ts: nowISO(), empresa:"vj",
        titulo: `Evento em ${diasAte} dias`,
        detalhe: `${(ev.local||"Local por definir").split(",")[0]} · Checklist: ${chDone}/${chTotal}`,
        nivel: chDone < chTotal ? "aviso" : "info", lida: false, icon:"🎤"
      });
    }
  });

  // AL — check-ins, check-outs e limpezas (hoje, amanhã, 3 dias, 7 dias)
  alRes.forEach(r => {
    if(r.status === "cancelada") return;
    const isLimp = r.status === "limpeza";
    const ci = new Date(r.ci), co = new Date(r.co);
    const diasCI = Math.ceil((ci.getTime() - hoje.getTime()) / 86400000);
    const diasCO = Math.ceil((co.getTime() - hoje.getTime()) / 86400000);
    const casaLabel = CASAS.find((c:any)=>c.id===r.casa)?.label || r.casa;

    if(isLimp) {
      // Avisos de limpeza
      if(diasCI === 0) notifs.push({ id:makeId(), ts:nowISO(), empresa:"quinta",
        titulo:"🧹 Limpeza HOJE",
        detalhe:`${casaLabel}${r.limpezaHora ? ` · ${r.limpezaHora}` : ""}`,
        nivel:"urgente", lida:false, icon:"🧹" });
      else if(diasCI === 1) notifs.push({ id:makeId(), ts:nowISO(), empresa:"quinta",
        titulo:"🧹 Limpeza amanhã",
        detalhe:`${casaLabel} — preparar material`,
        nivel:"aviso", lida:false, icon:"🧹" });
      else if(diasCI <= 3 && diasCI > 1) notifs.push({ id:makeId(), ts:nowISO(), empresa:"quinta",
        titulo:`🧹 Limpeza em ${diasCI} dias`,
        detalhe:`${casaLabel}`, nivel:"info", lida:false, icon:"🧹" });
      return;
    }

    // Check-in
    if(diasCI === 0) notifs.push({ id:makeId(), ts:nowISO(), empresa:"quinta",
      titulo:`Check-in HOJE: ${r.guest}`,
      detalhe:`${casaLabel} · ${r.ciHora||"15:00"} · ${r.hospedes} pax`,
      nivel:"urgente", lida:false, icon:"🏠" });
    else if(diasCI === 1) notifs.push({ id:makeId(), ts:nowISO(), empresa:"quinta",
      titulo:`Check-in amanhã: ${r.guest}`,
      detalhe:`${casaLabel} · ${r.ciHora||"15:00"} — verificar casa`,
      nivel:"aviso", lida:false, icon:"🏠" });
    else if(diasCI <= 3 && diasCI > 1) notifs.push({ id:makeId(), ts:nowISO(), empresa:"quinta",
      titulo:`Check-in em ${diasCI} dias: ${r.guest}`,
      detalhe:`${casaLabel} · ${r.hospedes} pax`,
      nivel:"info", lida:false, icon:"🏠" });
    else if(diasCI <= 7 && diasCI > 3) notifs.push({ id:makeId(), ts:nowISO(), empresa:"quinta",
      titulo:`Reserva em ${diasCI} dias: ${r.guest}`,
      detalhe:`${casaLabel} · ${r.hospedes} pax · ${fmt(r.valor)}`,
      nivel:"info", lida:false, icon:"📅" });

    // Check-out (só se não é o mesmo dia que ci)
    if(diasCO === 0 && diasCI !== 0) notifs.push({ id:makeId(), ts:nowISO(), empresa:"quinta",
      titulo:`Check-out HOJE: ${r.guest}`,
      detalhe:`${casaLabel} · ${r.coHora||"11:00"} · Limpeza a seguir`,
      nivel:"aviso", lida:false, icon:"🧹" });
    else if(diasCO === 1 && diasCI !== 1) notifs.push({ id:makeId(), ts:nowISO(), empresa:"quinta",
      titulo:`Check-out amanhã: ${r.guest}`,
      detalhe:`${casaLabel} · ${r.coHora||"11:00"}`,
      nivel:"info", lida:false, icon:"🏠" });
  });

  // HW3D — deadlines
  hw3dProjects.forEach(p => {
    if(!p.dataFim || p.etapa === "Concluído") return;
    const prazo = new Date(p.dataFim);
    const dias = Math.ceil((prazo.getTime() - hoje.getTime()) / 86400000);
    if(dias < 0) {
      notifs.push({
        id: makeId(), ts: nowISO(), empresa:"hw3d",
        titulo: "Projeto atrasado!",
        detalhe: `${p.titulo} · ${Math.abs(dias)} dias de atraso`,
        nivel: "urgente", lida: false, icon:"🧊"
      });
    } else if(dias <= 3) {
      notifs.push({
        id: makeId(), ts: nowISO(), empresa:"hw3d",
        titulo: `Deadline em ${dias} dias`,
        detalhe: `${p.titulo}`,
        nivel: "aviso", lida: false, icon:"⏰"
      });
    }
  });

  // Holdings — impostos a vencer
  impostos.forEach((imp: any) => {
    if(imp.pago || !imp.data) return;
    const vence = new Date(imp.data);
    const dias = Math.ceil((vence.getTime() - hoje.getTime()) / 86400000);
    if(dias <= 7 && dias >= 0) {
      notifs.push({
        id: makeId(), ts: nowISO(), empresa:"holdings",
        titulo: `${imp.tipo} vence em ${dias} dias`,
        detalhe: `Valor: ${fmt(imp.valor || 0)} · Data: ${imp.data}`,
        nivel: dias <= 3 ? "urgente" : "aviso", lida: false, icon:"🏦"
      });
    }
  });

  return notifs;
}

/* ─── NOTIFICATION BELL ────────────────────────────────────────────────────── */
function NotifBell({
  vjEvents, alRes, hw3dProjects, impostos
}: {
  vjEvents: any[]; alRes: any[]; hw3dProjects: any[]; impostos: any[];
}) {
  const [open, setOpen] = useState(false);
  const [lidas, setLidas] = useState<Set<string>>(new Set());
  const ref = useRef<HTMLDivElement>(null);

  // Notificações persistentes da BD (ex: sync Groovy)
  const { data: dbNotifs, update: updateNotif } = useKairosNotificacoes();
  const dbNotifsList = (dbNotifs||[]).filter((n:any)=>!n.lida).map((n:any)=>({
    id: `db_${n.id}`,
    dbId: n.id,
    nivel: "info" as const,
    icon: n.titulo?.split(" ")[0] || "🔔",
    titulo: n.titulo,
    detalhe: n.mensagem,
    fromDB: true,
  }));

  const dismissDB = async (dbId:string) => {
    if(!isDemoAccount()) await updateNotif(dbId, { lida: true });
  };

  const notifsBase = generateNotifs(vjEvents, alRes, hw3dProjects, impostos);
  const notifs = [...dbNotifsList, ...notifsBase];
  const activas = notifs.filter(n => !lidas.has(n.id));
  const urgentes = activas.filter(n => n.nivel === "urgente").length;
  const count = activas.length;

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if(ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const nivelColor = { urgente: T.red, aviso: T.amber, info: T.blueL };
  const nivelBg    = { urgente: `${T.red}14`, aviso: `${T.amber}12`, info: `${T.blueL}10` };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          position: "relative",
          background: open ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
          border: `1px solid ${open ? T.borderHi : T.border}`,
          borderRadius: 10, width: 36, height: 36,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", fontSize: 16, transition: "all 0.15s",
          color: urgentes > 0 ? T.red : count > 0 ? T.amber : T.muted,
        }}>
        🔔
        {count > 0 && (
          <div style={{
            position: "absolute", top: -4, right: -4,
            width: 18, height: 18, borderRadius: "50%",
            background: urgentes > 0 ? T.red : T.amber,
            color: "white", fontSize: 11, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: `2px solid ${T.bg}`,
            boxShadow: `0 2px 6px ${urgentes > 0 ? T.red : T.amber}60`,
          }}>{count > 9 ? "9+" : count}</div>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 10px)",
          width: 340, maxHeight: 480, overflowY: "auto",
          background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: 14, boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
          zIndex: 999, animation: "loginFade 0.15s ease forwards",
        }}>
          <div style={{
            padding: "14px 16px 10px", borderBottom: `1px solid ${T.border}`,
            display: "flex", justifyContent: "space-between", alignItems: "center",
            position: "sticky", top: 0, background: T.surface, zIndex: 1,
          }}>
            <div>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Notificações</span>
              {count > 0 && <span style={{ fontSize: 11, color: T.amber, marginLeft: 8 }}>{count} novas</span>}
            </div>
            {count > 0 && (
              <button onClick={() => setLidas(new Set(notifs.map((n:any) => n.id)))}
                style={{ fontSize: 11, color: T.blueL, background: "none", border: "none", cursor: "pointer" }}>
                Marcar todas como lidas
              </button>
            )}
          </div>

          {notifs.length === 0 ? (
            <div style={{ padding: "32px 20px", textAlign: "center", color: T.muted, fontSize: 13 }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
              Tudo em dia — sem alertas
            </div>
          ) : (
            notifs.map(n => {
              const isLida = lidas.has(n.id);
              const cor = nivelColor[n.nivel];
              return (
                <div key={n.id} style={{
                  padding: "12px 16px",
                  borderBottom: `1px solid ${T.border}`,
                  background: isLida ? "transparent" : nivelBg[n.nivel],
                  opacity: isLida ? 0.5 : 1,
                  transition: "all 0.2s",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setLidas(new Set([...Array.from(lidas), n.id]));
                  if((n as any).fromDB && (n as any).dbId) dismissDB((n as any).dbId);
                }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                      background: `${cor}18`, border: `1px solid ${cor}30`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 15,
                    }}>{n.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{n.titulo}</span>
                        <span style={{ fontSize: 11, color: T.muted, flexShrink: 0 }}>{(n as any).ts?relTime((n as any).ts):""}</span>
                      </div>
                      <div style={{ fontSize: 11, color: T.textSub, marginTop: 3, lineHeight: 1.4 }}>{n.detalhe}</div>
                      <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems:"center" }}>
                        {(n as any).empresa && <Tag label={COMPANY_NAMES[(n as any).empresa] || (n as any).empresa} color={cor}/>}
                        <Tag label={n.nivel} color={cor}/>
                        {(n as any).fromDB && (
                          <span style={{fontSize:10,color:T.muted,marginLeft:"auto"}}>toca para dispensar ✕</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

/* ─── LOG VIEW ─────────────────────────────────────────────────────────────── */
function LogView({ logs, empresa }: { logs: LogEntry[]; empresa: string }) {
  const filtered = empresa === "holdings"
    ? logs
    : logs.filter(l => l.empresa === empresa);

  const tipos = Array.from(new Set(filtered.map((l:any) => l.tipo)));
  const [tipoF, setTipoF] = useState("todos");
  const [search, setSearch] = useState("");

  const visible = filtered
    .filter(l => tipoF === "todos" || l.tipo === tipoF)
    .filter(l => !search || l.descricao.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b) => b.ts.localeCompare(a.ts))
    .slice(0, 100);

  const tipoColor: Record<string,string> = {
    reserva: T.amber, evento: T.purple, despesa: T.red, tarefa: T.teal,
    projeto: T.blueL, sistema: T.muted, limpeza: T.teal, pagamento: T.green,
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:12 }}>
        <KPI l="Total Entradas" v={filtered.length} c="" ac={T.blueL}/>
        <KPI l="Hoje"     v={filtered.filter(l=>l.ts.startsWith(new Date().toISOString().slice(0,10))).length} c="" ac={T.blueL}/>
        <KPI l="Esta Semana" v={filtered.filter(l=>{const d=new Date(l.ts);return Date.now()-d.getTime()<7*86400000;}).length} c="" ac={T.blueL}/>
        {empresa==="holdings" && <KPI l="Empresas" v={new Set(filtered.map(l=>l.empresa)).size} c="" ac={T.blueL}/>}
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap" as any }}>
        <input
          placeholder="Pesquisar..."
          value={search}
          onChange={(e:any)=>setSearch(e.target.value)}
          style={{
            padding:"7px 12px", borderRadius:8, background:"rgba(255,255,255,0.04)",
            border:`1px solid ${T.border}`, color:T.text, fontSize:12, width:200,
          }}
        />
        {["todos",...tipos].map(t=>(
          <button key={t} onClick={()=>setTipoF(t)} style={{
            padding:"10px 14px", borderRadius:6, border:"none", cursor:"pointer",
            fontSize:11, fontWeight:600,
            background:tipoF===t?`${tipoColor[t]||T.blueL}22`:"rgba(255,255,255,0.04)",
            color:tipoF===t?tipoColor[t]||T.blueL:T.muted,
          }}>{t}</button>
        ))}
      </div>

      <Card title={empresa==="holdings"?"Log Global — Todas as Empresas":`Log — ${COMPANY_NAMES[empresa]||empresa}`} accent={T.blueL}>
        {visible.length === 0 ? (
          <Empty icon="📋" msg="Sem entradas no log" sub="As acções na app aparecem aqui automaticamente"/>
        ) : (
          visible.map(l => {
            const cor = tipoColor[l.tipo] || T.muted;
            const empIcon = COMPANY_ICONS[l.empresa] || "⚡";
            return (
              <div key={l.id} style={{
                display:"flex", gap:12, padding:"10px 0",
                borderBottom:`1px solid rgba(255,255,255,0.04)`,
                alignItems:"flex-start",
              }}>
                <div style={{
                  width:30, height:30, borderRadius:8, flexShrink:0,
                  background:`${cor}14`, border:`1px solid ${cor}25`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:13,
                }}>{l.icon}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, color:T.text, fontWeight:500, lineHeight:1.4 }}>{l.descricao}</div>
                  <div style={{ display:"flex", gap:8, marginTop:5, flexWrap:"wrap" as any, alignItems:"center" }}>
                    {empresa==="holdings" && <Tag label={`${empIcon} ${COMPANY_NAMES[l.empresa]||l.empresa}`} color={T.muted}/>}
                    <Tag label={l.tipo} color={cor}/>
                    {l.valor !== undefined && l.valor > 0 && (
                      <span style={{ fontSize:11, fontWeight:700, color:T.amber, fontFamily:"monospace" }}>{fmt(l.valor)}</span>
                    )}
                    <span style={{ fontSize:11, color:T.muted, marginLeft:"auto" }}>
                      {new Date(l.ts).toLocaleString("pt-PT",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </Card>
    </div>
  );
}

// Hook para gerir logs globais
function useLogs(initial: LogEntry[] = []) {
  const [logs, setLogs] = useState<LogEntry[]>(initial);
  const addLog = async (empresa: string, tipo: string, descricao: string, icon: string, valor?: number) => {
    const entry: LogEntry = {
      id: makeId(), ts: nowISO(), empresa, tipo, descricao, icon,
      ...(valor !== undefined ? {valor} : {}),
    };
    setLogs(prev => [entry, ...prev].slice(0, 500));
    // Gravar no Supabase
    if (!isDemoAccount()) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.id) {
          await supabase.from("logs").insert({
            user_id: user.id,
            empresa, tipo, descricao, icon,
            valor: valor || 0,
            meta: {},
          });
        }
      } catch(e) {
        console.error("Erro ao gravar log:", e);
      }
    }
  };
  return { logs, addLog };
}

// Seed com alguns logs de exemplo
const SEED_LOGS: LogEntry[] = [];

/* ─── LOGIN PAGE ─────────────────────────────────────────────────────────────── */
function LoginPage({ onLogin }: { onLogin: (user: string, email: string) => void }) {
  const [mode, setMode]         = useState<"login"|"register">("login");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [name, setName]         = useState("");
  const [error, setError]       = useState("");
  const [info, setInfo]         = useState("");
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async () => {
    if (!email || !password) { setError("Preenche email e password."); return; }
    if (mode === "register" && !name) { setError("Introduz o teu nome."); return; }
    setLoading(true);
    setError("");
    setInfo("");

    if (mode === "register") {
      const { error: err } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: name } }
      });
      if (err) {
        setError(err.message);
        setLoading(false);
      } else {
        setInfo("✅ Conta criada! Verifica o teu email para confirmar.");
        setLoading(false);
      }
    } else {
      const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) {
        setError(err.message === "Invalid login credentials"
          ? "Email ou password incorrectos."
          : err.message);
        setLoading(false);
      } else if (data.user) {
        const displayName = data.user.user_metadata?.full_name
          || data.user.email?.split("@")[0]
          || "Utilizador";
        localStorage.setItem("kairos_user", displayName);
        localStorage.setItem("kairos_uid", data.user.id);
        localStorage.setItem("kairos_user_email", data.user.email || "");
        // Verificar onboarding APENAS via Supabase user_metadata
        // Nunca usar localStorage como fallback — pode pertencer a conta anterior
        const alreadyOnboarded = data.user.user_metadata?.kairos_onboarded === true;
        if (!alreadyOnboarded && data.user.email !== DEMO_EMAIL) {
          // Limpar resíduos de conta anterior e mostrar onboarding
          localStorage.removeItem("kairos_onboarded");
          localStorage.removeItem("kairos_biz_types");
          localStorage.removeItem("kairos_holding_name");
          localStorage.setItem("kairos_show_onboarding", "1");
        } else {
          localStorage.setItem("kairos_onboarded", "1");
        }
        onLogin(displayName, data.user.email || "");
      }
    }
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", background: T.bg,
      fontFamily: "'DM Sans', system-ui, sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        input { font-family: inherit; }
        @keyframes loginFade { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
      {/* Background grid */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 0,
        backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59,157,232,0.06) 0%, transparent 50%),
          radial-gradient(circle at 75% 75%, rgba(139,92,246,0.06) 0%, transparent 50%)`,
        pointerEvents: "none",
      }}/>
      <div style={{
        width: "100%", maxWidth: 420, padding: "0 20px",
        animation: "loginFade 0.4s ease forwards", position: "relative", zIndex: 1,
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 14,
            background: "white", display: "inline-flex",
            alignItems: "center", justifyContent: "center",
            marginBottom: 16, boxShadow: `0 8px 32px rgba(59,157,232,0.3)`,
          }}>
            <img src={LOGO_SRC} alt="Kairos" style={{ width: 48, height: 48, objectFit: "contain" }}/>
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text, letterSpacing: "-0.03em" }}>KAIROS OS</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 4, letterSpacing: "0.1em" }}>HOLDING SYSTEM</div>
        </div>

        {/* Card */}
        <div style={{
          background: T.panel, border: `1px solid ${T.border}`,
          borderRadius: 14, padding: 32, backdropFilter: "blur(20px)",
        }}>
          {/* Mode tabs */}
          <div style={{display:"flex",gap:0,marginBottom:24,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`}}>
            {(["login","register"] as const).map(m=>(
              <button key={m} onClick={()=>{setMode(m);setError("");setInfo("");}}
                style={{flex:1,padding:"10px",border:"none",cursor:"pointer",fontSize:13,fontWeight:600,
                  background:mode===m?T.blueL:"transparent",
                  color:mode===m?"white":T.muted,transition:"all 0.15s"}}>
                {m==="login"?"Entrar":"Criar Conta"}
              </button>
            ))}
          </div>

          {mode==="register" && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: T.muted, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}>Nome</div>
              <input type="text" placeholder="O teu nome" value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSubmit()}
                style={{ width:"100%", padding:"11px 14px", borderRadius:10,
                  background:"rgba(255,255,255,0.05)", border:`1px solid ${T.border}`,
                  color:T.text, fontSize:14, outline:"none", transition:"border-color 0.2s"}}
                onFocus={e=>e.target.style.borderColor=T.borderFocus}
                onBlur={e=>e.target.style.borderColor=T.border}/>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: T.muted, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}>Email</div>
            <input type="email" placeholder="nuno@kairos.pt" value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
              style={{ width:"100%", padding:"11px 14px", borderRadius:10,
                background:"rgba(255,255,255,0.05)", border:`1px solid ${T.border}`,
                color:T.text, fontSize:14, outline:"none", transition:"border-color 0.2s"}}
              onFocus={e=>e.target.style.borderColor=T.borderFocus}
              onBlur={e=>e.target.style.borderColor=T.border}/>
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}>Password</div>
              {mode==="login"&&(
                <button onClick={async()=>{
                  if(!email){setError("Introduz o teu email primeiro.");return;}
                  const {error:err} = await supabase.auth.resetPasswordForEmail(email,{redirectTo: "https://app.kairosholdings.xyz"});
                  if(err) setError(err.message);
                  else setInfo("✅ Email de recuperação enviado. Verifica a tua caixa de entrada.");
                }} style={{fontSize:11,color:T.blueL,background:"none",border:"none",cursor:"pointer",padding:0}}>
                  Esqueci a password
                </button>
              )}
            </div>
            <input type="password" placeholder="••••••••" value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
              style={{ width:"100%", padding:"11px 14px", borderRadius:10,
                background:"rgba(255,255,255,0.05)", border:`1px solid ${T.border}`,
                color:T.text, fontSize:14, outline:"none", transition:"border-color 0.2s"}}
              onFocus={e=>e.target.style.borderColor=T.borderFocus}
              onBlur={e=>e.target.style.borderColor=T.border}/>
          </div>

          {error && (
            <div style={{padding:"10px 14px",borderRadius:10,marginBottom:16,
              background:`${T.red}15`,border:`1px solid ${T.red}30`,
              fontSize:13,color:T.redL}}>{error}</div>
          )}
          {info && (
            <div style={{padding:"10px 14px",borderRadius:10,marginBottom:16,
              background:`${T.green}12`,border:`1px solid ${T.green}30`,
              fontSize:13,color:T.greenL}}>{info}</div>
          )}

          <button onClick={handleSubmit} disabled={loading}
            style={{width:"100%",padding:"13px",borderRadius:10,border:"none",
              background:loading?"rgba(59,157,232,0.3)":`linear-gradient(135deg,${T.blue},${T.blueL})`,
              color:"white",fontSize:14,fontWeight:700,
              cursor:loading?"not-allowed":"pointer",
              boxShadow:loading?"none":`0 4px 16px ${T.blueL}40`,
              transition:"all 0.2s",letterSpacing:"0.01em"}}>
            {loading?(mode==="login"?"A entrar...":"A criar conta..."):(mode==="login"?"Entrar":"Criar Conta")}
          </button>
        </div>

        {/* Demo button */}
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 10 }}>— ou —</div>
          <button
            onClick={async () => {
              setLoading(true);
              const { data, error: err } = await supabase.auth.signInWithPassword({
                email: DEMO_EMAIL,
                password: "KairosDemo2026!"
              });
              if (err) {
                // fallback: demo sem Supabase
                localStorage.setItem("kairos_user", "Demo");
                localStorage.setItem("kairos_user_email", DEMO_EMAIL);
                onLogin("Demo", DEMO_EMAIL);
              } else if (data.user) {
                localStorage.setItem("kairos_user", "Demo");
                localStorage.setItem("kairos_uid", data.user.id);
                localStorage.setItem("kairos_user_email", DEMO_EMAIL);
                onLogin("Demo", DEMO_EMAIL);
              }
              setLoading(false);
            }}
            style={{
              width: "100%", padding: "11px", borderRadius: 10, border: `1px solid ${T.border}`,
              background: "rgba(255,255,255,0.04)", color: T.textSub,
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={e => { (e.target as any).style.background = "rgba(255,255,255,0.08)"; }}
            onMouseLeave={e => { (e.target as any).style.background = "rgba(255,255,255,0.04)"; }}>
            🎭 Ver Demonstração
          </button>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 8 }}>
            Explora a app com dados de exemplo
          </div>
        </div>

        <div style={{ textAlign: "center", marginTop: 16, fontSize: 11, color: T.muted }}>
          Kairos OS v{APP_VERSION} · Powered by Supabase
        </div>
      </div>
    </div>
  );
}

/* ─── USER DROPDOWN ──────────────────────────────────────────────────────────── */
function UserMenu({ user, onLogout, themeKey, toggleTheme, lang, cycleLang }: {
  user: string; onLogout: () => void;
  themeKey?: string; toggleTheme?: ()=>void;
  lang?: string; cycleLang?: ()=>void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const initials = user.slice(0,1).toUpperCase();
  const langNames: Record<string,string> = {pt:"Português",en:"English",es:"Español"};
  const langFlag: Record<string,string> = {pt:"🇵🇹",en:"🇬🇧",es:"🇪🇸"};

  const openSettings = (page: string) => {
    setOpen(false);
    setTimeout(()=>window.dispatchEvent(new CustomEvent("kairos-settings",{detail:{page}})), 150);
  };

  const sections = [
    {
      items: [
        { icon:"👤", label:"Perfil & Conta",    sub:"Editar dados pessoais",       action:()=>openSettings("perfil") },
        { icon:"🏢", label:"Gerir Empresas",    sub:"Módulos activos",              action:()=>openSettings("empresas") },
        { icon:"💳", label:"Subscrição",         sub:"Plano e faturação",            action:()=>openSettings("subscricao") },
      ]
    },
    {
      items: [
        { icon:"🔗", label:"Integrações",        sub:"iCal, Telegram, Google Cal",  action:()=>openSettings("integrations") },
        { icon:"👥", label:"Equipa & Acessos",  sub:"Convidar colaboradores",       action:()=>openSettings("equipa") },
        { icon:"🔔", label:"Notificações",       sub:"Telegram, email, push",       action:()=>openSettings("notificacoes") },
        { icon:"⚙️", label:"Configurações",      sub:"App e preferências",          action:()=>openSettings("config") },
      ]
    },
    {
      items: [
        {
          icon: langFlag[lang||"pt"],
          label: `Idioma: ${langNames[lang||"pt"]}`,
          sub: "Clica para mudar",
          action: () => { cycleLang?.(); }
        },
        {
          icon: themeKey==="dark"?"🌙":"☀️",
          label: themeKey==="dark"?"Tema Escuro":"Tema Claro",
          sub: "Clica para mudar",
          action: () => { toggleTheme?.(); }
        },
      ]
    },
    {
      items: [
        { icon:"❓", label:"Ajuda & Suporte",   sub:"Documentação e contacto",     action:()=>openSettings("ajuda") },
        { icon:"📧", label:"Enviar Feedback",   sub:"Sugestões e bugs",            action:()=>openSettings("feedback") },
        { icon:"🔒", label:"Privacidade",        sub:"Dados e acesso suporte",      action:()=>openSettings("privacidade") },
      ]
    },
    {
      danger: true,
      items: [
        { icon:"🚪", label:"Terminar Sessão", action:onLogout, danger:true },
      ]
    },
  ];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex", alignItems: "center", gap: 7,
          padding: "5px 11px 5px 7px", borderRadius: 99,
          background: open ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
          border: `1px solid ${open ? T.borderHi : T.border}`,
          cursor: "pointer", transition: "all 0.15s",
        }}>
        <div style={{
          width: 26, height: 26, borderRadius: "50%",
          background: `linear-gradient(135deg, ${T.blueDark}, ${T.blueL})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 800, color: "white",
          boxShadow: `0 2px 8px ${T.blueL}40`, flexShrink: 0,
        }}>{initials}</div>
        <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{user}</span>
        <span style={{ fontSize: 11, color: T.muted, marginLeft: 2 }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 8px)",
          width: 260, background: T.surface,
          border: `1px solid ${T.border}`, borderRadius: 14,
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          zIndex: 999, overflow: "hidden",
          animation: "loginFade 0.15s ease forwards",
        }}>
          {/* User header */}
          <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border}`,
            background:"rgba(255,255,255,0.02)" }}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:36,height:36,borderRadius:"50%",flexShrink:0,
                background:`linear-gradient(135deg,${T.blueDark},${T.blueL})`,
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:15,fontWeight:800,color:"white"}}>{initials}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{user}</div>
                <div style={{ fontSize: 11, color: T.muted }}>Kairos Holdings · Admin</div>
              </div>
            </div>
          </div>

          {/* Sections */}
          {sections.map((section, si) => (
            <div key={si}>
              {si > 0 && <div style={{ height: 1, background: T.border, margin: "4px 0" }}/>}
              {section.items.map((item: any, i) => (
                <button key={i} onClick={item.action} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  width: "100%", padding: "9px 16px", border: "none",
                  background: "transparent",
                  color: item.danger ? T.redL : T.text,
                  fontSize: 13, cursor: "pointer", textAlign: "left",
                  transition: "background 0.12s",
                }}
                onMouseEnter={e => (e.currentTarget.style.background = item.danger ? `${T.red}12` : "rgba(255,255,255,0.04)")}
                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                  <span style={{ fontSize: 16, flexShrink:0 }}>{item.icon}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:item.danger?600:500}}>{item.label}</div>
                    {item.sub&&<div style={{fontSize:11,color:T.muted,marginTop:1}}>{item.sub}</div>}
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── HOME BASE ─────────────────────────────────────────────────────────────── */

// ─── HB: Poupanças & Dívidas ─────────────────────────────────────────────────
function HBPoupancas() {
  const HB = "#6366f1";
  const [tab, setTab] = useState<"patrimonio"|"dividas">("patrimonio");

  // ── ESTADO: Património (onde está o dinheiro) ──
  const { data: patrimoniosDB, insert: insertPatrimonio, update: updatePatrimonio, remove: removePatrimonio } = useHBPatrimonios();
  const setPatrimonios = (_:any) => {};
  // patrimonios alias definido depois do demo state
  const [patrimoniosDemo] = useState<any[]>([
    {id:1, nome:"Conta Poupança CGD", tipo:"banco",    valor:0, icone:"🏦", notas:""},
    {id:2, nome:"Dinheiro físico",     tipo:"cash",    valor:0, icone:"💵", notas:"Em casa"},
    {id:3, nome:"Mãe",                 tipo:"pessoa",  valor:0, icone:"👩", notas:"Dinheiro guardado"},
    {id:4, nome:"Binance",             tipo:"cripto",  valor:0, icone:"₿",  notas:"BTC + ETH"},
  ]);
  const [addingPat, setAddingPat] = useState(false);
  const [newPat, setNewPat] = useState({nome:"",tipo:"banco",valor:0,icone:"🏦",notas:""});
  // Modal de movimento (depósito/levantamento) em item de património
  const [movPat, setMovPat] = useState<any>(null);
  const [newMov, setNewMov] = useState({tipo:"deposito",valor:0,notas:"",data:new Date().toISOString().split("T")[0]});

  // ── ESTADO: Dívidas ──
  const [dividas, setDividas] = useState<any[]>([
    {id:1, quem:"Avô",   direcao:"eu_devo",   total:0, pago:0, notas:"Empréstimo pessoal", historico:[]},
    {id:2, quem:"Irmão", direcao:"deve_me",   total:0, pago:0, notas:"",                  historico:[]},
  ]);
  const [addingDiv, setAddingDiv] = useState(false);
  const [newDiv, setNewDiv] = useState({quem:"",direcao:"eu_devo",total:0,notas:""});
  const [pagDiv, setPagDiv] = useState<any>(null);
  const [newPag, setNewPag] = useState({valor:0,notas:"",data:new Date().toISOString().split("T")[0]});

  // ── CÁLCULOS ──
  const patrimonios = isDemoAccount() ? patrimoniosDemo : (patrimoniosDB || []);
  const totalPatrimonio  = patrimonios.reduce((s:number,p:any)=>s+(p.valor||0),0);
  const totalDevoEu      = dividas.filter((d:any)=>d.direcao==="eu_devo").reduce((s:number,d:any)=>s+((d.total||0)-(d.pago||0)),0);
  const totalDevemMe     = dividas.filter((d:any)=>d.direcao==="deve_me").reduce((s:number,d:any)=>s+((d.total||0)-(d.pago||0)),0);
  const saldoReal        = totalPatrimonio - totalDevoEu + totalDevemMe;

  const tiposPat: Record<string,string> = {banco:"Banco",cash:"Dinheiro",pessoa:"Pessoa",cripto:"Cripto",acoes:"Acções",outro:"Outro"};
  const iconesPat = ["🏦","💵","👩","👨","₿","📈","💳","🏠","🐷"];

  // ── ACÇÕES: Património ──
  const addPatrimonio = () => {
    if(!newPat.nome) return;
    setPatrimonios((ps:any)=>[...ps,{...newPat,id:ps.length+1,valor:+newPat.valor,movimentos:[]}]);
    setAddingPat(false);
    setNewPat({nome:"",tipo:"banco",valor:0,icone:"🏦",notas:""});
  };
  const registarMovimento = () => {
    if(!newMov.valor||!movPat) return;
    const delta = newMov.tipo==="deposito" ? +newMov.valor : -(+newMov.valor);
    setPatrimonios((ps:any)=>ps.map((p:any)=>p.id===movPat.id
      ? {...p, valor:Math.max(0,p.valor+delta),
          movimentos:[...(p.movimentos||[]),{...newMov,valor:+newMov.valor,delta,id:Date.now()}]}
      : p));
    setMovPat(null);
    setNewMov({tipo:"deposito",valor:0,notas:"",data:new Date().toISOString().split("T")[0]});
  };

  // ── ACÇÕES: Dívidas ──
  const addDivida = () => {
    if(!newDiv.quem||!newDiv.total) return;
    setDividas((ds:any)=>[...ds,{...newDiv,id:ds.length+1,total:+newDiv.total,pago:0,historico:[]}]);
    setAddingDiv(false);
    setNewDiv({quem:"",direcao:"eu_devo",total:0,notas:""});
  };
  const registarPagamento = () => {
    if(!newPag.valor||!pagDiv) return;
    setDividas((ds:any)=>ds.map((d:any)=>d.id===pagDiv.id
      ? {...d, pago:Math.min(d.total, d.pago+(+newPag.valor)),
          historico:[...d.historico,{...newPag,valor:+newPag.valor,id:Date.now()}]}
      : d));
    setPagDiv(null);
    setNewPag({valor:0,notas:"",data:new Date().toISOString().split("T")[0]});
  };

  const inputStyle = {width:"100%",padding:"8px 10px",borderRadius:10,background:T.surface,
    border:`1px solid ${T.border}`,color:T.text,fontSize:13};
  const labelStyle = {fontSize:11,color:T.muted,marginBottom:4,fontWeight:700 as any,textTransform:"uppercase" as any};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>

      {/* ── KPIs no topo ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12}}>
        <KPI l="Património Total" v={fmt(totalPatrimonio)} c="onde está o teu dinheiro" ac={HB}/>
        <KPI l="Total que Deves" v={fmt(totalDevoEu)} c="dívidas activas" ac={T.red}/>
        <KPI l="Total que te Devem" v={fmt(totalDevemMe)} c="a receber" ac={T.green}/>
      </div>

      {/* ── Saldo real (referência discreta) ── */}
      <div style={{padding:"10px 16px",borderRadius:10,background:T.panel,border:`1px solid ${T.border}`,
        display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:12,color:T.muted}}>💡 Se liquidares tudo hoje:</span>
        <span style={{fontSize:15,fontWeight:700,color:saldoReal>=0?T.green:T.red,fontFamily:"monospace"}}>
          {fmt(saldoReal)}
        </span>
      </div>

      {/* ── Tabs ── */}
      <div style={{display:"flex",gap:6}}>
        {(["patrimonio","dividas"] as const).map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{
            padding:"9px 20px",borderRadius:10,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,
            background:tab===t?HB:"transparent",
            color:tab===t?"white":T.muted,
            transition:"all 0.15s",
          }}>
            {t==="patrimonio"?"🏦 Património":"💸 Dívidas"}
          </button>
        ))}
      </div>

      {/* ══ TAB: PATRIMÓNIO ══ */}
      {tab==="patrimonio" && (
        <Card title="Onde está o teu dinheiro" accent={HB} action="+ Novo local" onAction={()=>setAddingPat(true)}>

          {addingPat && (
            <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,
              display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:10}}>
              <div>
                <div style={labelStyle}>Nome *</div>
                <input value={newPat.nome} onChange={(e:any)=>setNewPat({...newPat,nome:e.target.value})}
                  placeholder="Ex: Conta CGD, Mãe..." style={inputStyle}/>
              </div>
              <div>
                <div style={labelStyle}>Tipo</div>
                <select value={newPat.tipo} onChange={(e:any)=>setNewPat({...newPat,tipo:e.target.value})} style={inputStyle}>
                  {Object.entries(tiposPat).map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <div style={labelStyle}>Ícone</div>
                <select value={newPat.icone} onChange={(e:any)=>setNewPat({...newPat,icone:e.target.value})} style={inputStyle}>
                  {iconesPat.map(ic=><option key={ic} value={ic}>{ic}</option>)}
                </select>
              </div>
              <div>
                <div style={labelStyle}>Valor actual (€)</div>
                <input type="number" value={newPat.valor} onChange={(e:any)=>setNewPat({...newPat,valor:+e.target.value})} style={inputStyle}/>
              </div>
              <div>
                <div style={labelStyle}>Notas</div>
                <input value={newPat.notas} onChange={(e:any)=>setNewPat({...newPat,notas:e.target.value})} style={inputStyle}/>
              </div>
              <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
                <button onClick={addPatrimonio} style={{padding:"9px 18px",borderRadius:10,border:"none",
                  background:HB,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
                <button onClick={()=>setAddingPat(false)} style={{padding:"9px 12px",borderRadius:10,
                  border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}}>✕</button>
              </div>
            </div>
          )}

          {/* Modal: registo de movimento */}
          {movPat && (
            <div style={{padding:14,borderRadius:10,background:`${HB}12`,border:`1px solid ${HB}40`,marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>
                {movPat.icone} {movPat.nome} — Registar movimento
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10}}>
                <div>
                  <div style={labelStyle}>Tipo</div>
                  <select value={newMov.tipo} onChange={(e:any)=>setNewMov({...newMov,tipo:e.target.value})} style={inputStyle}>
                    <option value="deposito">+ Depositar / Adicionar</option>
                    <option value="levantamento">− Levantar / Retirar</option>
                  </select>
                </div>
                <div>
                  <div style={labelStyle}>Valor (€)</div>
                  <input type="number" value={newMov.valor} onChange={(e:any)=>setNewMov({...newMov,valor:+e.target.value})} style={inputStyle}/>
                </div>
                <div>
                  <div style={labelStyle}>Data</div>
                  <input type="date" value={newMov.data} onChange={(e:any)=>setNewMov({...newMov,data:e.target.value})} style={inputStyle}/>
                </div>
                <div>
                  <div style={labelStyle}>Notas</div>
                  <input value={newMov.notas} onChange={(e:any)=>setNewMov({...newMov,notas:e.target.value})}
                    placeholder="Ex: Transferi em Abril" style={inputStyle}/>
                </div>
                <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
                  <button onClick={registarMovimento} style={{padding:"9px 18px",borderRadius:10,border:"none",
                    background:newMov.tipo==="deposito"?T.green:T.red,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>
                    {newMov.tipo==="deposito"?"+ Adicionar":"− Retirar"}
                  </button>
                  <button onClick={()=>setMovPat(null)} style={{padding:"9px 12px",borderRadius:10,
                    border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}}>✕</button>
                </div>
              </div>
            </div>
          )}

          {patrimonios.length===0 && <Empty icon="🏦" msg="Sem locais de poupança" sub="Adiciona onde está o teu dinheiro"/>}

          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {patrimonios.map((p:any)=>(
              <div key={p.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",
                borderRadius:10,background:T.panel,border:`1px solid ${T.border}`}}>
                <div style={{width:40,height:40,borderRadius:10,background:`${HB}18`,flexShrink:0,
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>
                  {p.icone}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:T.text}}>{p.nome}</div>
                  <div style={{fontSize:11,color:T.muted}}>{tiposPat[p.tipo]||p.tipo}{p.notas?` · ${p.notas}`:""}</div>
                  {(p.movimentos||[]).length>0&&(
                    <div style={{fontSize:11,color:T.muted,marginTop:2}}>
                      {(p.movimentos||[]).length} movimento(s) · último: {(p.movimentos||[]).slice(-1)[0]?.data}
                    </div>
                  )}
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:16,fontWeight:700,color:HB,fontFamily:"monospace"}}>{fmt(p.valor||0)}</div>
                  <button onClick={()=>{setMovPat(p);setNewMov({tipo:"deposito",valor:0,notas:"",data:new Date().toISOString().split("T")[0]});}}
                    style={{marginTop:4,padding:"4px 10px",borderRadius:6,border:`1px solid ${HB}`,
                      background:"transparent",color:HB,cursor:"pointer",fontSize:11,fontWeight:600}}>
                    ± Movimento
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          {patrimonios.length>0&&(
            <div style={{marginTop:12,padding:"10px 14px",borderRadius:10,background:`${HB}10`,
              border:`1px solid ${HB}30`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:12,fontWeight:700,color:HB,textTransform:"uppercase",letterSpacing:"0.05em"}}>Total Património</span>
              <span style={{fontSize:18,fontWeight:800,color:HB,fontFamily:"monospace"}}>{fmt(totalPatrimonio)}</span>
            </div>
          )}
        </Card>
      )}

      {/* ══ TAB: DÍVIDAS ══ */}
      {tab==="dividas" && (
        <Card title="Dívidas — O que deves e o que te devem" accent={T.red} action="+ Nova dívida" onAction={()=>setAddingDiv(true)}>

          {addingDiv && (
            <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,
              display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:10}}>
              <div>
                <div style={labelStyle}>Quem *</div>
                <input value={newDiv.quem} onChange={(e:any)=>setNewDiv({...newDiv,quem:e.target.value})}
                  placeholder="Ex: Avô, Irmão..." style={inputStyle}/>
              </div>
              <div>
                <div style={labelStyle}>Direcção</div>
                <select value={newDiv.direcao} onChange={(e:any)=>setNewDiv({...newDiv,direcao:e.target.value})} style={inputStyle}>
                  <option value="eu_devo">Eu devo a esta pessoa</option>
                  <option value="deve_me">Esta pessoa deve-me</option>
                </select>
              </div>
              <div>
                <div style={labelStyle}>Valor total (€) <span style={{color:T.red}}>*</span></div>
                <input type="number" value={newDiv.total} onChange={(e:any)=>setNewDiv({...newDiv,total:+e.target.value})} style={inputStyle}/>
              </div>
              <div>
                <div style={labelStyle}>Notas</div>
                <input value={newDiv.notas} onChange={(e:any)=>setNewDiv({...newDiv,notas:e.target.value})}
                  placeholder="Ex: Empréstimo Dezembro 2024" style={inputStyle}/>
              </div>
              <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
                <button onClick={addDivida} style={{padding:"9px 18px",borderRadius:10,border:"none",
                  background:T.red,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
                <button onClick={()=>setAddingDiv(false)} style={{padding:"9px 12px",borderRadius:10,
                  border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}}>✕</button>
              </div>
            </div>
          )}

          {/* Modal: registar pagamento */}
          {pagDiv && (
            <div style={{padding:14,borderRadius:10,background:pagDiv.direcao==="eu_devo"?`${T.red}10`:`${T.green}10`,
              border:`1px solid ${pagDiv.direcao==="eu_devo"?T.red:T.green}40`,marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>
                {pagDiv.direcao==="eu_devo"?"💸 Paguei ao":"💰 Recebi de"} {pagDiv.quem} — Registar {pagDiv.direcao==="eu_devo"?"pagamento":"recebimento"}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10}}>
                <div>
                  <div style={labelStyle}>Valor (€)</div>
                  <input type="number" value={newPag.valor} onChange={(e:any)=>setNewPag({...newPag,valor:+e.target.value})} style={inputStyle}/>
                </div>
                <div>
                  <div style={labelStyle}>Data</div>
                  <input type="date" value={newPag.data} onChange={(e:any)=>setNewPag({...newPag,data:e.target.value})} style={inputStyle}/>
                </div>
                <div>
                  <div style={labelStyle}>Notas</div>
                  <input value={newPag.notas} onChange={(e:any)=>setNewPag({...newPag,notas:e.target.value})}
                    placeholder="Ex: Transferência MB Way" style={inputStyle}/>
                </div>
                <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
                  <button onClick={registarPagamento} style={{padding:"9px 18px",borderRadius:10,border:"none",
                    background:pagDiv.direcao==="eu_devo"?T.red:T.green,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>
                    {pagDiv.direcao==="eu_devo"?"✓ Registar pagamento":"✓ Registar recebimento"}
                  </button>
                  <button onClick={()=>setPagDiv(null)} style={{padding:"9px 12px",borderRadius:10,
                    border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}}>✕</button>
                </div>
              </div>
            </div>
          )}

          {dividas.length===0 && <Empty icon="💸" msg="Sem dívidas registadas" sub="Regista o que deves e o que te devem"/>}

          {/* Eu devo */}
          {dividas.filter((d:any)=>d.direcao==="eu_devo").length>0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:700,color:T.red,textTransform:"uppercase",
                letterSpacing:"0.08em",marginBottom:8,padding:"0 4px"}}>
                💸 Eu devo
              </div>
              {dividas.filter((d:any)=>d.direcao==="eu_devo").map((d:any)=>{
                const falta = (d.total||0)-(d.pago||0);
                const pct = d.total>0?Math.round(d.pago/d.total*100):0;
                const liquidada = falta<=0;
                return (
                  <div key={d.id} style={{padding:"12px 14px",borderRadius:10,background:T.panel,
                    border:`1px solid ${liquidada?T.green:T.red}30`,marginBottom:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:T.text}}>{d.quem}</div>
                        {d.notas&&<div style={{fontSize:11,color:T.muted}}>{d.notas}</div>}
                      </div>
                      <Tag label={liquidada?"Liquidada":"Em curso"} color={liquidada?T.green:T.red}/>
                      {!liquidada&&(
                        <button onClick={()=>{setPagDiv(d);setNewPag({valor:0,notas:"",data:new Date().toISOString().split("T")[0]});}}
                          style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${T.red}`,
                            background:"transparent",color:T.red,cursor:"pointer",fontSize:12,fontWeight:600,flexShrink:0}}>
                          + Paguei
                        </button>
                      )}
                    </div>
                    {/* Barra de progresso */}
                    <div style={{height:6,borderRadius:99,background:T.panel2,overflow:"hidden",marginBottom:6}}>
                      <div style={{height:"100%",width:`${pct}%`,background:liquidada?T.green:T.red,
                        borderRadius:99,transition:"width 0.4s"}}/>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.muted}}>
                      <span>Pago: <b style={{color:T.green}}>{fmt(d.pago||0)}</b></span>
                      <span>{pct}%</span>
                      <span>Falta: <b style={{color:T.red}}>{fmt(falta)}</b> / Total: {fmt(d.total||0)}</span>
                    </div>
                    {/* Histórico */}
                    {d.historico?.length>0&&(
                      <div style={{marginTop:8,padding:"6px 10px",borderRadius:8,background:T.panel2}}>
                        <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase",marginBottom:4}}>Histórico</div>
                        {[...d.historico].reverse().map((h:any)=>(
                          <div key={h.id} style={{display:"flex",justifyContent:"space-between",fontSize:11,
                            color:T.textSub,padding:"2px 0"}}>
                            <span>{h.data}</span>
                            <span>{h.notas||"—"}</span>
                            <span style={{color:T.green,fontFamily:"monospace",fontWeight:600}}>-{fmt(h.valor)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Devem-me */}
          {dividas.filter((d:any)=>d.direcao==="deve_me").length>0 && (
            <div>
              <div style={{fontSize:11,fontWeight:700,color:T.green,textTransform:"uppercase",
                letterSpacing:"0.08em",marginBottom:8,padding:"0 4px"}}>
                💰 Devem-me
              </div>
              {dividas.filter((d:any)=>d.direcao==="deve_me").map((d:any)=>{
                const falta = (d.total||0)-(d.pago||0);
                const pct = d.total>0?Math.round(d.pago/d.total*100):0;
                const liquidada = falta<=0;
                return (
                  <div key={d.id} style={{padding:"12px 14px",borderRadius:10,background:T.panel,
                    border:`1px solid ${liquidada?T.green:T.amber}30`,marginBottom:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:T.text}}>{d.quem}</div>
                        {d.notas&&<div style={{fontSize:11,color:T.muted}}>{d.notas}</div>}
                      </div>
                      <Tag label={liquidada?"Recebido":"A receber"} color={liquidada?T.green:T.amber}/>
                      {!liquidada&&(
                        <button onClick={()=>{setPagDiv(d);setNewPag({valor:0,notas:"",data:new Date().toISOString().split("T")[0]});}}
                          style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${T.green}`,
                            background:"transparent",color:T.green,cursor:"pointer",fontSize:12,fontWeight:600,flexShrink:0}}>
                          + Recebi
                        </button>
                      )}
                    </div>
                    <div style={{height:6,borderRadius:99,background:T.panel2,overflow:"hidden",marginBottom:6}}>
                      <div style={{height:"100%",width:`${pct}%`,background:liquidada?T.green:T.amber,
                        borderRadius:99,transition:"width 0.4s"}}/>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.muted}}>
                      <span>Recebido: <b style={{color:T.green}}>{fmt(d.pago||0)}</b></span>
                      <span>{pct}%</span>
                      <span>Por receber: <b style={{color:T.amber}}>{fmt(falta)}</b> / Total: {fmt(d.total||0)}</span>
                    </div>
                    {d.historico?.length>0&&(
                      <div style={{marginTop:8,padding:"6px 10px",borderRadius:8,background:T.panel2}}>
                        <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase",marginBottom:4}}>Histórico</div>
                        {[...d.historico].reverse().map((h:any)=>(
                          <div key={h.id} style={{display:"flex",justifyContent:"space-between",fontSize:11,
                            color:T.textSub,padding:"2px 0"}}>
                            <span>{h.data}</span>
                            <span>{h.notas||"—"}</span>
                            <span style={{color:T.green,fontFamily:"monospace",fontWeight:600}}>+{fmt(h.valor)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Totais de dívidas */}
          {dividas.length>0&&(
            <div style={{marginTop:12,display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <div style={{padding:"10px 14px",borderRadius:10,background:`${T.red}10`,border:`1px solid ${T.red}30`,textAlign:"center"}}>
                <div style={{fontSize:11,color:T.red,fontWeight:700,textTransform:"uppercase",marginBottom:2}}>Total que devo</div>
                <div style={{fontSize:17,fontWeight:800,color:T.red,fontFamily:"monospace"}}>{fmt(totalDevoEu)}</div>
              </div>
              <div style={{padding:"10px 14px",borderRadius:10,background:`${T.green}10`,border:`1px solid ${T.green}30`,textAlign:"center"}}>
                <div style={{fontSize:11,color:T.green,fontWeight:700,textTransform:"uppercase",marginBottom:2}}>Total que me devem</div>
                <div style={{fontSize:17,fontWeight:800,color:T.green,fontFamily:"monospace"}}>{fmt(totalDevemMe)}</div>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/* ─── HOME BASE ─────────────────────────────────────────────────────────────── */
function HBFinancas() {
  // Receitas das empresas directas (VJ + HW3D)
  const { data: vjDB }   = useVJEventos();
  const { data: hw3dDB } = useHW3DProjetos();

  const recVJ = (isDemoAccount() ? DEMO_VJ_EVENTS : vjDB||[])
    .filter((e:any)=>e.status==="confirmado"||e.status==="concluido")
    .reduce((s:number,e:any)=>s+parseFloat(e.valor||0),0);

  const recHW3D = (isDemoAccount() ? DEMO_HW3D_PIPELINE : hw3dDB||[])
    .filter((p:any)=>p.etapa==="Concluído"||p.etapa==="Em progresso")
    .reduce((s:number,p:any)=>s+parseFloat(p.valor||p.orcamento||0),0);

  const recEmpresarial = recVJ + recHW3D;
  const HB = "#6366f1";
  const { data: transacoesDB, insert: insertTransacao, remove: removeTransacao } = useHBTransacoes();
  const [transacoesLocal, setTransacoesLocal] = useState<any[]>([]);
  const transacoes = isDemoAccount() ? transacoesLocal : (transacoesDB || []);
  const setTransacoes = isDemoAccount() ? setTransacoesLocal : (_:any) => {};
  const [adding, setAdding] = useState(false);
  const [newT, setNewT] = useState({descricao:"",valor:0,tipo:"despesa",categoria:"alimentacao",data:new Date().toISOString().split("T")[0]});
  const cats = {receita:["salario","freelance","outro"],despesa:["alimentacao","habitacao","transporte","saude","lazer","educacao","outro"]};
  const addT = () => {
    if(!newT.descricao||!newT.valor) return;
    setTransacoes(ts=>[...ts,{...newT,id:ts.length+1,valor:+newT.valor}]);
    setAdding(false);
    setNewT({descricao:"",valor:0,tipo:"despesa",categoria:"alimentacao",data:new Date().toISOString().split("T")[0]});
  };
  const mes = new Date().getMonth();
  const ano = new Date().getFullYear();
  const destesMes = transacoes.filter(t=>{ const d=new Date(t.data); return d.getMonth()===mes&&d.getFullYear()===ano; });
  const receitas = destesMes.filter(t=>t.tipo==="receita").reduce((s,t)=>s+t.valor,0);
  const despesas = destesMes.filter(t=>t.tipo==="despesa").reduce((s,t)=>s+t.valor,0);
  const saldo = receitas - despesas;
  const inp = {padding:"9px 12px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13,width:"100%"} as any;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12}}>
        <KPI l="Receitas Pessoais" v={fmt(receitas)} c="este mês" ac={T.green}/>
        <KPI l="Despesas" v={fmt(despesas)} c="este mês" ac={T.red}/>
        <KPI l="Saldo Pessoal" v={fmt(saldo)} c="este mês" ac={saldo>=0?T.green:T.red}/>
      </div>

      {/* Receitas empresariais directas */}
      <Card title="💼 Receitas das Empresas (VJ + HW3D)" accent={T.purple}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
          <div style={{padding:"12px 14px",borderRadius:10,background:`${T.purple}10`,border:`1px solid ${T.purple}30`}}>
            <div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase",marginBottom:4}}>🎤 VJ Casamentos</div>
            <div style={{fontSize:20,fontWeight:900,color:T.purple,fontFamily:"monospace"}}>{fmt(recVJ)}</div>
            <div style={{fontSize:11,color:T.muted}}>eventos confirmados/concluídos</div>
          </div>
          <div style={{padding:"12px 14px",borderRadius:10,background:`${T.teal}10`,border:`1px solid ${T.teal}30`}}>
            <div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase",marginBottom:4}}>🧊 Homework 3D</div>
            <div style={{fontSize:20,fontWeight:900,color:T.teal,fontFamily:"monospace"}}>{fmt(recHW3D)}</div>
            <div style={{fontSize:11,color:T.muted}}>projetos em curso/concluídos</div>
          </div>
        </div>
        <div style={{padding:"10px 14px",borderRadius:8,background:`${T.green}10`,border:`1px solid ${T.green}30`,
          display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:13,fontWeight:700,color:T.text}}>Total empresarial</span>
          <span style={{fontSize:18,fontWeight:900,color:T.green,fontFamily:"monospace"}}>{fmt(recEmpresarial)}</span>
        </div>
        <div style={{fontSize:11,color:T.muted,marginTop:8}}>
          💡 Valores acumulados de todos os períodos. Adiciona receitas pessoais em Movimentos para controlar o teu fluxo pessoal.
        </div>
      </Card>

      <Card title="Movimentos" accent={HB} action="+ Adicionar" onAction={()=>setAdding(true)}>
        {adding && (
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10}}>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Tipo</div>
              <select value={newT.tipo} onChange={e=>setNewT({...newT,tipo:e.target.value,categoria:e.target.value==="receita"?"salario":"alimentacao"})} style={inp}>
                <option value="despesa">💸 Despesa</option>
                <option value="receita">💶 Receita</option>
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Descrição</div>
              <input value={newT.descricao} onChange={e=>setNewT({...newT,descricao:e.target.value})} placeholder="Ex: Supermercado" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Valor (€)</div>
              <input type="number" value={newT.valor||""} onChange={e=>setNewT({...newT,valor:+e.target.value})} placeholder="0" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Categoria</div>
              <select value={newT.categoria} onChange={e=>setNewT({...newT,categoria:e.target.value})} style={inp}>
                {(cats[newT.tipo as "receita"|"despesa"]||[]).map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Data</div>
              <input type="date" value={newT.data} onChange={e=>setNewT({...newT,data:e.target.value})} style={inp}/>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
              <button onClick={addT} style={{padding:"9px 16px",borderRadius:8,border:"none",background:HB,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"9px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}
        {transacoes.length===0&&<Empty icon="💰" msg="Sem movimentos" sub="Adiciona receitas e despesas pessoais"/>}
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:400}}>
            <tbody>
              {[...transacoes].reverse().map(t=>(
                <tr key={t.id} style={{borderBottom:`1px solid ${T.border}`}}>
                  <td style={{padding:"9px 12px",color:T.muted,whiteSpace:"nowrap"}}>{t.data}</td>
                  <td style={{padding:"9px 12px",color:T.text}}>{t.descricao}</td>
                  <td style={{padding:"9px 12px"}}><Tag label={t.categoria} color={t.tipo==="receita"?T.green:T.muted}/></td>
                  <td style={{padding:"9px 12px",color:t.tipo==="receita"?T.green:T.red,fontFamily:"monospace",textAlign:"right",whiteSpace:"nowrap"}}>
                    {t.tipo==="receita"?"+":"-"}{fmt(t.valor)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function HBObjetivos() {
  const HB = "#6366f1";
  const [objs, setObjs] = useState<any[]>([]);
  const [adding, setAdding] = useState(false);
  const [newO, setNewO] = useState({titulo:"",meta:0,atual:0,prazo:"",icone:"🎯"});
  const ICONS = ["🎯","🏠","🚗","✈️","💻","📚","🏋️","💰","🎸","🌍"];
  const add = () => {
    if(!newO.titulo||!newO.meta) return;
    setObjs(os=>[...os,{...newO,id:os.length+1,meta:+newO.meta,atual:+newO.atual}]);
    setAdding(false);
    setNewO({titulo:"",meta:0,atual:0,prazo:"",icone:"🎯"});
  };
  const addDeposito = (id:number, v:number) =>
    setObjs(os=>os.map(o=>o.id===id?{...o,atual:Math.min(o.meta,o.atual+v)}:o));
  const inp = {padding:"9px 12px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13,width:"100%"} as any;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <KPI l="Objectivos" v={objs.length} c="activos" ac={HB}/>
        <KPI l="Concluídos" v={objs.filter(o=>o.atual>=o.meta).length} c="100%" ac={T.green}/>
        <KPI l="Total Poupado" v={fmt(objs.reduce((s,o)=>s+o.atual,0))} c="todos os objectivos" ac={HB}/>
      </div>
      <Card title="Objectivos de Poupança" accent={HB} action="+ Novo Objectivo" onAction={()=>setAdding(true)}>
        {adding && (
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10}}>
            <div style={{gridColumn:"1/-1"}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Ícone</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {ICONS.map(ic=>(
                  <button key={ic} onClick={()=>setNewO({...newO,icone:ic})}
                    style={{padding:"5px 9px",borderRadius:6,border:`1px solid ${newO.icone===ic?HB:T.border}`,
                      background:newO.icone===ic?`${HB}20`:"transparent",cursor:"pointer",fontSize:18}}>{ic}</button>
                ))}
              </div>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Título</div>
              <input value={newO.titulo} onChange={e=>setNewO({...newO,titulo:e.target.value})} placeholder="Ex: Viagem ao Japão" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Meta (€)</div>
              <input type="number" value={newO.meta||""} onChange={e=>setNewO({...newO,meta:+e.target.value})} placeholder="5000" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Já poupado (€)</div>
              <input type="number" value={newO.atual||""} onChange={e=>setNewO({...newO,atual:+e.target.value})} placeholder="0" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Prazo</div>
              <input type="date" value={newO.prazo} onChange={e=>setNewO({...newO,prazo:e.target.value})} style={inp}/>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
              <button onClick={add} style={{padding:"9px 16px",borderRadius:8,border:"none",background:HB,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"9px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}
        {objs.length===0&&<Empty icon="🎯" msg="Sem objectivos" sub="Define objectivos de poupança para acompanhar o progresso"/>}
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {objs.map(o=>{
            const pct = Math.min(100, Math.round((o.atual/o.meta)*100));
            const done = o.atual>=o.meta;
            return (
              <div key={o.id} style={{padding:14,borderRadius:10,background:T.panel,border:`1px solid ${done?T.green:T.border}`}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  <span style={{fontSize:24}}>{o.icone}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:T.text}}>{o.titulo}</div>
                    {o.prazo&&<div style={{fontSize:11,color:T.muted}}>📅 {new Date(o.prazo).toLocaleDateString("pt-PT")}</div>}
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:13,fontWeight:700,color:done?T.green:HB,fontFamily:"monospace"}}>{fmt(o.atual)}</div>
                    <div style={{fontSize:11,color:T.muted}}>de {fmt(o.meta)}</div>
                  </div>
                </div>
                <div style={{height:8,borderRadius:99,background:"rgba(255,255,255,0.08)",overflow:"hidden",marginBottom:8}}>
                  <div style={{height:"100%",borderRadius:99,width:`${pct}%`,
                    background:done?T.green:`linear-gradient(90deg,${HB},#818cf8)`,transition:"width 0.3s"}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:11,color:done?T.green:T.muted}}>{pct}% {done?"✓ Concluído!":""}</span>
                  {!done&&(
                    <div style={{display:"flex",gap:4}}>
                      {[50,100,500].map(v=>(
                        <button key={v} onClick={()=>addDeposito(o.id,v)}
                          style={{padding:"10px 14px",borderRadius:6,border:`1px solid ${T.border}`,
                            background:"transparent",color:T.muted,cursor:"pointer",fontSize:11}}>
                          +€{v}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function HBWishlist() {
  const HB = "#6366f1";
  const { data: itemsDB, insert: insertItem, update: updateItem, remove: removeItem } = useHBWishlist();
  const [itemsLocal, setItemsLocal] = useState<any[]>([]);
  const items = isDemoAccount() ? itemsLocal : (itemsDB || []);
  const setItems = isDemoAccount() ? setItemsLocal : (_:any) => {};
  const [adding, setAdding] = useState(false);
  const [newI, setNewI] = useState({nome:"",url:"",preco:0,prioridade:"media",comprado:false});
  const add = () => {
    if(!newI.nome) return;
    setItems(is=>[...is,{...newI,id:is.length+1,preco:+newI.preco}]);
    setAdding(false);
    setNewI({nome:"",url:"",preco:0,prioridade:"media",comprado:false});
  };
  const priC:Record<string,string>={alta:T.red,media:T.amber,baixa:T.muted};
  const inp = {padding:"9px 12px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13,width:"100%"} as any;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <KPI l="Wishlist" v={items.filter(i=>!i.comprado).length} c="por comprar" ac={HB}/>
        <KPI l="Total Est." v={fmt(items.filter(i=>!i.comprado).reduce((s,i)=>s+i.preco,0))} c="valor wishlist" ac={HB}/>
        <KPI l="Comprados" v={items.filter(i=>i.comprado).length} c="realizados" ac={T.green}/>
      </div>
      <Card title="Wishlist" accent={HB} action="+ Adicionar" onAction={()=>setAdding(true)}>
        {adding && (
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10}}>
            <div style={{gridColumn:"1/-1"}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Nome</div>
              <input value={newI.nome} onChange={e=>setNewI({...newI,nome:e.target.value})} placeholder="Ex: Sony WH-1000XM5" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Preço Est. (€)</div>
              <input type="number" value={newI.preco||""} onChange={e=>setNewI({...newI,preco:+e.target.value})} placeholder="0" style={inp}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Prioridade</div>
              <select value={newI.prioridade} onChange={e=>setNewI({...newI,prioridade:e.target.value})} style={inp}>
                <option value="alta">🔴 Alta</option>
                <option value="media">🟡 Média</option>
                <option value="baixa">🟢 Baixa</option>
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Link (opcional)</div>
              <input value={newI.url} onChange={e=>setNewI({...newI,url:e.target.value})} placeholder="https://..." style={inp}/>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
              <button onClick={add} style={{padding:"9px 16px",borderRadius:8,border:"none",background:HB,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"9px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}
        {items.length===0&&<Empty icon="🛒" msg="Wishlist vazia" sub="Adiciona produtos ou coisas que queres comprar"/>}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {items.filter(i=>!i.comprado).map(i=>(
            <div key={i.id} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 14px",borderRadius:10,background:T.panel,border:`1px solid ${T.border}`}}>
              <Tag label={i.prioridade} color={priC[i.prioridade]}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:T.text}}>{i.nome}</div>
                {i.url&&<a href={i.url} target="_blank" rel="noreferrer" style={{fontSize:11,color:T.blueL}}>Ver produto →</a>}
              </div>
              {i.preco>0&&<span style={{fontSize:13,color:HB,fontFamily:"monospace",flexShrink:0}}>{fmt(i.preco)}</span>}
              <button onClick={()=>setItems(is=>is.map(x=>x.id===i.id?{...x,comprado:true}:x))}
                style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${T.green}`,background:"transparent",color:T.green,cursor:"pointer",fontSize:11,fontWeight:600,flexShrink:0}}>
                ✓ Comprado
              </button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function HBPagamentos() {
  const HB = "#6366f1";
  const { data: pagamentosDB, insert: insertPagamento, update: updatePagamento, remove: removePagamento } = useHBPagamentos();
  const [pagamentosLocal, setPagamentosLocal] = useState<any[]>([]);
  const pagamentos = isDemoAccount() ? pagamentosLocal : (pagamentosDB || []);
  const setPagamentos = isDemoAccount() ? setPagamentosLocal : (_:any) => {};
  const [adding, setAdding] = useState(false);
  const [newP, setNewP] = useState({nome:"",valor:0,dia:1,tipo:"mensal",categoria:"habitacao",ativo:true});
  const add = () => {
    if(!newP.nome||!newP.valor) return;
    setPagamentos(ps=>[...ps,{...newP,id:ps.length+1,valor:+newP.valor,dia:+newP.dia}]);
    setAdding(false);
    setNewP({nome:"",valor:0,dia:1,tipo:"mensal",categoria:"habitacao",ativo:true});
  };
  const totalMensal = pagamentos.filter(p=>p.ativo&&p.tipo==="mensal").reduce((s,p)=>s+p.valor,0);
  const totalAnual  = pagamentos.filter(p=>p.ativo&&p.tipo==="anual").reduce((s,p)=>s+p.valor,0);
  const inp = {padding:"9px 12px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13,width:"100%"} as any;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <KPI l="Mensal" v={fmt(totalMensal)} c="pagamentos fixos" ac={T.red}/>
        <KPI l="Anual" v={fmt(totalAnual)} c="pagamentos anuais" ac={T.amber}/>
        <KPI l="Total Anual" v={fmt(totalMensal*12+totalAnual)} c="custo total/ano" ac={HB}/>
      </div>
      <Card title="Pagamentos Recorrentes" accent={HB} action="+ Adicionar" onAction={()=>setAdding(true)}>
        {adding && (
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10}}>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Nome</div>
              <input value={newP.nome} onChange={e=>setNewP({...newP,nome:e.target.value})} placeholder="Netflix, Renda..." style={inp}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Valor (€)</div>
              <input type="number" value={newP.valor||""} onChange={e=>setNewP({...newP,valor:+e.target.value})} style={inp}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Frequência</div>
              <select value={newP.tipo} onChange={e=>setNewP({...newP,tipo:e.target.value})} style={inp}>
                <option value="mensal">Mensal</option>
                <option value="anual">Anual</option>
                <option value="semanal">Semanal</option>
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Dia do mês</div>
              <input type="number" min={1} max={31} value={newP.dia} onChange={e=>setNewP({...newP,dia:+e.target.value})} style={inp}/>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
              <button onClick={add} style={{padding:"9px 16px",borderRadius:8,border:"none",background:HB,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"9px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}
        {pagamentos.length===0&&<Empty icon="💳" msg="Sem pagamentos" sub="Adiciona subscrições e pagamentos recorrentes"/>}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {pagamentos.map(p=>(
            <div key={p.id} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 14px",borderRadius:10,
              background:p.ativo?T.panel:"rgba(255,255,255,0.01)",opacity:p.ativo?1:0.5,
              border:`1px solid ${T.border}`}}>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:T.text}}>{p.nome}</div>
                <div style={{fontSize:11,color:T.muted}}>Dia {p.dia} · {p.tipo}</div>
              </div>
              <span style={{fontSize:14,fontWeight:700,color:T.red,fontFamily:"monospace"}}>{fmt(p.valor)}</span>
              <button onClick={()=>setPagamentos(ps=>ps.map(x=>x.id===p.id?{...x,ativo:!x.ativo}:x))}
                style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${T.border}`,
                  background:"transparent",color:p.ativo?T.muted:T.green,cursor:"pointer",fontSize:11}}>
                {p.ativo?"Pausar":"Activar"}
              </button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function HomeBaseView({ page }: any) {
  const HB = "#6366f1";
  const totalObjetivos = 0;
  if(page==="financas")  return <HBFinancas/>;
  if(page==="poupancas") return <HBPoupancas/>;
  if(page==="objetivos") return <HBObjetivos/>;
  if(page==="wishlist")  return <HBWishlist/>;
  if(page==="pagamentos")return <HBPagamentos/>;
  if(page==="tarefas")   return <HBTarefasPessoais/>;
  // Dashboard overview
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12}}>
        <KPI l="Finanças" v="💰" c="receitas e despesas" ac={HB}/>
        <KPI l="Objectivos" v="🎯" c="poupanças" ac={HB}/>
        <KPI l="Wishlist" v="🛒" c="lista de desejos" ac={HB}/>
        <KPI l="Pagamentos" v="💳" c="recorrentes" ac={HB}/>
      </div>
      <Card title="🏡 Home Base — Área Pessoal" accent={HB}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {[
            {id:"financas",icon:"💰",label:"Finanças Pessoais",desc:"Receitas, despesas e saldo"},
            {id:"poupancas",icon:"🏦",label:"Poupanças & Dívidas",desc:"Património, onde está o teu dinheiro e o que deves"},
            {id:"objetivos",icon:"🎯",label:"Objectivos",desc:"Poupanças e metas pessoais"},
            {id:"wishlist",icon:"🛒",label:"Wishlist",desc:"Produtos que queres comprar"},
            {id:"pagamentos",icon:"💳",label:"Pagamentos",desc:"Subscrições recorrentes"},
          ].map(m=>(
            <div key={m.id} style={{padding:14,borderRadius:10,background:T.panel,border:`1px solid ${T.border}`,cursor:"pointer"}}
              onClick={()=>window.dispatchEvent(new CustomEvent("kairos-nav",{detail:{co:"homebase",page:m.id}}))}>
              <div style={{fontSize:24,marginBottom:6}}>{m.icon}</div>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>{m.label}</div>
              <div style={{fontSize:11,color:T.muted,marginTop:2}}>{m.desc}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function HBTarefasPessoais() {
  const HB = "#6366f1";
  const [tarefas, setTarefas] = useState<any[]>([]);
  const [adding, setAdding] = useState(false);
  const [newT, setNewT] = useState({titulo:"",prioridade:"media",data:"",status:"por fazer"});
  const add = () => {
    if(!newT.titulo) return;
    setTarefas(ts=>[...ts,{...newT,id:ts.length+1}]);
    setAdding(false);
    setNewT({titulo:"",prioridade:"media",data:"",status:"por fazer"});
  };
  const priC:Record<string,string>={alta:T.red,media:T.amber,baixa:T.muted};
  const inp = {padding:"9px 12px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13,width:"100%"} as any;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        <KPI l="Por Fazer" v={tarefas.filter(t=>t.status==="por fazer").length} c="" ac={T.muted}/>
        <KPI l="Concluídas" v={tarefas.filter(t=>t.status==="concluída").length} c="" ac={T.green}/>
      </div>
      <Card title="Tarefas Pessoais" accent={HB} action="+ Adicionar" onAction={()=>setAdding(true)}>
        {adding && (
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:14,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10}}>
            <div style={{gridColumn:"1/-1"}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Tarefa</div>
              <input value={newT.titulo} onChange={e=>setNewT({...newT,titulo:e.target.value})} placeholder="O que fazer..." style={inp}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Prioridade</div>
              <select value={newT.prioridade} onChange={e=>setNewT({...newT,prioridade:e.target.value})} style={inp}>
                <option value="alta">Alta</option><option value="media">Média</option><option value="baixa">Baixa</option>
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Data</div>
              <input type="date" value={newT.data} onChange={e=>setNewT({...newT,data:e.target.value})} style={inp}/>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
              <button onClick={add} style={{padding:"9px 16px",borderRadius:8,border:"none",background:HB,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"9px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}
        {tarefas.length===0&&<Empty icon="✅" msg="Sem tarefas pessoais" sub="Adiciona tarefas do teu dia-a-dia"/>}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {tarefas.filter(t=>t.status!=="concluída").map(t=>(
            <div key={t.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,background:T.panel,border:`1px solid ${T.border}`}}>
              <Tag label={t.prioridade} color={priC[t.prioridade]}/>
              <span style={{flex:1,fontSize:13,color:T.text}}>{t.titulo}</span>
              {t.data&&<span style={{fontSize:11,color:T.muted}}>{new Date(t.data).toLocaleDateString("pt-PT")}</span>}
              <button onClick={()=>setTarefas(ts=>ts.map(x=>x.id===t.id?{...x,status:"concluída"}:x))}
                style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${T.green}`,background:"transparent",color:T.green,cursor:"pointer",fontSize:11}}>✓</button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ─── HOLDINGS CALENDAR PLACEHOLDER ────────────────────────────────────────── */
function HoldingsCalendario({ vjEvents, alRes, hw3dProjects, kaiEvents, pbEvents }: any) {
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth());
  const [ano, setAno] = useState(hoje.getFullYear());
  const [sel, setSel] = useState<number|null>(null);
  const [adding, setAdding] = useState(false);
  const [manualEntries, setManualEntries] = useState<any[]>([]);
  const [newEntry, setNewEntry] = useState({empresa:"holdings",titulo:"",data:"",hora:"09:00",descricao:""});

  const meses = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const diasNoMes = new Date(ano,mes+1,0).getDate();
  const primeiroDia = new Date(ano,mes,1).getDay();
  const hojeStr = hoje.toISOString().split("T")[0];

  // Company markers — one per active company
  const empresaMarkers: Record<string,{cor:string,icon:string}> = {
    holdings:   { cor:T.blueL,   icon:"⚡" },
    hw3d:       { cor:T.teal,    icon:"🧊" },
    quinta:     { cor:T.amber,   icon:"🏠" },
    vj:         { cor:T.purple,  icon:"🎤" },
    kai_ai:     { cor:"#a855f7", icon:"🤖" },
    photobooth: { cor:T.pink,    icon:"📸" },
  };

  const eventosPorDia: Record<number,Array<{label:string;cor:string;icon:string;hora:string;desc:string}>> = {};
  const addEv = (data:string,label:string,cor:string,icon:string,hora="",desc="") => {
    const d = new Date(data);
    if(d.getFullYear()===ano && d.getMonth()===mes) {
      const dia = d.getDate();
      if(!eventosPorDia[dia]) eventosPorDia[dia]=[];
      eventosPorDia[dia].push({label,cor,icon,hora,desc});
    }
  };

  vjEvents?.forEach((e:any) => addEv(e.data, e.local?.split(",")[0]||"VJ", T.purple, "🎤", e.horario||"", e.local||""));
  alRes?.filter((r:any)=>r.status==="confirmada"||r.status==="confirmado").forEach((r:any) => {
    const ci = r.ci||r.check_in||"";
    const co = r.co||r.check_out||"";
    const nome = r.guest||r.casa_nome||"Hóspede";
    if(ci) addEv(ci, `Check-in: ${nome}`, T.amber, "🏠", r.ciHora||r.hora_ci||"16:00", nome);
    if(co) addEv(co, `Check-out: ${nome}`, T.teal,  "🏠", r.coHora||r.hora_co||"10:00", nome);
  });
  hw3dProjects?.filter((p:any)=>p.dataFim&&p.etapa!=="Concluído").forEach((p:any) =>
    addEv(p.dataFim, `Deadline: ${p.titulo||p.nome}`, T.teal, "🧊", "", p.titulo||p.nome));
  kaiEvents?.forEach((e:any) => addEv(e.data, e.titulo||e.descricao, "#a855f7", "🤖", e.hora||"", e.descricao||""));
  pbEvents?.forEach((e:any) => addEv(e.data, e.local||"PhotoBooth", T.pink, "📸", e.horario||"", e.local||""));
  manualEntries.forEach((e:any) => {
    const m = empresaMarkers[e.empresa]||empresaMarkers.holdings;
    addEv(e.data, e.titulo, m.cor, m.icon, e.hora, e.descricao);
  });

  const addEntry = () => {
    if(!newEntry.titulo||!newEntry.data) return;
    setManualEntries(es=>[...es,{...newEntry,id:es.length+1}]);
    setAdding(false);
    setNewEntry({empresa:"holdings",titulo:"",data:"",hora:"09:00",descricao:""});
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <Card title="Calendário Agregado — Todas as Empresas" accent={T.blueL}
        action="+ Entrada" onAction={()=>setAdding(true)}>

        {adding && (
          <div style={{padding:14,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:16,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10}}>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Empresa</div>
              <select value={newEntry.empresa} onChange={(e:any)=>setNewEntry({...newEntry,empresa:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
                {Object.entries(empresaMarkers).map(([id,m])=>(
                  <option key={id} value={id}>{m.icon} {id==="holdings"?"Holdings":id==="hw3d"?"HW3D":id==="quinta"?"Quinta":id==="vj"?"VJ Casamentos":id==="kai_ai"?"Kairos AI":"PhotoBooth"}</option>
                ))}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Título</div>
              <input value={newEntry.titulo} onChange={(e:any)=>setNewEntry({...newEntry,titulo:e.target.value})}
                placeholder="Ex: Reunião com cliente"
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Data</div>
              <input type="date" value={newEntry.data} onChange={(e:any)=>setNewEntry({...newEntry,data:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Hora</div>
              <input type="time" value={newEntry.hora} onChange={(e:any)=>setNewEntry({...newEntry,hora:e.target.value})}
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700,textTransform:"uppercase"}}>Descrição</div>
              <input value={newEntry.descricao} onChange={(e:any)=>setNewEntry({...newEntry,descricao:e.target.value})}
                placeholder="Detalhes do evento..."
                style={{width:"100%",padding:"8px 10px",borderRadius:8,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
              <button onClick={addEntry} style={{padding:"8px 16px",borderRadius:8,border:"none",background:T.blueL,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>Guardar</button>
              <button onClick={()=>setAdding(false)} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}} aria-label="Fechar">✕</button>
            </div>
          </div>
        )}

        {/* Legenda de empresas */}
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
          {Object.entries(empresaMarkers).map(([id,m])=>(
            <div key={id} style={{display:"flex",alignItems:"center",gap:4,padding:"2px 8px",borderRadius:6,background:`${m.cor}18`,border:`1px solid ${m.cor}40`}}>
              <span style={{fontSize:11}}>{m.icon}</span>
              <span style={{fontSize:11,color:m.cor,fontWeight:600}}>{id==="kai_ai"?"Kairos AI":id==="hw3d"?"HW3D":id==="quinta"?"Quinta":id==="vj"?"VJ":id==="photobooth"?"PhotoBooth":"Holdings"}</span>
            </div>
          ))}
        </div>

        {/* Month navigation */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <button onClick={()=>{if(mes===0){setMes(11);setAno(ano-1);}else setMes(mes-1);setSel(null);}}
            style={{background:"none",border:`1px solid ${T.border}`,color:T.text,padding:"10px 14px",borderRadius:6,cursor:"pointer",fontSize:14}}>‹</button>
          <span style={{fontSize:14,fontWeight:700,color:T.text}}>{meses[mes]} {ano}</span>
          <button onClick={()=>{if(mes===11){setMes(0);setAno(ano+1);}else setMes(mes+1);setSel(null);}}
            style={{background:"none",border:`1px solid ${T.border}`,color:T.text,padding:"10px 14px",borderRadius:6,cursor:"pointer",fontSize:14}}>›</button>
        </div>

        {/* Day headers */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:4}}>
          {["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"].map(d=>(
            <div key={d} style={{fontSize:11,color:T.muted,textAlign:"center",padding:"4px 0",fontWeight:700}}>{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
          {Array.from({length:primeiroDia}).map((_,i)=><div key={`e${i}`}/>)}
          {Array.from({length:diasNoMes}).map((_,i)=>{
            const dia=i+1;
            const dataStr=`${ano}-${String(mes+1).padStart(2,"0")}-${String(dia).padStart(2,"0")}`;
            const isHoje=dataStr===hojeStr;
            const evs=eventosPorDia[dia]||[];
            const isSel=sel===dia;
            return (
              <div key={dia} onClick={()=>setSel(isSel?null:dia)}
                style={{minHeight:36,padding:"2px 1px",borderRadius:5,
                  background:isSel?`${T.blueL}20`:isHoje?`${T.blueL}12`:"transparent",
                  border:isSel?`1px solid ${T.blueL}`:isHoje?`1px solid ${T.blueL}40`:`1px solid transparent`,
                  cursor:evs.length>0||isHoje?"pointer":"default"}}>
                <div style={{fontSize:10,color:isHoje?T.blueL:T.textSub,fontWeight:isHoje?700:400,textAlign:"center",marginBottom:1}}>{dia}</div>
                {evs.slice(0,2).map((ev,ei)=>(
                  <div key={ei} style={{fontSize:8,borderRadius:3,padding:"1px 2px",marginBottom:1,
                    background:`${ev.cor}25`,color:ev.cor,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" as any}}>
                    {ev.icon} {ev.hora?ev.hora+' ':''}{ev.label}
                  </div>
                ))}
                {evs.length>2&&<div style={{fontSize:8,color:T.muted,textAlign:"center"}}>+{evs.length-2}</div>}
              </div>
            );
          })}
        </div>

        {/* Day detail */}
        {sel&&eventosPorDia[sel]&&(
          <div style={{marginTop:14,padding:12,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`}}>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.1em"}}>
              {sel} de {meses[mes]}
            </div>
            {eventosPorDia[sel].map((ev,i)=>(
              <div key={i} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"8px 0",borderBottom:i<eventosPorDia[sel].length-1?`1px solid ${T.border}`:"none"}}>
                <span style={{fontSize:16,flexShrink:0}}>{ev.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,color:T.text,fontWeight:600}}>{ev.label}</div>
                  {ev.hora&&<div style={{fontSize:11,color:T.muted}}>🕐 {ev.hora}</div>}
                  {ev.desc&&<div style={{fontSize:11,color:T.textSub,marginTop:2}}>{ev.desc}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <ResumoCalendarioHoldings vjEvents={vjEvents} alRes={alRes} hw3dProjects={hw3dProjects} kaiEvents={kaiEvents} pbEvents={pbEvents}/>
    </div>
  );
}
// ─── Settings Drawer — abre lateral sobre a app ──────────────
function SettingsDrawer({ page, onClose }: { page: string; onClose: () => void }) {
  const pages: Record<string,{label:string;icon:string}> = {
    integrations: { label:"Integrações",        icon:"🔗" },
    perfil:       { label:"Perfil & Conta",      icon:"👤" },
    empresas:     { label:"Gerir Empresas",      icon:"🏢" },
    subscricao:   { label:"Subscrição",          icon:"💳" },
    equipa:       { label:"Equipa & Acessos",    icon:"👥" },
    notificacoes: { label:"Notificações",        icon:"🔔" },
    config:       { label:"Configurações",       icon:"⚙️" },
    ajuda:        { label:"Ajuda & Suporte",     icon:"❓" },
    feedback:     { label:"Enviar Feedback",     icon:"💬" },
    privacidade:  { label:"Privacidade",         icon:"🔒" },
  };
  const current = pages[page] || pages.integrations;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      {/* Overlay */}
      <div onClick={onClose} aria-hidden="true" style={{
        position:"fixed", inset:0, zIndex:190,
        background:"rgba(5,8,16,0.5)", backdropFilter:"blur(3px)",
      }}/>
      {/* Drawer */}
      <div style={{
        position:"fixed", top:0, right:0, bottom:0, zIndex:191,
        width:"min(520px, 100vw)", background:T.surface,
        borderLeft:`1px solid ${T.border}`,
        boxShadow:"-24px 0 64px rgba(0,0,0,0.3)",
        display:"flex", flexDirection:"column",
        animation:"slideInRight 0.22s ease",
      }}>
        <style>{`@keyframes slideInRight { from{transform:translateX(100%)} to{transform:translateX(0)} }`}</style>

        {/* Header */}
        <div style={{
          padding:"18px 24px", borderBottom:`1px solid ${T.border}`,
          display:"flex", alignItems:"center", gap:12, flexShrink:0,
        }}>
          <span style={{fontSize:22}}>{current.icon}</span>
          <div style={{flex:1}}>
            <div style={{fontSize:16,fontWeight:800,color:T.text}}>{current.label}</div>
          </div>
          <button onClick={onClose} aria-label="Fechar"
            style={{background:"transparent",border:`1px solid ${T.border}`,
              borderRadius:8,color:T.muted,cursor:"pointer",
              width:36,height:36,fontSize:16,display:"flex",
              alignItems:"center",justifyContent:"center"}}>✕</button>
        </div>

        {/* Nav lateral */}
        <div style={{display:"flex",height:"100%",overflow:"hidden"}}>
          <div style={{width:160,flexShrink:0,borderRight:`1px solid ${T.border}`,
            padding:"12px 8px",display:"flex",flexDirection:"column",gap:4}}>
            {Object.entries(pages).map(([id,p])=>(
              <button key={id}
                onClick={()=>window.dispatchEvent(new CustomEvent("kairos-settings",{detail:{page:id}}))}
                aria-current={page===id?"page":undefined}
                style={{
                  display:"flex",alignItems:"center",gap:8,padding:"9px 10px",
                  borderRadius:8,border:"none",cursor:"pointer",textAlign:"left",
                  background:page===id?`${T.blueL}18`:"transparent",
                  color:page===id?T.blueL:T.textSub,
                  fontWeight:page===id?700:400, fontSize:12,
                  transition:"all 0.15s",
                }}>
                <span style={{fontSize:14}}>{p.icon}</span>
                <span>{p.label}</span>
              </button>
            ))}
          </div>

          {/* Conteúdo */}
          <div style={{flex:1,overflowY:"auto",padding:"20px 24px"}}>
            {page==="integrations" && <IntegrationSettings/>}
            {page==="perfil"       && <SettingsPerfil/>}
            {page==="empresas"     && <SettingsEmpresas/>}
            {page==="subscricao"   && <SettingsSubscricao/>}
            {page==="equipa"       && <SettingsEquipa/>}
            {page==="notificacoes" && <SettingsNotificacoes/>}
            {page==="ajuda"        && <SettingsAjuda/>}
            {page==="feedback"     && <SettingsFeedback/>}
            {page==="config"      && <SettingsConfig/>}
            {page==="privacidade"  && <SettingsPrivacidade/>}
          </div>
        </div>
      </div>
    </>
  );
}

function SettingsView({ page }: any) {
  if (page === "perfil")       return <SettingsPerfil/>;
  if (page === "notificacoes") return <SettingsNotificacoes/>;
  if (page === "equipa")       return <SettingsEquipa/>;
  return <IntegrationSettings/>;
}

function SettingsPerfil() {
  const [nome, setNome]   = useState(localStorage.getItem("kairos_user") || "");
  const [email]           = useState(localStorage.getItem("kairos_user_email") || "");
  const [holding, setHolding] = useState(localStorage.getItem("kairos_holding_name") || "");
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await supabase.auth.updateUser({ data: { full_name: nome, kairos_holding_name: holding } });
      await supabase.from("profiles").upsert({ id: (await supabase.auth.getUser()).data.user?.id, holding_name: holding });
      localStorage.setItem("kairos_user", nome);
      localStorage.setItem("kairos_holding_name", holding);
      setSaved(true); setTimeout(()=>setSaved(false), 2500);
    } finally { setSaving(false); }
  };

  const inp = {width:"100%",padding:"9px 12px",borderRadius:10,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13};
  const lbl = {fontSize:11,color:T.muted,marginBottom:5,fontWeight:700 as any,textTransform:"uppercase" as any};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div><div style={lbl}>Nome</div>
        <input value={nome} onChange={e=>setNome(e.target.value)} style={inp}/></div>
      <div><div style={lbl}>Email</div>
        <input value={email} disabled style={{...inp,opacity:0.5}}/></div>
      <div><div style={lbl}>Nome do Grupo / Holding</div>
        <input value={holding} onChange={e=>setHolding(e.target.value)} placeholder="Ex: Kairos Holdings" style={inp}/></div>
      <div><div style={lbl}>Password</div>
        <button onClick={async()=>{
          await supabase.auth.resetPasswordForEmail(email,{redirectTo:"https://app.kairosholdings.xyz"});
          alert("Email de reset enviado!");
        }} style={{padding:"9px 16px",borderRadius:10,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}}>
          Alterar password (via email)
        </button></div>
      {saved&&<div style={{padding:"9px 14px",borderRadius:10,background:`${T.green}15`,border:`1px solid ${T.green}40`,color:T.green,fontSize:13}}>✅ Guardado</div>}
      <button onClick={save} disabled={saving} style={{padding:"10px 20px",borderRadius:10,border:"none",background:T.blueL,color:"white",fontWeight:700,cursor:"pointer",fontSize:13,opacity:saving?0.7:1}}>
        {saving?"A guardar...":"💾 Guardar"}
      </button>
    </div>
  );
}

function SettingsNotificacoes() {
  const [telegramOn, setTelegramOn] = useState(false);
  const [telegramId, setTelegramId] = useState("");
  const [emailOn, setEmailOn]       = useState(true);
  const [pushOn, setPushOn]         = useState(false);
  const [saved, setSaved]           = useState(false);

  useEffect(() => {
    supabase.from("user_integrations").select("telegram_chat_id,telegram_active").single()
      .then(({data})=>{ if(!data) return; setTelegramId(data.telegram_chat_id||""); setTelegramOn(!!data.telegram_active); });
  }, []);

  const save = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from("user_integrations").upsert({ user_id:user.id, telegram_chat_id:telegramId, telegram_active:telegramOn }, { onConflict:"user_id" });
    setSaved(true); setTimeout(()=>setSaved(false), 2500);
  };

  const inp = {width:"100%",padding:"9px 12px",borderRadius:10,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13};
  const lbl = {fontSize:11,color:T.muted,marginBottom:5,fontWeight:700 as any,textTransform:"uppercase" as any};
  const Toggle = ({on,set,label}:{on:boolean,set:(v:boolean)=>void,label:string}) => (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",borderBottom:`1px solid ${T.border}`}}>
      <span style={{fontSize:13,color:T.text}}>{label}</span>
      <button onClick={()=>set(!on)} style={{padding:"5px 14px",borderRadius:8,border:"none",cursor:"pointer",fontWeight:700,fontSize:12,background:on?T.green:T.panel2,color:on?"white":T.muted}}>{on?"✓ Activo":"Desligado"}</button>
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <Toggle on={emailOn} set={setEmailOn} label="📧 Notificações por email"/>
      <Toggle on={pushOn} set={setPushOn} label="📱 Push notifications (PWA)"/>
      <div style={{paddingTop:8}}>
        <Toggle on={telegramOn} set={setTelegramOn} label="✈️ Resumo semanal Telegram (domingos 20h)"/>
        {telegramOn && (
          <div style={{marginTop:10}}>
            <div style={lbl}>Telegram Chat ID</div>
            <input value={telegramId} onChange={e=>setTelegramId(e.target.value)} placeholder="Obtém em @userinfobot" style={inp}/>
            <div style={{fontSize:11,color:T.muted,marginTop:5}}>💡 Fala com <b>@userinfobot</b> no Telegram e copia o número que te envia.</div>
          </div>
        )}
      </div>
      {saved&&<div style={{padding:"9px 14px",borderRadius:10,background:`${T.green}15`,border:`1px solid ${T.green}40`,color:T.green,fontSize:13}}>✅ Guardado</div>}
      <button onClick={save} style={{padding:"10px 20px",borderRadius:10,border:"none",background:T.blueL,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>💾 Guardar</button>
    </div>
  );
}

function SettingsEquipa() {
  const [membros] = useState<any[]>([]);
  const [email, setEmail] = useState("");
  const [papel, setPapel] = useState("viewer");
  const papeis = [{v:"admin",l:"Administrador — acesso total"},{v:"worker",l:"Trabalhador — módulos atribuídos"},{v:"viewer",l:"Visualizador — só leitura"}];
  const inp = {width:"100%",padding:"9px 12px",borderRadius:10,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13};
  const lbl = {fontSize:11,color:T.muted,marginBottom:5,fontWeight:700 as any,textTransform:"uppercase" as any};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{padding:"12px 14px",borderRadius:10,background:`${T.amber}12`,border:`1px solid ${T.amber}30`,fontSize:12,color:T.amber}}>
        🚧 Sistema de convites em desenvolvimento. Em breve podes convidar colaboradores directamente daqui.
      </div>
      <div>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Convidar colaborador</div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div><div style={lbl}>Email</div><input value={email} onChange={e=>setEmail(e.target.value)} placeholder="colaborador@email.com" style={inp}/></div>
          <div><div style={lbl}>Papel</div>
            <select value={papel} onChange={e=>setPapel(e.target.value)} style={inp}>
              {papeis.map(p=><option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </div>
          <button style={{padding:"10px 20px",borderRadius:10,border:"none",background:T.blueL,color:"white",fontWeight:700,cursor:"not-allowed",fontSize:13,opacity:0.5}}>Enviar convite (em breve)</button>
        </div>
      </div>
      {membros.length===0&&<Empty icon="👥" msg="Sem colaboradores" sub="Convida membros da tua equipa para colaborarem"/>}
    </div>
  );
}

// ─── Gerir Empresas ──────────────────────────────────────────
function SettingsEmpresas() {
  const saved = localStorage.getItem("kairos_biz_types");
  const bizTypes: string[] = saved ? JSON.parse(saved) : [];
  const allBiz = BUSINESS_TYPES;
  const [selected, setSelected] = useState<string[]>(bizTypes);
  const [savedOk, setSavedOk] = useState(false);

  const toggle = (id: string) =>
    setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);

  const save = async () => {
    localStorage.setItem("kairos_biz_types", JSON.stringify(selected));
    await supabase.auth.updateUser({ data: { kairos_biz_types: selected } });
    setSavedOk(true); setTimeout(()=>setSavedOk(false), 2500);
    // Reload para aplicar menus
    setTimeout(()=>window.location.reload(), 1000);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{fontSize:12,color:T.muted}}>
        Selecciona os tipos de negócio activos. Os menus na sidebar actualizam automaticamente.
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {allBiz.map((b:any)=>{
          const on = selected.includes(b.id);
          return (
            <div key={b.id} onClick={()=>toggle(b.id)}
              style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",
                borderRadius:10,cursor:"pointer",transition:"all 0.15s",
                background:on?`${T.blueL}12`:T.panel,
                border:`1px solid ${on?T.blueL:T.border}`}}>
              <span style={{fontSize:22,flexShrink:0}}>{b.icon}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:on?700:400,color:T.text}}>{b.label}</div>
                <div style={{fontSize:11,color:T.muted}}>{b.desc}</div>
              </div>
              <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${on?T.blueL:T.border}`,
                background:on?T.blueL:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {on&&<span style={{color:"white",fontSize:12,fontWeight:700}}>✓</span>}
              </div>
            </div>
          );
        })}
      </div>
      {savedOk&&<div style={{padding:"9px 14px",borderRadius:10,background:`${T.green}15`,border:`1px solid ${T.green}40`,color:T.green,fontSize:13}}>✅ A actualizar menus...</div>}
      <button onClick={save} style={{padding:"10px 20px",borderRadius:10,border:"none",background:T.blueL,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>💾 Guardar e Actualizar Menus</button>
    </div>
  );
}

// ─── Subscrição ───────────────────────────────────────────────
function SettingsSubscricao() {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{padding:"16px 18px",borderRadius:12,background:`${T.blueL}10`,border:`1px solid ${T.blueL}30`}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
          <span style={{fontSize:24}}>💳</span>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:T.text}}>Plano Pro</div>
            <div style={{fontSize:12,color:T.green}}>✓ Activo</div>
          </div>
        </div>
        <div style={{fontSize:12,color:T.muted}}>Acesso completo a todos os módulos e funcionalidades.</div>
      </div>
      {[
        ["✓","Empresas ilimitadas"],
        ["✓","Integrações iCal Airbnb + Booking"],
        ["✓","Resumo semanal Telegram"],
        ["✓","Suporte prioritário"],
        ["✓","Acesso antecipado a novas funcionalidades"],
      ].map(([icon,label])=>(
        <div key={label} style={{display:"flex",alignItems:"center",gap:10,fontSize:13,color:T.text}}>
          <span style={{color:T.green,fontWeight:700}}>{icon}</span>{label}
        </div>
      ))}
      <div style={{padding:"12px 14px",borderRadius:10,background:T.panel,border:`1px solid ${T.border}`,fontSize:12,color:T.muted,marginTop:8}}>
        🚧 Gestão de faturação e upgrade/downgrade disponível em breve.
      </div>
    </div>
  );
}

// ─── Ajuda & Suporte ─────────────────────────────────────────
function SettingsAjuda() {
  const faqs = [
    {q:"Como adiciono uma nova reserva?", a:"Vai a Quinta da Regada → Reservas → botão '+ Reserva'. Preenche os dados e guarda. Se tiveres as integrações iCal configuradas, as reservas do Airbnb e Booking entram automaticamente."},
    {q:"Como configuro o sync automático do Airbnb?", a:"Vai a Definições → Integrações → cola o link iCal de cada alojamento. O link obtém-se no Airbnb em Calendário → Associar a outro site → Passo 1."},
    {q:"Como convido um colaborador?", a:"Vai a Definições → Equipa & Acessos. Esta funcionalidade está em desenvolvimento e estará disponível em breve."},
    {q:"Como recebo o resumo semanal no Telegram?", a:"Vai a Definições → Notificações → activa o Telegram e cola o teu Chat ID (obtém em @userinfobot no Telegram)."},
    {q:"Os dados ficam guardados em que dispositivo?", a:"Os dados ficam guardados na nuvem (Supabase), ligados à tua conta. Podes aceder de qualquer dispositivo com o mesmo login."},
  ];
  const [open, setOpen] = useState<number|null>(null);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div>
        <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:12}}>Perguntas Frequentes</div>
        {faqs.map((faq,i)=>(
          <div key={i} style={{marginBottom:6,borderRadius:10,border:`1px solid ${T.border}`,overflow:"hidden"}}>
            <button onClick={()=>setOpen(open===i?null:i)}
              style={{width:"100%",padding:"12px 14px",background:T.panel,border:"none",
                color:T.text,cursor:"pointer",textAlign:"left",fontSize:13,fontWeight:600,
                display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              {faq.q}
              <span style={{color:T.muted,fontSize:12}}>{open===i?"▲":"▼"}</span>
            </button>
            {open===i&&<div style={{padding:"10px 14px",background:T.surface,fontSize:12,color:T.muted,lineHeight:1.6}}>{faq.a}</div>}
          </div>
        ))}
      </div>
      <div style={{padding:"14px 16px",borderRadius:12,background:T.panel,border:`1px solid ${T.border}`}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:8}}>Contacto de Suporte</div>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.7}}>
          📧 suporte@kairosholdings.xyz<br/>
          Para questões técnicas urgentes ou relatório de bugs, envia email com a descrição do problema.<br/>
          Tempo de resposta: até 24h em dias úteis.
        </div>
      </div>
    </div>
  );
}

// ─── Feedback ────────────────────────────────────────────────
function SettingsConfig() {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{fontSize:13,color:T.muted,lineHeight:1.6}}>
        As configurações específicas de cada módulo estão disponíveis dentro do respectivo módulo:
      </div>
      {[
        {icon:"🏠",label:"Check-in, checkout e limpezas",onde:"Quinta da Regada → Alojamentos → Configurações de Horários"},
        {icon:"🎤",label:"Valor por data e comissão VJ",onde:"VJ Casamentos → Overview → ⚙️ Configurações VJ"},
        {icon:"💰",label:"Custo hora limpezas",onde:"Quinta da Regada → Limpezas → Custo/hora"},
      ].map((c,i)=>(
        <div key={i} style={{padding:"12px 14px",borderRadius:10,background:T.panel,border:`1px solid ${T.border}`}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
            <span style={{fontSize:20}}>{c.icon}</span>
            <div style={{fontSize:13,fontWeight:700,color:T.text}}>{c.label}</div>
          </div>
          <div style={{fontSize:11,color:T.muted}}>📍 {c.onde}</div>
        </div>
      ))}
      <div style={{padding:"10px 14px",borderRadius:10,background:`${T.blueL}10`,border:`1px solid ${T.blueL}30`,fontSize:12,color:T.muted}}>
        💡 Mais opções de configuração serão adicionadas aqui em futuras versões.
      </div>
    </div>
  );
}

function SettingsFeedback() {
  const [tipo, setTipo] = useState("sugestao");
  const [msg, setMsg]   = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from("logs").insert({
        empresa: "sistema", tipo: "feedback",
        descricao: `[${tipo}] ${msg}`,
        icon: tipo==="bug"?"🐛":tipo==="sugestao"?"💡":"⭐",
        meta: { tipo, user_email: user?.email, app_version: "2.4" }
      });
      setSent(true); setMsg("");
    } finally { setSending(false); }
  };

  const inp = {width:"100%",padding:"9px 12px",borderRadius:10,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13};
  const tipos = [{v:"sugestao",l:"💡 Sugestão"},{v:"bug",l:"🐛 Bug / Erro"},{v:"elogio",l:"⭐ Elogio"},{v:"outro",l:"💬 Outro"}];

  if (sent) return (
    <div style={{textAlign:"center",padding:"40px 20px"}}>
      <div style={{fontSize:40,marginBottom:12}}>🙏</div>
      <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:8}}>Obrigado pelo feedback!</div>
      <div style={{fontSize:13,color:T.muted,marginBottom:20}}>O teu contributo ajuda-nos a melhorar o Kairos OS.</div>
      <button onClick={()=>setSent(false)} style={{padding:"9px 20px",borderRadius:10,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}}>Enviar outro</button>
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{fontSize:12,color:T.muted}}>O teu feedback chega directamente à equipa de desenvolvimento.</div>
      <div>
        <div style={{fontSize:11,color:T.muted,marginBottom:6,fontWeight:700,textTransform:"uppercase" as any}}>Tipo</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap" as any}}>
          {tipos.map(t=>(
            <button key={t.v} onClick={()=>setTipo(t.v)}
              style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${tipo===t.v?T.blueL:T.border}`,
                background:tipo===t.v?`${T.blueL}18`:"transparent",color:tipo===t.v?T.blueL:T.muted,
                cursor:"pointer",fontSize:12,fontWeight:tipo===t.v?700:400}}>
              {t.l}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div style={{fontSize:11,color:T.muted,marginBottom:6,fontWeight:700,textTransform:"uppercase" as any}}>Mensagem</div>
        <textarea value={msg} onChange={(e:any)=>setMsg(e.target.value)}
          placeholder="Descreve a tua sugestão, bug ou comentário..."
          style={{...inp,height:120,resize:"vertical" as any}}/>
      </div>
      <button onClick={send} disabled={sending||!msg.trim()}
        style={{padding:"10px 20px",borderRadius:10,border:"none",background:T.blueL,color:"white",fontWeight:700,cursor:"pointer",fontSize:13,opacity:!msg.trim()||sending?0.6:1}}>
        {sending?"A enviar...":"📤 Enviar Feedback"}
      </button>
    </div>
  );
}

// ─── Privacidade ──────────────────────────────────────────────
function SettingsPrivacidade() {
  const [accessRequests, setAccessRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [granting, setGranting] = useState(false);
  const [duration, setDuration] = useState("24h");
  const [reason, setReason] = useState("");
  const [granted, setGranted] = useState(false);

  useEffect(() => {
    supabase.from("support_access").select("*").order("created_at",{ascending:false})
      .then(({data})=>{ setAccessRequests(data||[]); setLoading(false); });
  }, []);

  const grantAccess = async () => {
    setGranting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const hours = duration==="24h"?24:duration==="48h"?48:168;
      const expires = new Date(Date.now() + hours*3600000).toISOString();
      const token = Math.random().toString(36).slice(2) + Date.now().toString(36);
      await supabase.from("support_access").insert({
        user_id: user.id, token, reason, expires_at: expires, granted_by: user.id
      });
      setGranted(true); setReason("");
      const {data} = await supabase.from("support_access").select("*").order("created_at",{ascending:false});
      setAccessRequests(data||[]);
    } finally { setGranting(false); }
  };

  const revoke = async (id: string) => {
    await supabase.from("support_access").update({revoked:true,revoked_at:new Date().toISOString()}).eq("id",id);
    const {data} = await supabase.from("support_access").select("*").order("created_at",{ascending:false});
    setAccessRequests(data||[]);
  };

  const inp = {width:"100%",padding:"9px 12px",borderRadius:10,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{padding:"12px 14px",borderRadius:10,background:`${T.blueL}10`,border:`1px solid ${T.blueL}30`,fontSize:12,color:T.muted,lineHeight:1.6}}>
        🔒 Os teus dados são privados e só tu podes aceder. Podes autorizar temporariamente o acesso ao suporte técnico para resolução de problemas.
      </div>

      <div>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Autorizar Acesso de Suporte</div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div>
            <div style={{fontSize:11,color:T.muted,marginBottom:5,fontWeight:700,textTransform:"uppercase" as any}}>Duração</div>
            <select value={duration} onChange={(e:any)=>setDuration(e.target.value)} style={inp}>
              <option value="24h">24 horas</option>
              <option value="48h">48 horas</option>
              <option value="7d">7 dias</option>
            </select>
          </div>
          <div>
            <div style={{fontSize:11,color:T.muted,marginBottom:5,fontWeight:700,textTransform:"uppercase" as any}}>Motivo (opcional)</div>
            <input value={reason} onChange={(e:any)=>setReason(e.target.value)} placeholder="Ex: Erro nas reservas" style={inp}/>
          </div>
          {granted&&<div style={{padding:"9px 14px",borderRadius:10,background:`${T.green}15`,border:`1px solid ${T.green}40`,color:T.green,fontSize:13}}>✅ Acesso autorizado. A equipa de suporte foi notificada.</div>}
          <button onClick={grantAccess} disabled={granting}
            style={{padding:"10px 20px",borderRadius:10,border:"none",background:T.amber,color:"white",fontWeight:700,cursor:"pointer",fontSize:13,opacity:granting?0.7:1}}>
            {granting?"A processar...":"🔓 Autorizar Acesso Temporário"}
          </button>
        </div>
      </div>

      {accessRequests.length>0&&(
        <div>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>Histórico de Acessos</div>
          {accessRequests.map((r:any)=>{
            const expired = new Date(r.expires_at) < new Date();
            const active = !r.revoked && !expired;
            return (
              <div key={r.id} style={{padding:"10px 14px",borderRadius:10,background:T.panel,
                border:`1px solid ${active?T.amber:T.border}`,marginBottom:6,
                display:"flex",alignItems:"center",gap:10}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,color:T.text}}>{r.reason||"Sem motivo especificado"}</div>
                  <div style={{fontSize:11,color:T.muted}}>Expira: {new Date(r.expires_at).toLocaleString("pt-PT")}</div>
                </div>
                <Tag label={r.revoked?"Revogado":expired?"Expirado":"Activo"} color={active?T.amber:T.muted}/>
                {active&&<button onClick={()=>revoke(r.id)}
                  style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${T.red}`,
                    background:"transparent",color:T.red,cursor:"pointer",fontSize:11,fontWeight:600}}>
                  Revogar
                </button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}




// ─── Gerir Empresas ──────────────────────────────────────────
function SettingsConfigAL() {
  const LS_KEY = "kairos_al_config";
  const defaults = { horaCI:"16:00", horaCO:"10:00", horaLimpeza:"10:00", limpezaAuto:true, notifLimpeza:true };
  const [cfg, setCfg] = useState<any>(() => {
    try { return { ...defaults, ...JSON.parse(localStorage.getItem(LS_KEY)||"{}") }; }
    catch { return defaults; }
  });
  const [saved, setSaved] = useState(false);

  const save = () => {
    localStorage.setItem(LS_KEY, JSON.stringify(cfg));
    setSaved(true); setTimeout(()=>setSaved(false), 2500);
  };

  const inp = {padding:"9px 12px",borderRadius:10,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13};
  const lbl = {fontSize:11,color:T.muted,marginBottom:5,fontWeight:700 as any,textTransform:"uppercase" as any};
  const Row = ({label,children}:{label:string,children:any}) => (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",borderBottom:`1px solid ${T.border}`}}>
      <span style={{fontSize:13,color:T.text}}>{label}</span>
      {children}
    </div>
  );
  const Toggle = ({val,key}:{val:boolean,key:string}) => (
    <button onClick={()=>setCfg((c:any)=>({...c,[key]:!c[key]}))}
      style={{padding:"5px 14px",borderRadius:8,border:"none",cursor:"pointer",fontWeight:700,fontSize:12,background:val?T.green:T.panel2,color:val?"white":T.muted}}>
      {val?"✓ Activo":"Desligado"}
    </button>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:4}}>
      <div style={{fontSize:12,color:T.muted,marginBottom:8}}>
        Horários predefinidos para a Quinta da Regada. Podes alterar em cada reserva individualmente.
      </div>

      <Row label="🕐 Hora de Check-in predefinida">
        <input type="time" value={cfg.horaCI} onChange={e=>setCfg((c:any)=>({...c,horaCI:e.target.value}))} style={inp}/>
      </Row>
      <Row label="🕐 Hora de Check-out predefinida">
        <input type="time" value={cfg.horaCO} onChange={e=>setCfg((c:any)=>({...c,horaCO:e.target.value}))} style={inp}/>
      </Row>
      <Row label="🧹 Hora de limpeza predefinida">
        <input type="time" value={cfg.horaLimpeza} onChange={e=>setCfg((c:any)=>({...c,horaLimpeza:e.target.value}))} style={inp}/>
      </Row>
      <Row label="🤖 Agendar limpeza automaticamente no checkout">
        <Toggle val={cfg.limpezaAuto} key="limpezaAuto"/>
      </Row>
      <Row label="📲 Notificar responsável de limpeza">
        <Toggle val={cfg.notifLimpeza} key="notifLimpeza"/>
      </Row>

      <div style={{marginTop:12,padding:"12px 14px",borderRadius:10,background:`${T.blueL}10`,border:`1px solid ${T.blueL}30`,fontSize:12,color:T.muted}}>
        💡 Com "Agendar limpeza automaticamente" activo, quando adicionas uma reserva (via iCal ou manual), é automaticamente criada uma entrada no calendário de limpezas para o dia do checkout às {cfg.horaLimpeza}.
      </div>

      {saved&&<div style={{padding:"9px 14px",borderRadius:10,background:`${T.green}15`,border:`1px solid ${T.green}40`,color:T.green,fontSize:13,marginTop:8}}>✅ Configurações guardadas</div>}
      <button onClick={save} style={{marginTop:12,padding:"10px 20px",borderRadius:10,border:"none",background:T.blueL,color:"white",fontWeight:700,cursor:"pointer",fontSize:13}}>💾 Guardar Configurações</button>
    </div>
  );
}

function IntegrationSettings({ onClose }: { onClose?: () => void }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [error, setError]   = useState("");

  // Casas criadas pelo utilizador (para dropdown)
  const { data: casas } = useALCasas();

  // Estado dos campos
  const [airbnbUrls, setAirbnbUrls] = useState<Array<{url:string,casa_nome:string}>>([
    {url:"",casa_nome:""},
  ]);
  const [bookingUrls, setBookingUrls] = useState<Array<{url:string,casa_nome:string}>>([
    {url:"",casa_nome:""},
  ]);
  const [telegramId, setTelegramId]   = useState("");
  const [telegramOn, setTelegramOn]   = useState(false);
  const [sheetsUrl, setSheetsUrl]     = useState("");
  const [googleCal, setGoogleCal]     = useState("");

  // Carregar dados existentes
  useEffect(() => {
    supabase.from("user_integrations").select("*").single()
      .then(({ data, error }) => {
        if (error || !data) return;
        if (Array.isArray(data.airbnb_ical_urls) && data.airbnb_ical_urls.length)
          setAirbnbUrls(data.airbnb_ical_urls);
        if (Array.isArray(data.booking_ical_urls) && data.booking_ical_urls.length)
          setBookingUrls(data.booking_ical_urls);
        if (data.telegram_chat_id) setTelegramId(data.telegram_chat_id);
        if (data.telegram_active)  setTelegramOn(data.telegram_active);
        if (data.sheets_url)       setSheetsUrl(data.sheets_url);
        if (data.google_calendar_id) setGoogleCal(data.google_calendar_id);
      });
  }, []);

  const save = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Sessão expirada");

      const { error: err } = await supabase.from("user_integrations").upsert({
        user_id:            user.id,
        airbnb_ical_url:    airbnbUrls.filter((e:any) => e.url)[0]?.url || null,
        booking_ical_url:   bookingUrls.filter((e:any) => e.url)[0]?.url || null,
        airbnb_ical_urls:   airbnbUrls.filter((e:any) => e.url),
        booking_ical_urls:  bookingUrls.filter((e:any) => e.url),
        telegram_chat_id:   telegramId,
        telegram_active:    telegramOn,
        sheets_url:         sheetsUrl,
        google_calendar_id: googleCal,
      }, { onConflict: "user_id" });

      if (err) throw err;
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch(e: any) {
      setError(e.message || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  // Helper: dropdown de casas
  const CasaSelect = ({ value, onChange }: { value: string, onChange: (v:string)=>void }) => (
    casas && casas.length > 0 ? (
      <select value={value} onChange={(e:any)=>onChange(e.target.value)}
        style={{width:"100%",padding:"9px 12px",borderRadius:10,
          background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}>
        <option value="">— Seleccionar casa —</option>
        {casas.map((c:any) => (
          <option key={c.id} value={c.nome}>{c.icone||"🏠"} {c.nome}</option>
        ))}
      </select>
    ) : (
      <input value={value} onChange={(e:any)=>onChange(e.target.value)}
        placeholder="Ex: Girassol (cria casas em AL → Alojamentos)"
        style={{width:"100%",padding:"9px 12px",borderRadius:10,
          background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:13}}/>
    )
  );

  const inp = {
    width:"100%", padding:"9px 12px", borderRadius:10,
    background:T.surface, border:`1px solid ${T.border}`,
    color:T.text, fontSize:13,
  };
  const lbl = {
    fontSize:11, color:T.muted, marginBottom:5,
    fontWeight:700 as any, textTransform:"uppercase" as any, letterSpacing:"0.06em",
  };
  const sec = {
    padding:"18px 20px", borderRadius:14,
    background:T.panel, border:`1px solid ${T.border}`, marginBottom:14,
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{marginBottom:4}}>
        <div style={{fontSize:20,fontWeight:800,color:T.text}}>🔗 Integrações</div>
        <div style={{fontSize:13,color:T.muted,marginTop:4}}>
          Conecta as tuas plataformas — os dados sincronizam automaticamente de hora em hora
        </div>
      </div>
      <div>

          {/* ── Airbnb iCal ── */}
          <div style={sec}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
              <span style={{fontSize:22}}>🏠</span>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:T.text}}>Airbnb — Calendário iCal</div>
                <div style={{fontSize:11,color:T.muted}}>Um link por cada alojamento. Airbnb → Calendário → Associar a outro site → Copiar link</div>
              </div>
            </div>
            {airbnbUrls.map((entry, i) => (
              <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 200px auto",gap:8,marginBottom:8,alignItems:"center"}}>
                <div>
                  {i===0&&<div style={lbl}>Link iCal (.ics)</div>}
                  <input value={entry.url}
                    onChange={e=>setAirbnbUrls(arr=>arr.map((a,j)=>j===i?{...a,url:e.target.value}:a))}
                    placeholder="https://www.airbnb.pt/calendar/ical/..."
                    style={inp}/>
                </div>
                <div>
                  {i===0&&<div style={lbl}>Alojamento</div>}
                  <CasaSelect value={entry.casa_nome}
                    onChange={v=>setAirbnbUrls(arr=>arr.map((a,j)=>j===i?{...a,casa_nome:v}:a))}/>
                </div>
                <div style={{display:"flex",alignItems:"flex-end",paddingBottom:1}}>
                  {i===0&&<div style={{...lbl,visibility:"hidden"}}>x</div>}
                  <button onClick={()=>setAirbnbUrls(arr=>arr.filter((_,j)=>j!==i))}
                    style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:8,
                      color:T.muted,cursor:"pointer",padding:"8px 12px",fontSize:13}}>✕</button>
                </div>
              </div>
            ))}
            <button onClick={()=>setAirbnbUrls(arr=>[...arr,{url:"",casa_nome:""}])}
              style={{marginTop:4,padding:"7px 14px",borderRadius:8,border:`1px dashed ${T.border}`,
                background:"transparent",color:T.muted,cursor:"pointer",fontSize:12,fontWeight:600}}>
              + Adicionar alojamento
            </button>
          </div>

          {/* ── Booking iCal ── */}
          <div style={sec}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
              <span style={{fontSize:22}}>🏨</span>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:T.text}}>Booking.com — Calendário iCal</div>
                <div style={{fontSize:11,color:T.muted}}>Extranet Booking → Calendário → Sincronizar calendários → Exportar</div>
              </div>
            </div>
            {bookingUrls.map((entry, i) => (
              <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 200px auto",gap:8,marginBottom:8,alignItems:"center"}}>
                <div>
                  {i===0&&<div style={lbl}>Link iCal (.ics)</div>}
                  <input value={entry.url}
                    onChange={e=>setBookingUrls(arr=>arr.map((a,j)=>j===i?{...a,url:e.target.value}:a))}
                    placeholder="https://admin.booking.com/hotel/hoteladmin/ical.html?..."
                    style={inp}/>
                </div>
                <div>
                  {i===0&&<div style={lbl}>Alojamento</div>}
                  <CasaSelect value={entry.casa_nome}
                    onChange={v=>setBookingUrls(arr=>arr.map((a,j)=>j===i?{...a,casa_nome:v}:a))}/>
                </div>
                <div style={{display:"flex",alignItems:"flex-end",paddingBottom:1}}>
                  {i===0&&<div style={{...lbl,visibility:"hidden"}}>x</div>}
                  <button onClick={()=>setBookingUrls(arr=>arr.filter((_,j)=>j!==i))}
                    style={{background:"transparent",border:`1px solid ${T.border}`,borderRadius:8,
                      color:T.muted,cursor:"pointer",padding:"8px 12px",fontSize:13}}>✕</button>
                </div>
              </div>
            ))}
            <button onClick={()=>setBookingUrls(arr=>[...arr,{url:"",casa_nome:""}])}
              style={{marginTop:4,padding:"7px 14px",borderRadius:8,border:`1px dashed ${T.border}`,
                background:"transparent",color:T.muted,cursor:"pointer",fontSize:12,fontWeight:600}}>
              + Adicionar alojamento
            </button>
          </div>

          {/* ── Telegram ── */}
          <div style={sec}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
              <span style={{fontSize:22}}>✈️</span>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:700,color:T.text}}>Telegram — Resumo Semanal</div>
                <div style={{fontSize:11,color:T.muted}}>Recebe um resumo automático todos os domingos às 20h</div>
              </div>
              <button onClick={()=>setTelegramOn(v=>!v)}
                style={{padding:"6px 14px",borderRadius:8,border:"none",cursor:"pointer",
                  fontWeight:700,fontSize:12,
                  background:telegramOn?T.green:T.panel2,
                  color:telegramOn?"white":T.muted}}>
                {telegramOn?"✓ Activo":"Desligado"}
              </button>
            </div>
            <div style={lbl}>Telegram Chat ID</div>
            <input value={telegramId} onChange={e=>setTelegramId(e.target.value)}
              placeholder="Ex: 123456789 (obtém em @userinfobot)"
              style={inp}/>
            <div style={{fontSize:11,color:T.muted,marginTop:6}}>
              💡 Para obteres o teu Chat ID: abre o Telegram, fala com <b>@userinfobot</b> e copia o número que ele te envia.
            </div>
          </div>

          {/* ── Google Sheets ── */}
          <div style={sec}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
              <span style={{fontSize:22}}>📊</span>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:T.text}}>Google Sheets (opcional)</div>
                <div style={{fontSize:11,color:T.muted}}>Importa dados de faturação ou reservas de uma folha partilhada</div>
              </div>
            </div>
            <div style={lbl}>URL da folha (partilhada publicamente)</div>
            <input value={sheetsUrl} onChange={e=>setSheetsUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/..."
              style={inp}/>
          </div>

          {/* Feedback e botão */}
          {error && (
            <div style={{padding:"10px 14px",borderRadius:10,background:`${T.red}15`,
              border:`1px solid ${T.red}40`,color:T.red,fontSize:13,marginBottom:12}}>
              ⚠️ {error}
            </div>
          )}
          {saved && (
            <div style={{padding:"10px 14px",borderRadius:10,background:`${T.green}15`,
              border:`1px solid ${T.green}40`,color:T.green,fontSize:13,marginBottom:12}}>
              ✅ Integrações guardadas! O sync iCal corre automaticamente de hora em hora.
            </div>
          )}

          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={save} disabled={saving}
              style={{padding:"11px 24px",borderRadius:10,border:"none",
                background:T.blueL,color:"white",cursor:"pointer",fontSize:13,fontWeight:700,
                opacity:saving?0.7:1}}>
              {saving?"A guardar...":"💾 Guardar Integrações"}
            </button>
          </div>
      </div>
    </div>
  );
}

function OnboardingModal({ user, onClose }: { user:string; onClose:()=>void }) {
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [nomeNegocio, setNomeNegocio] = useState("");
  const [pais, setPais] = useState("PT");

  const toggleBiz = (id:string) =>
    setSelected(s=>s.includes(id)?s.filter(x=>x!==id):[...s,id]);

  const finish = () => {
    localStorage.setItem("kairos_onboarded", "1");
    localStorage.setItem("kairos_biz_types", JSON.stringify(selected));
    // Guardar em Supabase user_metadata — persiste em todos os dispositivos
    supabase.auth.updateUser({
      data: {
        kairos_onboarded: true,
        kairos_biz_types: selected,
        kairos_holding_name: nomeNegocio,
      }
    }).catch(() => {}); // silencioso — o localStorage é o fallback
    if(nomeNegocio) localStorage.setItem("kairos_holding_name", nomeNegocio);
    onClose();
  };

  return (
    <div style={{
      position:"fixed",inset:0,zIndex:999,
      background:"rgba(5,8,16,0.92)",
      display:"flex",alignItems:"center",justifyContent:"center",
      padding:20,
    }}>
      <div style={{
        background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,
        padding:32,maxWidth:520,width:"100%",
        boxShadow:"0 24px 80px rgba(0,0,0,0.6)",
      }}>
        {/* Progress */}
        <div style={{display:"flex",gap:6,marginBottom:28}}>
          {[1,2,3].map(s=>(
            <div key={s} style={{flex:1,height:3,borderRadius:99,
              background:s<=step?T.blueL:"rgba(255,255,255,0.08)"}}/>
          ))}
        </div>

        {step===1 && (
          <>
            <div style={{fontSize:22,fontWeight:800,color:T.text,marginBottom:6}}>
              Bem-vindo, {user}! 👋
            </div>
            <div style={{fontSize:14,color:T.muted,marginBottom:24}}>
              Vamos configurar o Kairos OS para os teus negócios.
            </div>
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:6,fontWeight:700,textTransform:"uppercase"}}>
                Nome do teu grupo / holding (opcional)
              </div>
              <input value={nomeNegocio} onChange={e=>setNomeNegocio(e.target.value)}
                placeholder="Ex: Atlantic Group, Kairos Holdings..."
                style={{width:"100%",padding:"11px 14px",borderRadius:10,
                  background:"rgba(255,255,255,0.05)",border:`1px solid ${T.border}`,
                  color:T.text,fontSize:14,outline:"none"}}/>
            </div>
            <div style={{marginBottom:24}}>
              <div style={{fontSize:11,color:T.muted,marginBottom:6,fontWeight:700,textTransform:"uppercase"}}>País</div>
              <select value={pais} onChange={e=>setPais(e.target.value)}
                style={{width:"100%",padding:"11px 14px",borderRadius:10,background:T.surface,
                  border:`1px solid ${T.border}`,color:T.text,fontSize:14}}>
                <option value="PT">🇵🇹 Portugal</option>
                <option value="ES">🇪🇸 Espanha</option>
                <option value="BR">🇧🇷 Brasil</option>
                <option value="UK">🇬🇧 Reino Unido</option>
                <option value="FR">🇫🇷 França</option>
                <option value="DE">🇩🇪 Alemanha</option>
                <option value="OTHER">🌍 Outro</option>
              </select>
            </div>
            <button onClick={()=>setStep(2)} style={{
              width:"100%",padding:"13px",borderRadius:10,border:"none",
              background:`linear-gradient(135deg,${T.blue},${T.blueL})`,
              color:"white",fontSize:14,fontWeight:700,cursor:"pointer"}}>
              Continuar →
            </button>
          </>
        )}

        {step===2 && (
          <>
            <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:6}}>
              Que tipos de negócio geres?
            </div>
            <div style={{fontSize:13,color:T.muted,marginBottom:20}}>
              Selecciona todos os que se aplicam. Podes alterar depois.
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:8,marginBottom:24}}>
              {BUSINESS_TYPES.map(b=>(
                <div key={b.id} onClick={()=>toggleBiz(b.id)}
                  style={{
                    padding:"12px",borderRadius:10,cursor:"pointer",
                    border:`1px solid ${selected.includes(b.id)?T.blueL:T.border}`,
                    background:selected.includes(b.id)?`${T.blueL}15`:"transparent",
                    transition:"all 0.15s",
                  }}>
                  <div style={{fontSize:20,marginBottom:4}}>{b.icon}</div>
                  <div style={{fontSize:12,fontWeight:700,color:T.text}}>{b.label}</div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2}}>{b.desc}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setStep(1)} style={{padding:"11px 20px",borderRadius:10,
                border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}}>
                ← Voltar
              </button>
              <button onClick={()=>setStep(3)} disabled={selected.length===0}
                style={{flex:1,padding:"11px",borderRadius:10,border:"none",
                  background:selected.length>0?`linear-gradient(135deg,${T.blue},${T.blueL})`:"rgba(255,255,255,0.08)",
                  color:selected.length>0?"white":T.muted,fontSize:14,fontWeight:700,
                  cursor:selected.length>0?"pointer":"not-allowed"}}>
                Continuar →
              </button>
            </div>
          </>
        )}

        {step===3 && (
          <>
            <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:6}}>
              Tudo pronto! 🚀
            </div>
            <div style={{fontSize:13,color:T.muted,marginBottom:20}}>
              O Kairos OS está configurado para ti. Podes alterar qualquer definição mais tarde.
            </div>
            <div style={{padding:16,borderRadius:10,background:T.panel2,border:`1px solid ${T.border}`,marginBottom:24}}>
              {nomeNegocio&&<div style={{fontSize:13,color:T.text,marginBottom:6}}>🏢 <strong>{nomeNegocio}</strong></div>}
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {selected.map(id=>{
                  const b = BUSINESS_TYPES.find(x=>x.id===id);
                  return b ? <Tag key={id} label={`${b.icon} ${b.label}`} color={T.blueL}/> : null;
                })}
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setStep(2)} style={{padding:"11px 20px",borderRadius:10,
                border:`1px solid ${T.border}`,background:"transparent",color:T.muted,cursor:"pointer",fontSize:13}}>
                ← Voltar
              </button>
              <button onClick={finish} style={{flex:1,padding:"11px",borderRadius:10,border:"none",
                background:`linear-gradient(135deg,${T.blue},${T.blueL})`,
                color:"white",fontSize:14,fontWeight:700,cursor:"pointer"}}>
                Começar a usar →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── MAIN APP ───────────────────────────────────────────────────────────────── */
export default function App() {
  // Persist active page across refreshes
  const savedCo   = localStorage.getItem("kairos_co")   || "holdings";
  const savedPage = localStorage.getItem("kairos_page") || "overview";
  const savedUser = localStorage.getItem("kairos_user") || "";

  const [activeCo,     setActiveCo]   = useState(savedCo);
  const [activePage,   setActivePage] = useState(savedPage);
  const [collapsed,    setCollapsed]  = useState(false);
  const [mobileOpen,   setMobileOpen] = useState(false);
  const [loggedUser,   setLoggedUser] = useState(savedUser);
  const [themeKey,     setThemeKey]   = useState<"dark"|"light">((localStorage.getItem("kairos_theme") as any)||"dark");
  const [lang,         setLang]       = useState<string>(localStorage.getItem("kairos_lang")||"pt");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [settingsPage, setSettingsPage] = useState<string|null>(null);
  // Histórico de navegação para "voltar atrás"
  const [navHistory, setNavHistory]   = useState<Array<{co:string;page:string}>>([]);
  const [navFuture, setNavFuture]     = useState<Array<{co:string;page:string}>>([]);
  // Global state para calendário agregado
  // Dados globais — lazy loaded: só carregam quando entras em Holdings ou no NotifBell abre
  // Hooks correm SEMPRE (regras do React) mas com enabled=false não fazem fetch nem criam canal
  const needsGlobals = activeCo === "holdings" || activePage === "calendario";
  const { data: vjEventsDB }    = useTable('vj_eventos',  { orderBy: 'data',       ascending: false, enabled: needsGlobals });
  const { data: alResDB }       = useTable('al_reservas', { orderBy: 'check_in',   ascending: false, enabled: needsGlobals });
  const { data: hw3dProjDB }    = useTable('hw3d_projetos', { orderBy: 'created_at', ascending: false, realtime: false, enabled: needsGlobals });
  const { data: kaiEventosDB }  = useTable('kai_calendario', { orderBy: 'data',     realtime: false, enabled: needsGlobals });
  const { data: pbEventosDB }   = useTable('pb_eventos',  { orderBy: 'data',       ascending: false, realtime: false, enabled: needsGlobals });

  const vjEventsGlobal   = isDemoAccount() ? DEMO_VJ_EVENTS    : normResArr(vjEventsDB  || []);
  const alResGlobal      = isDemoAccount() ? DEMO_AL_RES       : normResArr(alResDB     || []);
  const hw3dProjGlobal   = isDemoAccount() ? DEMO_HW3D_PIPELINE: (hw3dProjDB  || []);
  const kaiEventsGlobal  = isDemoAccount() ? []                : (kaiEventosDB|| []);
  const pbEventsGlobal   = isDemoAccount() ? []                : (pbEventosDB || []);

  // Setters legacy — mantidos para compatibilidade com onEventsChange callbacks
  const setVjEventsGlobal  = (_:any) => {};
  const setAlResGlobal     = (_:any) => {};
  const setHw3dProjGlobal  = (_:any) => {};

  // Acessibilidade: Escape fecha drawer mobile + setas para navegação
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (mobileOpen) setMobileOpen(false);
        return;
      }
      // Seta esquerda/Alt+Left = voltar (quando não está num input/select)
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if ((e.key === "ArrowLeft" && e.altKey) || (e.key === "Backspace" && e.altKey)) {
        e.preventDefault();
        navigateBack();
      }
      if (e.key === "ArrowRight" && e.altKey) {
        e.preventDefault();
        navigateForward();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, navHistory, navFuture, activeCo, activePage]);
  const isMobile = useIsMobile();
  const mainRef = useRef<HTMLDivElement>(null);

  // Note: activeCo reset handled by sidebar visibleCompanies filter

  // Restore Supabase session on load
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user && !loggedUser) {
        const displayName = session.user.user_metadata?.full_name
          || session.user.email?.split("@")[0]
          || "Utilizador";
        localStorage.setItem("kairos_user", displayName);
        localStorage.setItem("kairos_uid", session.user.id);
        localStorage.setItem("kairos_user_email", session.user.email || "");
        // Sincronizar estado do onboarding com Supabase metadata
        const onboarded = session.user.user_metadata?.kairos_onboarded === true;
        if (onboarded) {
          localStorage.setItem("kairos_onboarded", "1");
          // Restaurar biz_types se existirem nos metadados
          if (session.user.user_metadata?.kairos_biz_types && !localStorage.getItem("kairos_biz_types")) {
            localStorage.setItem("kairos_biz_types", JSON.stringify(session.user.user_metadata.kairos_biz_types));
          }
          if (session.user.user_metadata?.kairos_holding_name && !localStorage.getItem("kairos_holding_name")) {
            localStorage.setItem("kairos_holding_name", session.user.user_metadata.kairos_holding_name);
          }
        }
        setLoggedUser(displayName);
      }
    });
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session && loggedUser) {
        setLoggedUser("");
        setActiveCo("holdings");
        setActivePage("overview");
      }
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show onboarding after login if flagged
  useEffect(() => {
    if (loggedUser && localStorage.getItem("kairos_show_onboarding") === "1") {
      localStorage.removeItem("kairos_show_onboarding");
      setShowOnboarding(true);
    }
  }, [loggedUser]);

  // Listen for navigation events from UserMenu and other components
  useEffect(() => {
    const handler = (e: Event) => {
      const { co, page } = (e as CustomEvent).detail;
      // settings tratado via kairos-settings event
      if (co) { setActiveCo(co); localStorage.setItem("kairos_co", co); }
      if (page) { setActivePage(page); localStorage.setItem("kairos_page", page); }
    };
    window.addEventListener("kairos-nav", handler);
    return () => window.removeEventListener("kairos-nav", handler);
  }, []);

  // Listener para abrir páginas de Definições como drawer lateral
  useEffect(() => {
    const handler = (e: Event) => {
      const { page } = (e as CustomEvent).detail;
      setSettingsPage(page);
    };
    window.addEventListener("kairos-settings", handler);
    return () => window.removeEventListener("kairos-settings", handler);
  }, []);

  // Apply theme to T — override with current theme values
  const theme = THEMES[themeKey];
  Object.assign(T, theme);

  const toggleTheme = () => {
    const next = themeKey==="dark"?"light":"dark";
    setThemeKey(next);
    localStorage.setItem("kairos_theme",next);
  };
  const cycleLang = () => {
    const langs=["pt","en","es"];
    const next=langs[(langs.indexOf(lang)+1)%langs.length];
    setLang(next);
    localStorage.setItem("kairos_lang",next);
  };
  const langFlag:Record<string,string>={pt:"🇵🇹",en:"🇬🇧",es:"🇪🇸"};

  // Global logs and notification state
  const { logs, addLog } = useLogs(SEED_LOGS);
  const [impostosGlobal]    = useState(TESOURARIA_INIT.impostos);

  // If not logged in, show login page
  if (!loggedUser) {
    return <LoginPage onLogin={user => {
      setLoggedUser(user);
      addLog("holdings","sistema",`Sessão iniciada por ${user}`,"⚡");
    }}/>;
  }

  const company = COMPANIES.find(c => c.id === activeCo);

  const navigate = (coId: string, pageId: string, pushHistory = true) => {
    if (pushHistory) {
      setNavHistory(h => [...h.slice(-49), { co: activeCo, page: activePage }]);
      setNavFuture([]); // limpar future ao navegar manualmente
    }
    setActiveCo(coId);
    setActivePage(pageId);
    localStorage.setItem("kairos_co", coId);
    localStorage.setItem("kairos_page", pageId);
    if (isMobile) setMobileOpen(false);
    if (mainRef.current) mainRef.current.scrollTop = 0;
  };

  const navigateBack = () => {
    if (navHistory.length === 0) return;
    const prev = navHistory[navHistory.length - 1];
    setNavFuture(f => [{ co: activeCo, page: activePage }, ...f]);
    setNavHistory(h => h.slice(0, -1));
    navigate(prev.co, prev.page, false);
  };

  const navigateForward = () => {
    if (navFuture.length === 0) return;
    const next = navFuture[0];
    setNavHistory(h => [...h, { co: activeCo, page: activePage }]);
    setNavFuture(f => f.slice(1));
    navigate(next.co, next.page, false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem("kairos_user");
    localStorage.removeItem("kairos_uid");
    localStorage.removeItem("kairos_user_email");
    localStorage.removeItem("kairos_co");
    localStorage.removeItem("kairos_page");
    setLoggedUser("");
    setActiveCo("holdings");
    setActivePage("overview");
  };

  // Filtrar empresas com base nos tipos de negócio do utilizador
  const visibleCompanies = (() => {
    if (isDemoAccount()) return COMPANIES; // demo vê tudo
    const saved = localStorage.getItem("kairos_biz_types");
    if (!saved) return COMPANIES; // sem onboarding, vê tudo
    const bizTypes: string[] = JSON.parse(saved);
    const allowedIds = new Set<string>(["holdings","homebase","settings"]); // sempre visíveis
    bizTypes.forEach(biz => {
      (BIZ_TO_COMPANY[biz]||[]).forEach((id:string) => allowedIds.add(id));
    });
    // Holdings só aparece se tiver 2+ empresas além de homebase e holdings
    const companyCount = Array.from(allowedIds).filter(id=>id!=="holdings"&&id!=="homebase"&&id!=="settings").length;
    if (companyCount < 2) allowedIds.delete("holdings");
    return COMPANIES.filter(c => allowedIds.has(c.id));
  })();

  const renderPage = () => {
    if (activePage === "logs") return <LogView logs={logs} empresa={activeCo}/>;
    if (activeCo === "holdings" && activePage === "calendario")
      return <HoldingsCalendario vjEvents={vjEventsGlobal} alRes={alResGlobal} hw3dProjects={hw3dProjGlobal} kaiEvents={kaiEventsGlobal} pbEvents={pbEventsGlobal}/>;
    switch(activeCo) {
      case "holdings":   return <HoldingsView page={activePage} logs={logs}/>;
      case "hw3d":       return <HW3DView page={activePage} addLog={addLog} onProjChange={setHw3dProjGlobal}/>;
      case "kai_ai":     return <KaiView page={activePage} addLog={addLog}/>;
      case "quinta":     return <QuintaView page={activePage} addLog={addLog} onResChange={setAlResGlobal}/>;
      case "photobooth": return <PhotoboothView page={activePage} addLog={addLog}/>;
      case "vj":         return <VJView page={activePage} addLog={addLog} onEventsChange={setVjEventsGlobal}/>;
      case "homebase":   return <HomeBaseView page={activePage}/>;
      case "settings":   return <SettingsView page={activePage}/>;
      case "media":      return <PlaceholderView empresa={activeCo} page={activePage} icon="📺" color={CO.media}/>;
      case "influencer": return <PlaceholderView empresa={activeCo} page={activePage} icon="🎵" color={CO.influencer}/>;
      case "kids":       return <PlaceholderView empresa={activeCo} page={activePage} icon="👶" color={CO.kids}/>;
      default:           return null;
    }
  };

  const sidebarW = collapsed ? 58 : 234;

  return (
    <>
    {showOnboarding && !isDemoAccount() && (
      <OnboardingModal user={loggedUser} onClose={()=>setShowOnboarding(false)}/>
    )}
    {/* Drawer de Definições */}
    {settingsPage && (
      <SettingsDrawer page={settingsPage} onClose={()=>setSettingsPage(null)}/>
    )}
    <div style={{
      minHeight: "100vh",
      display: "flex",
      background: T.bg,
      fontFamily: "'DM Sans', system-ui, sans-serif",
      color: T.text,
      position: "relative",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        @keyframes loginFade { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes kairos-skeleton {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @media (max-width: 768px) {
          .kpi-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { -webkit-text-size-adjust: 100%; }
        body { overscroll-behavior: none; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.18); }
        input, select, button, textarea { font-family: inherit; }
        button { -webkit-tap-highlight-color: transparent; }
        button:focus-visible { outline: 2px solid ${T.borderFocus}; outline-offset: 2px; }
        input:focus, textarea:focus { outline: none; border-color: ${T.borderFocus} !important; box-shadow: 0 0 0 3px ${T.borderFocus}30; }
        select:focus { outline: none; }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .page-enter { animation: fadeSlideUp 0.22s ease forwards; }
        .mobile-drawer { animation: fadeIn 0.2s ease forwards; }

        /* ─── A11y: Touch targets mínimos em mobile ─── */
        @media (max-width: 768px) {
          button, [role="button"] {
            min-height: 44px;
          }
          /* excepção: botões muito pequenos dentro de tabelas/listas */
          button.btn-icon { min-height: auto; }
        }

        /* ─── A11y: Reduzir movimento ─── */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* ─── A11y: Screen reader only ─── */
        .sr-only {
          position: absolute; width:1px; height:1px;
          padding:0; margin:-1px; overflow:hidden;
          clip: rect(0,0,0,0); white-space: nowrap; border:0;
        }

        /* ─── Skip link ─── */
        .skip-link {
          position: absolute; top:-40px; left:8px;
          background:${T.blueL}; color:white; padding:10px 14px;
          border-radius:6px; z-index:9999; font-weight:600;
        }
        .skip-link:focus { top:8px; }
      `}</style>

      {/* ── SKIP LINK (a11y) ── */}
      <a href="#main-content" className="skip-link">Saltar para o conteúdo</a>

      {/* ── MOBILE OVERLAY ── */}
      {isMobile && mobileOpen && (
        <div
          className="mobile-drawer"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
          style={{
            position: "fixed", inset: 0,
            background: "rgba(5,8,16,0.75)",
            backdropFilter: "blur(4px)",
            zIndex: 40,
          }}
        />
      )}

      {/* ── SIDEBAR ── */}
      <nav id="main-nav" role="navigation" aria-label="Menu principal" style={{
        width: isMobile ? 260 : sidebarW,
        minWidth: isMobile ? 260 : sidebarW,
        height: "100vh",
        background: "rgba(10,13,24,0.98)",
        borderRight: `1px solid ${T.border}`,
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1), width 0.25s ease",
        flexShrink: 0,
        overflow: "hidden",
        position: isMobile ? "fixed" : "sticky",
        top: 0,
        left: 0,
        zIndex: 50,
        transform: isMobile ? (mobileOpen ? "translateX(0)" : "translateX(-100%)") : "translateX(0)",
        boxShadow: isMobile && mobileOpen ? "4px 0 40px rgba(0,0,0,0.5)" : "none",
      }}>

        {/* Logo + Collapse */}
        <div style={{
          padding: "14px 12px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${T.border}`,
          background: "rgba(255,255,255,0.01)",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src={LOGO_SRC} alt="Kairos" style={{
              width: 30, height: 30, borderRadius: 8,
              objectFit: "contain", background: "white", padding: 3, flexShrink: 0,
            }}/>
            {(!collapsed || isMobile) && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: T.text, letterSpacing: "-0.03em" }}>KAIROS OS</div>
                <div style={{ fontSize: 11, color: T.muted, letterSpacing: "0.1em", marginTop: 1 }}>HOLDING SYSTEM</div>
              </div>
            )}
          </div>
          {!isMobile && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              style={{
                background: "transparent",
                border: `1px solid ${T.border}`,
                color: T.textSub,
                cursor: "pointer",
                borderRadius: 6,
                width: 28, height: 28,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, fontWeight: 700, flexShrink: 0,
                transition: "all 0.15s",
              }}>
              {collapsed ? "›" : "‹"}
            </button>
          )}
          {isMobile && (
            <button onClick={() => setMobileOpen(false)}
              style={{ background: "transparent", border: "none", color: T.muted, fontSize: 22, cursor: "pointer", padding: "0 4px" }} aria-label="Fechar">
              ✕
            </button>
          )}
        </div>

        {/* Nav items */}
        <div style={{ flex: 1, overflowY: "auto", padding: "6px 6px 16px" }}>
          {COMPANIES.map(co => {
            const isActive = activeCo === co.id;
            return (
              <div key={co.id} style={{ marginBottom: 1 }}>
                <button
                  onClick={() => navigate(co.id, co.menu[0].id)}
                  aria-current={isActive ? "page" : undefined}
                  aria-label={`${getCompanyLabel(co)}${isActive ? " (activo)" : ""}`}
                  title={collapsed && !isMobile ? co.label : undefined}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: collapsed && !isMobile ? "10px 0" : "9px 11px",
                    justifyContent: collapsed && !isMobile ? "center" : "flex-start",
                    borderRadius: 10, border: "none", cursor: "pointer",
                    width: "100%", textAlign: "left",
                    background: isActive ? `${co.color}14` : "transparent",
                    borderLeft: !collapsed || isMobile
                      ? `2px solid ${isActive ? co.color : "transparent"}`
                      : "none",
                    transition: "all 0.14s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = isActive ? `${co.color}14` : "rgba(255,255,255,0.04)")}
                  onMouseLeave={e => (e.currentTarget.style.background = isActive ? `${co.color}14` : "transparent")}>
                  <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1 }}>{co.icon}</span>
                  {(!collapsed || isMobile) && (
                    <span style={{
                      fontSize: 12, fontWeight: isActive ? 700 : 500,
                      color: isActive ? co.color : T.textSub,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      flex: 1,
                    }}>{getCompanyLabel(co)}</span>
                  )}
                </button>

                {/* Sub-menu */}
                {isActive && (!collapsed || isMobile) && (
                  <div style={{ paddingLeft: 14, marginTop: 2, marginBottom: 4 }}>
                    {co.menu.map(item => {
                      const isPage = activePage === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => navigate(co.id, item.id)}
                          aria-current={isPage ? "page" : undefined}
                          style={{
                            display: "flex", alignItems: "center", gap: 8,
                            padding: "7px 10px",
                            borderRadius: 8, border: "none", cursor: "pointer",
                            width: "100%", textAlign: "left",
                            background: isPage ? `${co.color}18` : "transparent",
                            color: isPage ? co.color : item.sub ? T.muted : T.textSub,
                            fontSize: item.sub ? 11 : 12,
                            fontWeight: isPage ? 600 : 400,
                            transition: "all 0.12s",
                            paddingLeft: item.sub ? 20 : 10,
                            marginBottom: 1,
                          }}
                          onMouseEnter={e => { if (!isPage) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                          onMouseLeave={e => { if (!isPage) e.currentTarget.style.background = "transparent"; }}>
                          <span style={{ fontSize: 12, flexShrink: 0, opacity: item.sub ? 0.6 : 0.85 }}>{item.icon}</span>
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom version indicator */}
        {(!collapsed || isMobile) && (
          <div style={{
            padding: "10px 14px", borderTop: `1px solid ${T.border}`,
            fontSize: 11, color: T.muted, letterSpacing: "0.08em",
            display: "flex", justifyContent: "space-between", flexShrink: 0,
          }}>
            <span>KAIROS OS</span>
            <span style={{ color: T.blueL }}>v{APP_VERSION}</span>
          </div>
        )}
      </nav>

      {/* ── MAIN CONTENT ── */}
      <main id="main-content" style={{
        flex: 1, display: "flex", flexDirection: "column",
        minWidth: 0, overflow: "hidden",
        marginLeft: isMobile ? 0 : 0,
      }}>

        {/* ── HEADER ── */}
        <header style={{
          height: 54,
          padding: "0 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${T.border}`,
          background: "rgba(5,8,16,0.88)",
          backdropFilter: "blur(20px)",
          position: "sticky", top: 0, zIndex: 30,
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Mobile hamburger */}
            {isMobile && (
              <button
                onClick={() => setMobileOpen(true)}
                aria-label="Abrir menu de navegação"
                aria-expanded={mobileOpen}
                aria-controls="main-nav"
                style={{
                  background: "transparent", border: `1px solid ${T.border}`,
                  borderRadius: 8, color: T.textSub, cursor: "pointer",
                  width: 44, height: 44, display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 18, marginRight: 4,
                }}>
                ☰
              </button>
            )}

            {/* ← Back / Forward → */}
            <div style={{ display: "flex", gap: 2, marginRight: 4 }}>
              <button
                onClick={navigateBack}
                disabled={navHistory.length === 0}
                aria-label="Página anterior"
                title="Voltar (Alt + ←)"
                style={{
                  background: "transparent",
                  border: `1px solid ${navHistory.length > 0 ? T.border : "transparent"}`,
                  borderRadius: 8, cursor: navHistory.length > 0 ? "pointer" : "default",
                  width: 32, height: 32, display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 14,
                  color: navHistory.length > 0 ? T.textSub : T.muted,
                  opacity: navHistory.length > 0 ? 1 : 0.3,
                  transition: "all 0.15s",
                }}>
                ←
              </button>
              <button
                onClick={navigateForward}
                disabled={navFuture.length === 0}
                aria-label="Página seguinte"
                title="Avançar (Alt + →)"
                style={{
                  background: "transparent",
                  border: `1px solid ${navFuture.length > 0 ? T.border : "transparent"}`,
                  borderRadius: 8, cursor: navFuture.length > 0 ? "pointer" : "default",
                  width: 32, height: 32, display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 14,
                  color: navFuture.length > 0 ? T.textSub : T.muted,
                  opacity: navFuture.length > 0 ? 1 : 0.3,
                  transition: "all 0.15s",
                }}>
                →
              </button>
            </div>
            <div style={{
              width: 8, height: 8, borderRadius: "50%",
              background: company?.color || T.blueL,
              boxShadow: `0 0 8px ${company?.color || T.blueL}`,
            }}/>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
              {company?.icon} {getCompanyLabel(company || {})}
            </span>
            {!isMobile && (
              <>
                <span style={{ fontSize: 11, color: T.border, margin: "0 2px" }}>›</span>
                <span style={{ fontSize: 12, color: T.textSub }}>
                  {company?.menu?.find((m:any) => m.id === activePage)?.label || ""}
                </span>
              </>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {!isMobile && (
              <span style={{ fontSize: 11, color: T.muted }}>
                {new Date().toLocaleDateString("pt-PT", { day: "numeric", month: "short", year: "numeric" })}
              </span>
            )}
            <NotifBell vjEvents={vjEventsGlobal} alRes={alResGlobal} hw3dProjects={hw3dProjGlobal} impostos={[]}/>
            <UserMenu
              user={loggedUser}
              onLogout={handleLogout}
              themeKey={themeKey}
              toggleTheme={toggleTheme}
              lang={lang}
              cycleLang={cycleLang}
            />
          </div>
        </header>

        {/* ── PAGE CONTENT ── */}
        <div
          ref={mainRef}
          style={{
            flex: 1,
            overflowY: "auto",
            padding: isMobile ? "16px 14px 24px" : "24px 26px",
          }}>
          <div className="page-enter">
            {renderPage()}
          </div>
        </div>
      </main>
    </div>
    </>
  );
}
